import React, { useState, useEffect } from "react";
import {
  ShoppingCart,
  Users,
  Package,
  CreditCard,
  DollarSign,
  Minus,
  Plus,
  Trash2,
  Search,
  AlertTriangle,
  Check,
  LogOut,
  User,
  Edit,
  Save,
  X,
  UserPlus,
  Eye,
  PackagePlus,
  Settings,
  TrendingUp,
  TrendingDown,
  Crown,
  Award,
  Star,
  Tag,
  Download,
  Calendar,
  BarChart3,
  TrendDown,
  FileSpreadsheet,
  Shield,
  UserCheck,
  UserX,
  Key,
  Power,
  Database,
  Banknote,
  Clock,
  Pause,
  Play,
  Bell,
  BellRing,
  Activity,
  FileText,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Printer,
} from "lucide-react";
import { useToast } from "./hooks/use-toast";
import { Toaster } from "./components/ui/toaster";
import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Separator } from "./components/ui/separator";
import { Alert, AlertDescription } from "./components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./components/ui/alert-dialog";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import "./App.css";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./components/ui/dropdown-menu";

// Product categories distinction
const itemCategories = [
  "Golf Equipment Rental",
  "Merchandise",
  "Food & Beverage"
];
// "Golf Rates" and "Driving Range Rates" are considered services

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const readStoredSession = () => {
  try {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) return { token: null, user: null };
    if (!localStorage.getItem("last_activity")) {
      localStorage.setItem("last_activity", String(Date.now()));
    }
    let storedUser = null;
    try {
      const raw = localStorage.getItem("user");
      storedUser = raw ? JSON.parse(raw) : null;
    } catch (e) {
      storedUser = null;
    }
    return { token: storedToken, user: storedUser };
  } catch (e) {
    return { token: null, user: null };
  }
};

const findTxnForLog = (log, txns) => {
  if (!log || !Array.isArray(txns) || txns.length === 0) return null;
  if (log.transaction_id) {
    const byId = txns.find((t) => String(t.id) === String(log.transaction_id));
    if (byId) return byId;
  }
  if (log.or_number) {
    return txns.find((t) => String(t.or_number) === String(log.or_number)) || null;
  }
  return null;
};
// Add this once
const DRIVING_RANGE_DURATIONS = {
  "30 Min - Driving Range": 30,
  "1 Hour - Driving Range": 60,
  "2 Hour - Driving Range": 120,
};

// Build an ISO string pinned to Asia/Manila (UTC+08:00)
function toManilaOffsetIso(date) {
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = f.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}+08:00`;
}

const MANILA_TZ = "Asia/Manila";

function manilaNowDate() {
  // Represents the current instant, safely constructed using Manila local parts.
  return new Date(toManilaOffsetIso(new Date()));
}

function manilaDateFromHHMM(hhmm, referenceDate = new Date()) {
  const s = String(hhmm || "").trim();
  if (!/^\d{1,2}:\d{2}$/.test(s)) return null;

  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: MANILA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = f.formatToParts(referenceDate).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  const [hRaw, mRaw] = s.split(":");
  const hh = String(Number(hRaw)).padStart(2, "0");
  const mm = String(Number(mRaw)).padStart(2, "0");
  return new Date(`${parts.year}-${parts.month}-${parts.day}T${hh}:${mm}:00+08:00`);
}

function manilaHHMM(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: MANILA_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(d)
    .reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
  return `${parts.hour}:${parts.minute}`;
}

function manilaTimeLabel(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: MANILA_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}



// Login Component
function LoginForm({ onLogin }) {
  const [credentials, setCredentials] = useState({
    name: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Login failed");
      }

      const data = await response.json();
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));

      onLogin(data.user, data.access_token);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      <Card className="w-full max-w-md shadow-2xl">
<CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-emerald-500 to-teal-500">
                          <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:space-x-4 transition-all duration-300">
                            <div className="bg-white border-2 border-gray-200 rounded-lg flex-shrink-0 flex items-center justify-center"
                                style={{ width: "90px", height: "90px" }}>
                              <img
                                src="https://customer-assets.emergentagent.com/job_clubhouse-pos/artifacts/2tien3l0_IMG_0267.png"
                                alt="Pili Grove Golf Club Logo"
                                className="object-contain w-full h-full aspect-square"
                                draggable={false}
                              />
                            </div>
                            <div className="text-center sm:text-left min-w-0">
                              <CardTitle
                                className="font-bold transition-all duration-300"
                                style={{
                                  fontSize: "clamp(1rem, 4vw, 1.7rem)", // 20px–36px
                                  lineHeight: 1.1,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis"
                                }}
                              >
                                PILI GROVE GOLF CLUB
                              </CardTitle>
                              <CardDescription
                                className="text-emerald-100 transition-all duration-300"
                                style={{
                                  fontSize: "clamp(0.85rem, 2vw, 1.125rem)" // 13.6px–18px
                                }}
                              >
                                Point of Sale System
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
        <CardContent className="p-4">
          <form onSubmit={handleLogin} className="space-y-4">
            
            <div >
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={credentials.name}
                onChange={(e) =>
                  setCredentials({ ...credentials, name: e.target.value })
                }
                required
                placeholder="Enter your name"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                required
                placeholder="Enter your password"
              />
            </div>

            {error && (
              <Alert className="border-red-500 bg-red-50">
                <AlertDescription className="text-red-700">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* <div className="mt-6 text-sm text-center text-gray-600">
            <p>
              <strong>Demo Accounts:</strong>
            </p>
            <p>Admin: admin / admin123</p>
            <p>Cashier: cashier / cashier123</p>
          </div> */}
        </CardContent>
      </Card>
    </div>
  );
}


// Product Management Component
function ProductManagement({ token, user, fetchLowStockItems }) {
  const [products, setProducts] = useState([]);


  const [cart, setCart] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isStockDialogOpen, setIsStockDialogOpen] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    category: "",
    price: "",
    description: "",
    stock_quantity: "",
    low_stock_threshold: 5,
  });
// --- NEW: initial product form + helper to open Create Product dialog with cleared form
  const initialProductForm = {
    name: "",
    category: "",
    price: "",
    description: "",
    stock_quantity: "",
    low_stock_threshold: 5,
    duration: ""
  };

  const openCreateProductDialog = () => {
    setSelectedProduct(null);
    setProductForm(initialProductForm);
    setIsCreateDialogOpen(true);
  };
  const [stockAdjustment, setStockAdjustment] = useState({
    adjustment_type: "add",
    quantity: "",
    reason: "",
  });


  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

// use effect edit product

  // useEffect(() => {
  //   if (
  //     isEditDialogOpen &&
  //     productForm.category === "Driving Range Rates" &&
  //     productForm.name
  //   ) {
  //     let autoDuration = "";
  //     const hourRegex = /(\d+)\s*(hour|hr|hrs|hours)\b/gi;
  //     const minRegex = /(\d+)\s*(min|mins|minute|minutes)\b/gi;
  //     const hourMatch = [...productForm.name.matchAll(hourRegex)];
  //     const minMatch = [...productForm.name.matchAll(minRegex)];
  //     if (hourMatch.length > 0) autoDuration = parseInt(hourMatch[0][1], 10) * 60;
  //     else if (minMatch.length > 0) autoDuration = parseInt(minMatch[0][1], 10);

  //     if (autoDuration && productForm.duration !== autoDuration) {
  //       setProductForm((prev) => ({
  //         ...prev,
  //         duration: autoDuration,
  //       }));
  //     }
  //   }
  // }, [isEditDialogOpen, productForm.name, productForm.category]);


  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

const handleCreateProduct = async (e) => {
  e.preventDefault();
  let payload = {
    name: productForm.name,
    category: productForm.category,
    price: parseFloat(productForm.price),
    description: productForm.description,
  };
  // Only add stock fields for item categories
  if (itemCategories.includes(productForm.category)) {
    payload.stock_quantity = parseInt(productForm.stock_quantity);
    payload.low_stock_threshold = parseInt(productForm.low_stock_threshold);
  }

  // Add duration for Driving Range Rates (ensure numeric)
  if (productForm.category === "Driving Range Rates" && productForm.duration !== "" && productForm.duration !== undefined) {
    const dur = Number(productForm.duration);
    if (!Number.isNaN(dur)) payload.duration = Math.round(dur);
  }

  try {
    const response = await fetch(`${BACKEND_URL}/api/products`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      setIsCreateDialogOpen(false);
      setProductForm({
        name: "",
        category: "",
        price: "",
        description: "",
        stock_quantity: "",
        low_stock_threshold: 5,
        duration: ""
      });
      fetchProducts();
      fetchLowStockItems();
      fetchCategories();
    } else {
      const error = await response.json();
      alert(error.detail || "Failed to create product");
    }
  } catch (error) {
    console.error("Error creating product:", error);
  }
};
// Update the handleUpdateProduct function in ProductManagement component
const handleUpdateProduct = async (e) => {
  e.preventDefault();
  
  if (!selectedProduct || !selectedProduct.id) {
    alert("No product selected for update");
    return;
  }
  
  try {
    console.log("🔍 Updating product:", selectedProduct.id);
    console.log("🔍 Update data:", productForm);
    
    const updateData = {
      ...productForm,
      price: productForm.price ? parseFloat(productForm.price) : undefined,
      stock_quantity: productForm.stock_quantity ? parseInt(productForm.stock_quantity) : undefined,
      low_stock_threshold: productForm.low_stock_threshold ? parseInt(productForm.low_stock_threshold) : undefined,
    };

    // Remove undefined values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined || updateData[key] === "") {
        delete updateData[key];
      }
    });

    const response = await fetch(`${BACKEND_URL}/api/products/${selectedProduct.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updateData),
    });

    if (response.ok) {
      const result = await response.json();
      console.log("✅ Product updated:", result);
      
      setIsEditDialogOpen(false);
      setSelectedProduct(null);
      setProductForm({
        name: "",
        category: "",
        price: "",
        description: "",
        stock_quantity: "",
        low_stock_threshold: 5,
      });
      
      fetchProducts();
      fetchLowStockItems();
      
    } else {
      const errorData = await response.json();
      console.error("❌ Update failed:", errorData);
      
      if (response.status === 404) {
        alert(`Product not found. It may have been deleted by another user. Error: ${errorData.detail}`);
        fetchProducts(); // Refresh the list
      } else {
        alert(`Failed to update product: ${errorData.detail || 'Unknown error'}`);
      }
    }
  } catch (error) {
    console.error("❌ Error updating product:", error);
    alert(`Error updating product: ${error.message}`);
  }
};

// TODO: allow deleting products only when they have no transactions
  const handleDeleteProduct = async (productId, productName) => {
    if (user?.role !== "admin") {
      alert("Access denied. Only admin users can delete products.");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete "${productName}"? This action cannot be undone.`
      )
    ) {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/products/${productId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          fetchProducts();
          fetchLowStockItems();
        } else {
          const error = await response.json();
          alert(error.detail || "Failed to delete product");
        }
      } catch (error) {
        console.error("Error deleting product:", error);
        alert("Error deleting product");
      }
    }
  };


  const handleStockAdjustment = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/products/${selectedProduct.id}/adjust-stock`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...stockAdjustment,
            quantity: parseInt(stockAdjustment.quantity),
          }),
        }
      );

      if (response.ok) {
        setIsStockDialogOpen(false);
        setStockAdjustment({
          adjustment_type: "add",
          quantity: "",
          reason: "",
        });
        setSelectedProduct(null);
        fetchProducts();
        fetchLowStockItems();
      }
    } catch (error) {
      console.error("Error adjusting stock:", error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Product Management</h2>
          <p className="text-gray-600">
            Manage your golf club products and inventory
          </p>
        </div>
        <Button
          onClick={openCreateProductDialog}
           className="bg-emerald-500 hover:bg-emerald-600"
         >
           <PackagePlus className="w-4 h-4 mr-2" />
           Add Product
         </Button>
      </div>

      {/* Categories Overview */}
      {categories.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {categories.map((category) => (
            <Card
              key={category.name}
              className="border-l-4 border-l-emerald-500"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {category.count} products
                    </p>
                  </div>
                  <Package className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => (
          <Card key={product.id} className="transition-shadow hover:shadow-lg">
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-gray-800">
                    {product.name}
                  </h3>
                  <Badge
                    style={{ pointerEvents: "none" }}
                    variant="outline"
                    className="text-emerald-600 border-emerald-300"
                  >
                    ₱{product.price.toFixed(2)}
                  </Badge>
                </div>

                <p className="text-sm text-gray-600">{product.description}</p>

                <div className="flex items-center justify-between">
                  <div className="flex flex-row gap-1">
                    <Badge
                      style={{ pointerEvents: "none" }}
                      variant="secondary"
                      className="text-blue-800 bg-blue-100"
                    >
                      {product.category}
                    </Badge>
                    <Badge
                      style={{ pointerEvents: "none" }}
                      variant={product.is_active ? "default" : "secondary"}
                      className={product.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}
                    >
                      {product.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                    {itemCategories.includes(product.category) && (
                      <>
                        <Badge
                          style={{ pointerEvents: "none" }}
                          variant={
                            product.stock_quantity <= product.low_stock_threshold
                              ? "destructive"
                              : "default"
                          }
                          className={
                            product.stock_quantity <= product.low_stock_threshold
                              ? ""
                              : "bg-green-100 text-green-800"
                          }
                        >
                          Stock: {product.stock_quantity}
                        </Badge>
                      </>
                    )}
                </div>
                  
                <div className="text-xs text-gray-500">
                    {itemCategories.includes(product.category) && (
                  <p>Low stock threshold: {product.low_stock_threshold}</p>  
                    )}
                  <p>
                    Updated:{" "}
                    {new Date(product.updated_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex space-x-2">
                      {itemCategories.includes(product.category) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedProduct(product);
                            setStockAdjustment({
                              adjustment_type: "add",
                              quantity: "",
                              reason: "",
                            });
                            setIsStockDialogOpen(true);
                          }}
                          className="flex-1"
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          Stock
                        </Button>
                      )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedProduct(product);
                      setProductForm(product);
                      setIsEditDialogOpen(true);
                    }}
                    className="flex-1"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {user?.role === "admin" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        handleDeleteProduct(product.id, product.name)
                      }
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Product Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
          </DialogHeader>
<form onSubmit={handleCreateProduct} className="space-y-4">

<div>
  <Label htmlFor="name">Product Name</Label>
  <Input
    id="name"
    value={productForm.name}
    onChange={(e) => {
      const name = e.target.value;
      let autoDuration = "";
      // Only auto-detect duration for Driving Range Rates
      if (productForm.category === "Driving Range Rates") {
        const hourRegex = /(\d+)\s*(hour|hr|hrs|hours)\b/gi;
        const minRegex = /(\d+)\s*(min|mins|minute|minutes)\b/gi;
        const hourMatch = [...name.matchAll(hourRegex)];
        const minMatch = [...name.matchAll(minRegex)];
        if (hourMatch.length > 0) autoDuration = parseInt(hourMatch[0][1], 10) * 60;
        else if (minMatch.length > 0) autoDuration = parseInt(minMatch[0][1], 10);
      }
      setProductForm({
        ...productForm,
        name,
        duration: productForm.category === "Driving Range Rates" ? autoDuration || "" : ""
      });
    }}
    required
        placeholder={
          productForm.category === "Driving Range Rates"
            ? "e.g. Driving Range 1 hour or Driving Range 30 min"
            : "Enter product name"
        }
    
  />
  {productForm.category === "Driving Range Rates" && productForm.duration && (
    <div className="text-xs text-gray-500 mt-1">
      Auto-detected duration: {productForm.duration} minutes
    </div>
  )}
</div>

  <div>
    <Label htmlFor="category">Category</Label>
    <Select
      value={productForm.category}
      onValueChange={(value) =>
        setProductForm({ ...productForm, category: value })
      }
    >
      <SelectTrigger>
        <SelectValue placeholder="Select category" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="Golf Equipment Rental">
          Golf Equipment Rental
        </SelectItem>
        <SelectItem value="Golf Rates">Golf Rates</SelectItem>
        <SelectItem value="Driving Range Rates">
          Driving Range Rates
        </SelectItem>
        <SelectItem value="Food & Beverage">
          Food & Beverage
        </SelectItem>
        <SelectItem value="Merchandise">Merchandise</SelectItem>
      </SelectContent>
    </Select>
  </div>
  <div>
    <Label htmlFor="price">Price (₱)</Label>
    <Input
      id="price"
      type="number"
      step="0.01"
      min="0"
      value={productForm.price}
      onChange={(e) =>
        setProductForm({ ...productForm, price: e.target.value })
      }
      required
    />
  </div>
  <div>
    <Label htmlFor="description">Description</Label>
    <Textarea
      id="description"
      value={productForm.description}
      onChange={(e) =>
        setProductForm({
          ...productForm,
          description: e.target.value,
        })
      }
      rows={3}
      required
    />
  </div>

  {/* Show duration field only for Driving Range Rates */}
  {productForm.category === "Driving Range Rates" && (
<div>
  <Label htmlFor="duration">Duration (minutes)</Label>
  <Input
    id="duration"
    type="number"
    min="1"
    value={productForm.duration || ""}
    onChange={e =>
      setProductForm({ ...productForm, duration: e.target.value })
    }
    required
    placeholder="e.g. 60 for 1 hour"
  />
  {productForm.duration && (
    <div className="text-xs text-gray-500 mt-1">
      Auto-filled from product name. Edit if needed.
    </div>
  )}
</div>
  )}

  {/* Only show stock fields for item categories */}
  {itemCategories.includes(productForm.category) && (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="stock_quantity">Initial Stock</Label>
        <Input
          id="stock_quantity"
          type="number"
          min="0"
          value={productForm.stock_quantity}
          onChange={(e) =>
            setProductForm({
              ...productForm,
              stock_quantity: e.target.value,
            })
          }
          required
        />
      </div>
      <div>
        <Label htmlFor="low_stock_threshold">Low Stock Threshold</Label>
        <Input
          id="low_stock_threshold"
          type="number"
          min="0"
          value={productForm.low_stock_threshold}
          onChange={(e) =>
            setProductForm({
              ...productForm,
              low_stock_threshold: e.target.value,
            })
          }
          required
        />
      </div>
    </div>
  )}
  <div className="flex justify-end space-x-2">
    <Button
      type="button"
      variant="outline"
      onClick={() => setIsCreateDialogOpen(false)}
    >
      Cancel
    </Button>
    <Button
      type="submit"
      className="bg-emerald-500 hover:bg-emerald-600"
    >
      Create Product
    </Button>
  </div>
</form>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
  <DialogContent className="max-w-md">
    <DialogHeader>
      <DialogTitle>Edit Product</DialogTitle>
    </DialogHeader>
    <form onSubmit={handleUpdateProduct} className="space-y-4">
      
<div>
  <Label htmlFor="name">Product Name</Label>
  <Input
    id="name"
    value={productForm.name}
    onChange={(e) => {
      const name = e.target.value;
      let autoDuration = "";
      // Only auto-detect duration for Driving Range Rates
      if (productForm.category === "Driving Range Rates") {
        const hourRegex = /(\d+)\s*(hour|hr|hrs|hours)\b/gi;
        const minRegex = /(\d+)\s*(min|mins|minute|minutes)\b/gi;
        const hourMatch = [...name.matchAll(hourRegex)];
        const minMatch = [...name.matchAll(minRegex)];
        if (hourMatch.length > 0) autoDuration = parseInt(hourMatch[0][1], 10) * 60;
        else if (minMatch.length > 0) autoDuration = parseInt(minMatch[0][1], 10);
      }
      setProductForm({
        ...productForm,
        name,
        duration: productForm.category === "Driving Range Rates" ? autoDuration || "" : ""
      });
    }}
    required
        placeholder={
          productForm.category === "Driving Range Rates"
            ? "e.g. Driving Range 1 hour or Driving Range 30 min"
            : "Enter product name"
        }
    
  />
  {productForm.category === "Driving Range Rates" && productForm.duration && (
    <div className="text-xs text-gray-500 mt-1">
      Auto-detected duration: {productForm.duration} minutes
    </div>
  )}
</div>
      <div>
        <Label htmlFor="edit_is_active">Status</Label>
        <Select
          value={productForm.is_active ? "active" : "inactive"}
          onValueChange={(value) =>
            setProductForm({ ...productForm, is_active: value === "active" })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="edit_category">Category</Label>
        <Select
          value={productForm.category}
          onValueChange={(value) =>
            setProductForm({ ...productForm, category: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Golf Equipment Rental">
              Golf Equipment Rental
            </SelectItem>
            <SelectItem value="Golf Rates">Golf Rates</SelectItem>
            <SelectItem value="Driving Range Rates">
              Driving Range Rates
            </SelectItem>
            <SelectItem value="Food & Beverage">
              Food & Beverage
            </SelectItem>
            <SelectItem value="Merchandise">Merchandise</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="edit_price">Price (₱)</Label>
        <Input
          id="edit_price"
          type="number"
          step="0.01"
          min="0"
          value={productForm.price}
          onChange={(e) =>
            setProductForm({ ...productForm, price: e.target.value })
          }
          required
        />
      </div>
      <div>
        <Label htmlFor="edit_description">Description</Label>
        <Textarea
          id="edit_description"
          value={productForm.description}
          onChange={(e) =>
            setProductForm({
              ...productForm,
              description: e.target.value,
            })
          }
          rows={3}
          required
        />
      </div>

      {/* Show duration field only for Driving Range Rates */}
      {productForm.category === "Driving Range Rates" && (
<div>
  <Label htmlFor="duration">Duration (minutes)</Label>
  <Input
    id="duration"
    type="number"
    min="1"
    value={productForm.duration || ""}
    onChange={e =>
      setProductForm({ ...productForm, duration: e.target.value })
    }
    required
    placeholder="e.g. 60 for 1 hour"
  />
  {productForm.duration && (
    <div className="text-xs text-gray-500 mt-1">
      Auto-filled from product name. Edit if needed.
    </div>
  )}
</div>
      )}

      {itemCategories.includes(productForm.category) && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="edit_stock_quantity">Current Stock</Label>
            <Input
              id="edit_stock_quantity"
              type="number"
              min="0"
              value={productForm.stock_quantity}
              onChange={(e) =>
                setProductForm({
                  ...productForm,
                  stock_quantity: e.target.value,
                })
              }
              required
            />
          </div>
          <div>
            <Label htmlFor="edit_low_stock_threshold">
              Low Stock Threshold
            </Label>
            <Input
              id="edit_low_stock_threshold"
              type="number"
              min="0"
              value={productForm.low_stock_threshold}
              onChange={(e) =>
                setProductForm({
                  ...productForm,
                  low_stock_threshold: e.target.value,
                })
              }
              required
            />
          </div>
        </div>
      )}
      <div className="flex justify-end space-x-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsEditDialogOpen(false)}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          Update Product
        </Button>
      </div>
    </form>
  </DialogContent>
</Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={isStockDialogOpen} onOpenChange={setIsStockDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
            <DialogDescription>
              {selectedProduct &&
                `Current stock: ${selectedProduct.stock_quantity} units`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStockAdjustment} className="space-y-4">
            <div>
              <Label htmlFor="adjustment_type">Adjustment Type</Label>
              <Select
                value={stockAdjustment.adjustment_type}
                onValueChange={(value) =>
                  setStockAdjustment({
                    ...stockAdjustment,
                    adjustment_type: value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span>Add Stock</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="subtract">
                    <div className="flex items-center space-x-2">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      <span>Subtract Stock</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="set">
                    <div className="flex items-center space-x-2">
                      <Settings className="w-4 h-4 text-blue-600" />
                      <span>Set Stock Level</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="quantity">
                {stockAdjustment.adjustment_type === "set"
                  ? "New Stock Level"
                  : "Quantity"}
              </Label>
              <Input
                id="quantity"
                type="number"
                min="0"
                value={stockAdjustment.quantity}
                onChange={(e) =>
                  setStockAdjustment({
                    ...stockAdjustment,
                    quantity: e.target.value,
                  })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                value={stockAdjustment.reason}
                onChange={(e) =>
                  setStockAdjustment({
                    ...stockAdjustment,
                    reason: e.target.value,
                  })
                }
                rows={2}
                placeholder="e.g., Received new shipment, Damaged items, Stock count correction"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsStockDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Adjust Stock
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}




// User Management Component
function UserManagement({ token, user }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    username: "",
    email: "",
    full_name: "",
    role: "cashier",
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [createPassword, setCreatePassword] = useState("");
  const [confirmCreatePassword, setConfirmCreatePassword] = useState("");

  // --- NEW: initial form + helper to open Create User dialog with cleared form
  const initialUserForm = {
    username: "",
    email: "",
    full_name: "",
    role: "cashier",
  };

  const openCreateUserDialog = () => {
    // clear any previously selected user (avoid reusing edit values)
    setSelectedUser(null);

    // clear username/email/full name/role
    setUserForm(initialUserForm);
    // clear password fields as well
    setCreatePassword("");
    setConfirmCreatePassword("");

    setIsCreateDialogOpen(true);
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        console.error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (createPassword !== confirmCreatePassword) {
      alert("Passwords do not match");
      return;
    }

    if (createPassword.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...userForm,
          password: createPassword,
        }),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setUserForm({
          username: "",
          email: "",
          full_name: "",
          role: "cashier",
        });
        setCreatePassword("");
        setConfirmCreatePassword("");
        fetchUsers();
        alert("User created successfully");
      } else {
        let message = "Failed to create user";
        try {
          const error = await response.json();
          message =
            error?.detail ||
            error?.message ||
            error?.error ||
            `Failed to create user (HTTP ${response.status})`;
        } catch {
          try {
            const text = await response.text();
            message = text
              ? `${text} (HTTP ${response.status})`
              : `Failed to create user (HTTP ${response.status})`;
          } catch {
            message = `Failed to create user (HTTP ${response.status})`;
          }
        }
        alert(message);
      }
    } catch (error) {
      console.error("Error creating user:", error);
      alert("Error creating user");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/users/${selectedUser.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(userForm),
        }
      );

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
        alert("User updated successfully");
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Error updating user");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (
      window.confirm(
        `Are you sure you want to delete user "${userName}"? This action cannot be undone.`
      )
    ) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          fetchUsers();
          alert("User deleted successfully");
        } else {
          const error = await response.json();
          alert(error.detail || "Failed to delete user");
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Error deleting user");
      }
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const action = currentStatus ? "deactivate" : "activate";
    if (window.confirm(`Are you sure you want to ${action} this user?`)) {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/users/${userId}/toggle-status`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          fetchUsers();
          const newStatus = currentStatus ? "deactivated" : "activated";
          alert(`User ${newStatus} successfully`);
        } else {
          const error = await response.json();
          alert(error.detail || `Failed to ${action} user`);
        }
      } catch (error) {
        console.error(`Error ${action}ing user:`, error);
        alert(`Error ${action}ing user`);
      }
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert("New passwords do not match");
      return;
    }

    if (passwordForm.new_password.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/users/${selectedUser.id}/password`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            current_password: passwordForm.current_password || undefined,
            new_password: passwordForm.new_password,
          }),
        }
      );

      if (response.ok) {
        setIsPasswordDialogOpen(false);
        setPasswordForm({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
        setSelectedUser(null);
        alert("Password updated successfully");
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to update password");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      alert("Error updating password");
    }
  };

function singaporeTimeRange(times) {
  if (!Array.isArray(times) || times.length === 0) return "6:00 AM - 6:00 PM";
  // Sort times (assume "HH:mm" format)
  const sorted = times
    .filter(Boolean)
    .sort((a, b) => {
      const [aH, aM] = a.split(':').map(Number);
      const [bH, bM] = b.split(':').map(Number);
      return aH !== bH ? aH - bH : aM - bM;
    });
  const format = (t) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 === 0 ? 12 : h % 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
  };
  return `${format(sorted[0])} - ${format(sorted[sorted.length - 1])}`;
}


const formatSingaporeDateTime = (isoString) => {
  if (!isoString) return "Never";
  return new Date(isoString).toLocaleString("en-SG", {
    timeZone: "Asia/Singapore",
  });
};
  const formatDateTime = (isoString) => {
    if (!isoString) return "Never";
    return new Date(isoString).toLocaleString("en-PH");
  };

  const getRoleIcon = (role) => {
    if (role === "admin") return Shield;
    if (role === "puncher") return Clock;
    return User;
  };

  const getRoleColor = (role) => {
    if (role === "admin") return "text-red-600 bg-red-100";
    if (role === "puncher") return "text-yellow-600 bg-yellow-100";
    return "text-blue-600 bg-blue-100";
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Only show to admin users
  if (user?.role !== "admin") {
    return (
      <div className="py-12 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <h2 className="mb-2 text-xl font-semibold text-gray-600">
          Access Restricted
        </h2>
        <p className="text-gray-500">
          Only admin users can manage system users.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-gray-600">
            Manage system users, roles, and permissions
          </p>
        </div>
        <Button
          onClick={openCreateUserDialog}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {users.map((userItem) => {
          const RoleIcon = getRoleIcon(userItem.role);
          const roleColorClass = getRoleColor(userItem.role);
          const isExternalUser = String(userItem.id || "").startsWith("ext-");

          return (
            <Card
              key={userItem.id}
              className="transition-shadow border-l-4 hover:shadow-lg border-l-emerald-500"
            >
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`flex items-center space-x-2 p-2 rounded-full ${roleColorClass}`}>
                        <RoleIcon className="w-5 h-5" />
                        <Badge
                          variant="outline"
                          className={roleColorClass}
                          style={{ border: "none" }}
                        >
                          {userItem.role.charAt(0).toUpperCase() + userItem.role.slice(1)}
                        </Badge>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          {userItem.full_name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          @{userItem.username}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Badge
                        style={{ pointerEvents: "none" }}
                        variant={userItem.is_active ? "default" : "secondary"}
                      >
                        {userItem.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">{userItem.email}</p>
                    <div className="text-xs text-gray-500">
                      <p>Created: {formatDateTime(userItem.created_date)}</p>
                      <p>Last Login: {formatDateTime(userItem.last_login)}</p>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(userItem);
                        setUserForm({
                          username: userItem.username,
                          email: userItem.email,
                          full_name: userItem.full_name,
                          role: userItem.role,
                        });
                        setIsEditDialogOpen(true);
                      }}
                      className="flex-1"
                      disabled={isExternalUser}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(userItem);
                        setPasswordForm({
                          current_password: "",
                          new_password: "",
                          confirm_password: "",
                        });
                        setIsPasswordDialogOpen(true);
                      }}
                      disabled={isExternalUser}
                    >
                      <Key className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={userItem.is_active ? "outline" : "default"}
                      onClick={() =>
                        handleToggleUserStatus(userItem.id, userItem.is_active)
                      }
                      disabled={isExternalUser || userItem.id === user.id}
                    >
                      {userItem.is_active ? (
                        <UserX className="w-3 h-3" />
                      ) : (
                        <UserCheck className="w-3 h-3" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        handleDeleteUser(userItem.id, userItem.full_name)
                      }
                      disabled={isExternalUser || userItem.id === user.id}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create User Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          // keep the original state setter
          setIsCreateDialogOpen(open);
          // clear form & password when dialog closed so values don't persist
          if (!open) {
            setUserForm(initialUserForm);
            setCreatePassword("");
            setConfirmCreatePassword("");
            setSelectedUser(null);
          } else {
            // also clear when opening to guard against leftover values / browser autofill
            setUserForm(initialUserForm);
            setCreatePassword("");
            setConfirmCreatePassword("");
            setSelectedUser(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={userForm.username}
                onChange={(e) =>
                  setUserForm({ ...userForm, username: e.target.value })
                }
                required
                placeholder="Enter username"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({ ...userForm, email: e.target.value })
                }
                required
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={userForm.full_name}
                onChange={(e) =>
                  setUserForm({ ...userForm, full_name: e.target.value })
                }
                required
                placeholder="Enter full name"
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) =>
                  setUserForm({ ...userForm, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-blue-600" />
                      <span>Cashier</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-red-600" />
                      <span>Admin</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="puncher">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <span>Puncher</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                required
                placeholder="Enter password (min 6 characters)"
                minLength={6}
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={confirmCreatePassword}
                onChange={(e) => setConfirmCreatePassword(e.target.value)}
                required
                placeholder="Confirm password"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Create User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div>
              <Label htmlFor="edit_username">Username</Label>
              <Input
                id="edit_username"
                value={userForm.username}
                onChange={(e) =>
                  setUserForm({ ...userForm, username: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({ ...userForm, email: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_full_name">Full Name</Label>
              <Input
                id="edit_full_name"
                value={userForm.full_name}
                onChange={(e) =>
                  setUserForm({ ...userForm, full_name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_role">Role</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) =>
                  setUserForm({ ...userForm, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-blue-600" />
                      <span>Cashier</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-red-600" />
                      <span>Admin</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="puncher">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <span>Puncher</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Update User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Update Dialog */}
      <Dialog
        open={isPasswordDialogOpen}
        onOpenChange={setIsPasswordDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Password</DialogTitle>
            <DialogDescription>
              {selectedUser && `Update password for ${selectedUser.full_name}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            {selectedUser && selectedUser.id === user.id && (
              <div>
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      current_password: e.target.value,
                    })
                  }
                  required
                  placeholder="Enter current password"
                />
              </div>
            )}
            <div>
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    new_password: e.target.value,
                  })
                }
                required
                placeholder="Enter new password (min 6 characters)"
                minLength={6}
              />
            </div>
            <div>
              <Label htmlFor="confirm_new_password">Confirm New Password</Label>
              <Input
                id="confirm_new_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    confirm_password: e.target.value,
                  })
                }
                required
                placeholder="Confirm new password"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPasswordDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Update Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Format transaction timestamp in PH time, 12-hour format, with month name
const formatTransactionDateTime = (isoString) => {
  if (!isoString) return "Never";
  // Ensure the ISO string is treated as UTC if no timezone info present
  let str = String(isoString);
  if (!str.endsWith("Z") && !str.includes("+") && !/[+-]\d{2}:\d{2}$/.test(str)) {
    str += "Z";
  }
  const date = new Date(str);
  if (isNaN(date.getTime())) return "Never";
  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Manila",
  });
};

// Activity Logs Management Component (Admin Only)
function ActivityLogsManagement({ token, user }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;
  
  // Modal state
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [missingDate, setMissingDate] = useState(
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" })
  );
  const [missingData, setMissingData] = useState(null);
  const [isLoadingMissing, setIsLoadingMissing] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

  const fetchLogs = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      
      if (actionFilter) params.append("action", actionFilter);
      if (entityTypeFilter) params.append("entity_type", entityTypeFilter);
      if (userFilter) params.append("user_id", userFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (searchQuery) params.append("search", searchQuery);
      
      const response = await fetch(`${BACKEND_URL}/api/activity-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        setError("Access denied. Only admin users can view activity logs.");
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error("Failed to fetch activity logs");
      }
      
      const data = await response.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error("Error fetching logs:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/activity-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const fetchUsers = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/activity-logs/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, entityTypeFilter, userFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchMissingData();
  }, [token]);

  const fetchMissingData = async () => {
    if (!token || !missingDate) return;
    setIsLoadingMissing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/missing-data?date=${encodeURIComponent(missingDate)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMissingData(await res.json());
      }
    } catch (err) {
      console.error("Error fetching missing data:", err);
    } finally {
      setIsLoadingMissing(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const handleReset = () => {
    setSearchQuery("");
    setActionFilter("");
    setEntityTypeFilter("");
    setUserFilter("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const exportLogs = () => {
    try {
      const csvContent = [
        ["Log ID", "Timestamp", "User", "Role", "Action", "Entity Type", "Entity Name", "Details"].join(","),
        ...logs.map(log => [
          log.id,
          new Date(log.timestamp).toLocaleString(),
          log.user_name,
          log.user_role,
          log.action,
          log.entity_type,
          formatLogEntityLabel(log),
          String(formatLogDetails(log) || "").replace(/,/g, ";")
        ].join(","))
      ].join("\n");
      
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity_logs_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const getActionBadge = (action) => {
    const config = {
      create: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Created" },
      update: { bg: "bg-blue-100", text: "text-blue-700", label: "Updated" },
      delete: { bg: "bg-red-100", text: "text-red-700", label: "Deleted" },
      login: { bg: "bg-purple-100", text: "text-purple-700", label: "Login" },
      logout: { bg: "bg-gray-100", text: "text-gray-700", label: "Logout" },
      stock_adjustment: { bg: "bg-orange-100", text: "text-orange-700", label: "Stock Adj." },
      reschedule: { bg: "bg-indigo-100", text: "text-indigo-700", label: "Rescheduled" },
      clear_logs: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Clear Logs" },
    };
    return config[action] || { bg: "bg-gray-100", text: "text-gray-700", label: action };
  };

  const getEntityBadge = (entityType) => {
    const config = {
      transaction: { bg: "bg-green-100", text: "text-green-700", label: "Transaction" },
      product: { bg: "bg-blue-100", text: "text-blue-700", label: "Product" },
      member: { bg: "bg-purple-100", text: "text-purple-700", label: "Member" },
      user: { bg: "bg-indigo-100", text: "text-indigo-700", label: "User" },
      player_log: { bg: "bg-teal-100", text: "text-teal-700", label: "Player" },
      membership_type: { bg: "bg-pink-100", text: "text-pink-700", label: "Membership Type" },
      activity_log: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Activity Log" },
    };
    return config[entityType] || { bg: "bg-gray-100", text: "text-gray-700", label: entityType || "—" };
  };

  const formatLogActor = (log) => {
    const d = log?.details || {};
    const name = log?.user_name || d.performed_by || d.created_by || d.deleted_by || "Unknown user";
    const username = log?.username || d.performed_by_username || d.created_by_username || d.deleted_by_username || "";
    const role = log?.user_role || d.performed_by_role || d.created_by_role || d.deleted_by_role || "";
    return { name, username, role };
  };

  const formatLogTimestamp = (value) => {
    if (!value) return "—";
    let str = String(value);
    if (!str.endsWith("Z") && !str.includes("+") && !/[+-]\d{2}:\d{2}$/.test(str)) {
      str += "Z";
    }
    const date = new Date(str);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Manila",
    });
  };

  const formatLogMoney = (n) => {
    if (n == null || n === "") return "";
    const num = Number(n);
    if (!Number.isFinite(num)) return "";
    return `₱${num.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
  };

  const formatLogEntityLabel = (log) => {
    const d = log?.details || {};
    const customer = d.member_name || d.player_name || d.customer_name;
    const or = d.or_number;
    const amt = formatLogMoney(d.total_amount ?? d.total);
    const parts = [];
    if (customer) parts.push(customer);
    if (or) parts.push(`OR ${or}`);
    if (amt) parts.push(amt);
    if (parts.length) return parts.join(" · ");
    const name = String(log?.entity_name || "").trim();
    if (name && !/^[0-9a-f]{8}-/i.test(name) && name !== log?.entity_id) return name;
    return "—";
  };

  const formatLogDetails = (log) => {
    const d = log?.details || {};
    const actor = formatLogActor(log);
    const who = actor.name;
    const customer = d.member_name || d.player_name || d.customer_name;
    const or = d.or_number;
    const amt = formatLogMoney(d.total_amount ?? d.total);
    const closedBy = d.closed_by_cashier_name || d.closed_by;
    if (log.action === "login") {
      return `${who} logged in${d.login_method ? ` (${d.login_method})` : ""}`;
    }
    if (log.action === "logout") {
      return `${who} logged out`;
    }
    if (log.entity_type === "transaction") {
      const whoPart = `${who} ${log.action === "delete" ? "deleted" : log.action === "update" ? "updated" : "created"}`;
      const bits = [`${whoPart} transaction`];
      if (customer) bits.push(customer);
      if (or) bits.push(`OR ${or}`);
      if (amt) bits.push(amt);
      if (d.items_count) bits.push(`${d.items_count} item${Number(d.items_count) === 1 ? "" : "s"}`);
      if (closedBy) bits.push(`closed by ${closedBy}`);
      if (d.source === "catalog") bits.push("(catalog)");
      return bits.join(" · ");
    }
    if (log.entity_type === "player_log") {
      const player = customer || log.entity_name || "player";
      const extra = d.transaction_deleted ? " and its transaction" : "";
      const orPart = or ? ` (OR ${or})` : "";
      const punched = d.punched_by_name ? ` · punched by ${d.punched_by_name}` : "";
      return `${who} ${log.action === "delete" ? "deleted" : log.action === "update" ? "updated" : "created"} player ${player}${orPart}${extra}${punched}`;
    }
    if (or && amt) return `OR: ${or} · ${amt}${customer ? ` · ${customer}` : ""}`;
    if (or) return `OR: ${or}${customer ? ` · ${customer}` : ""}`;
    if (customer) return customer;
    if (d.adjustment_type) return `${d.adjustment_type}: ${d.quantity}`;
    return formatLogEntityLabel(log);
  };

  // Access denied view
  if (error === "Access denied. Only admin users can view activity logs.") {
    return (
      <Card className="max-w-2xl mx-auto mt-8">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
          <h2 className="mb-2 text-xl font-bold text-gray-800">Access Denied</h2>
          <p className="text-gray-600">
            Only admin users can view activity logs. This feature is restricted for security purposes.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Activity Logs</h2>
          <p className="text-gray-600">Track all actions and changes in the system</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsStatsModalOpen(true)}
            className="border-gray-300"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Statistics
          </Button>
          <Button
            variant="outline"
            onClick={exportLogs}
            className="border-gray-300"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            onClick={() => fetchLogs()}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Logs</p>
                  <p className="text-xl font-bold text-gray-800">{stats.total_logs?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <Activity className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last 24 Hours</p>
                  <p className="text-xl font-bold text-gray-800">{stats.recent_activity_24h?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Plus className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="text-xl font-bold text-gray-800">{stats.by_action?.create?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Deleted</p>
                  <p className="text-xl font-bold text-gray-800">{stats.by_action?.delete?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border-amber-200">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Missing data
            </span>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={missingDate}
                onChange={(e) => setMissingDate(e.target.value)}
                className="h-8 w-[160px] text-sm"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={fetchMissingData}
                disabled={isLoadingMissing}
                className="h-8"
              >
                {isLoadingMissing ? "Checking..." : "Check day"}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {!missingData ? (
            <p className="text-sm text-gray-500">Check a date to find player logs without a sale, and sales without a player log (orphans like OR 9530051).</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase text-amber-800">
                  Player logs without a transaction ({missingData.logs_without_transaction?.length || 0})
                </p>
                {(missingData.logs_without_transaction || []).length === 0 ? (
                  <p className="text-sm text-gray-500">None</p>
                ) : (
                  <ul className="space-y-1 text-sm max-h-40 overflow-auto">
                    {missingData.logs_without_transaction.map((row) => (
                      <li key={row.id || `${row.or_number}-${row.player_name}`} className="text-gray-800">
                        <span className="font-medium">{row.player_name || "Player"}</span>
                        {row.or_number ? ` · OR ${row.or_number}` : " · no OR"}
                        {row.punched_by_name ? ` · punched by ${row.punched_by_name}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase text-amber-800">
                  Sales without a player log ({missingData.transactions_without_player_log?.length || 0})
                </p>
                {(missingData.transactions_without_player_log || []).length === 0 ? (
                  <p className="text-sm text-gray-500">None</p>
                ) : (
                  <ul className="space-y-1 text-sm max-h-40 overflow-auto">
                    {missingData.transactions_without_player_log.map((row) => (
                      <li key={row.id || row.or_number} className="text-gray-800">
                        <span className="font-medium">{row.member_name || "Walk-in"}</span>
                        {row.or_number ? ` · OR ${row.or_number}` : ""}
                        {row.total_amount != null ? ` · ₱${Number(row.total_amount).toLocaleString("en-PH", { minimumFractionDigits: 2 })}` : ""}
                        {row.closed_by_cashier_name || row.cashier ? ` · ${row.closed_by_cashier_name || row.cashier}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-gray-600">Search</Label>
              <div className="relative">
                <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                <Input
                  placeholder="Search by name, ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-600">Action</Label>
              <Select value={actionFilter || "all"} onValueChange={(val) => setActionFilter(val === "all" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Created</SelectItem>
                  <SelectItem value="update">Updated</SelectItem>
                  <SelectItem value="delete">Deleted</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="logout">Logout</SelectItem>
                  <SelectItem value="stock_adjustment">Stock Adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-600">Entity Type</Label>
              <Select value={entityTypeFilter || "all"} onValueChange={(val) => setEntityTypeFilter(val === "all" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="transaction">Transaction</SelectItem>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="player_log">Player Log</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-600">User</Label>
              <Select value={userFilter || "all"} onValueChange={(val) => setUserFilter(val === "all" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map(u => (
                    <SelectItem key={`${u.user_id}-${u.username || u.user_name}`} value={u.user_id}>
                      {u.user_name}{u.username && u.username !== u.user_name ? ` (@${u.username})` : ""}{u.user_role ? ` · ${u.user_role}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-gray-600">Date From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-gray-600">Date To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2 lg:col-span-2">
              <Button onClick={handleSearch} className="bg-emerald-500 hover:bg-emerald-600">
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
              <Button variant="outline" onClick={handleReset}>
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Activity Log ({total.toLocaleString()} records)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 mb-4 text-gray-400" />
              <p className="text-gray-500">No activity logs found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 font-medium text-left text-gray-600">Timestamp</th>
                      <th className="px-4 py-3 font-medium text-left text-gray-600">User</th>
                      <th className="px-4 py-3 font-medium text-left text-gray-600">Action</th>
                      <th className="px-4 py-3 font-medium text-left text-gray-600">Entity</th>
                      <th className="px-4 py-3 font-medium text-left text-gray-600">Name/ID</th>
                      <th className="px-4 py-3 font-medium text-left text-gray-600">Details</th>
                      <th className="px-4 py-3 font-medium text-left text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const actionConfig = getActionBadge(log.action);
                      const entityConfig = getEntityBadge(log.entity_type);
                      const actor = formatLogActor(log);
                      
                      return (
                        <tr 
                          key={log.id} 
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                            {formatLogTimestamp(log.timestamp)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div>
                                <div className="text-sm font-medium text-gray-800">{actor.name}</div>
                                <div className="text-xs text-gray-500">
                                  {actor.username && actor.username !== actor.name ? `@${actor.username} · ` : ""}
                                  {actor.role || "unknown role"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`${actionConfig.bg} ${actionConfig.text}`}>
                              {actionConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`${entityConfig.bg} ${entityConfig.text}`}>
                              {entityConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {formatLogEntityLabel(log)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatLogDetails(log)}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedLog(log);
                                setIsDetailModalOpen(true);
                              }}
                              className="text-emerald-600 hover:text-emerald-700"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} records
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Activity Log Details</DialogTitle>
            <DialogDescription>
              Complete information about this action
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-6">
              <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">Performed by</p>
                <p className="text-base font-semibold text-gray-900">{formatLogActor(selectedLog).name}</p>
                <p className="text-sm text-gray-600">
                  {formatLogActor(selectedLog).username ? `@${formatLogActor(selectedLog).username}` : "no username"} · {formatLogActor(selectedLog).role || "unknown role"}
                </p>
                <p className="text-sm text-gray-700 mt-2">{formatLogDetails(selectedLog)}</p>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Log ID</Label>
                  <p className="font-mono text-sm text-gray-800">{selectedLog.id}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Timestamp</Label>
                  <p className="text-gray-800">{formatLogTimestamp(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">User</Label>
                  <p className="text-gray-800">{formatLogActor(selectedLog).name}</p>
                  {formatLogActor(selectedLog).username && (
                    <p className="text-xs text-gray-500">@{formatLogActor(selectedLog).username}</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-500">Role</Label>
                  <Badge className="bg-purple-100 text-purple-700">{formatLogActor(selectedLog).role || "unknown"}</Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Action</Label>
                  <Badge className={`${getActionBadge(selectedLog.action).bg} ${getActionBadge(selectedLog.action).text}`}>
                    {getActionBadge(selectedLog.action).label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Entity Type</Label>
                  <Badge className={`${getEntityBadge(selectedLog.entity_type).bg} ${getEntityBadge(selectedLog.entity_type).text}`}>
                    {getEntityBadge(selectedLog.entity_type).label}
                  </Badge>
                </div>
              </div>

              {/* Entity Info */}
              {(selectedLog.entity_id || selectedLog.entity_name) && (
                <div>
                  <Label className="text-gray-500">Entity Information</Label>
                  <div className="p-3 mt-1 rounded-lg bg-gray-50">
                    {selectedLog.entity_name && <p className="text-gray-800"><strong>Name:</strong> {formatLogEntityLabel(selectedLog)}</p>}
                    {selectedLog.entity_id && <p className="font-mono text-sm text-gray-600"><strong>ID:</strong> {selectedLog.entity_id}</p>}
                  </div>
                </div>
              )}

              {/* Action Details */}
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <Label className="text-gray-500">Action Details</Label>
                  <div className="p-4 mt-1 rounded-lg bg-gray-50">
                    <dl className="grid grid-cols-2 gap-3">
                      {Object.entries(selectedLog.details).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-xs text-gray-500 capitalize">{key.replace(/_/g, " ")}</dt>
                          <dd className="text-sm text-gray-800">
                            {typeof value === "object" ? JSON.stringify(value) : String(value)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              )}

              {/* Before/After Data */}
              {(selectedLog.before_data || selectedLog.after_data) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedLog.before_data && (
                    <div>
                      <Label className="text-gray-500">Before (Original)</Label>
                      <pre className="p-3 mt-1 overflow-auto text-xs rounded-lg bg-gray-50 max-h-48">
                        {JSON.stringify(selectedLog.before_data, null, 2)}
                      </pre>
                    </div>
                  )}
                  {selectedLog.after_data && (
                    <div>
                      <Label className="text-gray-500">After (New)</Label>
                      <pre className="p-3 mt-1 overflow-auto text-xs rounded-lg bg-gray-50 max-h-48">
                        {JSON.stringify(selectedLog.after_data, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Statistics Modal */}
      <Dialog open={isStatsModalOpen} onOpenChange={setIsStatsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Activity Log Statistics</DialogTitle>
            <DialogDescription>
              Overview of system activity and actions
            </DialogDescription>
          </DialogHeader>
          
          {stats && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-500">Total Logs</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_logs?.toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-500">Last 24h</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.recent_activity_24h?.toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-500">Last 7 Days</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.recent_activity_7d?.toLocaleString()}</p>
                </div>
              </div>

              {/* By Action */}
              <div>
                <Label className="text-gray-500">Actions Breakdown</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {Object.entries(stats.by_action || {}).map(([action, count]) => {
                    const config = getActionBadge(action);
                    return (
                      <div key={action} className="flex items-center justify-between p-2 rounded bg-gray-50">
                        <Badge className={`${config.bg} ${config.text}`}>{config.label}</Badge>
                        <span className="font-bold text-gray-800">{count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* By Entity Type */}
              <div>
                <Label className="text-gray-500">Entity Types Breakdown</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {Object.entries(stats.by_entity_type || {}).map(([entityType, count]) => {
                    const config = getEntityBadge(entityType);
                    return (
                      <div key={entityType} className="flex items-center justify-between p-2 rounded bg-gray-50">
                        <Badge className={`${config.bg} ${config.text}`}>{entityType}</Badge>
                        <span className="font-bold text-gray-800">{count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* By User */}
              <div>
                <Label className="text-gray-500">Top Users by Activity</Label>
                <div className="mt-2 space-y-2">
                  {(stats.by_user || []).slice(0, 5).map((userStat, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded bg-gray-50">
                      <div>
                        <p className="font-medium text-gray-800">{userStat.user_name}</p>
                        <p className="text-xs text-gray-500">{userStat.user_role}</p>
                      </div>
                      <span className="font-bold text-gray-800">{userStat.count.toLocaleString()} actions</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Report Management Component
function ReportManagement({ token, user, products, transactions, reportSyncTick = 0 }){
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState("daily");
  const [customStartDate, setCustomStartDate] = useState("2025-08-11");
  const [customEndDate, setCustomEndDate] = useState("2025-08-17");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" })
  );
  
  // Admin debug tools state
  const [debugData, setDebugData] = useState(null);
  const [transactionsWithoutCashier, setTransactionsWithoutCashier] = useState(null);
  const [isLoadingDebug, setIsLoadingDebug] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Fetch cashier filter debug info
  const fetchDebugInfo = async () => {
    setIsLoadingDebug(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/debug/cashier-filter`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDebugData(data);
        console.log("🔍 Cashier Filter Debug:", data);
      } else {
        console.error("Failed to fetch debug info");
      }
    } catch (error) {
      console.error("Error fetching debug info:", error);
    } finally {
      setIsLoadingDebug(false);
    }
  };

  // Admin: Check transactions without cashier ID
  const checkTransactionsWithoutCashier = async () => {
    setIsLoadingDebug(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/transactions-without-cashier`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTransactionsWithoutCashier(data);
        console.log("🔍 Transactions without cashier:", data);
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to check transactions");
      }
    } catch (error) {
      console.error("Error checking transactions:", error);
    } finally {
      setIsLoadingDebug(false);
    }
  };

  // Admin: Backfill old transactions with admin's cashier ID
  const backfillCashierIds = async () => {
    if (!window.confirm("This will assign all transactions without closed_by_cashier_id to the admin. Continue?")) {
      return;
    }
    setIsLoadingDebug(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backfill-cashier-ids`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBackfillResult(data);
        console.log("✅ Backfill result:", data);
        alert(`Backfill complete! Updated ${data.updated_count} transactions.`);
        // Refresh the transactions without cashier list
        checkTransactionsWithoutCashier();
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to backfill transactions");
      }
    } catch (error) {
      console.error("Error backfilling transactions:", error);
    } finally {
      setIsLoadingDebug(false);
    }
  };

const [selectedMonth, setSelectedMonth] = useState(
  new Date().toISOString().slice(0, 7) // "YYYY-MM"
);

  // --- Report Player Log management state ---
  const [reportPlayerLogTab, setReportPlayerLogTab] = useState("report");
  const [reportDeleteConfirm, setReportDeleteConfirm] = useState(null);
  const [reportDeletePreview, setReportDeletePreview] = useState(null);
  const [reportDeleteAlsoTxn, setReportDeleteAlsoTxn] = useState(true);
  const [isLoadingReportDeletePreview, setIsLoadingReportDeletePreview] = useState(false);
  const [puncherFilter, setPuncherFilter] = useState("all");

  // Get unique punchers from player logs for the filter dropdown
  const uniquePunchers = React.useMemo(() => {
    const logs = Array.isArray(reportData?.player_logs) ? reportData.player_logs : [];
    const map = new Map();
    logs.forEach(l => {
      const pid = l.punched_by_id;
      const pname = l.punched_by_name;
      if (pid && pname && !map.has(pid)) {
        map.set(pid, pname);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [reportData?.player_logs]);

  // Fetch delete preview for report player log
  const fetchReportDeletePreview = async (logId) => {
    setIsLoadingReportDeletePreview(true);
    setReportDeletePreview(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/player-logs/delete-preview/${logId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setReportDeletePreview(await res.json());
      }
    } catch (e) {
      console.error("Failed to fetch report delete preview:", e);
    } finally {
      setIsLoadingReportDeletePreview(false);
    }
  };

  // Handle delete player log from report
  const handleReportDeletePlayerLog = async (log, alsoDeleteTransaction = false) => {
    if (!log?.id) return;
    try {
      const url = new URL(`${BACKEND_URL}/api/player-logs/${log.id}`);
      if (alsoDeleteTransaction) url.searchParams.set("delete_transaction", "true");
      const res = await fetch(url.toString(), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        // Re-generate the report to refresh the data
        generateReport();
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to delete player log.");
      }
    } catch (error) {
      console.error("Error deleting report player log:", error);
      alert("Failed to delete player log.");
    }
  };

  // Handle restore player logs linked to a transaction
  const [restoringTxnId, setRestoringTxnId] = useState(null);

  // Delete transaction state
  const [deleteTxnConfirm, setDeleteTxnConfirm] = useState(null);
  const [deleteTxnAlsoLogs, setDeleteTxnAlsoLogs] = useState(true);
  const [isDeletingTxn, setIsDeletingTxn] = useState(false);

  const handleDeleteTransaction = async () => {
    if (!deleteTxnConfirm) return;
    setIsDeletingTxn(true);
    try {
      const url = new URL(`${BACKEND_URL}/api/transactions/${encodeURIComponent(deleteTxnConfirm.id)}`);
      if (deleteTxnAlsoLogs) url.searchParams.set("delete_player_logs", "true");
      const res = await fetch(url.toString(), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setDeleteTxnConfirm(null);
        generateReport();
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to delete transaction.");
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      alert("Failed to delete transaction.");
    } finally {
      setIsDeletingTxn(false);
    }
  };

  // OR assignment modal state
  const [orModalTxn, setOrModalTxn] = useState(null);
  const [orModalValue, setOrModalValue] = useState("");
  const [orModalList, setOrModalList] = useState([]);
  const [orModalLoading, setOrModalLoading] = useState(false);
  const [orModalSaving, setOrModalSaving] = useState(false);

  const [catalogCashiers, setCatalogCashiers] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState(() => (Array.isArray(products) ? products : []));
  const [catalogForm, setCatalogForm] = useState({
    cashier_id: (user?.role === "cashier" || user?.role === "puncher") ? (user?.id || "") : "",
    date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" }),
    or_number: "",
    customer_name: "",
    product_id: "",
    amount: "",
    payment_method: "cash",
    discount_type: "none",
    discount_amount: "",
    attach_player_log: true,
  });
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [catalogMessage, setCatalogMessage] = useState("");
  const catalogProductsByCategory = React.useMemo(() => {
    const list = (Array.isArray(catalogProducts) ? catalogProducts : []).filter((p) => p?.id && p.is_active !== false);
    const groups = {};
    for (const p of list) {
      const cat = p.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    }
    Object.keys(groups).forEach((cat) => {
      groups[cat].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    });
    const preferred = ["Driving Range Rates", "Golf Rates", "Golf Equipment Rental", "Food & Beverage", "Merchandise"];
    const keys = Object.keys(groups).sort((a, b) => {
      const ia = preferred.indexOf(a);
      const ib = preferred.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.localeCompare(b);
    });
    return keys.map((category) => ({ category, items: groups[category] }));
  }, [catalogProducts]);
  const catalogOriginal = Number(catalogForm.amount) || 0;
  const catalogDiscount = (catalogForm.discount_type === "pwd" || catalogForm.discount_type === "senior")
    ? Math.round(catalogOriginal * 0.2 * 100) / 100
    : catalogForm.discount_type === "custom"
      ? Math.max(0, Number(catalogForm.discount_amount) || 0)
      : 0;
  const catalogNet = Math.max(0, Math.round((catalogOriginal - catalogDiscount) * 100) / 100);

  const openOrModal = async (transaction) => {
    setOrModalTxn(transaction);
    setOrModalValue(transaction.or_number || "");
    setOrModalLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cashier-or`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrModalList(
          Array.isArray(data)
            ? data.map(String).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            : []
        );
      }
    } catch (e) {
      console.error("Error fetching OR list:", e);
    } finally {
      setOrModalLoading(false);
    }
  };

  const handleSaveOrModal = async () => {
    if (!orModalTxn || !orModalValue.trim()) return;
    setOrModalSaving(true);
    try {
      // ── Validate OR number against external API before saving ──
      try {
        const valRes = await fetch(`${BACKEND_URL}/api/validate-or-number`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ or_number: orModalValue.trim(), transaction_id: orModalTxn.id }),
        });
        if (valRes && valRes.ok) {
          const valData = await valRes.json();
          if (!valData.valid) {
            if (valData.needs_retry && valData.conflicting_transaction_id) {
              const retryChoice = window.confirm(
                `${valData.reason}\n\nWould you like to retry syncing the conflicting transaction now?`
              );
              if (retryChoice) {
                try {
                  const retryRes = await fetch(`${BACKEND_URL}/api/transactions/retry-qa-sync/${encodeURIComponent(valData.conflicting_transaction_id)}`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (retryRes && retryRes.ok) {
                    alert("Conflicting transaction synced to QA. Please select a different OR number.");
                  } else {
                    alert("Sync retry failed. Please contact an admin or choose a different OR number.");
                  }
                } catch {
                  alert("Sync retry failed. Please choose a different OR number.");
                }
              }
              // Refresh OR list
              try {
                const refreshRes = await fetch(`${BACKEND_URL}/api/cashier-or`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (refreshRes.ok) {
                  const refreshData = await refreshRes.json();
                  setOrModalList(
                    Array.isArray(refreshData)
                      ? refreshData.map(String).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                      : []
                  );
                }
              } catch {}
              setOrModalValue("");
              setOrModalSaving(false);
              return;
            } else {
              alert(valData.reason || "This OR number is not available.");
              setOrModalValue("");
              setOrModalSaving(false);
              return;
            }
          }
        }
      } catch (valErr) {
        console.warn("OR validation check failed (proceeding anyway):", valErr);
      }

      const res = await fetch(`${BACKEND_URL}/api/transactions/${encodeURIComponent(orModalTxn.id)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ or_number: orModalValue.trim(), workflow_status: "completed" }),
      });
      if (res.ok) {
        setOrModalTxn(null);
        setOrModalValue("");
        generateReport();
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to update OR number.");
      }
    } catch (error) {
      console.error("Error updating OR number:", error);
      alert("Failed to update OR number.");
    } finally {
      setOrModalSaving(false);
    }
  };
  const handleRestorePlayerLogs = async (transaction) => {
    const txnId = transaction.id || transaction.transaction_id;
    if (!txnId) return;
    setRestoringTxnId(txnId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/player-logs/restore-by-transaction/${encodeURIComponent(txnId)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.modified_count > 0) {
          if (data.restore_protected) {
            alert(`✅ Restored ${data.modified_count} player log(s). They are protected from auto-removal until an OR number is assigned.`);
          } else {
            alert(`✅ Restored ${data.modified_count} player log(s) back to active.`);
          }
        } else {
          alert("No inactive player logs found for this transaction.");
        }
        generateReport();
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to restore player logs.");
      }
    } catch (error) {
      console.error("Error restoring player logs:", error);
      alert("Failed to restore player logs.");
    } finally {
      setRestoringTxnId(null);
    }
  };

const parseManilaDate = (dateStr) => {
    if (!dateStr && dateStr !== 0) return null;
    try {
      if (dateStr instanceof Date) return dateStr;
      let s = String(dateStr).trim();

      // If plain "HH:MM" -> interpret as today in Manila
      if (/^\d{1,2}:\d{2}$/.test(s)) {
        const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
        const [h, m] = s.split(":").map(Number);
        return new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
      }

      // Convert "YYYY-MM-DD HH:MM" -> "YYYY-MM-DDTHH:MM"
      if (!s.includes("T") && /^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}/.test(s)) {
        s = s.replace(/\s+/, "T");
      }

      // If ISO-like but missing timezone, append Manila offset
      const hasTZ = /[Z\+\-]\d{2}:?\d{2}$/.test(s) || s.endsWith("Z");
      const isoLike = /^\d{4}-\d{2}-\d{2}T/.test(s);
      if (isoLike && !hasTZ) {
        s = s + "+08:00";
      } else if (!hasTZ && !isoLike) {
        // best-effort: append Manila offset for non-ISO strings
        s = s + "+08:00";
      }

      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    } catch (err) {
      console.error("parseManilaDate error:", dateStr, err);
      return null;
    }
  };


  // Normalize player logs once for report rendering (apply puncher filter)
  const normalizedPlayerLogs = React.useMemo(() => {
    let logs = Array.isArray(reportData?.player_logs) ? reportData.player_logs : [];
    // Exclude superseded logs (old paused logs that were resumed into a new log)
    logs = logs.filter(l => !l.resumed_into_id);
    // Apply puncher filter
    if (puncherFilter && puncherFilter !== "all") {
      logs = logs.filter(l => l.punched_by_id === puncherFilter);
    }
    return logs.map((l, i) => ({
      ...l,
      key: l.id || `${l.or_number || "no-or"}_${i}`,
    }));
  }, [reportData?.player_logs, puncherFilter]);

  const reportJoinTransactions = React.useMemo(() => {
    const reportTxns = Array.isArray(reportData?.transactions) ? reportData.transactions : [];
    const liveTxns = Array.isArray(transactions) ? transactions : [];
    const linkedTxns = (Array.isArray(reportData?.player_logs) ? reportData.player_logs : [])
      .map((l) => l?.linked_sale)
      .filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const t of [...reportTxns, ...linkedTxns, ...liveTxns]) {
      const key = String(t?.id || t?.or_number || "");
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      if (t) out.push(t);
    }
    return out;
  }, [reportData?.transactions, reportData?.player_logs, transactions]);

  const findReportTxnForLog = (log) => {
    if (!log) return null;
    if (log.linked_sale && (log.linked_sale.id || log.linked_sale.or_number || (log.linked_sale.items || []).length)) {
      return log.linked_sale;
    }
    if (log.transaction_id) {
      const byId = reportJoinTransactions.find((t) => String(t.id) === String(log.transaction_id));
      if (byId) return byId;
    }
    if (log.or_number) {
      const byOr = reportJoinTransactions.find((t) => String(t.or_number) === String(log.or_number));
      if (byOr) return byOr;
    }
    return null;
  };

  const cashierSiteBadge = (log) => {
    const c = log?.cashier_site;
    if (!c || c.status === "no_or") return null;
    const map = {
      matched: { label: "Matches cashier site", cls: "bg-emerald-100 text-emerald-800" },
      consumed: { label: "On cashier site", cls: "bg-emerald-50 text-emerald-700" },
      cashier_only: { label: "Cashier site only", cls: "bg-amber-100 text-amber-800" },
      not_on_site: { label: "Not on cashier site", cls: "bg-amber-100 text-amber-800" },
      mismatch: { label: `Mismatch${c.issues?.length ? ` (${c.issues.join(", ")})` : ""}`, cls: "bg-red-100 text-red-800" },
      unchecked: { label: "Cashier site n/a", cls: "bg-gray-100 text-gray-600" },
    };
    const cfg = map[c.status] || { label: c.status, cls: "bg-gray-100 text-gray-600" };
    const bits = [];
    if (c.external_name) bits.push(c.external_name);
    if (c.external_amount != null) {
      bits.push(`₱${Number(c.external_amount).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`);
    }
    return (
      <span className={`inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${cfg.cls}`} title={bits.join(" · ")}>
        {cfg.label}
      </span>
    );
  };

 // Header date display used by history table (fallback to selectedDate)
 const headerDateDisplay = React.useMemo(() => {
   try {
     if (!selectedDate) return "";
     const d = new Date(selectedDate);
     return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
   } catch {
     return String(selectedDate || "");
   }
 }, [selectedDate]);


  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear()  
  );
  const [selectedWeek, setSelectedWeek] = useState(`2025-W33`);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [serviceOrder, setServiceOrder] = useState("dr-first");
  const generateReport = async () => {
    setIsLoading(true);
    try {
      const reportRequest = {
        report_type: reportType,
        ...(reportType === "custom" && {
          start_date: customStartDate,
          end_date: customEndDate,
        }),
        ...(reportType === "daily" && { date: selectedDate }),
        ...(reportType === "weekly" && {
          start_date: customStartDate,
          end_date: customEndDate,
        }),
        ...(paymentMethodFilter !== "all" && {
          payment_method: paymentMethodFilter,
        }),
      };
        
      if (reportType === "monthly") {
        const [year, month] = selectedMonth.split("-");
        // Send plain YYYY-MM-DD strings (interpreted as Philippine dates by backend)
        const firstDay = `${year}-${month.padStart(2, "0")}-01`;
        const lastDay = new Date(Number(year), Number(month), 0).getDate();
        const endDay = `${year}-${month.padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
        reportRequest.start_date = firstDay;
        reportRequest.end_date = endDay;
        // Request player logs for monthly reports
        reportRequest.include_player_logs = true;
      }

      // Include player logs for all report types
      reportRequest.include_player_logs = true;

      const response = await fetch(`${BACKEND_URL}/api/reports/sales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reportRequest),
      });
  
      if (response.ok) {
        const data = await response.json();
        console.log("🔍 Report data received:", data);
        console.log("🔍 Transactions count:", data.transactions?.length || 0);
        console.log(`👤 Current user: ${user?.full_name} (Role: ${user?.role})`);
        if (user?.role === "cashier") {
          console.log("✅ CASHIER VIEW: Reports should be filtered to only your closed transactions");
          // Log sample closed_by_cashier_id values for debugging
          if (data.transactions?.length > 0) {
            const sampleIds = data.transactions.slice(0, 5).map(t => t.closed_by_cashier_id);
            console.log("📋 Sample closed_by_cashier_id values:", sampleIds);
          }
        } else if (user?.role === "puncher") {
          console.log("✅ PUNCHER VIEW: Reports include all punchers' created transactions for the selected period");
        } else if (user?.role === "admin") {
          console.log("👑 ADMIN VIEW: All transactions are shown");
        }
        setReportData(data);
      } else {
        const error = await response.json();
        console.error("🔍 Report generation failed:", error);
        alert(error.detail || "Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Error generating report");
    } finally {
      setIsLoading(false);
    }
  };

  const generateReportRef = React.useRef(generateReport);
  generateReportRef.current = generateReport;
  React.useEffect(() => {
    if (!reportSyncTick) return;
    generateReportRef.current();
  }, [reportSyncTick]);

  const handleCatalogSale = async (e) => {
    e.preventDefault();
    if (user?.role !== "admin" && user?.role !== "cashier" && user?.role !== "puncher") return;
    if (!catalogForm.product_id) {
      setCatalogMessage("Select a product from Product Management.");
      return;
    }
    setCatalogSaving(true);
    setCatalogMessage("");
    try {
      const original = Number(catalogForm.amount);
      const dtype = catalogForm.discount_type || "none";
      const customDisc = Number(catalogForm.discount_amount) || 0;
      const payload = {
        date: catalogForm.date,
        or_number: String(catalogForm.or_number || "").trim(),
        customer_name: String(catalogForm.customer_name || "").trim(),
        product_id: catalogForm.product_id,
        amount: original,
        payment_method: catalogForm.payment_method || "cash",
        discount_type: dtype,
        attach_player_log: !!catalogForm.attach_player_log,
      };
      if (dtype === "custom") payload.discount_amount = customDisc;
      if (user?.role === "admin") payload.cashier_id = catalogForm.cashier_id;
      const res = await fetch(`${BACKEND_URL}/api/transactions/catalog`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d) => d.msg || d).join("; ")
          : (detail || "Failed to add catalog sale.");
        setCatalogMessage(msg);
        return;
      }
      const attached = data.attached_player_logs
        ? ` · attached ${data.attached_player_logs} player log(s)`
        : "";
      setCatalogMessage(`Saved OR ${payload.or_number} for ${payload.customer_name}${attached}.`);
      setCatalogForm((prev) => ({
        ...prev,
        or_number: "",
        customer_name: "",
        attach_player_log: true,
      }));
      generateReport();
    } catch (err) {
      console.error("Catalog sale failed:", err);
      setCatalogMessage("Failed to add catalog sale.");
    } finally {
      setCatalogSaving(false);
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      // const reportRequest = {
      //   report_type: reportType,
      //     ...(reportType === "monthly" && {
      //       start_date: customStartDate,
      //       end_date: customEndDate,
      //     }),
      //   ...(reportType === "daily" && { date: selectedDate }),
      
      //   ...(reportType === "weekly" && {
      //     start_date: customStartDate,
      //     end_date: customEndDate,
      //   }),
      //   ...(reportType === "custom" && {
      //     start_date: customStartDate,
      //     end_date: customEndDate,
      //   }),
      //   ...(paymentMethodFilter !== "all" && {
      //     payment_method: paymentMethodFilter,
      //   }),
      // };

      const reportRequest = {
        report_type: reportType,
        ...(reportType === "custom" && {
          start_date: customStartDate,
          end_date: customEndDate,
        }),
        ...(reportType === "daily" && { date: selectedDate }),
        ...(reportType === "weekly" && {
          start_date: customStartDate,
          end_date: customEndDate,
        }),
        ...(paymentMethodFilter !== "all" && {
          payment_method: paymentMethodFilter,
        }),
        service_order: serviceOrder, 
      };
        
      if (reportType === "monthly") {
        const [year, month] = selectedMonth.split("-");
        // Send plain YYYY-MM-DD strings (interpreted as Philippine dates by backend)
        const firstDay = `${year}-${month.padStart(2, "0")}-01`;
        const lastDay = new Date(Number(year), Number(month), 0).getDate();
        const endDay = `${year}-${month.padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
        reportRequest.start_date = firstDay;
        reportRequest.end_date = endDay;
        // Ensure exported Excel includes player logs for monthly reports
        reportRequest.include_player_logs = true;
      }

      // Include player logs for all report types
      reportRequest.include_player_logs = true;

      const response = await fetch(`${BACKEND_URL}/api/reports/sales/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reportRequest),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get("content-disposition");
        const filename = contentDisposition
          ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
          : "sales_report.xlsx";
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to export report");
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      alert("Error exporting report");
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return "₱0.00";
    }
    return `₱${amount.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getWeekStartDate = (year, week) => {
    const date = new Date(year, 0, 1);
    const dayOfWeek = date.getDay();
    const daysToAdd = (week - 1) * 7 - dayOfWeek + 1;
    date.setDate(date.getDate() + daysToAdd);
    return date;
  };

  function getDateFromWeek(weekString) {
  const [year, week] = weekString.split('-W');
  
  // Create date for January 4th (always in week 1)
  const jan4 = new Date(year, 0, 4);
  
  // Find Monday of week 1
  const daysFromMonday = (jan4.getDay() + 6) % 7;
  const firstMonday = new Date(jan4);
  firstMonday.setDate(jan4.getDate() - daysFromMonday);
  
  // Calculate target week's Monday
  const targetMonday = new Date(firstMonday);
  targetMonday.setDate(firstMonday.getDate() + (week - 1) * 7);
  
  targetMonday.setDate(targetMonday.getDate() + 1);
  return targetMonday;
}

  const formatDateTime = (isoString) => {
    return new Date(isoString).toLocaleString("en-PH");
  };

  // Derived sorted lists according to serviceOrder
  const sortedTransactions = React.useMemo(() => {
    if (!Array.isArray(reportData?.transactions)) return [];
    const order = serviceOrder;
    const svcPriority = (row) => {
      const svc = String(row.service_type || "").toLowerCase();
      const itemsText = JSON.stringify(row.items || row.transaction_items || []);
      const isDR = svc.includes("driving") || /driving range/i.test(itemsText);
      const isGolf = svc.includes("golf") || /\bgolf\b/i.test(itemsText);
      if (order === "dr-first") return isDR ? 0 : isGolf ? 1 : 2;
      return isGolf ? 0 : isDR ? 1 : 2;
    };
    return [...reportData.transactions].sort((a, b) => svcPriority(a) - svcPriority(b));
  }, [reportData?.transactions, serviceOrder]);

  const sortedPlayerLogs = React.useMemo(() => {
    const logs = Array.isArray(normalizedPlayerLogs) ? normalizedPlayerLogs : [];
    const order = serviceOrder;
    const priority = (l) => {
      const svc = String(l.service_type || "").toLowerCase();
      const isDR = svc.includes("driving");
      const isGolf = svc.includes("golf");
      if (order === "dr-first") return isDR ? 0 : isGolf ? 1 : 2;
      return isGolf ? 0 : isDR ? 1 : 2;
    };
    return [...logs].sort((a, b) => priority(a) - priority(b));
  }, [normalizedPlayerLogs, serviceOrder]);


  useEffect(() => {
  if (
    (reportType === "monthly" && selectedMonth && selectedYear) 
  ) {
    generateReport();
  }
}, [
  reportType, selectedMonth, selectedYear, paymentMethodFilter, token,]);

  // Auto-generate report when component mounts or report type changes
  useEffect(() => {
    if (reportType !== "custom" || (customStartDate && customEndDate)) {
      generateReport();
    }
  }, [reportType, selectedDate, paymentMethodFilter, token]);


  // Generate report for custom date range when both dates are selected
  useEffect(() => {
    if (reportType === "custom" && customStartDate && customEndDate) {
      generateReport();
    }
  }, [customStartDate, customEndDate, paymentMethodFilter, token]);

  useEffect(() => {
    if (reportType === "weekly" && customStartDate && customEndDate) {
      generateReport();
    }
  }, [customStartDate, customEndDate, paymentMethodFilter, token]);

  useEffect(() => {
    if (!token || (user?.role !== "admin" && user?.role !== "cashier" && user?.role !== "puncher")) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/users/cashiers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const list = (Array.isArray(data) ? data : []).filter((c) => c?.id && c.is_active !== false);
        setCatalogCashiers(list);
        if ((user?.role === "cashier" || user?.role === "puncher") && user?.id) {
          setCatalogForm((prev) => ({ ...prev, cashier_id: user.id }));
        } else if (user?.role === "admin" && list.length === 1 && list[0]?.id) {
          setCatalogForm((prev) => (prev.cashier_id ? prev : { ...prev, cashier_id: list[0].id }));
        }
      } catch (err) {
        console.error("Failed to load accounts for catalog:", err);
      }
    })();
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/active-products`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) setCatalogProducts(data);
      } catch (err) {
        console.error("Failed to load products for catalog:", err);
      }
    })();
  }, [token, user?.role, user?.id]);

  // TODO: REPORT DATA TRANSACTION PAGE
  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="p-6 bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="flex items-center space-x-2 text-2xl font-bold">
              <BarChart3 className="h-7 w-7 text-emerald-500" />
              <span>Sales Reports</span>
            </h2>
            <p className="text-gray-600">
              Generate and export comprehensive sales reports
            </p>
          </div>
          <div className="flex space-x-2">
            {reportData && (
              <Button
                onClick={exportToExcel}
                disabled={isExporting}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {isExporting ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
                    <span>Exporting...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>Export to Excel</span>
                  </div>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Report Type Selection */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-5">
          <div className="space-y-2">
            <Label>Report Type</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily Report</SelectItem>
                <SelectItem value="weekly">Weekly Report</SelectItem>
                <SelectItem value="monthly">Monthly Report</SelectItem>
                <SelectItem value="custom">Custom Date Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select
              value={paymentMethodFilter}
              onValueChange={setPaymentMethodFilter}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payment Methods</SelectItem>
                <SelectItem value="cash">💵 Cash Only</SelectItem>
                <SelectItem value="card">💳 Card Only</SelectItem>
                <SelectItem value="online">🌐 Online Payment Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {reportType === "daily" && (
            <div className="space-y-2">
              <Label>Select Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          )}
          
      {reportType === "monthly" && (
        <div className="space-y-2">
          <Label>Select Month & Year</Label>
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="border rounded px-2 py-1"
          />
        </div>
      )}

          {reportType === "weekly" && (
            <div className="space-y-2">
              <Label>Select Week</Label>
              <Input
                type="week"
                value={selectedWeek}
                onChange={(e) => {
                  // get the week
                  const weekValue = e.target.value;
                  setSelectedWeek(weekValue);
                  console.log(weekValue);
                  

                  // instantiate a date object variable
                  let start = getDateFromWeek(weekValue);

                  // get the start and end of the week, and set to start and end date
                  let end = new Date(start);
                  end.setDate(start.getDate() + 6);

                  // Convert to YYYY-MM-DD format strings in Philippine time
                  const startDateString = start.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
                  const endDateString = end.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });

                  setCustomStartDate(startDateString);
                  setCustomEndDate(endDateString);

                  // const weekValue = e.target.value; // Format: "2025-W32"
                  // setSelectedWeek(weekValue);
                  // const [year, week] = weekValue.split('-W');
                  // console.log("year:", year)
                  // console.log("week:", week)
                  // const weekStart = getWeekStartDate(parseInt(year), parseInt(week));
                  // console.log("start:", weekStart)
                  // const weekEnd = new Date(weekStart);
                  // weekEnd.setDate(weekStart.getDate() + 6);
    
                  // // setCustomStartDate(weekStart.toISOString().split('T')[0]);
                  // // setCustomEndDate(weekEnd.toISOString().split('T')[0]);
                  // setCustomStartDate(weekStart);
                  // setCustomEndDate(weekEnd);
                  // console.log("start:", customStartDate)
                  // console.log("end:", customEndDate)
                }}
                placeholder="Select week"
              />
              {selectedWeek && (
                <div className="p-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded">
                  📍 {customStartDate && new Date(customStartDate).toLocaleDateString()} - {customEndDate && new Date(customEndDate).toLocaleDateString()}
                </div>
              )}
            </div>
          )}

          {reportType === "custom" && (
            <>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="flex items-end">
            <Button
              onClick={generateReport}
              disabled={isLoading}
              className="w-full bg-blue-500 hover:bg-blue-600"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
                  <span>Generating...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-4 h-4" />
                  <span>Generate Report</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Current User Info Banner - Shows for all users */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <User className="w-5 h-5 text-blue-600" />
            <div>
              <span className="text-sm text-blue-700">
                Logged in as: <strong>{user?.full_name || user?.username || "Unknown"}</strong>
              </span>
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-200 text-blue-800 rounded-full">
                {user?.role}
              </span>
            </div>
          </div>
          {user?.role === "cashier" && (
            <div className="text-xs text-blue-600">
              Sales totals and Filtered Transactions stay limited to sales you closed. Player Logs amounts and cashier-site badges use those same closed sales.
            </div>
          )}
          {user?.role === "puncher" && (
            <div className="text-xs text-blue-600">
              Sales totals and Filtered Transactions stay limited to puncher-created bookings. Player Logs still show the day's punched rows, with amounts from the linked sale and a cashier-website check.
            </div>
          )}
          {user?.role === "admin" && (
            <div className="text-xs text-blue-600">
              📊 Reports show all transactions (admin view)
            </div>
          )}
        </div>
      </div>

      {(user?.role === "admin" || user?.role === "cashier" || user?.role === "puncher") && (
        <Card className="border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-emerald-600" />
              Missing sales catalog
            </CardTitle>
            <CardDescription>
              Enter a missing sale for any User Management account (local, puncher, cashier, or cashier-site). Pick the item from Product Management so the name and price stay exact. Duplicate OR numbers are rejected.
              {user?.role === "puncher" ? " Punchers save onto their own report." : ""}
              {user?.role === "cashier" ? " Cashiers save onto their own report." : ""}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <form onSubmit={handleCatalogSale} className="space-y-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
                {user?.role === "admin" && (
                  <div className="space-y-1">
                    <Label>Account</Label>
                    <Select
                      value={catalogForm.cashier_id || undefined}
                      onValueChange={(val) => setCatalogForm((prev) => ({ ...prev, cashier_id: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                      <SelectContent>
                        {catalogCashiers.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {(c.full_name || c.username || c.id)}
                            {c.username ? ` (@${c.username})` : ""}
                            {c.role ? ` · ${c.role}` : ""}
                            {c.source === "cashier_site" ? " · cashier site" : " · local"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={catalogForm.date}
                    onChange={(e) => setCatalogForm((prev) => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label>OR number</Label>
                  <Input
                    value={catalogForm.or_number}
                    onChange={(e) => setCatalogForm((prev) => ({ ...prev, or_number: e.target.value }))}
                    placeholder="9930061"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label>Customer</Label>
                  <Input
                    value={catalogForm.customer_name}
                    onChange={(e) => setCatalogForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                    placeholder="MARK"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label>Payment</Label>
                  <Select
                    value={catalogForm.payment_method}
                    onValueChange={(val) => setCatalogForm((prev) => ({ ...prev, payment_method: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1 md:col-span-2">
                  <Label>Item / service</Label>
                  <Select
                    value={catalogForm.product_id || undefined}
                    onValueChange={(val) => {
                      const p = catalogProducts.find((x) => String(x.id) === String(val));
                      setCatalogForm((prev) => ({
                        ...prev,
                        product_id: val,
                        amount: p?.price != null ? String(p.price) : prev.amount,
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select from Product Management" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {catalogProductsByCategory.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-gray-500">No products found</div>
                      ) : (
                        catalogProductsByCategory.map((group) => (
                          <SelectGroup key={group.category}>
                            <SelectLabel>{group.category}</SelectLabel>
                            {group.items.map((p) => (
                              <SelectItem key={p.id} value={String(p.id)}>
                                {p.name}
                                {p.price != null ? ` · ₱${Number(p.price).toLocaleString("en-PH", { minimumFractionDigits: 2 })}` : ""}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Price</Label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={catalogForm.amount}
                    onChange={(e) => setCatalogForm((prev) => ({ ...prev, amount: e.target.value }))}
                    placeholder="Auto from product"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label>Discount</Label>
                  <Select
                    value={catalogForm.discount_type}
                    onValueChange={(val) => setCatalogForm((prev) => ({
                      ...prev,
                      discount_type: val,
                      discount_amount: val === "custom" ? prev.discount_amount : "",
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="pwd">PWD (20% off)</SelectItem>
                      <SelectItem value="senior">Senior Citizen (20% off)</SelectItem>
                      <SelectItem value="custom">Custom amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {catalogForm.discount_type === "custom" ? (
                  <div className="space-y-1">
                    <Label>Discount amount</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={catalogForm.discount_amount}
                      onChange={(e) => setCatalogForm((prev) => ({ ...prev, discount_amount: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                ) : null}
              </div>
              {(catalogDiscount > 0 || catalogForm.product_id) ? (
                <p className="text-sm text-gray-600">
                  Original ₱{catalogOriginal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                  {catalogDiscount > 0 ? (
                    <>
                      {" "}· Discount −₱{catalogDiscount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                      {" "}· Net <span className="font-semibold text-emerald-700">₱{catalogNet.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                    </>
                  ) : (
                    <> · Net <span className="font-semibold text-emerald-700">₱{catalogNet.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span></>
                  )}
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <label className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={catalogForm.attach_player_log}
                    onChange={(e) => setCatalogForm((prev) => ({ ...prev, attach_player_log: e.target.checked }))}
                  />
                  Attach to existing player log with this OR
                </label>
                <div className="flex items-end">
                  <Button
                    type="submit"
                    disabled={catalogSaving || !catalogForm.product_id || (user?.role === "admin" && !catalogForm.cashier_id)}
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                  >
                    {catalogSaving ? "Saving..." : "Add to report"}
                  </Button>
                </div>
              </div>
              {catalogMessage ? (
                <p className="text-sm text-gray-700">{catalogMessage}</p>
              ) : null}
            </form>
          </CardContent>
        </Card>
      )}

      {/* Admin Debug Tools Panel - Admin Only */}
      {user?.role === "admin" && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-amber-800">Admin: Cashier Filter Debug Tools</h3>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchDebugInfo}
                disabled={isLoadingDebug}
                className="border-amber-400 text-amber-700 hover:bg-amber-100"
              >
                {isLoadingDebug ? "Loading..." : "🔍 Check My Filter"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={checkTransactionsWithoutCashier}
                disabled={isLoadingDebug}
                className="border-amber-400 text-amber-700 hover:bg-amber-100"
              >
                📋 Find Missing Cashier IDs
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={backfillCashierIds}
                disabled={isLoadingDebug}
                className="border-red-400 text-red-700 hover:bg-red-100"
              >
                🔧 Backfill Old Transactions
              </Button>
            </div>
          </div>
          
          {/* Debug Data Display */}
          {debugData && (
            <div className="mt-3 p-3 bg-white rounded border border-amber-200">
              <h4 className="text-sm font-medium text-amber-700 mb-2">Current User Filter Info:</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">User ID:</span>
                  <div className="font-mono text-xs bg-gray-100 p-1 rounded mt-1 break-all">
                    {debugData.current_user?.id || "N/A"}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Role:</span>
                  <div className="font-semibold">{debugData.current_user?.role || "N/A"}</div>
                </div>
                <div>
                  <span className="text-gray-500">Total Completed:</span>
                  <div className="font-semibold text-blue-600">{debugData.total_completed_transactions}</div>
                </div>
                <div>
                  <span className="text-gray-500">Matching Your ID:</span>
                  <div className="font-semibold text-emerald-600">{debugData.my_transactions_count}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-gray-500">Transactions with closed_by_id: {debugData.transactions_with_closed_by_id}</span>
                </div>
              </div>
              {debugData.distinct_cashier_ids && debugData.distinct_cashier_ids.length > 0 && (
                <div className="mt-3">
                  <span className="text-xs text-gray-500">Distinct cashier IDs in DB ({debugData.distinct_cashier_ids.length}):</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {debugData.distinct_cashier_ids.map((id, idx) => (
                      <span key={idx} className={`px-2 py-0.5 text-xs rounded font-mono ${id === debugData.current_user?.id ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-gray-100'}`}>
                        {id || "(empty)"} {id === debugData.current_user?.id && "← YOU"}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Transactions Without Cashier Display */}
          {transactionsWithoutCashier && (
            <div className="mt-3 p-3 bg-white rounded border border-amber-200">
              <h4 className="text-sm font-medium text-amber-700 mb-2">
                Transactions Missing Cashier ID: 
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                  {transactionsWithoutCashier.count}
                </span>
              </h4>
              {transactionsWithoutCashier.count > 0 && (
                <div className="text-xs text-gray-600">
                  <p className="mb-2">These transactions were created before cashier tracking was implemented.</p>
                  <p className="text-amber-600">
                    💡 Click "Backfill Old Transactions" to assign them to admin, or manually reassign them later.
                  </p>
                  {transactionsWithoutCashier.transactions && transactionsWithoutCashier.transactions.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-1">ID</th>
                            <th className="text-left py-1">OR Number</th>
                            <th className="text-left py-1">Member</th>
                            <th className="text-left py-1">Amount</th>
                            <th className="text-left py-1">Timestamp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactionsWithoutCashier.transactions.slice(0, 10).map((tx, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="py-1 font-mono">{tx.id?.substr(-8)}</td>
                              <td className="py-1">{tx.or_number || "N/A"}</td>
                              <td className="py-1">{tx.member_name || "N/A"}</td>
                              <td className="py-1">₱{tx.total_amount?.toFixed(2) || "0.00"}</td>
                              <td className="py-1">{tx.timestamp ? new Date(tx.timestamp).toLocaleDateString() : "N/A"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {transactionsWithoutCashier.transactions.length > 10 && (
                        <p className="mt-1 text-gray-400">... and {transactionsWithoutCashier.transactions.length - 10} more</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {transactionsWithoutCashier.count === 0 && (
                <p className="text-sm text-emerald-600">✅ All transactions have cashier IDs assigned!</p>
              )}
            </div>
          )}

          {/* Backfill Result */}
          {backfillResult && (
            <div className="mt-3 p-3 bg-emerald-50 rounded border border-emerald-200">
              <h4 className="text-sm font-medium text-emerald-700">Backfill Complete:</h4>
              <p className="text-sm">Updated {backfillResult.updated} transactions</p>
              <p className="text-xs text-gray-500">Assigned to: {backfillResult.assigned_to} ({backfillResult.assigned_id})</p>
            </div>
          )}
        </div>
      )}

      {/* Report Results */}
      {reportData && (
        <div className="space-y-6">
          {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(reportData.total_sales)}</p>
              </div>
              <Banknote className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Transactions</p>
                <p className="text-2xl font-bold text-blue-600">{reportData.total_transactions}</p>
              </div>
              <CreditCard className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Items Sold</p>
                <p className="text-2xl font-bold text-purple-600">{reportData.total_items_sold}</p>
              </div>
              <Package className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Services Sold</p>
                <p className="text-2xl font-bold text-blue-600">{reportData.total_services_sold}</p>
              </div>
              <Award className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Transaction</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(reportData.average_transaction)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

          {/* Period Info */}
          <Card className="shadow-xl">
            <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-emerald-500 to-teal-500">
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>Report Period: {reportData.period}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Payment Methods */}
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">
                    Payment Methods
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">💵 Cash:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.cash_sales)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">💳 Card:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.card_sales)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">🌐 Online Payment:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.online_sales)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer Types */}
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">
                    Customer Types
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">👥 Members:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.member_sales)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">🚶 Walk-ins:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.non_member_sales)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Financial Summary */}
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">
                    Financial Summary
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">💸 Discounts:</span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(reportData.total_discounts)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Products */}
          {reportData.top_products && reportData.top_products.length > 0 && (
            <Card className="shadow-xl">
              <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-purple-500 to-pink-500">
                <CardTitle className="flex items-center space-x-2">
                  <Award className="w-5 h-5" />
                  <span>Top Selling Products</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {reportData.top_products.map((product, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 rounded-lg bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 font-bold text-purple-600 bg-purple-100 rounded-full">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-800">
                            {product.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {product.quantity} units sold
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-purple-600">
                          {formatCurrency(product.revenue)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filtered Transactions */}
          {reportData.transactions && reportData.transactions.length > 0 && (
            <Card className="shadow-xl">
              <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-blue-500 to-indigo-500">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="w-5 h-5" />
                    <span>
                      Filtered Transactions ({reportData.transactions.length})
                    </span>
                  </div>
                  <div className="text-sm font-normal opacity-90">
                    {reportType.charAt(0).toUpperCase() + reportType.slice(1)}
                    {paymentMethodFilter !== "all" &&
                      ` • ${
                        paymentMethodFilter.charAt(0).toUpperCase() +
                        paymentMethodFilter.slice(1)
                      } only`}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Filter Summary */}
                        <div className="p-3 mb-4 border-l-4 border-blue-500 rounded-lg bg-blue-50">
                          <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="font-medium">Period:</span>
                            <span>{reportData.period}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <CreditCard className="w-4 h-4 text-blue-600" />
                            <span className="font-medium">Payment:</span>
                            <span>
                            {paymentMethodFilter === "all"
                              ? "All Methods"
                              : paymentMethodFilter.charAt(0).toUpperCase() +
                              paymentMethodFilter.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Award className="w-4 h-4 text-blue-600" />
                            <span className="font-medium">Service order:</span>
                            <Select value={serviceOrder} onValueChange={setServiceOrder}>
                              <SelectTrigger className="h-7 min-w-[160px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="dr-first">Driving Range → Golf</SelectItem>
                                <SelectItem value="golf-first">Golf → Driving Range</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center space-x-1">
                            <BarChart3 className="w-4 h-4 text-blue-600" />
                            <span className="font-medium">Total:</span>
                            <span className="font-bold text-blue-700">
                            {formatCurrency(reportData.total_sales)}
                            </span>
                          </div>
                          </div>
                        </div>

                        <div className="overflow-x-auto max-h-64 overflow-y-scroll">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b ">
                                <th className="px-3 py-2 text-left">Transaction ID</th>
                                <th className="px-3 py-2 text-left">Date & Time</th>
                                <th className="px-3 py-2 text-left">Customer</th>
                                <th className="px-3 py-2 text-left">Payment Method</th>
                                <th className="px-3 py-2 text-left">Payment Details</th>
                                <th className="px-3 py-2 text-left">OR Number</th> 
                                <th className="px-3 py-2 text-left">Items</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2 text-right">Discount</th>
                                <th className="px-3 py-2 text-left">Closed By</th>
                                {user?.role === "admin" || user?.role === "puncher" ? (
                                  <th className="px-3 py-2 text-center">Action</th>
                                ) : null}
                              </tr>
                            </thead>
                            <tbody>
                              {sortedTransactions.map((transaction, index) => (
                                <tr
                                  key={transaction.id || index}
                                  className="border-b"
                                >
                                  <td className="px-3 py-2 font-mono text-xs">
                                    #{transaction.id.split("_")[1] || transaction.id.substr(-8)}
                                  </td>
                                    <td className="px-3 py-2">
                                      {formatTransactionDateTime(transaction.timestamp)}
                                    </td>
                                  <td className="px-3 py-2">
                                    <span
                                      className={
                                        transaction.member_name === "Walk-in"
                                          ? "text-gray-500"
                                          : "text-blue-600"
                                      }
                                    >
                                      {transaction.member_name}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <Badge
                                      variant="outline"
                                      className={`text-xs ${
                                        transaction.payment_method === "cash"
                                          ? "border-green-500 text-green-700"
                                          : transaction.payment_method === "card"
                                          ? "border-blue-500 text-blue-700"
                                          : transaction.payment_method === "online"
                                          ? "border-purple-500 text-purple-700"
                                          : "border-gray-500 text-gray-700"
                                      }`}
                                    >
                                      {transaction.payment_method === "cash"
                                        ? "💵 Cash"
                                        : transaction.payment_method === "card"
                                        ? "💳 Card"
                                        : transaction.payment_method === "online"
                                        ? "🌐 Online Payment"
                                        : transaction.payment_method}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-600">
                                    {transaction.payment_method === "cash" && (
                                      <div>Cash Onsite</div>
                                    )}
                                    {transaction.payment_method === "card" && (
                                      <div className="space-y-1">
                                        {transaction.card_number && (
                                          <div>Card: {transaction.card_number}</div>
                                        )}
                                        {transaction.transaction_number && (
                                          <div>Txn: {transaction.transaction_number}</div>
                                        )}
                                      </div>
                                    )}
                                    {transaction.payment_method === "online" && (
                                      <div className="space-y-1">
                                        {transaction.payment_gateway && (
                                          <div>Gateway: {transaction.payment_gateway}</div>
                                        )}
                                        {transaction.account_number && (
                                          <div>Account: {transaction.account_number}</div>
                                        )}
                                        {transaction.transaction_number && (
                                          <div>Ref: {transaction.transaction_number}</div>
                                        )}
                                      </div>
                                    )}
                                    {transaction.payment_method === "cash" && (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-700">
                                    {transaction.or_number ? (
                                      <span
                                        className={user?.role === "puncher" ? "text-gray-700" : "cursor-pointer hover:text-blue-600 hover:underline"}
                                        onClick={() => { if (user?.role !== "puncher") openOrModal(transaction); }}
                                        title={user?.role === "puncher" ? transaction.or_number : "Click to edit OR number"}
                                      >
                                        {transaction.or_number}
                                      </span>
                                    ) : user?.role === "puncher" ? (
                                      <span className="text-gray-400">-</span>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-6 px-2 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-100 font-semibold whitespace-nowrap"
                                        onClick={() => openOrModal(transaction)}
                                      >
                                        Assign OR
                                      </Button>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {transaction.items_count}
                                  </td>
                                  <td className="px-3 py-2 font-medium text-right">
                                    {formatCurrency(transaction.total_amount)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-green-600">
                                    {formatCurrency(transaction.discount_amount)}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-600">
                                    <div className="flex flex-col">
                                      <span className="font-medium">{transaction.closed_by_cashier_name || transaction.cashier || "Unknown"}</span>
                                      {transaction.closed_at && (
                                        <span className="text-xs text-gray-400">Closed: {formatTransactionDateTime(transaction.closed_at)}</span>
                                      )}
                                    </div>
                                  </td>
                                  {(user?.role === "admin" || user?.role === "puncher") && (
                                    <td className="px-3 py-2 text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        {user?.role === "admin" && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                          disabled={restoringTxnId === (transaction.id || transaction.transaction_id)}
                                          onClick={() => handleRestorePlayerLogs(transaction)}
                                          title="Restore player log(s) for this transaction back to active"
                                        >
                                          {restoringTxnId === (transaction.id || transaction.transaction_id) ? (
                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                          ) : (
                                            <><RefreshCw className="w-3.5 h-3.5 mr-1" />Restore</>
                                          )}
                                        </Button>
                                        )}
                                        {(user?.role === "admin" || String(transaction.cashier_id || "") === String(user?.id || "")) && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 px-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                                          onClick={() => { setDeleteTxnConfirm(transaction); setDeleteTxnAlsoLogs(true); }}
                                          title="Delete this transaction"
                                        >
                                          <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                                        </Button>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          {reportData.transactions.length === 0 && (
                          <div className="py-8 text-center text-gray-500">
                            <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="text-lg font-medium">
                            No transactions found
                            </p>
                            <p className="text-sm">
                            Try adjusting your filters or date range
                            </p>
                          </div>
                          )}
                        </div>
                        </CardContent>
                      </Card>
          )}

          {/* No Transactions State */}
          {reportData &&
            (!reportData.transactions ||
              reportData.transactions.length === 0) && (
              <Card className="shadow-xl">
                <CardContent className="p-12 text-center">
                  <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="mb-2 text-xl font-semibold text-gray-600">
                    No Transactions Found
                  </h3>
                  <p className="mb-4 text-gray-500">
                    No transactions match your current filters:
                  </p>
                  <div className="inline-flex items-center px-4 py-2 space-x-4 text-sm text-gray-600 bg-gray-100 rounded-lg">
                    <span>📅 {reportData.period}</span>
                    <span>•</span>
                    <span>
                      💳{" "}
                      {paymentMethodFilter === "all"
                        ? "All Payment Methods"
                        : paymentMethodFilter.charAt(0).toUpperCase() +
                          paymentMethodFilter.slice(1)}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    Try selecting a different date range or payment method
                    filter.
                  </p>
                </CardContent>
              </Card>
          )}
        </div>
      )}

      {/* No Data State */}
      {!reportData && !isLoading && (
        <Card className="shadow-xl">
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="mb-2 text-xl font-semibold text-gray-600">
              No Report Generated
            </h3>
            <p className="text-gray-500">
              Select report parameters and click "Generate Report" to view sales
              data.
            </p>
          </CardContent>
        </Card>
      )}
{/* TODO: PLAYER LOGS REPORT TABLE */}
      {/* --- PLAYER LOGS REPORT TABLE --- */}
      <Card className="shadow-xl">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
            <CardTitle className="flex items-center space-x-2 text-lg">
              <Clock className="w-5 h-5 text-emerald-500" />
              <span>Player Logs ({normalizedPlayerLogs.length})</span>
            </CardTitle>
            {(user?.role === "puncher" || user?.role === "admin" || user?.role === "cashier") && (
              <p className="text-xs text-gray-500 font-normal mt-1">
                {user?.role === "cashier"
                  ? "Amounts and cashier-site badges use sales you closed. Totals stay on your Filtered Transactions only."
                  : "Amounts use the day's linked sale (not only puncher-created rows). OR badges check the cashier website."}
              </p>
            )}
            </div>
            <div className="flex items-center gap-3">
              {/* Puncher filter */}
              {uniquePunchers.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-gray-500 whitespace-nowrap">Puncher:</Label>
                  <Select value={puncherFilter} onValueChange={setPuncherFilter}>
                    <SelectTrigger className="h-8 min-w-[150px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Punchers</SelectItem>
                      {uniquePunchers.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 pt-2">
      <Tabs value={reportPlayerLogTab} onValueChange={setReportPlayerLogTab}>
        <TabsList className="mb-3">
          {user?.role === "admin" && <TabsTrigger value="report">📊 Report</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="manage">⚙️ Manage</TabsTrigger>}
        </TabsList>

        {/* --- REPORT TAB (read-only) --- */}
        <TabsContent value="report">
      <div className="overflow-auto max-h-[420px]">
        {normalizedPlayerLogs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium text-lg">No player logs found</p>
            <p className="text-sm">No records found for {headerDateDisplay}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr className="text-xs text-left">
                <th className="px-3 py-2">No.</th>
                <th className="px-3 py-2">Bay/Course</th>
                <th className="px-3 py-2">OR Number</th>
                <th className="px-3 py-2">Player</th>
                <th className="px-3 py-2">Service</th>
                <th className="px-3 py-2">All Services</th>
                <th className="px-3 py-2">Items Added</th>
                <th className="px-3 py-2">Time In</th>
                <th className="px-3 py-2">Time Out</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">Original</th>
                <th className="px-3 py-2">Discount</th>
                <th className="px-3 py-2">Payment</th>
              </tr>
            </thead>
            <tbody>
                {sortedPlayerLogs.map((log, i) => {
                      const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
                      const results = [];


                  const getPlannedDurationMinutes = (log) => {
                      // If paused snapshot exists, prefer it
                      if (log?.paused_planned_duration_minutes) {
                        return Number(log.paused_planned_duration_minutes) || 0;
                      }

                      if (log.service_type === "Golf Rates") {
                        return 240; // 4 hours fixed for golf
                      }

                      const txn = findReportTxnForLog(log);
                      if (!txn || !txn.items) return 0;

                      let total = 0;
                      let hasDR = false;

                      txn.items.forEach(it => {
                        const prod = products.find(p => p.id === it.product_id);
                        const qty = (it.quantity || 1);

                        // Only consider Driving Range Rates items
                        if (prod?.category !== "Driving Range Rates") {
                          return; // skip non-DR items entirely
                        }
                        hasDR = true;

                        if (prod?.duration) {
                          total += Number(prod.duration) * qty;
                          return;
                        }

                        // Parse only when the product is DR
                        const name = (prod?.name || "").toLowerCase();

                        const hourMatch = name.match(/(\d+(?:\.\d+)?)\s*(hour|hr|hrs|hours)\b/);
                        if (hourMatch) {
                          total += Math.round(parseFloat(hourMatch[1]) * 60) * qty;
                          return;
                        }

                        const minMatch = name.match(/(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes)\b/);
                        if (minMatch) {
                          total += Math.round(parseFloat(minMatch[1])) * qty;
                          return;
                        }

                        // No blind “60-minute” fallback for non-matching names
                      });

                      // If nothing matched but it is clearly a DR booking, fallback once
                      return hasDR ? (total || 60) : 0;
                    };


const hhmmToManila = (hhmm, refDate) => {
  if (!hhmm) return null;
  const s = String(hhmm).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM|am|pm))?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const ampm = m[3];
  if (ampm) {
    const up = ampm.toUpperCase();
    if (up === "PM" && h !== 12) h += 12;
    if (up === "AM" && h === 12) h = 0;
  }
  const todayPH = refDate
    ? (refDate instanceof Date ? new Date(refDate.toLocaleString("en-US", { timeZone: "Asia/Manila" })) : new Date(new Date(String(refDate)).toLocaleString("en-US", { timeZone: "Asia/Manila" })))
    : new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
  return new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, mm, 0, 0);
};

const parseManilaDateSafe = (input, options = {}) => {
  if (input === null || input === undefined || input === "") return null;
  try {
    if (input instanceof Date) return input;
    let s = String(input).trim();

    // reference Manila date for HH:MM anchors
    const refDate = options.refDate
      ? (options.refDate instanceof Date ? options.refDate : new Date(String(options.refDate)))
      : new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));

    // HH:MM or HH:MM AM/PM -> Manila local datetime anchored to refDate
    const hhmm = s.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM|am|pm))?$/);
    if (hhmm) {
      return hhmmToManila(s, refDate);
    }

    // convert "YYYY-MM-DD HH:MM" -> "YYYY-MM-DDTHH:MM"
    if (!s.includes("T") && /^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}/.test(s)) {
      s = s.replace(/\s+/, "T");
    }

    const isoLike = /^\d{4}-\d{2}-\d{2}T/.test(s);
    const hasTZ = /[Z\+\-]\d{2}:?\d{2}$/.test(s) || s.endsWith("Z");

    // If ISO-like but missing tz -> assume Manila +08:00
    if (isoLike && !hasTZ) {
      s = s + "+00:00";
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }

    if (hasTZ || isoLike) {
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }

    const fallback = new Date(s);
    return isNaN(fallback.getTime()) ? null : fallback;
  } catch (err) {
    console.error("parseManilaDateSafe error:", input, err);
    return null;
  }
};



const txn = log.raw || findReportTxnForLog(log) || {};

// ── Targeted discount info (used by All Services column for strike-through display) ──
const _txItemDiscounts = log.item_discounts || txn?.item_discounts || {};
const _discountedPids = new Set();
let _discountPct = 0;
try {
  Object.entries(_txItemDiscounts).forEach(([ik, info]) => {
    const d = ik.lastIndexOf("-");
    const pid = d > 0 ? ik.substring(0, d) : ik;
    if (pid) _discountedPids.add(pid);
    if (info?.type === "pwd" || info?.type === "senior") _discountPct = 20;
  });
} catch (_) {}
const _hasTargeted = _discountedPids.size > 0 && _discountPct > 0;
const _logSvcType = String(log.service_type || "").trim();

// --- TIME IN ---
let timeIn = null;
if (log.was_resumed && log.resumed_time_in) {
  // resumed booking (highest priority)
  timeIn = parseManilaDate(log.resumed_time_in);
} else if (log.time_in) {
  // explicit time stored on the log (guest logs / resumed-created logs)
  timeIn = parseManilaDate(log.time_in);
} else if (txn?.start_time) {
  // fallback to transaction start_time "HH:MM"
  const [h, m] = txn.start_time.split(":").map(Number);
  const today = new Date();
  timeIn = new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0, 0);
}
// ✅ PRIORITY 1: Use resumed_time_in if this log was resumed


// --- Golf TIME IN ---
let golfTimeIn = null;
if (log.time_in && log.service_type === "Golf Rates") {
  // Per-log explicit time (guests or resumed logs) should take precedence
  golfTimeIn = parseManilaDate(log.time_in) || null;
} else if (txn?.startgolf_time) {
  // Fallback to transaction tee time only when log has no explicit time_in
  if (String(txn.startgolf_time).includes("T") || String(txn.startgolf_time).includes("-")) {
    golfTimeIn = new Date(txn.startgolf_time);
  } else if (/^\d{1,2}:\d{2}$/.test(String(txn.startgolf_time))) {
    const [h, m] = String(txn.startgolf_time).split(":").map(Number);
    const today = new Date();
    golfTimeIn = new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0, 0);
  }
}

// --- TIME OUT ---
let timeOut = null;
if (log.was_resumed && log.resumed_time_out) {
  // resumed booking (highest priority)
  timeOut = parseManilaDate(log.resumed_time_out);
} else if (log.time_out) {
  // explicit end time on the log (guest logs / resumed-created logs)
  timeOut = parseManilaDate(log.time_out);
} else if (timeIn) {
  // compute from planned duration if no explicit time_out
  const planned = getPlannedDurationMinutes(log);
  if (planned > 0) {
    timeOut = new Date(timeIn.getTime() + planned * 60000);
  }
}

// --- GOLF TIME OUT --- (replace previous block similarly)
let golfTimeOut = null;
if (log.time_out && log.service_type === "Golf Rates") {
  golfTimeOut = parseManilaDate(log.time_out) || null;
} else if (txn?.endgolf_time) {
  if (String(txn.endgolf_time).includes("T") || String(txn.endgolf_time).includes("-")) {
    golfTimeOut = new Date(txn.endgolf_time);
  } else if (/^\d{1,2}:\d{2}$/.test(String(txn.endgolf_time))) {
    const [h, m] = String(txn.endgolf_time).split(":").map(Number);
    const today = new Date();
    golfTimeOut = new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0, 0);
  }
} else if (golfTimeIn) {
  // default duration for golf (4 hours) if nothing else provided
  golfTimeOut = new Date(golfTimeIn.getTime() + 4 * 60 * 60 * 1000);
}

// If neither explicit end exists, compute from effectiveStart + planned duration
const planned = Math.round(Number(getPlannedDurationMinutes(log) || 0));
const effectiveStart = (log.service_type === "Golf Rates") ? (golfTimeIn || timeIn) : timeIn;
if (!(golfTimeOut instanceof Date) && !(timeOut instanceof Date) && effectiveStart && planned > 0) {
  const computed = new Date(effectiveStart.getTime() + planned * 60000);
  if (log.service_type === "Golf Rates") golfTimeOut = computed;
  else timeOut = computed;
}

// Normalize next-day cases
const effectiveEnd = (log.service_type === "Golf Rates") ? (golfTimeOut || timeOut) : timeOut;
let normalizedEnd = effectiveEnd;
if (effectiveStart instanceof Date && normalizedEnd instanceof Date && normalizedEnd.getTime() <= effectiveStart.getTime()) {
  normalizedEnd = new Date(normalizedEnd.getTime() + 24 * 60 * 60 * 1000);
}

// Final display values (may be null)
const displayTimeIn = effectiveStart instanceof Date && !isNaN(effectiveStart.getTime()) ? effectiveStart : null;
const displayTimeOut = normalizedEnd instanceof Date && !isNaN(normalizedEnd.getTime()) ? normalizedEnd : null;

// Calculate duration using existing helper
const durationInfo = computeDurationForLog({
  log,
  timeIn: displayTimeIn,
  timeOut: displayTimeOut,
  golfTimeIn: displayTimeIn,
  golfTimeOut: displayTimeOut,
  transactions: reportJoinTransactions,
  products
});

   // --- SERVICE-SPECIFIC PAYMENT ALLOCATION ---
  // Build txnItems and product lookup
  const txnItems = Array.isArray(txn.items) ? txn.items : (Array.isArray(log.items) ? log.items : []);
  const prodById = (products || []).reduce((acc, p) => { acc[String(p.id)] = p; return acc; }, {});
  const serviceKey = String(log.service_type || "").trim();

  // expand each item into per-unit tokens (strict: require numeric price & qty)
   const tokens = [];
  (txnItems || []).forEach(it => {
    const product = it.product || (it.product_id ? prodById[String(it.product_id)] : null);
    const name = String(it.product_name || it.name || product?.name || "");
    const qty = Math.max(0, Number(it.quantity ?? it.qty ?? 1) || 0);
    const price = Number(it.price ?? it.unit_price ?? it.amount ?? NaN);
    if (!Number.isFinite(price) || qty <= 0) return;

    const matchesDriving = (product && product.category === "Driving Range Rates") || /driving range/i.test(name);
    const matchesGolf = (product && product.category === "Golf Rates") || /\bgolf\b/i.test(name);

    if ((serviceKey === "Driving Range Rates" && matchesDriving) ||
        (serviceKey === "Golf Rates" && matchesGolf)) {
      // infer holes for golf products when possible
      const inferHoles = (() => {
        if (product && (Number(product.holes) || Number(product.total_holes))) {
          return Number(product.holes || product.total_holes) || null;
        }
        if (/9\s*-?\s*holes|nine\s*holes/i.test(name)) return 9;
        if (/18\s*-?\s*holes|eighteen\s*holes/i.test(name)) return 18;
        return null;
      })();

      const serviceType = matchesGolf ? "golf" : (matchesDriving ? "driving" : "other");

      for (let i = 0; i < qty; i++) {
        // Prefer canonical product.price when available (unit price).
        // Otherwise fall back to item price divided by quantity (handles line-total cases).
        const productUnit = product && Number.isFinite(Number(product.price)) ? Number(product.price) : null;
        const inferredUnit = productUnit ?? (Number(price) / Math.max(1, qty));
        const unitAmount = Math.round(inferredUnit * 100) / 100;

        tokens.push({
          product_id: product?.id ?? null,
          name: product?.name || name,
          unitAmount: unitAmount || 0,
          service: serviceType,
          holes: inferHoles
        });
      }
    }
  });

  const serviceMatchedTotal = tokens.reduce((s, t) => s + (t.unitAmount || 0), 0);
  // Row totals (from normalized row or txn)
  const rowCardTotal = Number(log.card_gcash ?? txn.card_gcash ?? txn.card_gcash_total ?? 0) || 0;
  const rowCashTotal = Number(log.cash ?? txn.cash ?? 0) || 0;
  const pm = String(txn.payment_method || log.payment_method || "").toLowerCase();

  // Build players list for this log (main player + guests)
  const players = [];
  // main player (only if not a guest log)
  if (!log.is_guest) {
    players.push({
      id: log.id || `${log.or_number}-main`,
      name: String(log.player_name || log.member_name || txn?.member_name || "Player").trim(),
      preferred_product_id: log?.selected_service?.product_id ?? null
    });
  }
  // guests from common fields (driving range guest_bookings, golf guest arrays)
  const guestLists = [].concat(Array.isArray(log.guest_bookings) ? log.guest_bookings : [],
                               Array.isArray(log.golf_guest_bookings) ? log.golf_guest_bookings : []);
  guestLists.forEach((g, i) => {
    players.push({
      id: g.id ?? `${log.or_number}-guest-${i}`,
      name: String(g.guest_name || g.player_name || `Guest ${i + 1}`).trim(),
      preferred_product_id: g?.product_id ?? g?.selected_service?.product_id ?? null
    });
  });

  // allocation: try match tokens -> players by product_id preference, else FIFO assign
  const assigned = []; // { token, player }
  const playerAssignedCounts = {}; // id -> count

  // 1) match by product_id preference
  tokens.forEach((token) => {
    let matchedPlayer = null;
    if (token.product_id != null) {
      matchedPlayer = players.find(p => p.preferred_product_id != null && String(p.preferred_product_id) === String(token.product_id));
    }
    if (matchedPlayer) {
      assigned.push({ token, player: matchedPlayer });
      playerAssignedCounts[matchedPlayer.id] = (playerAssignedCounts[matchedPlayer.id] || 0) + 1;
    } else {
      // leave for second pass
      assigned.push({ token, player: null });
    }
  });

  // 2) assign remaining tokens to players FIFO (main then guests)
  let nextPlayerIndex = 0;
  for (let i = 0; i < assigned.length; i++) {
    if (assigned[i].player) continue;
    if (players.length === 0) break;
    const p = players[nextPlayerIndex % players.length];
    assigned[i].player = p;
    playerAssignedCounts[p.id] = (playerAssignedCounts[p.id] || 0) + 1;
    nextPlayerIndex++;
  }
  // Build payment fragments aggregated by player + product + kind,
  // but track quantity and unit price so the label can show "×N (₱unit each)".
  let paymentFragments = [];
  if (tokens.length > 0) {
    const fragMap = new Map();
    const mixedRowTotal = rowCardTotal + rowCashTotal;
    const hasMixedRow = mixedRowTotal > 0 && rowCardTotal > 0 && rowCashTotal > 0;

    const pushToMap = (key, payload) => {
      if (!fragMap.has(key)) fragMap.set(key, { ...payload });
      else {
        const ex = fragMap.get(key);
        ex.qty = (ex.qty || 0) + (payload.qty || 0);
        ex.amount = Math.round((Number(ex.amount || 0) + Number(payload.amount || 0)) * 100) / 100;
        // keep unit as payload.unit (assume consistent)
        fragMap.set(key, ex);
      }
    };

    assigned.forEach(({ token, player }) => {
      const playerName = player?.name || null;
      const unit = Number(token.unitAmount || 0);
      const qty = 1; // tokens are already expanded per unit
      const totalForToken = unit * qty;

      // decide kind per-row (mixed -> split proportionally)
      if (hasMixedRow) {
        const cardPart = Math.round((totalForToken * (rowCardTotal / mixedRowTotal)) * 100) / 100;
        const cashPart = Math.round((totalForToken - cardPart) * 100) / 100;
        if (cardPart > 0) {
          const key = `${String(player?.id ?? "anon")}|${String(token.product_id ?? "noprod")}|card`;
          pushToMap(key, {
            playerName,
            name: token.name,
            product_id: token.product_id,
            service: token.Service,
            holes: token.holes,
            unit,
            qty,
            amount: cardPart,
            kind: "card"
          });
        }
        if (cashPart > 0) {
          const key = `${String(player?.id ?? "anon")}|${String(token.product_id ?? "noprod")}|cash`;
          pushToMap(key, {
            playerName,
            name: token.name,
            product_id: token.product_id,
            service: token.Service,
            holes: token.holes,
            unit,
            qty,
            amount: cashPart,
            kind: "cash"
          });
        }
      } else {
        let kind = (pm === "cash") ? "cash" : (pm === "card" || pm === "online") ? "card" : null;
        if (!kind) kind = rowCardTotal > 0 ? "card" : (rowCashTotal > 0 ? "cash" : "unknown");
        const key = `${String(player?.id ?? "anon")}|${String(token.product_id ?? "noprod")}|${kind}`;
        pushToMap(key, {
          playerName,
          name: token.name,
          product_id: token.product_id,
          service: token.Service,
          holes: token.holes,
          unit,
          qty,
          amount: totalForToken,
          kind
        });
      }
    });

    // convert map entries into final fragments and format label to show per-unit info
    paymentFragments = Array.from(fragMap.values()).map(f => {
      const unitLabel = Number.isFinite(f.unit) && f.unit > 0
        ? ` (₱${f.unit.toLocaleString("en-PH", { minimumFractionDigits: 2 })} each)`
        : "";
      const playerPrefix = f.playerName ? `${f.playerName} — ` : "";
      // Show only per-unit price in the label and use unit price for amount (not line total)
      // TODO: ... 
      return {
        label: `${f.name}`,
        amount: Number(f.unit || 0), // <-- changed: show unit price instead of total
        kind: f.kind,
        playerId: null,
        product_id: f.product_id,
        service: f.service,
        holes: f.holes
      };
    });
  } else {
    // fallback: show row totals as-is
    if (rowCardTotal > 0) paymentFragments.push({ label: "Card/GCash", amount: rowCardTotal, kind: "card", service: serviceKey || null });
    if (rowCashTotal > 0) paymentFragments.push({ label: "Cash", amount: rowCashTotal, kind: "cash", service: serviceKey || null });
  }

  
  return (
    <tr 
      key={log.id || i} 
      className="border-b hover:bg-gray-50"
    >
      <td className="px-3 py-2 font-mono text-xs">{i + 1}</td>
      <td className="px-3 py-2">
  {(() => {
    const computeHolesLabel = (log) => {
      // 1) explicit backend-provided selected hole count
      if (Number.isFinite(Number(log?.golfhole))) return `${Number(log.golfhole)}H`;

      // 2) if individual_courses present, build combined label (e.g. "27H (9H+18H)")
      if (Array.isArray(log?.individual_courses) && log.individual_courses.length) {
        const parts = log.individual_courses
          .map(c => {
            const h = Number(c?.holes);
            return Number.isFinite(h) && h > 0 ? `${h}H` : null;
          })
          .filter(Boolean);
        if (parts.length === 1) return parts[0];
        if (parts.length > 1) {
          const total = parts.reduce((s, p) => s + Number(p.replace("H", "")), 0);
          return `${total}H (${parts.join("+")})`;
        }
      }

      // 3) fallback to total_holes
      if (Number.isFinite(Number(log?.total_holes))) return `${Number(log.total_holes)}H`;

      // 4) last-resort parse of course_type text
      const ct = String(log?.course_type || "").toLowerCase();
      if (ct.includes("9")) return "9H";
      return "18H";
    };

    return log.bay_number ? (
      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">B{log.bay_number}</span>
    ) : (log.total_holes || log.individual_courses) ? (
      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{computeHolesLabel(log)}</span>
    ) : "-";
  })()}
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        <div>{log.or_number || "-"}</div>
        {cashierSiteBadge(log)}
      </td>
      <td className="px-3 py-2 font-medium">{log.player_name || log.member_name || "Walk-in"}</td>
      <td className="px-3 py-2">
        <span className={`text-xs px-2 py-1 rounded ${log.service_type === "Golf Rates" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
          {log.service_type || "Unknown"}
        </span>
      </td>
      {/* All Services column - shows all items with original & discounted prices */}
      <td className="px-3 py-2">
        <div className="text-xs space-y-1 max-w-[240px]">
          {Array.isArray(log.all_services) && log.all_services.length > 0 ? (
            <>
              {log.all_services.slice(0, 4).map((svc, idx) => {
                const qty = svc.quantity || 1;
                const unitPrice = Number(svc.unit_price || 0);
                const svcLineTotal = unitPrice * qty;
                const svcPid = String(svc.product_id || "");
                const svcCat = String(svc.category || "");

                // how many instances of this product in THIS log were targeted
                const discountedCounts = (log.discounted_unit_counts) || {};
                const qtyDiscountedForThisLog = Math.min(Number(discountedCounts[svcPid] || 0), qty);

                const discountedUnitPrice = Math.round(unitPrice * (1 - (_discountPct / 100)) * 100) / 100;
                const discountedPortion = qtyDiscountedForThisLog * discountedUnitPrice;
                const nonDiscountedPortion = (qty - qtyDiscountedForThisLog) * unitPrice;
                const finalLineTotal = Math.round((discountedPortion + nonDiscountedPortion) * 100) / 100;

                const colorClass = svc.category === "Golf Rates" ? "text-blue-600" : svc.category === "Driving Range Rates" ? "text-green-600" : "text-gray-600";

                return (
                  <div key={idx} title={`${svc.name} — ₱${svcLineTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`}>
                    <span className={colorClass}>
                      {qty > 1 ? `${qty}× ` : ""}{svc.name}
                    </span>
                    <div className="flex items-center gap-1 mt-0.5">
                      {qtyDiscountedForThisLog > 0 ? (
                        qtyDiscountedForThisLog === qty ? (
                          <>
                            <span className="line-through text-gray-400">₱{svcLineTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                            <span className="text-green-600 font-medium">₱{finalLineTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-gray-500">₱{nonDiscountedPortion.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                            <span className="text-green-600 font-medium"> + ₱{discountedPortion.toLocaleString("en-PH", { minimumFractionDigits: 2 })} (discounted)</span>
                          </>
                        )
                      ) : (
                        <span className="text-gray-500">₱{svcLineTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {log.all_services.length > 4 && (
                <div className="text-gray-400">+{log.all_services.length - 4} more</div>
              )}
            </>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      </td>
      {/* Items Added column - shows non-service add-on items (e.g. bucket of balls, merchandise) */}
      {/* Only the main player in a group booking shows items; secondary players have items_added=[] */}
      <td className="px-3 py-2">
        <div className="text-xs space-y-0.5 max-w-[200px]">
          {(() => {
            // Prefer backend-computed items_added (respects group distribution)
            let addOns;
            if (Array.isArray(log.items_added)) {
              addOns = log.items_added;
            } else if (Array.isArray(log.all_services)) {
              addOns = log.all_services.filter(svc => svc.category && svc.category !== "Golf Rates" && svc.category !== "Driving Range Rates");
            } else {
              addOns = [];
            }
            if (addOns.length === 0) return <span className="text-gray-400">-</span>;
            const total = addOns.reduce((sum, item) => sum + (Number(item.unit_price || 0) * (item.quantity || 1)), 0);
            return (
              <>
                {addOns.map((item, idx) => {
                  const qty = item.quantity || 1;
                  const price = Number(item.unit_price || 0);
                  const colorClass = item.category === "Food & Beverage" ? "text-orange-600"
                    : item.category === "Merchandise" ? "text-purple-600"
                    : "text-gray-600";
                  return (
                    <div key={idx} className={colorClass}>
                      {qty > 1 ? `${qty}× ` : ""}{item.name}
                      <span className="text-gray-400 ml-1">₱{(price * qty).toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                    </div>
                  );
                })}
                {total > 0 && (
                  <div className="text-green-700 font-semibold border-t border-gray-200 mt-0.5 pt-0.5">
                    Total: ₱{total.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </td>
      <td className="px-3 py-2 text-xs">
        {displayTimeIn ? displayTimeIn.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true }) : "-"}
      </td>
      <td className="px-3 py-2 text-xs">
        {displayTimeOut ? displayTimeOut.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true }) : "-"}
      </td>
      
      <td className="px-3 py-2">
        <div>{durationInfo.text}</div>
      </td>

      {/* Original Amount column */}
      <td className="px-3 py-2 text-xs text-right">
        {Number.isFinite(log.original_amount) && log.original_amount > 0 ? (
          <span className="text-gray-700">₱{log.original_amount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>

      {/* Discount column — backend computes targeted discount correctly */}
      <td className="px-3 py-2 text-xs text-right">
        {(log.discount_amount || 0) > 0 ? (
          <div>
            <span className="text-red-600 font-medium">-₱{log.discount_amount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
            {log.discount_type && (
              <div className="text-xs text-gray-500 mt-0.5">
                {log.discount_type === "pwd" ? "🦽 PWD" : log.discount_type === "senior" ? "👴 Senior" : log.discount_type}
              </div>
            )}
            {log.discount_applied_by && (
              <div className="text-xs text-gray-400 truncate max-w-[80px]" title={`Applied by: ${log.discount_applied_by}`}>
                by: {log.discount_applied_by}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>

      {/* Payment column — uses backend's correctly computed discounted amount */}
      <td className="px-3 py-2">
        <div className="text-xs space-y-1">
          {(() => {
            const discountedAmount = Number.isFinite(log.discounted_amount) && log.discounted_amount > 0
              ? log.discounted_amount
              : Number.isFinite(log.original_amount) && log.original_amount > 0
                ? log.original_amount
                : Number(txn.total_amount) > 0
                  ? Number(txn.total_amount)
                  : null;
            const pm = String(txn.payment_method || log.payment_method || "").toLowerCase();
            
            if (discountedAmount !== null && discountedAmount > 0) {
              const isCash = pm === "cash";
              return (
                <div className={isCash ? "text-green-600 font-medium" : "text-blue-600 font-medium"}>
                  {isCash ? "💵" : "💳"} ₱{discountedAmount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                </div>
              );
            } else if (paymentFragments.length === 0) {
              return <span className="text-gray-400">-</span>;
            } else {
              return paymentFragments.map((p, idx) => (
                <div key={idx} className={p.kind === "cash" ? "text-green-600" : "text-blue-600"}>
                  {p.kind === "cash" ? "💵" : "💳"} {p.label ? `${p.label}: ` : ""}₱{p.amount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                </div>
              ));
            }
          })()}
        </div>
      </td>
    </tr>
  );
})}
            </tbody>
          </table>
        )}
      </div>
        </TabsContent>

        {/* --- MANAGE TAB (with Action column) --- */}
        <TabsContent value="manage">
      <div className="overflow-auto max-h-[420px]">
        {normalizedPlayerLogs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium text-lg">No player logs found</p>
            <p className="text-sm">No records found for {headerDateDisplay}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr className="text-xs text-left">
                <th className="px-3 py-2">No.</th>
                <th className="px-3 py-2">Bay/Course</th>
                <th className="px-3 py-2">OR Number</th>
                <th className="px-3 py-2">Player</th>
                <th className="px-3 py-2">Service</th>
                <th className="px-3 py-2">Time In</th>
                <th className="px-3 py-2">Time Out</th>
                <th className="px-3 py-2">Payment</th>
                <th className="px-3 py-2">Puncher</th>
                <th className="px-3 py-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedPlayerLogs.map((log, i) => {
                const txn = findReportTxnForLog(log) || {};
                const pm = String(txn.payment_method || log.payment_method || "").toLowerCase();
                const displayAmount = Number.isFinite(log.discounted_amount) && log.discounted_amount > 0
                  ? log.discounted_amount
                  : Number.isFinite(log.original_amount) && log.original_amount > 0
                    ? log.original_amount
                    : Number(txn.total_amount) > 0
                      ? Number(txn.total_amount)
                      : 0;

                // Time In display
                let manageTimeIn = null;
                if (log.time_in) {
                  try { manageTimeIn = new Date(String(log.time_in).includes("T") || String(log.time_in).includes("+") ? log.time_in : log.time_in + "+08:00"); } catch(_){}
                }
                // Time Out display
                let manageTimeOut = null;
                if (log.time_out) {
                  try { manageTimeOut = new Date(String(log.time_out).includes("T") || String(log.time_out).includes("+") ? log.time_out : log.time_out + "+08:00"); } catch(_){}
                }

                return (
                  <tr key={log.id || i} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{i + 1}</td>
                    <td className="px-3 py-2">
                      {log.bay_number ? (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">B{log.bay_number}</span>
                      ) : (log.total_holes || log.individual_courses) ? (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {log.total_holes || log.golfhole || "18"}H
                        </span>
                      ) : "-"}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      <div>{log.or_number || "-"}</div>
                      {cashierSiteBadge(log)}
                    </td>
                    <td className="px-3 py-2 font-medium">{log.player_name || log.member_name || "Walk-in"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-1 rounded ${log.service_type === "Golf Rates" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                        {log.service_type || "Unknown"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {manageTimeIn && !isNaN(manageTimeIn.getTime())
                        ? manageTimeIn.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true })
                        : "-"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {manageTimeOut && !isNaN(manageTimeOut.getTime())
                        ? manageTimeOut.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true })
                        : "-"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {displayAmount > 0 ? (
                        <span className={pm === "cash" ? "text-green-600 font-medium" : "text-blue-600 font-medium"}>
                          {pm === "cash" ? "💵" : "💳"} ₱{displayAmount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                        </span>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {log.punched_by_name || "-"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setReportDeleteConfirm(log);
                          fetchReportDeletePreview(log.id);
                        }}
                        title="Delete player log"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
        </TabsContent>
      </Tabs>
        </CardContent>
      </Card>

      {/* --- Delete Player Log from Report Confirmation Dialog --- */}
      <Dialog
        open={!!reportDeleteConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setReportDeleteConfirm(null);
            setReportDeletePreview(null);
            setReportDeleteAlsoTxn(true);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Delete Player Log
            </DialogTitle>
            <DialogDescription>
              This will permanently remove this player log entry from the report. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {reportDeleteConfirm && (
            <div className="space-y-4">
              {/* Player Log Summary */}
              <div className="p-3 bg-red-50 rounded border border-red-200">
                <p className="text-xs font-semibold text-red-700 mb-2 uppercase tracking-wide">Player Log</p>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Player</span>
                    <span className="font-medium">{reportDeleteConfirm.player_name || reportDeleteConfirm.member_name || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">OR Number</span>
                    <span className="font-medium">{reportDeleteConfirm.or_number || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{reportDeleteConfirm.service_type === "Golf Rates" ? "Holes" : "Bay"}</span>
                    <span className="font-medium">
                      {reportDeleteConfirm.service_type === "Golf Rates"
                        ? (reportDeleteConfirm.total_holes || reportDeleteConfirm.golfhole || "-") + "H"
                        : reportDeleteConfirm.bay_number ? `B${reportDeleteConfirm.bay_number}` : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service</span>
                    <span className="font-medium">{reportDeleteConfirm.service_type || "-"}</span>
                  </div>
                  {reportDeleteConfirm.punched_by_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Punched by</span>
                      <span className="font-medium">{reportDeleteConfirm.punched_by_name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Transaction Summary (from preview) */}
              {isLoadingReportDeletePreview && (
                <div className="flex items-center gap-2 text-sm text-gray-500 p-3">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading transaction details...
                </div>
              )}

              {reportDeletePreview?.transaction && (
                <div className="p-3 bg-orange-50 rounded border border-orange-200">
                  <p className="text-xs font-semibold text-orange-700 mb-2 uppercase tracking-wide">Associated Transaction (Sales Report)</p>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">OR Number</span>
                      <span className="font-medium">{reportDeletePreview.transaction.or_number || "-"}</span>
                    </div>
                    {reportDeletePreview.transaction.items?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <span className="text-gray-600 text-xs font-medium">Items:</span>
                        {reportDeletePreview.transaction.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between pl-2 text-xs">
                            <span className="text-gray-700">{item.quantity || 1}× {item.product_name || item.name || "Item"}</span>
                            <span className="font-medium">₱{Number(item.price || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-between border-t border-orange-200 pt-1 mt-1">
                      <span className="text-gray-700 font-semibold">Total</span>
                      <span className="font-bold text-orange-800">₱{Number(reportDeletePreview.transaction.total || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Checkbox: also delete from sales report */}
              {reportDeletePreview?.transaction && (
                <label className="flex items-start gap-2 p-2 rounded bg-yellow-50 border border-yellow-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-red-600"
                    checked={reportDeleteAlsoTxn}
                    onChange={(e) => setReportDeleteAlsoTxn(e.target.checked)}
                  />
                  <span className="text-sm text-yellow-900">
                    <strong>Also delete transaction from sales report</strong>
                    <br />
                    <span className="text-xs text-yellow-700">This will remove transaction OR #{reportDeletePreview.transaction.or_number} (₱{Number(reportDeletePreview.transaction.total || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}) from sales records.</span>
                  </span>
                </label>
              )}

              {/* No transaction found message */}
              {!isLoadingReportDeletePreview && reportDeletePreview && !reportDeletePreview.transaction && (
                <div className="text-sm text-gray-500 italic p-2">
                  No associated transaction found in sales report.
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setReportDeleteConfirm(null);
                    setReportDeletePreview(null);
                    setReportDeleteAlsoTxn(true);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={async () => {
                    try {
                      const shouldDeleteTxn = reportDeleteAlsoTxn && !!reportDeletePreview?.transaction;
                      await handleReportDeletePlayerLog(reportDeleteConfirm, shouldDeleteTxn);
                    } catch (e) {
                      console.error("Error deleting report player log:", e);
                    } finally {
                      setReportDeleteConfirm(null);
                      setReportDeletePreview(null);
                      setReportDeleteAlsoTxn(true);
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {reportDeleteAlsoTxn && reportDeletePreview?.transaction ? "Delete Log & Transaction" : "Delete Log"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* OR Number Assignment Modal */}
      <Dialog
        open={!!orModalTxn}
        onOpenChange={(open) => {
          if (!open) {
            setOrModalTxn(null);
            setOrModalValue("");
            setOrModalList([]);
          }
        }}
      >
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-lg">Assign OR Number</span>
              {orModalTxn?.or_number && (
                <span className="text-sm font-mono bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                  Current: OR #{orModalTxn.or_number}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {orModalTxn?.timestamp
                ? new Date(orModalTxn.timestamp).toLocaleString("en-PH", {
                    timeZone: "Asia/Manila",
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : ""}
              {orModalTxn?.id && (
                <span className="ml-2 text-gray-400">
                  ID: #{orModalTxn.id.split("_")[1] || orModalTxn.id.substr(-8)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {orModalTxn && (
            <div className="space-y-4">
              {/* Transaction Info */}
              <div className="border rounded-lg p-3 bg-gradient-to-r from-gray-50 to-white">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Customer</div>
                    <div className="font-semibold text-gray-800">{orModalTxn.member_name || "Walk-in"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Payment</div>
                    <div className="font-medium text-gray-700 capitalize">{orModalTxn.payment_method || "-"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Amount</div>
                    <div className="font-bold text-gray-900">{formatCurrency(orModalTxn.total_amount)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Closed By</div>
                    <div className="font-medium text-gray-700">{orModalTxn.closed_by_cashier_name || orModalTxn.cashier || "-"}</div>
                  </div>
                </div>
              </div>

              {/* OR Number Selection */}
              <div className="border rounded-lg p-3 bg-white">
                <Label className="text-xs font-semibold text-gray-700">OR Number</Label>
                <div className="mt-1 flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="text-sm w-full h-9 px-3 justify-between bg-white border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 flex-1"
                        disabled={orModalLoading || orModalSaving}
                      >
                        <span className={orModalValue ? "text-gray-900 font-medium" : "text-gray-400"}>
                          {orModalLoading
                            ? "Loading OR numbers..."
                            : orModalValue
                            ? `OR #${orModalValue}`
                            : "Select OR number from booklet"}
                        </span>
                        <span className="ml-2 text-gray-400">▼</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[--radix-popper-anchor-width] max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                      {orModalList.length === 0 ? (
                        <DropdownMenuItem disabled className="px-3 py-2 text-sm text-gray-400">
                          No OR numbers available
                        </DropdownMenuItem>
                      ) : (
                        orModalList.map((num) => (
                          <DropdownMenuItem
                            key={num}
                            onClick={() => setOrModalValue(String(num))}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-600 hover:text-white focus:bg-blue-600 focus:text-white"
                          >
                            {num}
                          </DropdownMenuItem>
                        ))
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => { setOrModalTxn(null); setOrModalValue(""); setOrModalList([]); }}
                  disabled={orModalSaving}
                  className="h-9 px-4 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveOrModal}
                  disabled={!orModalValue.trim() || orModalSaving || orModalLoading}
                  className="h-9 px-4 text-xs"
                >
                  {orModalSaving ? (
                    <div className="flex items-center space-x-1.5">
                      <div className="w-3 h-3 border-b-2 border-white rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </div>
                  ) : (
                    "Save OR Number"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* --- Delete Transaction Confirmation Dialog --- */}
      <Dialog
        open={!!deleteTxnConfirm}
        onOpenChange={(open) => { if (!open) { setDeleteTxnConfirm(null); setDeleteTxnAlsoLogs(true); } }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Delete Transaction
            </DialogTitle>
            <DialogDescription>
              This will permanently remove this transaction from the sales report. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteTxnConfirm && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 rounded border border-red-200">
                <p className="text-xs font-semibold text-red-700 mb-2 uppercase tracking-wide">Transaction Details</p>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID</span>
                    <span className="font-mono text-xs">#{deleteTxnConfirm.id?.split("_")[1] || deleteTxnConfirm.id?.substr(-8)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">OR Number</span>
                    <span className="font-medium">{deleteTxnConfirm.or_number || "Not assigned"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Customer</span>
                    <span className="font-medium">{deleteTxnConfirm.member_name || "Walk-in"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment</span>
                    <span className="font-medium capitalize">{deleteTxnConfirm.payment_method || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-bold text-red-800">{formatCurrency(deleteTxnConfirm.total_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Items</span>
                    <span className="font-medium">{deleteTxnConfirm.items_count || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Closed By</span>
                    <span className="font-medium">{deleteTxnConfirm.closed_by_cashier_name || deleteTxnConfirm.cashier || "Unknown"}</span>
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-2 p-2 rounded bg-yellow-50 border border-yellow-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-red-600"
                  checked={deleteTxnAlsoLogs}
                  onChange={(e) => setDeleteTxnAlsoLogs(e.target.checked)}
                />
                <span className="text-sm text-yellow-900">
                  <strong>Also delete associated player logs</strong>
                  <br />
                  <span className="text-xs text-yellow-700">This will remove any player log entries linked to this transaction.</span>
                </span>
              </label>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setDeleteTxnConfirm(null); setDeleteTxnAlsoLogs(true); }}
                  disabled={isDeletingTxn}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleDeleteTransaction}
                  disabled={isDeletingTxn}
                >
                  {isDeletingTxn ? (
                    <div className="flex items-center space-x-1.5">
                      <div className="w-3 h-3 border-b-2 border-white rounded-full animate-spin"></div>
                      <span>Deleting...</span>
                    </div>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-1" />{deleteTxnAlsoLogs ? "Delete Transaction & Logs" : "Delete Transaction"}</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* --- END PLAYER LOGS TABLE --- */}
    </div>
  );
}



// Membership Type Management Component
function MembershipTypeManagement({ token, user }) {
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({
    name: "",
    discount_percentage: "",
    description: "",
    benefits: [],
  });

  // initial form state and helper to open the Create dialog with a cleared form
  const initialTypeForm = {
    name: "",
    discount_percentage: "",
    description: "",
    benefits: [],
  };

  const openCreateMembershipDialog = () => {
    setTypeForm(initialTypeForm);
    setIsCreateDialogOpen(true);
  };


  const fetchMembershipTypes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/membership-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setMembershipTypes(data);
    } catch (error) {
      console.error("Error fetching membership types:", error);
    }
  };

  const handleCreateMembershipType = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/membership-types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...typeForm,
          discount_percentage: parseFloat(typeForm.discount_percentage),
          benefits: typeForm.benefits || [],
        }),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setTypeForm({
          name: "",
          discount_percentage: "",
          description: "",
          benefits: [],
        });
        fetchMembershipTypes();
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to create membership type");
      }
    } catch (error) {
      console.error("Error creating membership type:", error);
      alert("Error creating membership type");
    }
  };

  const handleUpdateMembershipType = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...typeForm,
        discount_percentage: typeForm.discount_percentage
          ? parseFloat(typeForm.discount_percentage)
          : undefined,
      };

      const response = await fetch(
        `${BACKEND_URL}/api/membership-types/${selectedType.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedType(null);
        fetchMembershipTypes();
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to update membership type");
      }
    } catch (error) {
      console.error("Error updating membership type:", error);
      alert("Error updating membership type");
    }
  };

  const handleDeleteMembershipType = async (typeId, typeName) => {
    if (user?.role !== "admin") {
      alert("Access denied. Only admin users can delete membership types.");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete "${typeName}" membership type? This action cannot be undone.`
      )
    ) {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/membership-types/${typeId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          fetchMembershipTypes();
        } else {
          const error = await response.json();
          alert(error.detail || "Failed to delete membership type");
        }
      } catch (error) {
        console.error("Error deleting membership type:", error);
        alert("Error deleting membership type");
      }
    }
  };

  const addBenefit = () => {
    setTypeForm({
      ...typeForm,
      benefits: [...(typeForm.benefits || []), ""],
    });
  };

  const updateBenefit = (index, value) => {
    const newBenefits = [...(typeForm.benefits || [])];
    newBenefits[index] = value;
    setTypeForm({
      ...typeForm,
      benefits: newBenefits,
    });
  };

  const removeBenefit = (index) => {
    const newBenefits = [...(typeForm.benefits || [])];
    newBenefits.splice(index, 1);
    setTypeForm({
      ...typeForm,
      benefits: newBenefits,
    });
  };

  const getMembershipIcon = (name) => {
    const lowercaseName = name.toLowerCase();
    if (lowercaseName.includes("vip") || lowercaseName.includes("platinum"))
      return Crown;
    if (lowercaseName.includes("premium") || lowercaseName.includes("gold"))
      return Award;
    if (lowercaseName.includes("standard") || lowercaseName.includes("silver"))
      return Star;
    return Users;
  };

  const getMembershipColor = (name) => {
    const lowercaseName = name.toLowerCase();
    if (lowercaseName.includes("vip") || lowercaseName.includes("platinum"))
      return "text-purple-600 bg-purple-100";
    if (lowercaseName.includes("premium") || lowercaseName.includes("gold"))
      return "text-yellow-600 bg-yellow-100";
    if (lowercaseName.includes("standard") || lowercaseName.includes("silver"))
      return "text-blue-600 bg-blue-100";
    return "text-gray-600 bg-gray-100";
  };

  useEffect(() => {
    fetchMembershipTypes();
  }, []);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Membership Type Management</h2>
          <p className="text-gray-600">
            Configure membership tiers and benefits
          </p>
        </div>
        <Button
          onClick={openCreateMembershipDialog}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          <Crown className="w-4 h-4 mr-2" />
          Add Membership Type
        </Button>
      </div>

      {/* Membership Types Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {membershipTypes.map((type) => {
          const IconComponent = getMembershipIcon(type.name);
          const colorClass = getMembershipColor(type.name);

          return (
            <Card
              key={type.id}
              className="transition-shadow border-l-4 hover:shadow-lg border-l-emerald-500"
            >
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full ${colorClass}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          {type.name}
                        </h3>
                        <Badge
                          variant="outline"
                          className="text-emerald-600 border-emerald-300"
                        >
                          {type.discount_percentage}% discount
                        </Badge>
                      </div>
                    </div>
                    <Badge 
                    style={{ pointerEvents: "none" }}
                    variant={type.is_active ? "default" : "secondary"}
                    
                    >
                      {type.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>

                  <p className="text-sm text-gray-600">{type.description}</p>

                  {type.benefits && type.benefits.length > 0 && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-gray-700">
                        Benefits:
                      </h4>
                      <ul className="space-y-1 text-xs text-gray-600">
                        {type.benefits.slice(0, 3).map((benefit, index) => (
                          <li
                            key={index}
                            className="flex items-center space-x-2"
                          >
                            <Check className="flex-shrink-0 w-3 h-3 text-green-500" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                        {type.benefits.length > 3 && (
                          <li className="text-gray-500">
                            +{type.benefits.length - 3} more benefits
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedType(type);
                        setTypeForm({
                          name: type.name,
                          discount_percentage:
                            type.discount_percentage.toString(),
                          description: type.description || "",
                          benefits: type.benefits || [],
                        });
                        setIsEditDialogOpen(true);
                      }}
                      className="flex-1"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    {user?.role === "admin" && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() =>
                          handleDeleteMembershipType(type.id, type.name)
                        }
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Membership Type Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Membership Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMembershipType} className="space-y-4">
            <div>
              <Label htmlFor="name">Membership Type Name</Label>
              <Input
                id="name"
                value={typeForm.name}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, name: e.target.value })
                }
                required
                placeholder="e.g., Premium, VIP, Gold"
              />
            </div>
            <div>
              <Label htmlFor="discount_percentage">
                Discount Percentage (%)
              </Label>
              <Input
                id="discount_percentage"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={typeForm.discount_percentage}
                onChange={(e) =>
                  setTypeForm({
                    ...typeForm,
                    discount_percentage: e.target.value,
                  })
                }
                required
                placeholder="e.g., 15"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={typeForm.description}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, description: e.target.value })
                }
                rows={3}
                placeholder="Describe this membership type..."
              />
            </div>
            <div>
              <Label>Benefits</Label>
              <div className="space-y-2">
                {(typeForm.benefits || []).map((benefit, index) => (
                  <div key={index} className="flex space-x-2">
                    <Input
                      value={benefit}
                      onChange={(e) => updateBenefit(index, e.target.value)}
                      placeholder="Enter benefit..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeBenefit(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBenefit}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Benefit
                </Button>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Create Membership Type
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Membership Type Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Membership Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateMembershipType} className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Membership Type Name</Label>
              <Input
                id="edit_name"
                value={typeForm.name}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_discount_percentage">
                Discount Percentage (%)
              </Label>
              <Input
                id="edit_discount_percentage"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={typeForm.discount_percentage}
                onChange={(e) =>
                  setTypeForm({
                    ...typeForm,
                    discount_percentage: e.target.value,
                  })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_description">Description</Label>
              <Textarea
                id="edit_description"
                value={typeForm.description}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, description: e.target.value })
                }
                rows={3}
              />
            </div>
            <div>
              <Label>Benefits</Label>
              <div className="space-y-2">
                {(typeForm.benefits || []).map((benefit, index) => (
                  <div key={index} className="flex space-x-2">
                    <Input
                      value={benefit}
                      onChange={(e) => updateBenefit(index, e.target.value)}
                      placeholder="Enter benefit..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeBenefit(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBenefit}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Benefit
                </Button>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Update Membership Type
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Member Management Component
function MemberManagement({ token, user }) {
  const [members, setMembers] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [selectedMember, setSelectedMember] = useState({
  type: "walkin",
  name: "",
  discount_percentage: 0,
  membership_type: "Walk-in",
});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [memberForm, setMemberForm] = useState({
    name: "",
    email: "",
    phone: "",
    membership_type: "",
    discount_percentage: 0,
    address: "",
    notes: "",
    date_joined: "", // <-- add this line
    errors: "",
  });

  // initial form state constant
  const initialMemberForm = {
    name: "",
    email: "",
    phone: "",
    membership_type: "",
    discount_percentage: 0,
    address: "",
    notes: "",
    date_joined: "",
    errors: "",
  };

  // helper to open Create dialog and clear form
  const openCreateMemberDialog = () => {
    setMemberForm(initialMemberForm);
    setIsCreateDialogOpen(true);
  };

  const fetchMembershipTypes = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/membership-types/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      setMembershipTypes(data);
    } catch (error) {
      console.error("Error fetching membership types:", error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setMembers(data);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/members`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(memberForm),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setMemberForm({
          name: "",
          email: "",
          phone: "",
          membership_type: "",
          address: "",
          notes: "",
        });
        fetchMembers();
      } else {
        const error = await response.json();
        setMemberForm((prev) => ({ ...prev, errors: error.detail || ["Failed to create member"] }));
      }
    } catch (error) {
      console.error("Error creating member:", error);
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/members/${selectedMember.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(memberForm),
        }
      );

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedMember(null);
        fetchMembers();
      }
    } catch (error) {
      console.error("Error updating member:", error);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (user?.role !== "admin") {
      alert("Access denied. Only admin users can delete members.");
      return;
    }

    if (window.confirm("Are you sure you want to delete this member?")) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/members/${memberId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          fetchMembers();
        } else {
          const error = await response.json();
          alert(error.detail || "Failed to delete member");
        }
      } catch (error) {
        console.error("Error deleting member:", error);
        alert("Error deleting member");
      }
    }
  };

  const handleViewDetails = async (member) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/members/${member.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const memberDetails = await response.json();
      setSelectedMember(memberDetails);
      setIsDetailsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching member details:", error);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchMembershipTypes();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Member Management</h2>
        <Button
          onClick={openCreateMemberDialog}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {members.map((member) => (
          <Card key={member.id} className="transition-shadow hover:shadow-lg">
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-gray-800">{member.name}</h3>
                  <Badge
                    style={{ pointerEvents: "none" }}
                    variant={
                      
                      member.status === "active" ? "default" : "secondary"
                    }
                  >
                    {member.status}
                  </Badge>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>{member.email}</p>
                  <p>{member.phone}</p>
                   <p>Date Joined: {member.date_joined ? new Date(member.date_joined).toLocaleDateString() : "N/A"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <Badge
                    style={{ pointerEvents: "none" }}
                    variant="secondary"
                    className="text-blue-800 bg-blue-100"
                  >
                    {member.membership_type}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-300"
                  >
                    {member.discount_percentage}% off
                  </Badge>
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDetails(member)}
                    className="flex-1"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedMember(member);
                      setMemberForm(member);
                      setIsEditDialogOpen(true);
                    }}
                    className="flex-1"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {user?.role === "admin" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteMember(member.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Member Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          // when dialog closes, clear the form fields
          if (!open) {
            setMemberForm({
              name: "",
              email: "",
              phone: "",
              membership_type: "",
              discount_percentage: 0,
              address: "",
              notes: "",
              date_joined: "",
              errors: "",
            });
          }
          setIsCreateDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMember} className="space-y-4">
            {memberForm.errors && (
              <Alert className="border-red-500 bg-red-50">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <div className="text-red-700">
                  <ul className="list-disc list-inside">
                    <li>{memberForm.errors}</li>
                  </ul>
                </div>
              </Alert>
            )}
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={memberForm.name}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={memberForm.email}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, email: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={memberForm.phone}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, phone: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="membership_type">Membership Type</Label>
                      <Select
                        value={memberForm.membership_type}
                        onValueChange={(value) => {
                          const selectedType = membershipTypes.find((type) => type.name === value);
                          setMemberForm({
                            ...memberForm,
                            membership_type: value,
                            discount_percentage: selectedType ? selectedType.discount_percentage : 0,
                          });
                        }}
                      >
                <SelectTrigger>
                  <SelectValue placeholder="Select membership type" />
                </SelectTrigger>
                <SelectContent>
                  {membershipTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name} ({type.discount_percentage}% discount)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={memberForm.address}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, address: e.target.value })
                }
              />
            </div>
            <div>
            <Label htmlFor="date_joined">Date Joined</Label>
            <Input
              id="date_joined"
              type="date"
              value={memberForm.date_joined}
              onChange={(e) =>
                setMemberForm({ ...memberForm, date_joined: e.target.value })
              }
              required
            />
          </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={memberForm.notes}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, notes: e.target.value })
                }
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Create Member
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateMember} className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Full Name</Label>
              <Input
                id="edit_name"
                value={memberForm.name}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={memberForm.email}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, email: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_phone">Phone</Label>
              <Input
                id="edit_phone"
                value={memberForm.phone}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, phone: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_membership_type">Membership Type</Label>
              <Select
                value={memberForm.membership_type}
                onValueChange={(value) => {
                  setMemberForm({
                    ...memberForm,
                    membership_type: value,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {membershipTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name} ({type.discount_percentage}% discount)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_status">Status</Label>
              <Select
                value={memberForm.status}
                onValueChange={(value) =>
                  setMemberForm({ ...memberForm, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_address">Address</Label>
              <Input
                id="edit_address"
                value={memberForm.address}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, address: e.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="edit_date_joined">Date Joined</Label>
              <Input
                id="edit_date_joined"
                type="date"
                value={memberForm.date_joined}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, date_joined: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={memberForm.notes}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, notes: e.target.value })
                }
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Update Member
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Member Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Name
                  </Label>
                  <p className="text-lg font-semibold">{selectedMember.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500 me-2">
                    Status
                  </Label>
                  <Badge
                    style={{ pointerEvents: "none" }}
                    variant={
                      
                      selectedMember.status === "active"
                        ? "default"
                        : "secondary"
                    }
                  >
                    {selectedMember.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Email
                  </Label>
                  <p>{selectedMember.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Phone
                  </Label>
                  <p>{selectedMember.phone}</p>
                </div>
                 <div>
                  <Label className="text-sm font-medium text-gray-500">Date Joined</Label>
                  <p>{selectedMember.date_joined ? new Date(selectedMember.date_joined).toLocaleDateString() : "N/A"}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500 me-2">
                    Membership Type
                  </Label>
                  <Badge
                    style={{ pointerEvents: "none" }}
                    variant="secondary"
                    className="text-blue-800 bg-blue-100"
                  >
                    {selectedMember.membership_type}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500 me-2">
                    Discount
                  </Label>
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-300"
                  >
                    {selectedMember.discount_percentage}% off
                  </Badge>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Address
                  </Label>
                  <p>{selectedMember.address || "Not provided"}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Notes
                  </Label>
                  <p>{selectedMember.notes || "No notes"}</p>
                </div>
              </div>

              {selectedMember.transaction_history &&
                selectedMember.transaction_history.length > 0 && (
                  <div>
                    <Label className="mb-3 text-lg font-semibold">
                      Recent Transactions
                    </Label>
                    <div className="space-y-2 overflow-y-auto max-h-60">
                      {selectedMember.transaction_history.map((transaction) => (
                        <Card key={transaction.id} className="p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">
                                ₱{transaction.total_amount.toFixed(2)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(
                                  transaction.timestamp
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge variant="outline">
                              {transaction.payment_method}
                            </Badge>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Golf-specific reservation component
function GolfTeeTimeBooking({ 
  newGolfReservation, 
  setNewGolfReservation, 
  selectedMember, 
  orNumber,
  addGolfReservation,
  fetchGolfReservations,
  toast,
  isGolfTimeSlotAvailable,
  golfRatesItems,
  products,
  manualTeeTime,
  setManualTeeTime,
  determineCourseFromCart,
  reservedGolfSlot,
  setReservedGolfSlot, 
  isCartSummaryModalOpen, 
  members = [],
  setCart,
  onGolfReservationStatsChange,
  golfGroupBookingCount = 1,
  setGolfGroupBookingCount,
}) {
  const [currentTime, setCurrentTime] = React.useState(new Date());
  const [teeTimeError, setTeeTimeError] = React.useState("");
  const [teeTimeAvailable, setTeeTimeAvailable] = React.useState(false);
  const [manualTeeTimePH, setManualTeeTimePH] = React.useState(null);
  
  // ✅ NEW: Per-service player data storage (replaces guest system)
  const [servicePlayerData, setServicePlayerData] = React.useState({});
  const [currentServiceKey, setCurrentServiceKey] = React.useState("");

  // ✅ Extra group players added via inline controls (store as tokens so user picks product)
  const [extraGroupTokens, setExtraGroupTokens] = React.useState([]);
  const [showAddExtraSelect, setShowAddExtraSelect] = React.useState(false);
  // When true, applying the same tee time to all players will auto-fill unfilled slots
  const [applySameTeeTimeToAll, setApplySameTeeTimeToAll] = React.useState(true);
  // Group booking toggle: when true, show group booking UI; forceGroupFromCart is true when cart has multiple golf items
  const [groupBookingEnabled, setGroupBookingEnabled] = React.useState(false);
  const [forceGroupFromCart, setForceGroupFromCart] = React.useState(false);
  // ✅ Group Name: single name that auto-fills all player slots as "GroupName - Player N"
  const [groupName, setGroupName] = React.useState("");

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fmt = (d) =>
    (d instanceof Date ? d : new Date(d)).toLocaleTimeString("en-PH", { timeZone: MANILA_TZ, hour: "2-digit", minute: "2-digit", hour12: true });

  const courseInfo = determineCourseFromCart();
  const courseDurationMinutes = 240; // 4 hours
  // Allow a small grace window for selecting times slightly in the past
  const PAST_GRACE_MINUTES = 2; // minutes
  const PAST_GRACE_MS = PAST_GRACE_MINUTES * 60 * 1000;

  // ✅ Build service tokens for golf (quantity-aware)
  const golfServiceTokens = React.useMemo(() => {
    const tokens = [];
    (golfRatesItems || []).forEach((it) => {
      const prod = (products || []).find(p => String(p.id) === String(it.product_id));
      if (!prod) return;

      let holes = 18;
      const productName = (prod.name || "").toLowerCase();
      if (productName.includes("9 holes") || productName.includes("9-holes")) holes = 9;
      else if (productName.includes("18 holes") || productName.includes("18-holes")) holes = 18;

      const course_type = holes === 9 ? "9-holes" : "18-holes";
      const qty = Number(it.quantity || 1);
      for (let i = 0; i < qty; i++) {
        tokens.push({
          key: `${prod.id}#${i + 1}`,
          product_id: String(prod.id),
          name: prod.name,
          holes,
          course_type,      // ✅ added
        });
      }
    });
    // Group booking: add extra player slots for additional group players beyond cart items
    if (extraGroupTokens && extraGroupTokens.length > 0) {
      for (let i = 0; i < extraGroupTokens.length; i++) {
        const et = extraGroupTokens[i];
        const playerNum = tokens.length + 1;
        tokens.push({
          key: `golfgroup#${playerNum}-${et.product_id}`,
          product_id: et.product_id,
          name: `${et.name} (Extra Player ${i + 1})`,
          holes: et.holes,
          course_type: et.course_type,
          isGroupExtra: true
        });
      }
    }
    return tokens;
  }, [golfRatesItems, products, extraGroupTokens]);

  // ✅ Effective group size = base cart tokens + extra players
  const baseTokenCount = React.useMemo(() => {
    return (golfRatesItems || []).reduce((sum, it) => sum + Number(it.quantity || 1), 0);
  }, [golfRatesItems]);
  const effectiveGroupSize = baseTokenCount + (extraGroupTokens ? extraGroupTokens.length : 0);

  // Options for adding an extra slot (all active Golf Rates products, not just cart)
  const extraAddOptions = React.useMemo(() => {
    const map = {};
    (products || []).forEach(prod => {
      if (!prod || prod.category !== "Golf Rates") return;
      if (prod.is_active === false) return; // skip deactivated products
      const productId = String(prod.id);
      if (!map[productId]) {
        const productName = prod.name || "";
        const holes = productName.toLowerCase().includes('9') ? 9 : 18;
        map[productId] = { product_id: productId, name: productName, holes, course_type: holes === 9 ? '9-holes' : '18-holes' };
      }
    });
    return Object.values(map);
  }, [products]);

  // Auto-enable / force group booking when cart contains more than one golf item
  React.useEffect(() => {
    if (baseTokenCount > 1) {
      setGroupBookingEnabled(true);
      setForceGroupFromCart(true);
    } else {
      // if no longer multiple items, allow user to toggle group booking off
      setForceGroupFromCart(false);
      setGroupBookingEnabled(effectiveGroupSize > 1 ? true : false);
    }
  }, [baseTokenCount, effectiveGroupSize]);

  // ✅ Sync effective group size back to parent for transaction payload
  React.useEffect(() => {
    if (typeof setGolfGroupBookingCount === 'function' && effectiveGroupSize !== golfGroupBookingCount) {
      setGolfGroupBookingCount(effectiveGroupSize);
    }
  }, [effectiveGroupSize, setGolfGroupBookingCount]);
  // ✅ Auto-select first service token on mount
  React.useEffect(() => {
    if (!currentServiceKey && golfServiceTokens.length) {
      setCurrentServiceKey(golfServiceTokens[0].key);
    }
  }, [golfServiceTokens.length]);

  // ✅ Clear all data when modal closes
  React.useEffect(() => {
    if (!isCartSummaryModalOpen) {
      setServicePlayerData({});
      setCurrentServiceKey("");
      setManualTeeTime("");
      setManualTeeTimePH(null);
      setReservedGolfSlot(null);
      setExtraGroupTokens([]);
      setGroupName("");
      setNewGolfReservation(prev => ({ ...(prev || {}), golf_guest_bookings: [], group_name: "" }));
    }
  }, [isCartSummaryModalOpen, setNewGolfReservation, setManualTeeTime, setReservedGolfSlot]);

  // ✅ Calculate filled/available slots
  const filledSlots = Object.keys(servicePlayerData).filter(key => 
    servicePlayerData[key]?.player_name && servicePlayerData[key]?.reserved
  ).length;
  
  const availableSlots = golfServiceTokens.length - filledSlots;

  // Navigate to POS and show specific golf booking row + open details
  const viewGolfBooking = (key, data) => {
    try {
      const or = (data && (data.or_number || data.orNumber || data.or)) || "";
      const bay = (data && (data.bay_number || data.bayNumber || data.bay)) || "";

      // Populate fields in this GolfTeeTimeBooking when possible
      try {
        // select the service token
        setCurrentServiceKey && setCurrentServiceKey(key);

        // merge incoming data into servicePlayerData for that key
        setServicePlayerData && setServicePlayerData(prev => ({ ...(prev || {}), [key]: { ...(prev?.[key] || {}), ...(data || {}) } }));

        // Determine start/end datetimes (prefer ISO fields)
        let startDt = null;
        let endDt = null;
        if (data?.tee_time_iso) {
          startDt = new Date(data.tee_time_iso);
          endDt = data?.end_time_iso ? new Date(data.end_time_iso) : new Date(startDt.getTime() + 4 * 60 * 60 * 1000);
        } else if (data?.tee_time) {
          const t = String(data.tee_time || "");
          if (t.includes("T") || t.includes("-")) {
            startDt = new Date(t);
            endDt = new Date(startDt.getTime() + 4 * 60 * 60 * 1000);
          } else {
            startDt = manilaDateFromHHMM(t, manilaNowDate());
            endDt = startDt ? new Date(startDt.getTime() + 4 * 60 * 60 * 1000) : null;
          }
        } else if (data?.ui_label_start) {
          startDt = manilaDateFromHHMM(data.ui_label_start, manilaNowDate());
          endDt = startDt ? new Date(startDt.getTime() + 4 * 60 * 60 * 1000) : null;
        }

        // Set manual tee time input (HH:MM) and helper object
        if (startDt) {
          const hhmm = manilaHHMM(startDt);
          setManualTeeTime && setManualTeeTime(hhmm);
          setManualTeeTimePH && setManualTeeTimePH({ start: startDt, end: endDt, ui_label_start: manilaTimeLabel(startDt), ui_label_end: endDt ? manilaTimeLabel(endDt) : "" });
          setReservedGolfSlot && setReservedGolfSlot({ tee_time: startDt.toISOString(), end_time_iso: endDt ? endDt.toISOString() : null, ui_label_start: manilaTimeLabel(startDt), ui_label_end: endDt ? manilaTimeLabel(endDt) : "", service_key: key, product_name: data?.product_name || "" });
        } else {
          setManualTeeTime && setManualTeeTime(toInputHHMM(data?.ui_label_start || data?.tee_time || ""));
          setManualTeeTimePH && setManualTeeTimePH(null);
        }
      } catch (err) {
        console.warn("Could not populate golf booking fields:", err);
      }

      // Also navigate to POS & highlight the row for quick inspection
      try { setActiveTab && setActiveTab("pos"); } catch (e) {}
      const playerKey = `${or}-${bay}`;
      try { setHighlightedPlayerKey && setHighlightedPlayerKey(playerKey); } catch (e) {}

      // Scroll to the row and trigger click to open details (if row exists)
      setTimeout(() => {
        try {
          const row = document.getElementById(`player-row-${or}-${bay}`);
          if (row) {
            row.scrollIntoView({ behavior: "smooth", block: "center" });
            row.click();
          }
        } catch (err) {}
      }, 150);

      // auto-clear highlight
      setTimeout(() => { try { setHighlightedPlayerKey && setHighlightedPlayerKey(prev => prev === playerKey ? null : prev); } catch (e) {} }, 5000);
    } catch (err) {
      console.error("viewGolfBooking error:", err);
    }
  };

  // Propagate golf reservation stats upward (with loop guard)
  const lastGolfStatsRef = React.useRef({ filledSlots: 0, totalSlots: 0, incompleteKeys: [], extraCost: 0, extraItems: [] });
  React.useEffect(() => {
    if (typeof onGolfReservationStatsChange !== 'function') return;
    const incompleteKeys = golfServiceTokens
      .filter(tok => {
        const row = servicePlayerData[tok.key];
        return !(row && row.player_name && row.reserved && row.tee_time_iso && row.end_time_iso);
      })
      .map(t => t.key);
    // ✅ Compute extra player cost from extraGroupTokens (not in cart, so tracked separately)
    const extraItems = (extraGroupTokens || []).map(et => {
      const prod = (products || []).find(p => String(p.id) === String(et.product_id));
      return {
        product_id: String(et.product_id || ""),
        product_name: et.name || (prod ? prod.name : ""),
        price: prod ? Number(prod.price || 0) : 0,
        quantity: 1,
      };
    });
    const extraCost = extraItems.reduce((sum, it) => sum + it.price, 0);
    const next = { filledSlots, totalSlots: golfServiceTokens.length, incompleteKeys, extraCost, extraItems };
    const prev = lastGolfStatsRef.current;
    const changed = prev.filledSlots !== next.filledSlots || prev.totalSlots !== next.totalSlots ||
      (prev.extraCost || 0) !== (next.extraCost || 0) ||
      prev.incompleteKeys.length !== next.incompleteKeys.length ||
      prev.incompleteKeys.some((k, i) => k !== next.incompleteKeys[i]);
    if (changed) {
      lastGolfStatsRef.current = next;
      onGolfReservationStatsChange(next);
    }
  }, [filledSlots, golfServiceTokens, servicePlayerData, onGolfReservationStatsChange, extraGroupTokens, products]);

  // ✅ Get current service token
  const selectedService = React.useMemo(
    () => golfServiceTokens.find(t => t.key === currentServiceKey) || null,
    [golfServiceTokens, currentServiceKey]
  );

  const toInputHHMM = (v) => {
    if (!v && v !== 0) return "";
    if (v instanceof Date) {
      const hh = String(v.getHours()).padStart(2, "0");
      const mm = String(v.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    }
    const s = String(v).trim();
    // already HH:MM (24h or 1-2 digit hour)
    if (/^\d{1,2}:\d{2}$/.test(s)) {
      const [h, m] = s.split(":").map(x => String(Number(x)).padStart(2, "0"));
      return `${h}:${m}`;
    }
    // try parse "hh:mm AM/PM"
    const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (ampm) {
      let h = Number(ampm[1]);
      const m = Number(ampm[2]);
      if (/pm/i.test(ampm[3]) && h !== 12) h += 12;
      if (/am/i.test(ampm[3]) && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
    // try Date/ISO parse
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    return "";
  };

  // ✅ Load saved data when switching services
  React.useEffect(() => {
    if (!selectedService) return;

    const saved = servicePlayerData[currentServiceKey] || {};

    // Load saved time into picker (normalize to "HH:MM" 24-hour)
    const savedTimeRaw = saved.tee_time || "";
    const normalized = toInputHHMM(savedTimeRaw);

    // Only set the picker from saved data when the user is NOT actively editing
    // Also skip rehydration if this service is already reserved (keep input cleared)
    if (normalized && !userEditingTeeTimeRef.current && !saved.reserved) {
      if (manualTeeTime !== normalized) {
        setManualTeeTime(normalized);
      }
    }

    // Build the minimal payload we want in parent
    const nextPayload = {
      player_name: saved.player_name || "",
      tee_time: saved.tee_time_iso || "",
      end_time: saved.end_time_iso || "",
      course_type: courseInfo.type,
      total_holes: selectedService.holes,
      selected_service: {
        key: currentServiceKey,
        product_id: selectedService.product_id,
        name: selectedService.name,
        holes: selectedService.holes
      }
    };

    // Guard: avoid calling setter if parent already has same values (prevents update loops)
    try {
      const prevSnapshot = {
        player_name: newGolfReservation?.player_name || "",
        tee_time: newGolfReservation?.tee_time || "",
        end_time: newGolfReservation?.end_time || "",
        course_type: newGolfReservation?.course_type || "",
        total_holes: newGolfReservation?.total_holes || 0,
        selected_service: newGolfReservation?.selected_service || {}
      };

      const same =
        prevSnapshot.player_name === nextPayload.player_name &&
        prevSnapshot.tee_time === nextPayload.tee_time &&
        prevSnapshot.end_time === nextPayload.end_time &&
        prevSnapshot.course_type === nextPayload.course_type &&
        Number(prevSnapshot.total_holes) === Number(nextPayload.total_holes) &&
        JSON.stringify(prevSnapshot.selected_service) === JSON.stringify(nextPayload.selected_service);

      if (!same) {
        setNewGolfReservation(prev => ({ ...(prev || {}), ...nextPayload }));
      }
    } catch (err) {
      // Fallback
      setNewGolfReservation(prev => ({ ...(prev || {}), ...nextPayload }));
    }
  }, [currentServiceKey, selectedService, servicePlayerData, newGolfReservation, setNewGolfReservation]);
  // ✅ Handle time picker change

    // Prevent effects from overwriting user's manual edits
  const userEditingTeeTimeRef = React.useRef(false);
  const teeEditTimerRef = React.useRef(null);
  // ✅ Handle time picker change
  const handleManualTeeTimeChange = (e) => {
    const raw = e.target.value;

    // mark user as editing (prevent effects from overwriting)
    userEditingTeeTimeRef.current = true;
    if (teeEditTimerRef.current) clearTimeout(teeEditTimerRef.current);
    teeEditTimerRef.current = setTimeout(() => {
      userEditingTeeTimeRef.current = false;
      teeEditTimerRef.current = null;
    }, 800); // consider a short debounce — adjust if you like

    // Immediately update controlled value so the <input> follows user interaction
    setManualTeeTime(raw || "");

    // normalize to "HH:MM" (24-hour) so <input type="time"> always gets a valid value
    const normalized = toInputHHMM(raw);

    setReservedGolfSlot(null);

    if (!normalized) {
      setTeeTimeAvailable(false);
      setManualTeeTimePH(null);
      setTeeTimeError(null);
      return;
    }

    const [hours, minutes] = normalized.split(':').map(Number);
    const nowPH = manilaNowDate();
    const selectedTime = manilaDateFromHHMM(`${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`, nowPH);

    const nowThreshold = new Date(nowPH.getTime() - PAST_GRACE_MS);

    if (!selectedTime || selectedTime.getTime() < nowThreshold.getTime()) {
      setTeeTimeError("Cannot select past times");
      setTeeTimeAvailable(false);
      setManualTeeTimePH(null);
    } else {
      setTeeTimeError(null);
      setTeeTimeAvailable(true);

      const endTime = new Date(selectedTime.getTime() + (courseDurationMinutes * 60 * 1000));

      setManualTeeTimePH({
        start: selectedTime,
        end: endTime,
        ui_label_start: fmt(selectedTime),
        ui_label_end: fmt(endTime)
      });

      // ensure stored value is normalized (keeps UI consistent)
      setManualTeeTime(normalized);

      // Keep the reservation banner in sync: write the picked time into servicePlayerData for current token
      // so the "Tee Time Reserved" block updates immediately while editing.
      if (currentServiceKey) {
        setServicePlayerData(prev => {
          const cur = prev?.[currentServiceKey] || {};
          const next = {
            ...cur,
            tee_time: normalized,                  // human HH:MM
            tee_time_iso: selectedTime.toISOString(),
            end_time_iso: endTime.toISOString(),
            ui_label_start: fmt(selectedTime),
            ui_label_end: fmt(endTime)
          };
          if (prev && prev[currentServiceKey] && JSON.stringify(prev[currentServiceKey]) === JSON.stringify(next)) return prev;
          return { ...(prev || {}), [currentServiceKey]: next };
        });
      }
    }
  };

    // keep input and reserved banner in sync when servicePlayerData changes
  React.useEffect(() => {
    if (!currentServiceKey) return;
    const sel = servicePlayerData[currentServiceKey] || {};
    if (!sel) return;
    // Do not re-fill the time input if this slot is already reserved
    if (sel.reserved) return;

    // don't stomp user while typing
    if (userEditingTeeTimeRef.current) return;

    // prefer explicit HH:MM stored on token or derive from ISO
    const hh = toInputHHMM(sel.tee_time || sel.tee_time_iso || sel.ui_label_start || "");
    if (!hh) return;

    if (manualTeeTime !== hh) {
      setManualTeeTime(hh);
    }

    // update manualTeeTimePH so the "Tee Time: ..." small helper matches the banner
    try {
      const nowPH = manilaNowDate();
      const start = manilaDateFromHHMM(hh, nowPH);
      if (!start) return;

      // prefer ISO end if present, otherwise derive from course duration
      let end = sel.end_time_iso ? new Date(sel.end_time_iso) : new Date(start.getTime() + (courseDurationMinutes * 60 * 1000));

      // If values already match, avoid setState churn
      const same =
        manualTeeTimePH &&
        manualTeeTimePH.ui_label_start === (sel.ui_label_start || fmt(start)) &&
        manualTeeTimePH.ui_label_end === (sel.ui_label_end || fmt(end));
      if (!same) {
        setManualTeeTimePH({
          start,
          end,
          ui_label_start: sel.ui_label_start || fmt(start),
          ui_label_end: sel.ui_label_end || fmt(end)
        });
      }
    } catch (err) {
      // ignore malformed values
    }
  }, [servicePlayerData, currentServiceKey]);
  // ✅ Handle player name change
  const handlePlayerNameChange = (value) => {
    setServicePlayerData(prev => ({
      ...prev,
      [currentServiceKey]: {
        ...(prev[currentServiceKey] || {}),
        player_name: value,
        service_key: currentServiceKey,
        product_id: selectedService?.product_id,
        product_name: selectedService?.name,
        holes: selectedService?.holes || 18,
        course_type: selectedService ? (selectedService.holes === 9 ? "9-holes" : "18-holes") : "18-holes" // ✅ added
      }
    }));
  };

  // Simple validation: always show error when blank; focus input when blank on open/switch
  const currentPlayerNameVal = (servicePlayerData[currentServiceKey]?.player_name || "").trim();
  const showPlayerNameError = currentPlayerNameVal.length === 0;
  const playerNameInputRef = React.useRef(null);
  React.useEffect(() => {
    if (showPlayerNameError && playerNameInputRef.current) {
      try { playerNameInputRef.current.focus(); } catch {}
    }
  }, [currentServiceKey, isCartSummaryModalOpen]);

  // ✅ Reserve tee time for current service
  const handleReserveGolfSlot = () => {
    if (!manualTeeTimePH || !teeTimeAvailable) {
      console.log("❌ Cannot reserve: missing requirements");
      return;
    }
    
    // OR number check removed — only require a selected member
    if (!selectedMember) {
      toast({
        title: "Reservation failed",
        description: "Please select a member first.",
        variant: "destructive",
      });
      return;
    }

    const currentPlayerName = servicePlayerData[currentServiceKey]?.player_name || "";
    if (!currentPlayerName.trim()) {
      toast({
        title: "Reservation failed",
        description: "Please enter player name first.",
        variant: "destructive",
      });
      return;
    }

    const s = manualTeeTimePH.start;
    const e = manualTeeTimePH.end;
    
    // Save to current service slot
    setServicePlayerData(prev => ({
      ...prev,
      [currentServiceKey]: {
        ...(prev[currentServiceKey] || {}),
        player_name: currentPlayerName,
        tee_time: manualTeeTime,
        tee_time_iso: s.toISOString(),
        end_time_iso: e.toISOString(),
        ui_label_start: fmt(s),
        ui_label_end: fmt(e),
        reserved: true,
        service_key: currentServiceKey,
        product_id: selectedService?.product_id,
        product_name: selectedService?.name,
        holes: selectedService?.holes || 18,
        course_type: selectedService ? (selectedService.holes === 9 ? "9-holes" : "18-holes") : "18-holes" // ✅ added
      }
    }));
    
    setReservedGolfSlot({
      tee_time: s.toISOString(),
      end_time: e.toISOString(),
      course_type: selectedService ? (selectedService.holes === 9 ? "9-holes" : "18-holes") : "18-holes", // ✅ token-specific
      total_holes: selectedService?.holes || 18,
      ui_label_start: fmt(s),
      ui_label_end: fmt(e),
      member_id: selectedMember?.id,
      member_name: selectedMember?.name,
      or_number: orNumber,
      service_key: currentServiceKey
    });

    toast({
      title: "Tee Time Reserved!",
      description: `${selectedService?.name} reserved from ${fmt(s)} to ${fmt(e)} for ${currentPlayerName}.`,
    });

    // Clear the input and helper after reserving for a cleaner UX
    setManualTeeTime("");
    setManualTeeTimePH(null);
    setTeeTimeAvailable(false);
  };

  // ✅ Auto-fill player names from group name (when group name changes, update all slots)
  React.useEffect(() => {
    if (!groupBookingEnabled || !groupName.trim()) return;
    const gn = groupName.trim();
    setServicePlayerData(prev => {
      const next = { ...prev };
      let changed = false;
      golfServiceTokens.forEach((tok, idx) => {
        const existing = next[tok.key] || {};
        const expectedName = `${gn} - Player ${idx + 1}`;
        // Only auto-fill if the name is empty, was a previous auto-generated name, or matches the old pattern
        const curName = (existing.player_name || "").trim();
        const isAutoGenerated = !curName || /^Player \d+$/.test(curName) || /^.+ - Player \d+$/.test(curName);
        if (isAutoGenerated && curName !== expectedName) {
          next[tok.key] = { ...existing, player_name: expectedName, service_key: tok.key, product_id: tok.product_id, product_name: tok.name, holes: tok.holes || 18, course_type: tok.holes === 9 ? "9-holes" : "18-holes" };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [groupName, groupBookingEnabled, golfServiceTokens]);

  // ✅ NEW: Auto-fill all other golf slots with the same tee time when first slot is reserved
  // Runs when `applySameTeeTimeToAll` is enabled OR for extra group tokens (group extras ALWAYS inherit tee time)
  React.useEffect(() => {
    if (!currentServiceKey) return;

    // Find the first reserved slot
    const reservedEntries = Object.entries(servicePlayerData).filter(([_, data]) => data?.reserved);
    if (reservedEntries.length === 0) return;

    const [firstKey, firstData] = reservedEntries[0];
    if (!firstData.tee_time_iso || !firstData.end_time_iso) return;

    // Check for unfilled slots
    // When applySameTeeTimeToAll is ON: fill ALL unfilled slots
    // When applySameTeeTimeToAll is OFF: only fill extra group tokens (they must use same tee time)
    const unfilledSlots = golfServiceTokens.filter(tok => {
      const data = servicePlayerData[tok.key];
      if (data?.reserved) return false; // already filled
      if (applySameTeeTimeToAll) return true; // fill everything
      return tok.isGroupExtra; // only fill extra group players when toggle is off
    });

    if (unfilledSlots.length === 0) return;

    // Auto-fill unfilled slots with the first reserved slot's tee time
    setServicePlayerData(prev => {
      const next = { ...prev };
      const gn = (groupName || "").trim();
      for (const tok of unfilledSlots) {
        const existing = next[tok.key] || {};
        // Generate default player name for unfilled slots (use group name if set)
        const playerIdx = golfServiceTokens.findIndex(t => t.key === tok.key) + 1;
        const playerName = existing.player_name || (gn ? `${gn} - Player ${playerIdx}` : `Player ${playerIdx}`);
        next[tok.key] = {
          ...existing,
          player_name: playerName,
          tee_time: firstData.tee_time,
          tee_time_iso: firstData.tee_time_iso,
          end_time_iso: firstData.end_time_iso,
          ui_label_start: firstData.ui_label_start,
          ui_label_end: firstData.ui_label_end,
          reserved: true,
          service_key: tok.key,
          product_id: tok.product_id,
          product_name: tok.name,
          holes: tok.holes || 18,
          course_type: tok.holes === 9 ? "9-holes" : "18-holes"
        };
      }
      return next;
    });
  }, [servicePlayerData, golfServiceTokens, currentServiceKey, applySameTeeTimeToAll, groupName]);

  // ✅ Sync all service player data to parent
  React.useEffect(() => {
    const allPlayers = Object.entries(servicePlayerData)
      .filter(([_, data]) => data?.player_name && data?.reserved)
      .map(([key, data]) => ({
        guest_name: data.player_name,
        service_key: key,
        product_id: data.product_id,
        product_name: data.product_name,
        tee_time: data.tee_time_iso,
        end_time: data.end_time_iso,
        holes: data.holes,
        course_type: data.course_type,          // ✅ added
        ui_label_start: data.ui_label_start,
        ui_label_end: data.ui_label_end,
        reserved: true,
        total_holes: data.holes,
        golfhole: data.holes                     // ✅ optional explicit
      }));

    const gn = (groupName || "").trim();
    const nextJson = JSON.stringify(allPlayers) + gn;
    const prevJson = JSON.stringify(newGolfReservation?.golf_guest_bookings || []) + (newGolfReservation?.group_name || "");
    if (prevJson !== nextJson) {
      setNewGolfReservation(prev => ({ ...(prev || {}), golf_guest_bookings: allPlayers, group_name: gn || undefined }));
    }
  }, [servicePlayerData, setNewGolfReservation, groupName]);

  // ✅ When available tokens shrink (user pressed -), remove any service data
  // that no longer corresponds to existing tokens. Also ensure the
  // `currentServiceKey` and `reservedGolfSlot` stay valid.
  React.useEffect(() => {
    const tokenKeys = new Set((golfServiceTokens || []).map(t => t.key));
    // prune servicePlayerData keys that are no longer present
    setServicePlayerData(prev => {
      if (!prev) return prev || {};
      const keys = Object.keys(prev);
      let changed = false;
      const next = {};
      for (const k of keys) {
        if (tokenKeys.has(k)) {
          next[k] = prev[k];
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    // if currentServiceKey no longer exists, switch to first available
    if (currentServiceKey && !tokenKeys.has(currentServiceKey)) {
      const first = golfServiceTokens && golfServiceTokens[0] ? golfServiceTokens[0].key : "";
      setCurrentServiceKey(first);
    }

    // if reservedGolfSlot no longer corresponds to existing token, clear it
    if (reservedGolfSlot && reservedGolfSlot.service_key && !tokenKeys.has(reservedGolfSlot.service_key)) {
      setReservedGolfSlot(null);
    }
  }, [golfServiceTokens]);

  // ✅ Helper to check if time is in past
  const isTimePast = (timeStr) => {
    if (!timeStr) return false;
    try {
      const [h, m] = timeStr.split(':').map(Number);
      const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
      const nowThreshold = new Date(nowPH.getTime() - PAST_GRACE_MS);
      const checkDate = new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
      return checkDate.getTime() < nowThreshold.getTime();
    } catch {
      return false;
    }
  };

    // ✅ NEW: cancel single golf reservation (clear servicePlayerData and remove guest + cart item)
  const cancelReservation = (key) => {
    const cur = (servicePlayerData || {})[key] || {};
    const DEFAULT = {
      player_name: "",
      tee_time: "",
      tee_time_iso: "",
      end_time_iso: "",
      ui_label_start: "",
      ui_label_end: "",
      reserved: false,
      service_key: "",
      product_id: null,
      product_name: "",
      holes: null,
      course_type: ""          // ✅ keep field so cleanup consistent
    };
    // clear per-token saved data
    setServicePlayerData(prev => ({ ...(prev || {}), [key]: DEFAULT }));

    // remove matching guest from parent newGolfReservation.golf_guest_bookings
    setNewGolfReservation(prev => {
      const p = prev || {};
      const guests = Array.isArray(p.golf_guest_bookings) ? p.golf_guest_bookings.filter(g => String(g.service_key) !== String(key)) : [];
      return { ...(p || {}), golf_guest_bookings: guests };
    });

    // clear reservedGolfSlot if it belongs to this service
    setReservedGolfSlot(prev => {
      if (!prev) return prev;
      if (String(prev.service_key) === String(key) || String(prev.or_number || "") === String(orNumber || "")) return null;
      return prev;
    });

    // Remove matching product from cart (decrement or remove)
    // Skip for group-extra tokens — they don't correspond to actual cart items
    try {
      const tok = golfServiceTokens.find(t => t.key === key) || null;
      if (tok && tok.isGroupExtra) {
        // Group extra slot — remove the corresponding extra token so the slot disappears
        const pid = String(tok.product_id || "");
        setExtraGroupTokens(prev => {
          if (!Array.isArray(prev) || prev.length === 0) return prev || [];
          const idx = prev.findIndex(it => String(it.product_id) === pid);
          if (idx === -1) return prev;
          const next = [...prev];
          next.splice(idx, 1);
          return next;
        });
      } else {
        const targetProductId = tok?.product_id || cur.product_id || null;
        if (targetProductId && typeof setCart === "function") {
          setCart(prev => {
            if (!Array.isArray(prev)) return prev || [];
            const idx = prev.findIndex(it => String(it.product_id) === String(targetProductId));
            if (idx === -1) return prev;
            const item = prev[idx];
            if ((item.quantity || 1) > 1) {
              const next = [...prev];
              next[idx] = { ...item, quantity: Number(item.quantity) - 1 };
              return next;
            } else {
              const next = [...prev];
              next.splice(idx, 1);
              return next;
            }
          });
        }
      }
    } catch (e) {
      console.warn("Failed to remove golf service item from cart:", e);
    }

    toast && toast({ title: "Reservation cancelled", description: `${cur.player_name || "Player"} tee time removed.`, variant: "default" });
  };

  // Allow editing an existing reserved slot: unset reserved, populate time input
  const handleEditReservation = (key) => {
    const data = (servicePlayerData || {})[key] || {};
    if (!data) return;

    // Un-reserve the slot so the picker/input becomes editable
    setServicePlayerData(prev => ({ ...(prev || {}), [key]: { ...(prev?.[key] || {}), reserved: false } }));

    // Select the token
    setCurrentServiceKey(key);

    // Populate manual time input and helper if available
    const iso = data.tee_time_iso || data.end_time_iso || null;
    let hhmm = toInputHHMM(data.tee_time || data.ui_label_start || "");
    if (!hhmm && iso) {
      try {
        const sd = new Date(iso);
        hhmm = `${String(sd.getHours()).padStart(2, '0')}:${String(sd.getMinutes()).padStart(2, '0')}`;
      } catch (e) { }
    }

    if (hhmm) {
      setManualTeeTime(hhmm);
      const nowPH = manilaNowDate();
      const start = manilaDateFromHHMM(hhmm, nowPH);
      const end = data.end_time_iso ? new Date(data.end_time_iso) : (start ? new Date(start.getTime() + (courseDurationMinutes * 60 * 1000)) : null);
      if (start && end) {
        setManualTeeTimePH({ start, end, ui_label_start: fmt(start), ui_label_end: fmt(end) });
        setTeeTimeAvailable(true);
      }
    }

    // Clear reservedGolfSlot since we're editing
    setReservedGolfSlot(null);
  };


  return (
    <div className="space-y-4">
      {/* Course info header */}
      <div className="flex items-start justify-between mb-0">
        <div className="flex items-center gap-1 min-w-0">
          <div>
            <div className="text-sm text-blue-600">Golf Course:</div>
          </div>
          {Array.isArray(courseInfo.courses) && courseInfo.courses.length > 0 && (
            <div className="flex flex-wrap gap-1 ml-2">
              {courseInfo.courses.map((course, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-1 text-xs bg-blue-200 text-blue-800 rounded"
                >
                  {course.holes}H
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {effectiveGroupSize > 1 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
              <Users className="w-3 h-3" />
              Group of {effectiveGroupSize}
            </span>
          )}
          <span className="text-sm text-blue-600 whitespace-nowrap">
            Duration: 4 hours
          </span>
        </div>
      </div>

      {/* ═══ INLINE GROUP PLAYER CONTROLS ═══ */}
      {golfServiceTokens.length > 0 && (
        <div className="flex items-center justify-between p-2.5 rounded-lg border border-indigo-200 bg-indigo-50/60">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-indigo-600" />
            <div>
              <span className="text-sm font-medium text-indigo-800">Players</span>
              <span className="text-[11px] text-indigo-500 ml-1.5">
                {baseTokenCount} from cart{extraGroupTokens && extraGroupTokens.length > 0 ? ` + ${extraGroupTokens.length} extra` : ""}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setExtraGroupTokens(prev => (Array.isArray(prev) ? prev.slice(0, -1) : []));
              }}
              disabled={!extraGroupTokens || extraGroupTokens.length <= 0}
              className="h-7 w-7 p-0 rounded-full border-indigo-300 hover:bg-indigo-100"
            >
              <Minus className="w-3 h-3" />
            </Button>
            <div className="w-8 text-center">
              <span className="text-lg font-bold text-indigo-800">{effectiveGroupSize}</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAddExtraSelect(true)}
              disabled={effectiveGroupSize >= 20}
              className="h-7 w-7 p-0 rounded-full border-indigo-300 hover:bg-indigo-100"
            >
              <Plus className="w-3 h-3" />
            </Button>
            {showAddExtraSelect && (
              <div className="ml-2">
                <select
                  className="text-sm border rounded p-1 bg-white"
                  onChange={(e) => {
                    const pid = e.target.value;
                    if (!pid) { setShowAddExtraSelect(false); return; }
                    const opt = extraAddOptions.find(o => String(o.product_id) === String(pid));
                    if (opt) {
                      setExtraGroupTokens(prev => ([...(prev || []), opt]));
                    }
                    setShowAddExtraSelect(false);
                  }}
                >
                  <option value="">Add extra...</option>
                  {extraAddOptions.map(opt => (
                    <option key={opt.product_id} value={opt.product_id}>{opt.name}{opt.holes ? ` • ${opt.holes}H` : ''}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="ml-3 flex items-center">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={groupBookingEnabled}
                  onChange={(e) => setGroupBookingEnabled(Boolean(e.target.checked))}
                  className="h-4 w-4"
                />
                <span className="text-xs text-indigo-700">Group booking</span>
              </label>
              {forceGroupFromCart && (
                <span className="ml-2 text-[11px] text-indigo-500 italic"></span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SINGLE PLAYER MODE ═══ */}
      {!groupBookingEnabled && golfServiceTokens.length > 0 && (
        <>
          {/* Service selector */}
          <div className="mb-3 w-full max-w-[500px]">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Pick Golf Course</Label>
              <Badge className="bg-blue-100 text-blue-700 pointer-events-none">
                {filledSlots}/{golfServiceTokens.length} filled
              </Badge>
            </div>
            
            <Select
              value={currentServiceKey || ""}
              onValueChange={(val) => setCurrentServiceKey(val)}
            >
              <SelectTrigger className="w-full bg-white border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                <SelectValue placeholder="Select a golf service" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg">
                {golfServiceTokens.map((tok, idx) => {
                  const isReserved = servicePlayerData[tok.key]?.reserved;
                  const playerName = servicePlayerData[tok.key]?.player_name;
                  return (
                    <SelectItem
                      key={tok.key}
                      value={tok.key}
                      className="hover:bg-blue-50 focus:bg-blue-50"
                    >
                      <div className="flex items-center justify-between w-full gap-3">
                        <span className="font-mono text-xs">#{idx + 1}</span>
                        <span className="flex-1">{tok.name}</span>
                        {isReserved && playerName && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs pointer-events-none">
                            {playerName}
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Player name input */}
            <div className="mt-3 w-full">
              <Label className="block text-sm font-medium text-black mb-1">Player Name</Label>
              <Input
                ref={playerNameInputRef}
                value={servicePlayerData[currentServiceKey]?.player_name || ""}
                onChange={(e) => handlePlayerNameChange(e.target.value)}
                placeholder="Enter player's name"
                className={`text-sm w-full bg-white border placeholder-gray-500 focus:ring-2 focus:ring-blue-200 ${showPlayerNameError ? "border-red-500 focus:ring-red-400" : "border-gray-300 focus:border-blue-500"}`}
              />
              {showPlayerNameError && (
                <div className="mt-1 text-xs text-red-600">Player name is required</div>
              )}
            </div>
          </div>

          {/* Current time */}
          <div className="mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-xs text-blue-700">
              Current Time: {fmt(currentTime)}
            </span>
          </div>

          {/* Tee time picker */}
          <div className="mb-3">
            <Label className="mb-1 block">Pick Tee Time</Label>
            <div className="flex items-end gap-2">
              <input
                type="time"
                value={manualTeeTime}
                onChange={handleManualTeeTimeChange}
                disabled={!selectedService}
                className={`flex w-30 border rounded px-2 py-1 text-sm ${teeTimeError ? "border-red-500 ring-1 ring-red-200" : "focus:ring-2 focus:ring-blue-400"}`}
                aria-invalid={Boolean(teeTimeError)}
                step={60}
              />
              <Button
                size="sm"
                disabled={
                  !selectedService ||
                  !manualTeeTimePH ||
                  !teeTimeAvailable ||
                  !selectedMember ||
                  !servicePlayerData[currentServiceKey]?.player_name
                }
                className={`${
                  teeTimeAvailable && selectedMember && selectedService && servicePlayerData[currentServiceKey]?.player_name
                    ? "bg-blue-500 hover:bg-blue-600"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                } shrink-0`}
                onClick={handleReserveGolfSlot}
              >
                Reserve Tee Time
              </Button>
            </div>
            
            {teeTimeError && (
              <div className="text-xs text-red-600 mt-1">{teeTimeError}</div>
            )}
            {!selectedService && (
              <div className="text-xs text-red-600 mt-1">Select a golf service first</div>
            )}
            {manualTeeTimePH && teeTimeAvailable && (
              <div className="text-xs text-blue-600 mt-1">
                Tee Time: {manualTeeTimePH.ui_label_start} - {manualTeeTimePH.ui_label_end}
              </div>
            )}

            {servicePlayerData[currentServiceKey]?.reserved && (
              <div className="text-sm text-blue-600 mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                <div className="font-semibold">✅ Tee Time Reserved</div>
                <div className="text-xs">
                  {servicePlayerData[currentServiceKey].player_name} • {selectedService?.name} • {servicePlayerData[currentServiceKey].ui_label_start} - {servicePlayerData[currentServiceKey].ui_label_end}
                </div>
              </div>
            )}

            {(golfRatesItems?.length > 0) && filledSlots === 0 && (
              <Alert className="mt-2 border-yellow-500 bg-yellow-50">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <AlertDescription className="text-yellow-700">
                  Please reserve a tee time before confirming payment.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </>
      )}

      {/* ═══ GROUP BOOKING MODE ═══ */}
      {groupBookingEnabled && golfServiceTokens.length > 0 && (
        <>
          {/* Group Name Input */}
          <div className="p-3 rounded-lg border border-purple-200 bg-purple-50/60">
            <div className="flex items-center gap-2 mb-1.5">
              <Users className="w-4 h-4 text-purple-600" />
              <Label className="text-sm font-semibold text-purple-800">Group Name</Label>
              <span className="text-[11px] text-purple-500">(auto-fills all player names)</span>
            </div>
            <Input
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. Smith Family, Team Alpha"
              className="text-sm bg-white border-purple-300 focus:ring-purple-400 placeholder-gray-400"
            />
            {groupName.trim() && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {golfServiceTokens.map((tok, idx) => (
                  <span key={tok.key} className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                    {groupName.trim()} - Player {idx + 1}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Shared Tee Time Picker */}
          <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/60">
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-sm font-semibold text-blue-800">Shared Tee Time</Label>
                <p className="text-[11px] text-blue-600 mt-0.5">All {effectiveGroupSize} players share the same tee time</p>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-700">{fmt(currentTime)}</span>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <input
                type="time"
                value={manualTeeTime}
                onChange={handleManualTeeTimeChange}
                disabled={!selectedService}
                className={`flex w-32 border rounded px-2 py-1.5 text-sm bg-white ${teeTimeError ? "border-red-500 ring-1 ring-red-200" : "border-blue-300 focus:ring-2 focus:ring-blue-400"}`}
                aria-invalid={Boolean(teeTimeError)}
                step={60}
              />
              {manualTeeTimePH && teeTimeAvailable && (
                <span className="text-xs text-blue-600 pb-1">
                  {manualTeeTimePH.ui_label_start} — {manualTeeTimePH.ui_label_end}
                </span>
              )}
            </div>
            {teeTimeError && (
              <div className="text-xs text-red-600 mt-1">{teeTimeError}</div>
            )}
          </div>

          {/* Player Roster */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Player Roster</Label>
              <Badge className="bg-blue-100 text-blue-700 pointer-events-none text-xs">
                {filledSlots}/{golfServiceTokens.length} reserved
              </Badge>
            </div>
            <div className="space-y-2">
              {golfServiceTokens.map((tok, idx) => {
                const data = servicePlayerData[tok.key] || {};
                const isActive = currentServiceKey === tok.key;
                const isReserved = !!data.reserved;
                const playerName = data.player_name || "";

                return (
                  <div
                    key={tok.key}
                    onClick={() => setCurrentServiceKey(tok.key)}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                      isActive 
                        ? "border-blue-400 bg-blue-50 ring-1 ring-blue-200 shadow-sm" 
                        : isReserved 
                          ? "border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50" 
                          : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {/* Player number */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      isReserved ? "bg-emerald-500 text-white" : isActive ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"
                    }`}>
                      {isReserved ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                    </div>

                    {/* Name input / display */}
                    <div className="flex-1 min-w-0">
                      {isActive && !isReserved ? (
                        <Input
                          ref={idx === golfServiceTokens.findIndex(t => t.key === currentServiceKey) ? playerNameInputRef : undefined}
                          value={playerName}
                          onChange={(e) => handlePlayerNameChange(e.target.value)}
                          placeholder={`Player ${idx + 1} name`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm h-8 bg-white border-blue-300 focus:ring-blue-400 placeholder-gray-400"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={`text-sm truncate ${playerName ? "text-gray-900 font-medium" : "text-gray-400 italic"}`}>
                            {playerName || `Player ${idx + 1}`}
                          </span>
                          {tok.isGroupExtra && (
                            <span className="text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full flex-shrink-0">Group</span>
                          )}
                        </div>
                      )}
                      <div className="text-[11px] text-gray-500 mt-0.5">{tok.name}{tok.holes ? ` • ${tok.holes}H` : ""}</div>
                    </div>

                    {/* Status / Actions */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isReserved ? (
                        <>
                          <span className="text-[10px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded font-medium">
                            {data.ui_label_start}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditReservation(tok.key); }}
                            className="text-blue-500 hover:text-blue-700 transition-colors px-2 py-0.5 text-xs rounded"
                            title="Edit reservation"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); cancelReservation(tok.key); }}
                            className="text-red-400 hover:text-red-600 transition-colors p-0.5"
                            title="Cancel reservation"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <span className="text-[10px] text-gray-400 italic">Pending</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reserve button for current player / group */}
          {(() => {
            const currentData = servicePlayerData[currentServiceKey] || {};
            const anyReserved = Object.values(servicePlayerData).some(d => d?.reserved);
            const currentReserved = currentData.reserved;
            const currentHasName = (currentData.player_name || "").trim().length > 0;

            // If there's no reservation yet for the first player, show "Reserve for Group"
            // If the current player isn't reserved yet (auto-fill pending), show "Reserve" for them
            if (!currentReserved) {
              return (
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-xs mr-2">
                    <input
                      type="checkbox"
                      checked={applySameTeeTimeToAll}
                      onChange={(e) => setApplySameTeeTimeToAll(Boolean(e.target.checked))}
                      className="h-4 w-4"
                    />
                    <span className="text-xs text-gray-600">Apply same tee time to all players</span>
                  </label>
                  <Button
                    size="sm"
                    disabled={
                      !selectedService ||
                      !manualTeeTimePH ||
                      !teeTimeAvailable ||
                      !selectedMember ||
                      !currentHasName
                    }
                    className={`${
                      teeTimeAvailable && selectedMember && selectedService && currentHasName
                        ? "bg-blue-500 hover:bg-blue-600 text-white"
                        : "bg-gray-300 text-gray-600 cursor-not-allowed"
                    }`}
                    onClick={handleReserveGolfSlot}
                  >
                    {anyReserved ? (
                      <>Reserve Tee Time</>
                    ) : (
                      <><Users className="w-3.5 h-3.5 mr-1.5" /> Reserve for Group ({effectiveGroupSize} players)</>
                    )}
                  </Button>
                  {!currentHasName && (
                    <span className="text-xs text-red-500">Enter player name first</span>
                  )}
                  {!manualTeeTimePH && !teeTimeError && (
                    <span className="text-xs text-gray-400">Pick a tee time above</span>
                  )}
                </div>
              );
            }
            return null;
          })()}

          {/* All reserved confirmation */}
          {filledSlots === golfServiceTokens.length && golfServiceTokens.length > 0 && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800">
              <Check className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">All {filledSlots} players reserved</span>
              <span className="text-xs text-emerald-600 ml-auto">
                {Object.values(servicePlayerData).find(d => d?.reserved)?.ui_label_start || ""} — {Object.values(servicePlayerData).find(d => d?.reserved)?.ui_label_end || ""}
              </span>
            </div>
          )}

          {/* Warning if group has no reservations at all */}
          {(golfRatesItems?.length > 0) && filledSlots === 0 && (
            <Alert className="border-yellow-500 bg-yellow-50">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <AlertDescription className="text-yellow-700">
                Reserve a tee time for the group before confirming payment.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* ═══ RESERVATION SUMMARY (both modes) ═══ */}
      {filledSlots > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="text-sm font-semibold text-blue-800 mb-2">
            Reservation Summary ({filledSlots} player{filledSlots !== 1 ? 's' : ''})
          </div>
          <div className="space-y-1.5">
            {Object.entries(servicePlayerData)
              .filter(([_, data]) => data?.reserved)
              .map(([key, data], idx) => {
                const tok = golfServiceTokens.find(t => t.key === key);
                return (
                  <div
                    key={key}
                    onClick={() => viewGolfBooking(key, data)}
                    className="flex items-center justify-between text-xs bg-white p-2 rounded border border-blue-100 cursor-pointer hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-blue-900 truncate">{data.player_name}</div>
                        <div className="text-blue-600 truncate">{data.product_name} • {data.ui_label_start} - {data.ui_label_end}</div>
                      </div>
                      {tok?.isGroupExtra && (
                        <span className="text-[9px] bg-indigo-50 text-indigo-500 px-1 py-0.5 rounded flex-shrink-0">Group</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={(e) => { e.stopPropagation(); cancelReservation(key); }}
                      className="text-xs h-6 px-2"
                    >
                      Cancel
                    </Button>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}




// Stable empty array constant — prevents new reference each render when prop is omitted
const EMPTY_DR_ITEMS = [];

// TimeSlot Picker System
function TimeSlotPicker({
  newPlayerLog,
  setNewPlayerLog,
  durationMinutes,
  isTimeSlotAvailable,
  bayNumberError,
  selectedMember,
  orNumber,
  toast,
  manualTime,
  setManualTime,
  activePlayerLogs,
  transactions,
  products,
  isCartSummaryModalOpen,
  drivingRangeItems: drivingRangeItemsProp,
  cart,
  setCart,
  isResume = false,
  resumeOriginalBay = "",
  resumePlayerName = "",
  onReservationStatsChange,
  groupPlayersCount = 1,
  setGroupPlayersCount,
  isReschedule = false,
  rescheduleExcludeId = null,
}) {
  // Stable reference: avoid new [] each render when prop is undefined/null
  const drivingRangeItems = drivingRangeItemsProp || EMPTY_DR_ITEMS;
  const [reservedSlot, setReservedSlot] = React.useState(null);
  const [availableBays, setAvailableBays] = React.useState([]);
  const [debugInfo, setDebugInfo] = React.useState(null);
  const [timeError, setTimeError] = React.useState(null);

  // Group booking override: allow booking on already-taken bay/time
  const [allowGroupBooking, setAllowGroupBooking] = React.useState(false);

  // ✅ Extra group players added via inline controls (store as tokens so user picks product)
  const [extraGroupTokens, setExtraGroupTokens] = React.useState([]);
  const [showAddExtraSelect, setShowAddExtraSelect] = React.useState(false);

  // ✅ DR Group Name: single name that auto-fills all player slots as "GroupName - Player N"
  const [drGroupName, setDrGroupName] = React.useState("");

  // Current time for the picker UI (updates every second) — Manila time
  const [currentTime, setCurrentTime] = React.useState(() => manilaNowDate());
  React.useEffect(() => {
    const t = setInterval(() => setCurrentTime(manilaNowDate()), 1000);
    return () => clearInterval(t);
  }, []);

  // Helper: format any instant as Manila "HH:mm" for <input type="time">
  const toHHMM = (d) => manilaHHMM(d || manilaNowDate());
  // round up Date to next interval (minutes)
  const roundUpToNextInterval = (date, intervalMinutes = 10) => {
    const d = new Date(date);
    const ms = d.getTime();
    const intervalMs = intervalMinutes * 60 * 1000;
    const next = Math.ceil((ms) / intervalMs) * intervalMs;
    return new Date(next);
  };
  // keep a live current time string for input min value enforcement (exact current Manila time)
  const [nowForTimeInput, setNowForTimeInput] = React.useState(() => toHHMM(manilaNowDate()));
  React.useEffect(() => {
    const t = setInterval(() => setNowForTimeInput(toHHMM(manilaNowDate())), 1000);
    return () => clearInterval(t);
  }, []);

  const formatTimeInputValue = (t) => {
    if (!t) return "";
    const s = String(t).trim();
    if (/^\d{1,2}:\d{2}(:\d{2}(\.\d{3})?)?$/.test(s)) return s.slice(0, 5);
    const d = new Date(s);
    return isNaN(d.getTime()) ? "" : toHHMM(d);
  };

  const parseGuestStartToManila = (hhmmOrIso) => {
    if (!hhmmOrIso) return null;
    const s = String(hhmmOrIso).trim();
    if (/^\d{1,2}:\d{2}$/.test(s)) {
      return manilaDateFromHHMM(s, manilaNowDate());
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  const parseDurationMins = React.useCallback((prod) => {
    if (!prod) return 60;
    if (Number.isFinite(Number(prod.duration))) return Math.round(Number(prod.duration));
    const name = String(prod.name || "").toLowerCase();
    const h = name.match(/(\d+(?:\.\d+)?)\s*(hour|hr|hrs|hours)\b/);
    const m = name.match(/(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes)\b/);
    if (h) return Math.round(parseFloat(h[1]) * 60);
    if (m) return Math.round(parseFloat(m[1]));
    return 60;
  }, []);

    // ADD: normalize to HH:mm helper (put near other helpers)
const normalizeHHMM = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h,m] = s.split(":").map(Number);
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
  }
  const d = new Date(s);
  if (d instanceof Date && !isNaN(d.getTime())) {
    return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  }
  return "";
};


  const serviceTokens = React.useMemo(() => {
    const tokens = [];
    (drivingRangeItems || []).forEach((it) => {
      const prod = (products || []).find(p => String(p.id) === String(it.product_id));
      if (!prod) return;
      const qty = Number(it.quantity || 1);
      const duration = parseDurationMins(prod);
      for (let i = 0; i < qty; i++) {
        tokens.push({
          key: `${prod.id}#${i + 1}`,
          product_id: String(prod.id),
          name: prod.name,
          duration
        });
      }
    });
    // Group booking: add extra player slots from extraGroupTokens
    if (extraGroupTokens && extraGroupTokens.length > 0) {
      for (let i = 0; i < extraGroupTokens.length; i++) {
        const et = extraGroupTokens[i];
        const playerNum = tokens.length + 1;
        tokens.push({
          key: `drgroup#${playerNum}-${et.product_id}`,
          product_id: et.product_id,
          name: `${et.name} (Extra Player ${i + 1})`,
          duration: et.duration || 60,
          isGroupExtra: true
        });
      }
    }
    // Reschedule mode: if no cart items, inject a synthetic token so the picker works
    if (isReschedule && tokens.length === 0) {
      tokens.push({
        key: "reschedule#1",
        product_id: "reschedule",
        name: "Reschedule Slot",
        duration: Number(durationMinutes) || 60,
      });
    }
    // Resume mode: same as reschedule — inject a synthetic token so the picker works
    if (isResume && tokens.length === 0) {
      tokens.push({
        key: "resume#1",
        product_id: "resume",
        name: "Resume Slot",
        duration: Number(durationMinutes) || 60,
      });
    }
    return tokens;
  }, [drivingRangeItems, products, parseDurationMins, extraGroupTokens, isReschedule, isResume, durationMinutes]);
  
  // ✅ Effective group size = base cart tokens + extra players
  const baseTokenCount = React.useMemo(() => {
    return (drivingRangeItems || []).reduce((sum, it) => sum + Number(it.quantity || 1), 0);
  }, [drivingRangeItems]);
  const effectiveGroupSize = baseTokenCount + (extraGroupTokens ? extraGroupTokens.length : 0);

  // Options for adding an extra slot — ALL Driving Range products (not just cart)
  const extraAddOptions = React.useMemo(() => {
    const map = {};
    (products || []).forEach(prod => {
      if (prod.category !== "Driving Range Rates") return;
      const productId = String(prod.id);
      if (!map[productId]) {
        const duration = parseDurationMins(prod);
        map[productId] = { product_id: productId, name: prod.name, duration };
      }
    });
    return Object.values(map);
  }, [products, parseDurationMins]);

  // ✅ Sync effective group size back to parent for transaction payload
  React.useEffect(() => {
    if (typeof setGroupPlayersCount === 'function' && effectiveGroupSize !== groupPlayersCount) {
      setGroupPlayersCount(effectiveGroupSize);
    }
  }, [effectiveGroupSize, groupPlayersCount, setGroupPlayersCount]);

  // ✅ NEW: Per-service saved selections (keyed by token.key)
  const [serviceSelections, setServiceSelections] = React.useState({});
  

  // ✅ NEW: Calculate filled slots
  const filledSlots = React.useMemo(() => {
    return Object.values(serviceSelections).filter(sel => 
      sel?.ui_locked && sel?.bay_number && sel?.time_in
    ).length;
  }, [serviceSelections]);
  // TODO: consider time_out requirement?

  // Propagate reservation stats upward (filled vs total) for payment gating
  const lastStatsJsonRef = React.useRef("");
  React.useEffect(() => {
    if (typeof onReservationStatsChange !== 'function') return;
    const incompleteKeys = serviceTokens
      .filter(tok => {
        const sel = serviceSelections[tok.key];
        return !(sel && sel.ui_locked && sel.bay_number && sel.player_name && sel.time_in && sel.time_out);
      })
      .map(t => t.key);

    // ✅ Include reservedEntries so processPayment can hydrate bay/time data
    const reservedEntries = serviceTokens
      .map(tok => {
        const sel = serviceSelections[tok.key];
        if (!sel || !sel.ui_locked || !sel.bay_number) return null;
        return {
          key: tok.key,
          bay_number: sel.bay_number,
          player_name: sel.player_name || "",
          time_in: sel.time_in || "",
          time_out: sel.time_out || "",
          ui_label_start: sel.ui_label_start || "",
          ui_label_end: sel.ui_label_end || "",
          ui_locked: !!sel.ui_locked,
          selected_service: sel.selected_service || null,
        };
      })
      .filter(Boolean);

    // ✅ Compute extra player cost from extraGroupTokens (not in cart, tracked separately)
    const extraItems = (extraGroupTokens || []).map(et => {
      const prod = (products || []).find(p => String(p.id) === String(et.product_id));
      return {
        product_id: String(et.product_id || ""),
        product_name: et.name || (prod ? prod.name : ""),
        price: prod ? Number(prod.price || 0) : 0,
        quantity: 1,
      };
    });
    const extraCost = extraItems.reduce((sum, it) => sum + it.price, 0);

    const nextStats = {
      filledSlots,
      totalSlots: serviceTokens.length,
      incompleteKeys,
      reservedEntries,
      extraCost,
      extraItems,
      groupName: (drGroupName || "").trim() || undefined,
    };
    const nextJson = JSON.stringify(nextStats);
    if (nextJson !== lastStatsJsonRef.current) {
      lastStatsJsonRef.current = nextJson;
      onReservationStatsChange(nextStats);
    }
  }, [filledSlots, serviceSelections, serviceTokens, onReservationStatsChange, extraGroupTokens, products, drGroupName]);

  // Initialize/cleanup serviceSelections when tokens change
    // Merge new tokens into existing selections so we don't accidentally drop
    // per-token reservations when the token list changes (e.g. add 2hr after 1hr).
  React.useEffect(() => {
    setServiceSelections(prev => {
      const existing = prev || {};
      let changed = false;
      const next = { ...existing };
      for (const tok of serviceTokens) {
        if (!next[tok.key]) {
          next[tok.key] = {
            bay_number: "",
            time_in: "",
            time_in_ui: "",            // FIX: snapshot
            time_out: "",
            ui_label_start: "",
            ui_label_end: "",
            ui_locked: false,
            player_name: ""
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [serviceTokens]);

  // View a driving-range booking: populate UI and highlight POS row
  const viewDrivingBooking = (key, data) => {
    try {
      const bay = data?.bay_number || data?.bayNumber || data?.bay || "";
      const playerName = data?.player_name || data?.player_name || "";
      const startLabel = data?.ui_label_start || data?.time_in || data?.ui_label_start || "";
      const endLabel = data?.ui_label_end || data?.time_out || "";

      // select token
      try { setMainServiceKey && setMainServiceKey(key); } catch (e) {}

      // merge into serviceSelections
      try {
        setServiceSelections && setServiceSelections(prev => ({ ...(prev || {}), [key]: { ...(prev?.[key] || {}), ...(data || {}), ui_locked: true, bay_number: bay, player_name: playerName, ui_label_start: startLabel, ui_label_end: endLabel } }));
      } catch (e) {}

      // populate newPlayerLog and reserved UI
      try {
        setNewPlayerLog && setNewPlayerLog(prev => ({ ...(prev || {}), bay_number: bay, ui_locked: true, time_in: data?.time_in || "", time_out: data?.time_out || "", ui_label_start: startLabel, ui_label_end: endLabel, player_name: playerName }));

        // compute start/end Date objects where possible
        const tok = (serviceTokens || []).find(t => t.key === key) || null;
        let startDt = null;
        if (data?.time_in) startDt = parseGuestStartToManila(data.time_in) || manilaDateFromHHMM(String(data.time_in), manilaNowDate());
        else if (data?.ui_label_start) startDt = manilaDateFromHHMM(String(data.ui_label_start), manilaNowDate());

        if (startDt) {
          const dur = (tok && Number.isFinite(Number(tok.duration))) ? Number(tok.duration) : 60;
          const endDt = new Date(startDt.getTime() + (dur * 60 * 1000));
          setManualTime && setManualTime(manilaHHMM(startDt));
          setAvailableBays && setAvailableBays(prev => prev && prev.includes(String(bay)) ? prev : [...(prev || []), String(bay)]);
          setReservedSlot && setReservedSlot({ bay_number: String(bay), time_in: startDt.toISOString(), time_out: endDt.toISOString(), member_id: selectedMember?.id, player_name: playerName, or_number: orNumber, ui_label_start: manilaTimeLabel(startDt), ui_label_end: manilaTimeLabel(endDt) });
        }
      } catch (e) {
        // ignore
      }

      // highlight & scroll to POS row if possible
      try { setActiveTab && setActiveTab("pos"); } catch (e) {}
      try {
        const playerKey = `${String(data?.or_number || data?.or || "")} - ${String(bay)}`;
        setHighlightedPlayerKey && setHighlightedPlayerKey(playerKey);
        setTimeout(() => {
          try {
            const row = document.getElementById(`player-row-${data?.or_number || data?.or || ""}-${bay}`);
            if (row) { row.scrollIntoView({ behavior: 'smooth', block: 'center' }); row.click(); }
          } catch (err) {}
        }, 150);
        setTimeout(() => { try { setHighlightedPlayerKey && setHighlightedPlayerKey(prev => prev === playerKey ? null : prev); } catch (e) {} }, 5000);
      } catch (e) {}
    } catch (err) {
      console.error('viewDrivingBooking error:', err);
    }
  };

  // Current selected service
  const [mainServiceKey, setMainServiceKey] = React.useState("");
  const selectedMainService = React.useMemo(
    () => serviceTokens.find(t => t.key === mainServiceKey) || null,
    [serviceTokens, mainServiceKey]
  );
  
  // Auto-pick first available token
  React.useEffect(() => {
    if (!mainServiceKey && serviceTokens.length) {
      setMainServiceKey(serviceTokens[0].key);
    }
  }, [serviceTokens.length, mainServiceKey]);

  // ✅ Auto-fill player names from DR group name (when group name changes, update all slots)
  React.useEffect(() => {
    if (effectiveGroupSize <= 1 || !drGroupName.trim()) return;
    const gn = drGroupName.trim();
    setServiceSelections(prev => {
      const next = { ...prev };
      let changed = false;
      serviceTokens.forEach((tok, idx) => {
        const existing = next[tok.key] || {};
        const expectedName = `${gn} - Player ${idx + 1}`;
        const curName = (existing.player_name || "").trim();
        // Only auto-fill if name is empty, was a previous auto-generated name, or matches the old pattern
        const isAutoGenerated = !curName || /^Player \d+$/.test(curName) || /^.+ - Player \d+$/.test(curName);
        if (isAutoGenerated && curName !== expectedName) {
          next[tok.key] = { ...existing, player_name: expectedName };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // Also sync the visible Player Name input for the currently selected token
    if (mainServiceKey) {
      const curIdx = serviceTokens.findIndex(t => t.key === mainServiceKey);
      if (curIdx !== -1) {
        const expected = `${gn} - Player ${curIdx + 1}`;
        setNewPlayerLog(prev => {
          const cur = (prev?.player_name || "").trim();
          const isAuto = !cur || /^Player \d+$/.test(cur) || /^.+ - Player \d+$/.test(cur);
          if (isAuto && cur !== expected) return { ...(prev || {}), player_name: expected };
          return prev;
        });
      }
    }
  }, [drGroupName, effectiveGroupSize, serviceTokens, mainServiceKey]);

  // ✅ NEW: Auto-fill all other slots with the same bay/time when main slot is locked
  // This allows all players to share the same bay and time slot
  React.useEffect(() => {
    if (!mainServiceKey) return;
    const mainSel = serviceSelections[mainServiceKey];
    if (!mainSel || !mainSel.ui_locked || !mainSel.bay_number || !mainSel.time_in) return;

    // Check if there are unfilled slots that need auto-filling
    const unfilledSlots = serviceTokens.filter(tok => {
      if (tok.key === mainServiceKey) return false;
      const sel = serviceSelections[tok.key];
      return !sel || !sel.ui_locked || !sel.bay_number || !sel.time_in;
    });

    if (unfilledSlots.length === 0) return;

    // Auto-fill unfilled slots with main slot's bay/time
    // Each token gets its OWN time_out based on its individual duration (e.g. 30min vs 1hr)
    setServiceSelections(prev => {
      const next = { ...prev };
      for (const tok of unfilledSlots) {
        const existing = next[tok.key] || {};
        // Preserve existing player_name if set, otherwise generate from DR group name or default
        const gn = (drGroupName || "").trim();
        const playerIdx = serviceTokens.findIndex(t => t.key === tok.key) + 1;
        const playerName = existing.player_name || (gn ? `${gn} - Player ${playerIdx}` : `Player ${playerIdx}`);
        // Compute per-token time_out using THIS token's duration, not the main's
        const tokDuration = tok.duration || 60;
        const startDt = new Date(mainSel.time_in);
        let tokTimeOut = mainSel.time_out;
        let tokEndLabel = mainSel.ui_label_end;
        if (!isNaN(startDt.getTime())) {
          const endDt = new Date(startDt.getTime() + tokDuration * 60000);
          tokTimeOut = endDt.toISOString();
          tokEndLabel = manilaTimeLabel(endDt);
        }
        next[tok.key] = {
          ...existing,
          bay_number: mainSel.bay_number,
          time_in: mainSel.time_in,
          time_in_ui: mainSel.time_in_ui || "",
          time_out: tokTimeOut,
          ui_label_start: mainSel.ui_label_start,
          ui_label_end: tokEndLabel,
          ui_locked: true,
          player_name: playerName
        };
      }
      return next;
    });
  }, [mainServiceKey, serviceSelections, serviceTokens]);



  // When switching service, load its saved time into the input
  // React.useEffect(() => {
  //   if (!selectedMainService) return;
  //   const sel = serviceSelections[mainServiceKey] || {};
  //   const hhmm = sel.time_in ? (String(sel.time_in).match(/^\d{1,2}:\d{2}/) ? sel.time_in : "") : "";

  //   // Only set manual time when there is a saved hh:mm; do NOT clear it to ""
  //   if (hhmm && typeof setManualTime === "function" && (manualTime || "") !== hhmm) {
  //     setManualTime(hhmm);
  //   }

  //   // Build minimal selected_service metadata (include token key)
  //   const nextSelectedService = {
  //     key: mainServiceKey,
  //     product_id: selectedMainService.product_id,
  //     name: selectedMainService.name,
  //     duration: selectedMainService.duration,
  //   };

  //   // Merge only present saved fields; never wipe parent when sel is empty
  //   setNewPlayerLog(prev => {
  //     const base = { ...(prev || {}) };
  //     const fill = { selected_service: nextSelectedService };
  //     if (sel.time_in) fill.time_in = sel.time_in;
  //     if (sel.time_out) fill.time_out = sel.time_out;
  //     if (sel.bay_number) fill.bay_number = sel.bay_number;
  //     if (sel.ui_label_start) fill.ui_label_start = sel.ui_label_start;
  //     if (sel.ui_label_end) fill.ui_label_end = sel.ui_label_end;
  //     if (typeof sel.ui_locked !== "undefined") fill.ui_locked = !!sel.ui_locked;

  //     // If operator hasn't typed a name, populate player_name from the token if present
  //     if (!userTypedNameRef.current && sel.player_name && String(sel.player_name).trim() !== "") {
  //       fill.player_name = sel.player_name;
  //     }

  //     // If nothing meaningful to merge, only set selected_service if changed
  //     const hasAny =
  //       Boolean(fill.player_name || fill.time_in || fill.time_out || fill.bay_number || fill.ui_label_start || fill.ui_label_end || typeof fill.ui_locked !== "undefined");
  //     if (!hasAny) {
  //       const prevSel = base.selected_service || {};
  //       const selUnchanged =
  //         String(prevSel.key || "") === String(nextSelectedService.key || "") &&
  //         String(prevSel.product_id || "") === String(nextSelectedService.product_id || "") &&
  //         String(prevSel.name || "") === String(nextSelectedService.name || "") &&
  //         Number(prevSel.duration || 0) === Number(nextSelectedService.duration || 0);
  //       return selUnchanged ? prev : { ...base, selected_service: nextSelectedService };
  //     }

  //     const merged = { ...base, ...fill };
  //     const same =
  //       String(base.bay_number || "") === String(merged.bay_number || "") &&
  //       String(base.time_in || "") === String(merged.time_in || "") &&
  //       String(base.time_out || "") === String(merged.time_out || "") &&
  //       String(base.ui_label_start || "") === String(merged.ui_label_start || "") &&
  //       String(base.ui_label_end || "") === String(merged.ui_label_end || "") &&
  //       !!base.ui_locked === !!merged.ui_locked &&
  //       String((base.selected_service && base.selected_service.key) || "") === String(nextSelectedService.key || "");
  //     return same ? prev : merged;
  //   });
  // }, [mainServiceKey, selectedMainService?.product_id, selectedMainService?.duration, serviceSelections, manualTime]);


    // ✅ NEW: ref to hold latest selections so we don't force-effect re-runs
  const serviceSelectionsRef = React.useRef(serviceSelections);
   // keep ref in sync with state so effects that read the ref get the latest snapshot
 React.useEffect(() => {
     serviceSelectionsRef.current = serviceSelections;
 }, [serviceSelections]);
  React.useEffect(() => {}, [mainServiceKey, selectedMainService?.product_id, selectedMainService?.duration]);

  // When switching service, load its saved time + player without wiping bay/ui_locked
  React.useEffect(() => {
    serviceSelectionsRef.current = serviceSelections;
  }, [serviceSelections]);

  // Service switch effect (replaces previous)
  React.useEffect(() => {
    if (!selectedMainService) return;
    const sel = serviceSelectionsRef.current?.[mainServiceKey] || {};
    const savedHHMM = normalizeHHMM(sel.time_in_ui || sel.time_in);
    if (savedHHMM && manualTime !== savedHHMM) {
      setManualTime(savedHHMM);
    }

    const nextSelectedService = {
      key: mainServiceKey,
      product_id: selectedMainService.product_id,
      name: selectedMainService.name,
      duration: selectedMainService.duration,
    };

    setNewPlayerLog(prev => {
      const base = { ...(prev || {}) };
      const fill = { selected_service: nextSelectedService };

      if (sel.time_in) fill.time_in = sel.time_in;
      if (sel.time_out) fill.time_out = sel.time_out;
      if (sel.bay_number) fill.bay_number = sel.bay_number;
      if (sel.ui_label_start) fill.ui_label_start = sel.ui_label_start;
      if (sel.ui_label_end) fill.ui_label_end = sel.ui_label_end;
      if (typeof sel.ui_locked !== "undefined") fill.ui_locked = !!sel.ui_locked;

      // Restore player name unless user currently typed a different non-empty one
      const currentName = (base.player_name || "").trim();
      const savedName = (sel.player_name || "").trim();
      if (savedName && currentName !== savedName) {
        fill.player_name = savedName;
      }

      const merged = { ...base, ...fill };
      const same =
        base.bay_number === merged.bay_number &&
        base.time_in === merged.time_in &&
        base.time_out === merged.time_out &&
        base.ui_label_start === merged.ui_label_start &&
        base.ui_label_end === merged.ui_label_end &&
        base.ui_locked === merged.ui_locked &&
        (base.player_name || "").trim() === (merged.player_name || "").trim() &&
        (base.selected_service?.key || "") === (nextSelectedService.key || "");

      return same ? prev : merged;
    });
  }, [mainServiceKey, selectedMainService?.product_id, selectedMainService?.duration]);



    // Mirror manualTime changes with preservation
  React.useEffect(() => {
    if (!selectedMainService) return;
    const hhmm = normalizeHHMM(manualTime);
    if (!hhmm) return;

    const start = manilaDateFromHHMM(hhmm, manilaNowDate());
    if (!start) return;
    const end = new Date(start.getTime() + Number(selectedMainService.duration || 60)*60000);

    setServiceSelections(prev => {
      const cur = prev[mainServiceKey] || {};
      const prevHHMM = normalizeHHMM(cur.time_in_ui || cur.time_in);
      // No-op guard: if time hasn't changed, return same reference
      if (prevHHMM === hhmm && cur.time_in === start.toISOString() && cur.time_out === end.toISOString()) {
        return prev;
      }
      // Preserve bay_number and ui_locked here — do not auto-unselect when time changes.
      const updated = {
        ...cur,
        time_in: start.toISOString(),
        time_in_ui: hhmm,
        time_out: end.toISOString(),
        ui_label_start: manilaTimeLabel(start),
        ui_label_end: manilaTimeLabel(end),
        // Preserve bay_number/ui_locked — availability checks will clear if actually conflicting
      };
      return { ...prev, [mainServiceKey]: updated };
    });

    setNewPlayerLog(prev => {
      const curSel = serviceSelectionsRef.current?.[mainServiceKey] || {};
      const prevHHMM = normalizeHHMM(curSel.time_in_ui || curSel.time_in);
      const changed = prevHHMM !== hhmm;
      // Always update times/labels and selected_service but preserve existing bay/ui_locked.
      if (!changed && prev?.time_in === start.toISOString() && prev?.time_out === end.toISOString()) return prev;
      return {
        ...(prev||{}),
        // preserve prev.bay_number and prev.ui_locked to avoid auto-unselect
        time_in: start.toISOString(),
        time_out: end.toISOString(),
        ui_label_start: manilaTimeLabel(start),
        ui_label_end: manilaTimeLabel(end),
        selected_service: {
          key: mainServiceKey,
          product_id: selectedMainService.product_id,
          name: selectedMainService.name,
          duration: selectedMainService.duration,
        }
      };
    });
  }, [manualTime, selectedMainService?.duration, selectedMainService?.product_id, mainServiceKey]);



    // Keep the currently-selected token across modal open/close so per-token
    // reservations (bay, player_name) remain visible when switching back.
  React.useEffect(() => {
    if (isCartSummaryModalOpen) {
      setReservedSlot(null);
      setAvailableBays([]);
      setDebugInfo(null);
      setTimeError(null);
      // intentionally do NOT clear mainServiceKey here
    } else {
      setDebugInfo(null);
      // Clear extra group tokens and group name when modal closes
      // Guard: only update if they actually have content to avoid new-ref loops
      setExtraGroupTokens(prev => (prev.length > 0 ? [] : prev));
      setDrGroupName(prev => (prev !== "" ? "" : prev));
      // do not clear mainServiceKey on close either — let caller explicitly reset if needed
    }
  }, [isCartSummaryModalOpen]);

  React.useEffect(() => {
    if (!manualTime && !isCartSummaryModalOpen) {
      setManualTime(manilaHHMM(manilaNowDate()));
    }
  }, [manualTime, setManualTime, isCartSummaryModalOpen]);

  const fmt = (d) =>
    (d instanceof Date ? d : new Date(d)).toLocaleTimeString("en-PH", { timeZone: MANILA_TZ, hour: "2-digit", minute: "2-digit", hour12: true });

  const safeDuration = React.useMemo(() => {
    if (selectedMainService?.duration) return selectedMainService.duration;
    const mins = Number(durationMinutes);
    if (!Number.isFinite(mins) || mins <= 0) return 60;
    return Math.round(mins);
  }, [durationMinutes, selectedMainService]);

  function addMinutes(date, mins) {
    return new Date(date.getTime() + mins * 60000);
  }

  // Stable manualStartPH — only depends on manualTime, NOT on currentTime/nowPH.
  // This prevents re-computation every second which would cascade into bay-availability effects.
  const manualStartPH = React.useMemo(() => {
    if (!manualTime || !/^\d{2}:\d{2}$/.test(manualTime)) return null;
    return manilaDateFromHHMM(manualTime, manilaNowDate());
  }, [manualTime]);

  const manualEndPH = manualStartPH ? addMinutes(manualStartPH, safeDuration) : null;

  // Separate memo for the error message — this CAN depend on currentTime (updates every second)
  const manualStartError = React.useMemo(() => {
    if (!manualStartPH) return null;
    const nowMinute = manilaDateFromHHMM(manilaHHMM(currentTime), currentTime);
    return manualStartPH.getTime() < nowMinute.getTime() ? "Selected time is already past" : null;
  }, [manualStartPH, currentTime]);

  React.useEffect(() => {
    setTimeError(manualStartError || null);
  }, [manualStartError]);

  React.useEffect(() => {
    if (!manualStartPH) {
      const cur = serviceSelectionsRef.current?.[mainServiceKey];
      const hasSaved = !!(cur && (cur.time_in_ui || cur.time_in));
      if (hasSaved) return; // keep existing reservation visible
      setReservedSlot(null);
      setAvailableBays([]);
      setDebugInfo(null);
      setTimeError(null);
      setNewPlayerLog(prev => {
        if (!prev) return prev;
        const next = { ...prev };
        next.bay_number = "";
        next.time_in = "";
        next.time_out = "";
        next.ui_label_start = "";
        next.ui_label_end = "";
        next.ui_locked = false;
        return next;
      });
    }
  }, [manualStartPH, mainServiceKey]);

  // Mirror manualTime change only if actual time changed (compare minutes)
  React.useEffect(() => {
    const selTok = selectedMainService;
    if (!selTok) return;
    const hhmm = normalizeHHMM(manualTime);
    if (!hhmm) return;

    // build todayPH date
    const tzNow = new Date().toLocaleString("en-US",{timeZone:"Asia/Manila"});
    const todayPH = new Date(tzNow);
    const [h,m] = hhmm.split(":").map(Number);
    const start = new Date(todayPH);
    start.setHours(h,m,0,0);
    const end = new Date(start.getTime() + Number(selTok.duration||60)*60000);

    setServiceSelections(prev => {
      const cur = prev[mainServiceKey] || {};
      const prevHHMM = normalizeHHMM(cur.time_in_ui || cur.time_in);
      // If same HH:MM keep bay/ui_locked
      const timeChanged = prevHHMM !== hhmm;
      const updated = {
        ...cur,
        time_in_ui: hhmm,
        time_in: start.toISOString(),
        time_out: end.toISOString(),
        ui_label_start: start.toLocaleTimeString("en-PH",{hour:"2-digit",minute:"2-digit",hour12:true}),
        ui_label_end: end.toLocaleTimeString("en-PH",{hour:"2-digit",minute:"2-digit",hour12:true}),
        ...(timeChanged ? { bay_number:"", ui_locked:false } : {})
      };
      return { ...prev, [mainServiceKey]: updated };
    });

    setNewPlayerLog(prev => {
      const curToken = serviceSelectionsRef.current?.[mainServiceKey] || {};
      const prevHHMM = normalizeHHMM(curToken.time_in_ui || curToken.time_in);
      const timeChanged = prevHHMM !== hhmm;
      if (!timeChanged) return prev;
      const filled = {
        bay_number: "",
        ui_locked: false,
        time_in: start.toISOString(),
        time_out: end.toISOString(),
        ui_label_start: start.toLocaleTimeString("en-PH",{hour:"2-digit",minute:"2-digit",hour12:true}),
        ui_label_end: end.toLocaleTimeString("en-PH",{hour:"2-digit",minute:"2-digit",hour12:true}),
        selected_service: {
          product_id: selTok.product_id,
            name: selTok.name,
            duration: selTok.duration,
            key: mainServiceKey
        }
      };
      // preserve existing player_name
      if (prev?.player_name) filled.player_name = prev.player_name;
      return { ...(prev||{}), ...filled };
    });
  }, [manualTime, selectedMainService?.duration, selectedMainService?.product_id]);

    // Prevent clearing reservation if token already has saved slot
  React.useEffect(() => {
    if (manualStartPH) return;
    const sel = serviceSelectionsRef.current?.[mainServiceKey];
    const hasReservation = !!(sel?.ui_locked && sel?.bay_number && (sel.time_in_ui || sel.time_in));
    if (hasReservation) return; // do not clear
    setReservedSlot(null);
    setAvailableBays([]);
    setTimeError(null);
    setNewPlayerLog(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        bay_number: "",
        time_in: "",
        time_out: "",
        ui_label_start: "",
        ui_label_end: "",
        ui_locked: false,
      };
    });
  }, [manualStartPH, mainServiceKey]);

  const parseManilaDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      if (dateStr instanceof Date) return dateStr;
      let isoStr = String(dateStr);
      if (!isoStr.includes("+") && !isoStr.includes("Z")) isoStr += "+08:00";
      return new Date(isoStr);
    } catch (e) {
      console.error("Date parsing error:", dateStr, e);
      return null;
    }
  };

  const getPlannedDurationMinutes = (log) => {
    if (log?.paused_planned_duration_minutes) {
      return Number(log.paused_planned_duration_minutes) || 0;
    }
    if (log.service_type === "Golf Rates") {
      return 240;
    }
    const txn = transactions.find(t => t.or_number === log.or_number);
    if (!txn || !txn.items) return 0;
    let total = 0;
    let hasDR = false;
    txn.items.forEach(it => {
      const prod = products.find(p => p.id === it.product_id);
      const qty = (it.quantity || 1);
      if (prod?.category !== "Driving Range Rates") {
        return;
      }
      hasDR = true;
      if (prod?.duration) {
        total += Number(prod.duration) * qty;
        return;
      }
      const name = (prod?.name || "").toLowerCase();
      const hourMatch = name.match(/(\d+(?:\.\d+)?)\s*(hour|hr|hrs|hours)\b/);
      if (hourMatch) {
        total += Math.round(parseFloat(hourMatch[1]) * 60) * qty;
        return;
      }
      const minMatch = name.match(/(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes)\b/);
      if (minMatch) {
        total += Math.round(parseFloat(minMatch[1])) * qty;
        return;
      }
    });
    return hasDR ? (total || 60) : 0;
  };

  function getBayBookingsForToday(bayNumber) {
    if (!Array.isArray(activePlayerLogs) || !bayNumber) return [];
    const nowManila = new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" });
    const todayManila = new Date(nowManila);

    const activeBookings = activePlayerLogs
      .filter(log => {
        // Exclude the booking being rescheduled so it doesn't block its own new time
        if (rescheduleExcludeId && String(log.id) === String(rescheduleExcludeId)) return false;
        return String(log.bay_number) === String(bayNumber);
      })
      .map(log => {
        const txn = transactions.find(t => t.or_number === log.or_number) || null;
        let timeIn = null;
        let timeOut = null;
        if (log.was_resumed && log.resumed_time_in) {
          timeIn = parseManilaDate(log.resumed_time_in);
        } else if (txn?.start_time && /^\d{1,2}:\d{2}$/.test(txn.start_time)) {
          const [h, m] = txn.start_time.split(":").map(Number);
          timeIn = new Date(todayManila);
          timeIn.setHours(h, m, 0, 0);
        } else if (log.time_in) {
          timeIn = parseManilaDate(log.time_in);
        }
        if (log.was_resumed && log.resumed_time_out) {
          timeOut = parseManilaDate(log.resumed_time_out);
        } else if (txn?.end_time && /^\d{1,2}:\d{2}$/.test(txn.end_time)) {
          const [h, m] = txn.end_time.split(":").map(Number);
          timeOut = new Date(todayManila);
          timeOut.setHours(h, m, 0, 0);
        } else if (log.time_out) {
          timeOut = parseManilaDate(log.time_out);
        } else if (timeIn) {
          const planned = getPlannedDurationMinutes(log);
          if (planned > 0) timeOut = new Date(timeIn.getTime() + planned * 60000);
        }
        if (timeIn instanceof Date && timeOut instanceof Date) {
          if (timeOut.getTime() <= timeIn.getTime()) {
            timeOut = new Date(timeOut.getTime() + 24 * 60 * 60 * 1000);
          }
        }
        return { timeIn, timeOut, log, txn };
      })
      .filter(b => b.timeIn instanceof Date && b.timeOut instanceof Date && !isNaN(b.timeIn.getTime()) && !isNaN(b.timeOut.getTime()))
      .filter(b => {
        const bookingDate = new Date(b.timeIn);
        const manila = new Date(bookingDate.toLocaleString("en-US", { timeZone: "Asia/Manila" }));
        return (
          manila.getFullYear() === todayManila.getFullYear() &&
          manila.getMonth() === todayManila.getMonth() &&
          manila.getDate() === todayManila.getDate()
        );
      });

    const localReserved = (reservedSlot && String(reservedSlot.bay_number) === String(bayNumber) && reservedSlot.time_in && reservedSlot.time_out)
      ? (() => {
          const rIn = parseManilaDate(reservedSlot.time_in) || parseGuestStartToManila(reservedSlot.time_in);
          const rOut = parseManilaDate(reservedSlot.time_out) || (rIn ? new Date(rIn.getTime() + safeDuration * 60000) : null);
          if (rIn instanceof Date && rOut instanceof Date && !isNaN(rIn.getTime()) && !isNaN(rOut.getTime())) {
            return { timeIn: rIn, timeOut: rOut, log: { ...reservedSlot, local: true }, txn: null };
          }
          return null;
        })()
      : null;

    const combined = [...activeBookings];
    if (localReserved) {
      const isSameAsCurrentSelection =
        newPlayerLog?.ui_locked &&
        String(newPlayerLog?.bay_number) === String(bayNumber) &&
        String(reservedSlot?.bay_number) === String(newPlayerLog?.bay_number) &&
        (String(reservedSlot?.time_in || "") === String(newPlayerLog?.time_in || "") || String(reservedSlot?.or_number || "") === String(newPlayerLog?.or_number || ""));
      if (!isSameAsCurrentSelection) combined.push(localReserved);
    }

    // ✅ NEW: Include other saved service selections
    try {
      const others = Object.entries(serviceSelections || {})
        .filter(([key]) => key !== mainServiceKey)
        .map(([key, sel]) => ({ key, ...sel }))
        .filter(s => String(s.bay_number || "") === String(bayNumber) && s.time_in);
      for (const s of others) {
        const sIn = parseGuestStartToManila(s.time_in) || parseManilaDate(s.time_in);
        let sOut = parseManilaDate(s.time_out) || null;
        if (!sOut && sIn) {
          const tok = serviceTokens.find(t => t.key === s.key);
          const dur = Number(tok?.duration || safeDuration || 60);
          sOut = new Date(sIn.getTime() + dur * 60000);
        }
        if (sIn instanceof Date && sOut instanceof Date && !isNaN(sIn.getTime()) && !isNaN(sOut.getTime())) {
          combined.push({ timeIn: sIn, timeOut: sOut, log: { ...s, localOther: true }, txn: null });
        }
      }
    } catch (e) {
      // ignore
    }

    return combined;
  }

  React.useEffect(() => {
    if (!manualStartPH || !manualEndPH) {
      if (availableBays.length !== 0) setAvailableBays([]);
      return;
    }
    const availBays = [];
    const TOLERANCE_MS = 60 * 1000;
    const selectedStart = manualStartPH.getTime();
    const selectedEnd = manualEndPH.getTime();

    // ✅ Collect bays that already have a locked reservation in serviceSelections
    // so they are never accidentally removed from availableBays
    const alreadyReservedBays = new Set();
    try {
      Object.values(serviceSelections || {}).forEach(sel => {
        if (sel?.ui_locked && sel?.bay_number) {
          alreadyReservedBays.add(String(sel.bay_number));
        }
      });
    } catch (e) { /* ignore */ }

    for (let i = 1; i <= 15; i++) {
      const bayNumber = i.toString();
      const bookings = getBayBookingsForToday(bayNumber);
      // Count intersecting bookings to allow multiple players at the same time
      const intersectingBookings = bookings.filter(({ timeIn, timeOut }) => {
        const bStart = timeIn.getTime();
        const bEnd = timeOut.getTime();
        if (bEnd + TOLERANCE_MS <= selectedStart) return false;
        if (bStart >= selectedEnd + TOLERANCE_MS) return false;
        const intersects = selectedStart < bEnd && selectedEnd > bStart;
        return intersects;
      }).length;
      const hasConflict = intersectingBookings > 0;

      // ✅ FIXED: When group booking is enabled, include conflicted bays as available
      // Also always keep bays that are already reserved by any service token
      if (!hasConflict || allowGroupBooking || alreadyReservedBays.has(bayNumber)) {
        availBays.push(bayNumber);
      }
    }
    if (availBays.length !== availableBays.length || availBays.some((b, idx) => b !== availableBays[idx])) {
      setAvailableBays(availBays);
    }
    // ✅ FIXED: Don't clear bay if it's already reserved in serviceSelections
    if (newPlayerLog.bay_number && !availBays.includes(newPlayerLog.bay_number) && !alreadyReservedBays.has(String(newPlayerLog.bay_number))) {
      setNewPlayerLog(prev => ({
        ...prev,
        bay_number: "",
        time_in: "",
        time_out: "",
        ui_label_start: "",
        ui_label_end: "",
        ui_locked: false,
      }));
    }
  }, [manualStartPH, manualEndPH, activePlayerLogs, transactions, products, newPlayerLog.bay_number, allowGroupBooking, serviceSelections]);

  const validateReservationRequirements = () => {
    if (!manualStartPH || !manualEndPH) {
      toast({
        title: "Reservation failed",
        description: "Please select a valid time slot.",
        variant: "destructive",
      });
      return false;
    }

    // NOTE: OR requirement removed here — only enforce member selection before reserving
    if (!selectedMember) {
      toast({
        title: "Reservation failed",
        description: "Please select a member first.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };
  // ✅ NEW: Sync all other services to parent guest_bookings
  React.useEffect(() => {
    const others = [];
    try {
      for (const [key, sel] of Object.entries(serviceSelections || {})) {
        if (key === mainServiceKey) continue;
        if (!sel) continue;
        if (!sel.time_in || !sel.bay_number) continue;
        const tok = serviceTokens.find(t => t.key === key);
        const tokDuration = Number(tok?.duration || 0) || Number(durationMinutes || 60);
        // Ensure time_out reflects this token's individual duration
        let effectiveTimeOut = sel.time_out;
        if (sel.time_in && tokDuration > 0) {
          const startDt = new Date(sel.time_in);
          if (!isNaN(startDt.getTime())) {
            effectiveTimeOut = new Date(startDt.getTime() + tokDuration * 60000).toISOString();
          }
        }
        others.push({
          // use stored player_name for this token so guest list shows the typed name
          guest_name: sel.player_name || "",
          bay_number: sel.bay_number,
          time_in: sel.time_in,
          time_out: effectiveTimeOut,
          duration_minutes: tokDuration,
          product_id: tok?.product_id,
          product_name: tok?.name,
          service_key: key
        });
      }
    } catch {}
    const nextJson = JSON.stringify(others);
    setNewPlayerLog(prev => {
      const prevJson = JSON.stringify(Array.isArray(prev?.guest_bookings) ? prev.guest_bookings : []);
      if (prevJson === nextJson) return prev;
      return { ...(prev || {}), guest_bookings: others };
    });
  }, [serviceSelections, mainServiceKey, serviceTokens, durationMinutes, setNewPlayerLog]);


  const totalDrivingRangeSlots = React.useMemo(() => {
    return (drivingRangeItems || []).reduce((sum, item) => sum + (item?.quantity || 1), 0);
  }, [drivingRangeItems]);



  // NEW: track whether operator has manually typed a player name (prevents unwanted overwrites)
  const userTypedNameRef = React.useRef(false);

  // NEW: cancel single reservation (clear serviceSelections and remove guest entry)
   const cancelReservation = (key) => {
     const curSel = (serviceSelections || {})[key] || {};
     const DEFAULT_SEL = {
       bay_number: "",
       time_in: "",
       time_out: "",
       ui_label_start: "",
       ui_label_end: "",
       ui_locked: false,
       player_name: ""
     };
 
     // clear the per-token saved selection
     setServiceSelections(prev => ({ ...(prev || {}), [key]: DEFAULT_SEL }));
 
     // remove any guest_bookings for that service_key and clear main selection if it matches
     setNewPlayerLog(prev => {
       const p = prev || {};
       const guests = Array.isArray(p.guest_bookings) ? p.guest_bookings.filter(g => String(g.service_key) !== String(key)) : [];
       const next = { ...p, guest_bookings: guests };
 
       // Only clear the main selected_service if it was selected for THIS exact token key.
       // Do NOT clear when product_id matches (that would affect other tokens using same product).
       if (p?.selected_service?.key === key) {
         next.selected_service = null;
         next.player_name = "";
         next.bay_number = "";
         next.time_in = "";
         next.time_out = "";
         next.ui_label_start = "";
         next.ui_label_end = "";
         next.ui_locked = false;
       }
 
       return next;
     });
 
     // clear local reserved banner if it belongs to this service
     setReservedSlot(prev => {
       if (!prev) return prev;
       if (String(prev.service_key) === String(key) || String(prev.bay_number) === String(curSel.bay_number)) return null;
       return prev;
     });
 
    // Remove the relevant service item from cart (decrement or remove)
    // Skip for group-extra tokens — they don't correspond to actual cart items
    try {
      const tok = serviceTokens.find(t => t.key === key) || null;
      if (tok && tok.isGroupExtra) {
        // Group extra slot — don't touch the cart
      } else {
        const targetProductId = tok?.product_id || curSel.product_id || null;
        if (targetProductId && typeof setCart === "function") {
          setCart(prev => {
            if (!Array.isArray(prev)) return prev || [];
            const idx = prev.findIndex(it => String(it.product_id) === String(targetProductId));
            if (idx === -1) return prev;
            const item = prev[idx];
            if ((item.quantity || 1) > 1) {
              const next = [...prev];
              next[idx] = { ...item, quantity: Number(item.quantity) - 1 };
              return next;
            } else {
              const next = [...prev];
              next.splice(idx, 1);
              return next;
            }
          });
        }
      }
    } catch (e) {
      console.warn("Failed to remove service item from cart:", e);
    }
 
     // optional feedback
     toast && toast({ title: "Reservation cancelled", description: `${curSel.player_name || "Player"} reservation removed.`, variant: "default" });
   };

             // Validation helpers for player name input
          const isPlayerNameRequired = React.useCallback(() => {
            // Require a player name when there are service tokens and a main service is selected.
            return Array.isArray(serviceTokens) && serviceTokens.length > 0 && Boolean(selectedMainService);
          }, [serviceTokens?.length, selectedMainService]);

          const isPlayerNameValid = (name) => {
            const s = String(name || "").trim();
            // Minimal validation: at least 2 characters (adjust as needed)
            return s.length >= 2;
          };

          const playerNameInvalid = isPlayerNameRequired() && !isPlayerNameValid(newPlayerLog?.player_name);


          
  return (
    <div className="space-y-4">
      {/* Driving Range: header + selector */}
      {serviceTokens.length > 0 && (
        <div className="mb-3 w-full max-w-2xl">
          {/* ═══ PROMINENT HEADER BANNER ═══ */}
          <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-white tracking-wide">Pick Driving Range</span>
              {effectiveGroupSize > 1 && (
                <Badge className="bg-white/20 text-white border border-white/30 text-[11px] px-2.5 py-0.5 font-semibold">
                  Group ({effectiveGroupSize})
                </Badge>
              )}
            </div>
            <Badge className={`text-[11px] px-2.5 py-1 font-bold pointer-events-none ${
              filledSlots >= serviceTokens.length
                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                : 'bg-white/90 text-green-800 border border-white/50'
            }`}>
              {filledSlots}/{serviceTokens.length} filled
            </Badge>
          </div>

          <Select
            value={mainServiceKey || ""}
            onValueChange={(val) => {
              if (val === mainServiceKey) return;

              const chosenKey = val || (serviceTokens && serviceTokens[0] && serviceTokens[0].key) || "";
              if (!chosenKey) return;

              setMainServiceKey(chosenKey);

              // Always clear the typed player name when switching service
              // so the previous token's name is not carried over.
              userTypedNameRef.current = false;
              setNewPlayerLog((prev) => ({ ...(prev || {}), player_name: "" }));

              const sel = serviceSelections[chosenKey] || {};
              // Derive HH:mm from saved time: support both HH:mm and ISO
              let hhmm = "";
              if (sel.time_in) {
                const s = String(sel.time_in);
                if (/^\d{1,2}:\d{2}/.test(s)) {
                  hhmm = s;
                } else if (s.includes("T")) {
                  try {
                    const d = new Date(s);
                    if (!isNaN(d)) {
                      const ph = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Manila" }));
                      const h = String(ph.getHours()).padStart(2, "0");
                      const m = String(ph.getMinutes()).padStart(2, "0");
                      hhmm = `${h}:${m}`;
                    }
                  } catch {}
                }
              }

              // Only restore manualTime if the token has a saved time
              if (hhmm && (manualTime || "") !== hhmm) setManualTime(hhmm);

              // Do not auto-restore player_name on service change; keep input blank

              // Only restore bay/time info if the token has a reservation
              if (sel.bay_number && (sel.time_in || sel.ui_label_start)) {
                const tok = serviceTokens.find(t => t.key === chosenKey) || null;
                if (tok) {
                  setNewPlayerLog(prev => {
                    const base = { ...(prev || {}) };
                    const nextSelected = {
                      key: chosenKey,
                      product_id: tok.product_id,
                      name: tok.name,
                      duration: tok.duration,
                    };
                    const fill = { selected_service: nextSelected };
                    if (sel?.bay_number) fill.bay_number = sel.bay_number;
                    if (sel?.time_in) fill.time_in = sel.time_in;
                    if (sel?.time_out) fill.time_out = sel.time_out;
                    if (sel?.ui_label_start) fill.ui_label_start = sel.ui_label_start;
                    if (sel?.ui_label_end) fill.ui_label_end = sel.ui_label_end;
                    if (typeof sel?.ui_locked !== "undefined") fill.ui_locked = !!sel.ui_locked;
                    return { ...base, ...fill };
                  });
                }
              }
              // If no reservation, do NOT clear the UI
            }}
          >
            <SelectTrigger className="w-full bg-white border border-gray-300 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
              <SelectValue placeholder="Select a driving range slot" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-200 shadow-lg">
              {(serviceTokens || []).map((tok, idx) => {
                const sel = serviceSelections[tok.key] || {};
                const taken = Boolean(sel.ui_locked && sel.bay_number && sel.time_in);
                const takenByMe = mainServiceKey === tok.key && sel.ui_locked;
                // Allow selecting this token if the currently picked bay (newPlayerLog.bay_number)
                // matches the bay stored on this token (so operator can still pick/change service after bay selection)
                const reservedByCurrentSelection = Boolean(
                  newPlayerLog?.bay_number &&
                  sel.bay_number &&
                  String(newPlayerLog.bay_number) === String(sel.bay_number)
                );
                const displayIndex = idx + 1;
                return (
                  <SelectItem
                    key={tok.key}
                    value={tok.key}
                    // disabled={taken && !takenByMe && !reservedByCurrentSelection}
                    // disabled={taken}
                    className="hover:bg-blue-50 focus:bg-blue-50"
                  >
                    <div className="flex items-center justify-between w-full gap-3">
                      <span className="font-mono text-xs">#{displayIndex}</span>
                      <span className="flex-1">{tok.name}</span>
                      {tok.isGroupExtra && (
                        <Badge className="bg-indigo-100 text-indigo-600 text-[9px] px-1.5 py-0 pointer-events-none">Extra</Badge>
                      )}
                      {taken && sel.player_name && (
                        <Badge className="bg-pink-100 text-pink-700 text-xs pointer-events-none">
                          {sel.player_name}
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* ═══ DR GROUP BOOKING SECTION ═══ */}
          {!isReschedule && !isResume && (
          <div className={`mt-2.5 rounded-lg border ${effectiveGroupSize > 1 ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-200 bg-gray-50/50'} overflow-hidden`}>
            {/* Group Header */}
            <div className={`px-3 py-2 flex items-center justify-between ${effectiveGroupSize > 1 ? 'bg-indigo-100/60 border-b border-indigo-200' : 'bg-gray-100/60 border-b border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Users className={`w-4 h-4 ${effectiveGroupSize > 1 ? 'text-indigo-600' : 'text-gray-500'}`} />
                <span className={`text-sm font-semibold ${effectiveGroupSize > 1 ? 'text-indigo-800' : 'text-gray-700'}`}>
                  {effectiveGroupSize > 1 ? 'Group Booking' : 'Player'}
                </span>
                {effectiveGroupSize > 1 && (
                  <Badge className="bg-indigo-200 text-indigo-800 text-[10px] px-1.5 py-0">
                    {effectiveGroupSize} players
                  </Badge>
                )}
                <span className="text-[11px] text-gray-500">
                  {baseTokenCount} from cart{extraGroupTokens && extraGroupTokens.length > 0 ? ` + ${extraGroupTokens.length} extra` : ""}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (!extraGroupTokens || extraGroupTokens.length <= 0) return;
                    // Find the last extra token's key so we can clear its reservation
                    const lastExtraToken = [...serviceTokens].reverse().find(t => t.isGroupExtra);
                    if (lastExtraToken) {
                      setServiceSelections(prev => {
                        const next = { ...prev };
                        delete next[lastExtraToken.key];
                        return next;
                      });
                    }
                    setExtraGroupTokens(prev => (Array.isArray(prev) ? prev.slice(0, -1) : []));
                  }}
                  disabled={!extraGroupTokens || extraGroupTokens.length <= 0}
                  className="h-7 w-7 p-0 rounded-full border-indigo-300 hover:bg-indigo-100"
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <div className="w-8 text-center">
                  <span className={`text-lg font-bold ${effectiveGroupSize > 1 ? 'text-indigo-800' : 'text-gray-700'}`}>{effectiveGroupSize}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAddExtraSelect(true)}
                  disabled={effectiveGroupSize >= 20}
                  className="h-7 w-7 p-0 rounded-full border-indigo-300 hover:bg-indigo-100"
                >
                  <Plus className="w-3 h-3" />
                </Button>
                {showAddExtraSelect && (
                  <div className="ml-2">
                    <select
                      className="text-sm border rounded p-1 bg-white"
                      onChange={(e) => {
                        const pid = e.target.value;
                        if (!pid) { setShowAddExtraSelect(false); return; }
                        const opt = extraAddOptions.find(o => String(o.product_id) === String(pid));
                        if (opt) {
                          setExtraGroupTokens(prev => ([...(prev || []), opt]));
                        }
                        setShowAddExtraSelect(false);
                      }}
                    >
                      <option value="">Add extra player...</option>
                      {extraAddOptions.map(opt => (
                        <option key={opt.product_id} value={opt.product_id}>
                          {opt.name} ({opt.duration}min)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Group Body */}
            <div className="p-3 space-y-2.5">
              {/* ═══ GROUP NAME (visible when group) ═══ */}
              {effectiveGroupSize > 1 && (
                <div className="p-2.5 rounded-lg border border-purple-200 bg-purple-50/60">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Users className="w-3.5 h-3.5 text-purple-600" />
                    <Label className="text-xs font-semibold text-purple-800">Group Name</Label>
                    <span className="text-[10px] text-purple-500">(auto-fills all player names)</span>
                  </div>
                  <Input
                    value={drGroupName}
                    onChange={(e) => setDrGroupName(e.target.value)}
                    placeholder="e.g. Smith Family, Team Alpha"
                    className="text-sm h-8 bg-white border-purple-300 focus:ring-purple-400 placeholder-gray-400"
                  />
                  {drGroupName.trim() && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {serviceTokens.map((tok, idx) => (
                        <span key={tok.key} className={`text-[10px] px-2 py-0.5 rounded-full ${tok.isGroupExtra ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>
                          {drGroupName.trim()} - Player {idx + 1}{tok.isGroupExtra ? ' ★' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ═══ PLAYER NAME ═══ */}
              <div>
                <Label className="block text-xs font-medium text-gray-700 mb-1">
                  {effectiveGroupSize > 1
                    ? `Player Name (Slot #${serviceTokens.findIndex(t => t.key === mainServiceKey) + 1})`
                    : 'Player Name'}
                </Label>
                <Input
                  value={newPlayerLog?.player_name ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    const trimmed = String(v || "").trim();

                    // ✅ Only set typed flag when user manually enters text
                    userTypedNameRef.current = trimmed !== "";

                    // Update input state
                    setNewPlayerLog(prev => ({ ...(prev || {}), player_name: v }));

                    // Persist typed name into the currently-selected token
                    const fallbackKey = (serviceTokens && serviceTokens[0] && serviceTokens[0].key) || null;
                    const chosenKey = mainServiceKey || fallbackKey;
                    if (!chosenKey) return;

                    setServiceSelections(prev => {
                      const cur = prev[chosenKey] || {};
                      const newPlayerNameForToken = trimmed || "";
                      if ((cur.player_name || "") === newPlayerNameForToken) return prev;
                      return { ...prev, [chosenKey]: { ...cur, player_name: newPlayerNameForToken } };
                    });

                    if (!mainServiceKey && chosenKey) setMainServiceKey(chosenKey);
                  }}
                  placeholder={effectiveGroupSize > 1 ? "Auto-filled from group name or type manually" : "Enter player's name"}
                  className={`text-sm h-8 w-full bg-white border border-gray-300 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${playerNameInvalid ? "border-red-500 focus:outline-none focus:ring-2 focus:ring-red-400" : ""}`}
                />
                {playerNameInvalid && (
                  <div className="mt-1 text-xs text-red-600">Player name is required</div>
                )}
              </div>

              <div className="text-[11px] text-gray-500">
                Duration will be computed from the selected service.
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Current time display */}
      <div className="mb-2 flex items-center gap-2">
        <Clock className="w-4 h-4 text-blue-600" />
        <span className="font-semibold text-xs text-blue-700">
          Current Time: {fmt(currentTime)}
        </span>
      </div>

      <div className="mb-3">
        <Label className="mb-1 text-sm font-semibold flex items-center justify-between">
          <span>Pick Start Time</span>
        </Label>
        <div className="flex items-end gap-2">
          <input
            type="time"
            value={manualTime}
            onChange={(e) => {
              if (serviceTokens.length > 0 && !selectedMainService) {
                toast({
                  title: "Select service first",
                  description: "Please select a service for main player before choosing a start time.",
                  variant: "destructive",
                });
                return;
              }
              const pickedRaw = e.target.value || "";
              const picked = normalizeHHMM(pickedRaw);

              // enforce min using Manila timestamps; allow reserving the current minute
              if (picked) {
                const refNow = manilaNowDate();
                const pickedDt = manilaDateFromHHMM(picked, refNow);
                // Build a ref time truncated to the current minute (seconds = 0)
                const refNowMinute = manilaDateFromHHMM(manilaHHMM(refNow), refNow);
                // If a specific min string exists (nowForTimeInput), use it; otherwise use current minute
                const minDt = nowForTimeInput ? manilaDateFromHHMM(nowForTimeInput, refNow) : refNowMinute;
                // Allow picking the same minute as now (so users can reserve "now")
                if (pickedDt && minDt && pickedDt.getTime() < minDt.getTime()) {
                  // set to current minute (not rounded-up interval)
                  setManualTime(manilaHHMM(refNowMinute));
                } else {
                  // If a bay is already selected, check whether that bay is free at the new time
                  const chosenBay = String(newPlayerLog?.bay_number || "");
                  const durationForCheck = Number(selectedMainService?.duration || durationMinutes || 60);
                  let blocked = false;
                  if (chosenBay && pickedDt) {
                    try {
                      const refEnd = new Date(pickedDt.getTime() + durationForCheck * 60000);
                      const bookings = getBayBookingsForToday(chosenBay) || [];
                      const TOL = 60 * 1000;
                      const conflicting = bookings.filter(({ timeIn, timeOut }) => {
                        const bS = timeIn.getTime();
                        const bE = timeOut.getTime();
                        if (bE + TOL <= pickedDt.getTime()) return false;
                        if (bS >= refEnd.getTime() + TOL) return false;
                        return pickedDt.getTime() < bE && refEnd.getTime() > bS;
                      }).length;
                      if (conflicting > 0 && !allowGroupBooking) {
                        blocked = true;
                        toast({ title: "Bay busy", description: `Bay ${chosenBay} is already taken at that time. Enable group booking to allow shared bay reservations, or pick another time.`, variant: "destructive" });
                      }
                    } catch (err) { /* ignore */ }
                  }
                  if (!blocked) {
                    setManualTime(picked);
                  }
                }
              } else {
                setManualTime(pickedRaw);
              }

              // Only update parent when something actually changes to avoid triggering effects unnecessarily
              setNewPlayerLog((prev) => {
                const prevState = prev || {};
                const next = {
                  bay_number: "",
                  ui_locked: false,
                  time_in: "",
                  time_out: "",
                  ui_label_start: "",
                  ui_label_end: "",
                };

                const noChange =
                  String(prevState.bay_number || "") === String(next.bay_number) &&
                  !!prevState.ui_locked === !!next.ui_locked &&
                  String(prevState.time_in || "") === String(next.time_in) &&
                  String(prevState.time_out || "") === String(next.time_out) &&
                  String(prevState.ui_label_start || "") === String(next.ui_label_start) &&
                  String(prevState.ui_label_end || "") === String(next.ui_label_end);

                return noChange ? prev : { ...prevState, ...next };
              });
            }}
            disabled={serviceTokens.length > 0 && !selectedMainService}
            title={serviceTokens.length > 0 && !selectedMainService ? "Select service first" : undefined}
            className={`border rounded px-2 py-1 text-sm w-40 ${timeError ? "border-red-500 ring-1 ring-red-200" : "focus:ring-2 focus:ring-emerald-400 transition-all"} ${
              serviceTokens.length > 0 && !selectedMainService ? "opacity-60 cursor-not-allowed" : ""
            }`}
            aria-invalid={Boolean(timeError)}
            step={120}
          />
          {/* Group booking override toggle */}
          {/* <div className="ml-3 flex items-center gap-1.5">
            <input
              id="allow-group-booking"
              type="checkbox"
              checked={allowGroupBooking}
              onChange={(e) => {
                setAllowGroupBooking(Boolean(e.target.checked));
              }}
              className="accent-emerald-500"
            />
            <label htmlFor="allow-group-booking" className="text-xs text-gray-600 select-none cursor-pointer">
              Allow group booking {allowGroupBooking && <span className="text-emerald-600 font-semibold">(enabled)</span>}
            </label>
          </div> */}
        </div>
        {serviceTokens.length > 0 && !selectedMainService && (
          <div className="mt-2 text-xs text-red-600">
            Please select a service for the player before picking a start time.
          </div>
        )}
        {timeError && (
          <div className="mt-2 text-xs text-red-600">
            {timeError}
          </div>
        )}
        {manualStartPH && (
          <div className="mt-2 text-sm">
            {availableBays.length > 0 ? (
              <div className="text-emerald-600">
                ✅ {availableBays.length} bays available from {fmt(manualStartPH)} to {fmt(manualEndPH)}
              </div>
            ) : (
              <div className="text-red-600">
                ❌ No bays available at this time. Please select a different time.
              </div>
            )}
          </div>
        )}
      </div>

        <div className="mb-4">
          <Label className="mb-2 block">Select Bay</Label>
          <div className="grid grid-cols-5 gap-x-2 gap-y-3 px-1 mb-4">
            {[...Array(15)].map((_, i) => {
              const bayNum = (i + 1).toString();
              const displayBayNum = `B${bayNum}`;
          
              // Find if any saved service selection already reserved this bay
              const reservedEntry = Object.entries(serviceSelections || {}).find(
                ([_, sel]) => sel?.ui_locked && String(sel.bay_number) === String(bayNum)
              );
              const reservedKey = reservedEntry ? reservedEntry[0] : null;
              const reservedData = reservedEntry ? reservedEntry[1] : null;
              const reservedByCurrentSelection = reservedKey === mainServiceKey;
              
              // Detect if this is the original bay from a paused booking being resumed
              const isResumeOriginalBay = isResume && resumeOriginalBay && String(resumeOriginalBay) === String(bayNum);
              
              // show tag if reserved by other token (NOT for the resume original bay - that should be friendly)
              const reservedTagVisible = Boolean(reservedData && !reservedByCurrentSelection);

              const tokenSel = (serviceSelections && serviceSelections[mainServiceKey]) || {};
              const isSelected = String(newPlayerLog.bay_number) === String(bayNum) || String(tokenSel.bay_number) === String(bayNum);

              // Original bay from paused booking is always available during resume
              const isAvailable = availableBays.includes(bayNum) || reservedByCurrentSelection || isResumeOriginalBay;

              return (
                <Button
                  key={bayNum}
                  size="sm"
                  variant={isSelected ? "default" : "outline"}
                  disabled={!isAvailable}

                 // make relative so we can position the small reserved badge
                 className={`relative w-full py-2 text-xs transition-all duration-200
                  ${isSelected
                    ? "bg-emerald-500 text-white font-bold ring-2 ring-emerald-400"
                    : reservedTagVisible
                      ? "bg-pink-100 text-pink-700 hover:bg-pink-200"
                      : isResumeOriginalBay
                        ? "bg-blue-100 text-blue-700 hover:bg-blue-200 ring-1 ring-blue-300"
                        : isAvailable
                          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                  onClick={() => {
                    if (!isAvailable) return;
                    if (!validateReservationRequirements()) return;

                    const fallbackKey = (serviceTokens && serviceTokens[0] && serviceTokens[0].key) || null;
                    const chosenKey = mainServiceKey || fallbackKey;
                    if (!chosenKey) {
                      toast({ title: "No service selected", description: "Please select a service before reserving a bay.", variant: "destructive" });
                      return;
                    }

                    if (!mainServiceKey) setMainServiceKey(chosenKey);

                    const tokenSavedName = (serviceSelections && serviceSelections[chosenKey] && serviceSelections[chosenKey].player_name) || "";
                    const candidateName = String(newPlayerLog?.player_name || tokenSavedName || "").trim();
                    if (!candidateName) {
                      toast({
                        title: "Player name required",
                        description: "Please enter a player name before reserving this bay.",
                        variant: "destructive",
                      });
                      return;
                    }
                    const playerNameForReserve = candidateName;

                    setServiceSelections(prev => {
                      const cur = prev[chosenKey] || {};
                      const updated = {
                        ...cur,
                        bay_number: bayNum,
                        time_in: manualStartPH ? manualStartPH.toISOString() : "",
                        // also store UI HH:mm to restore cleanly
                        time_in_ui: manualStartPH ? toHHMM(manualStartPH) : "",
                        time_out: manualEndPH ? manualEndPH.toISOString() : "",
                        ui_label_start: manualStartPH ? fmt(manualStartPH) : "",
                        ui_label_end: manualEndPH ? fmt(manualEndPH) : "",
                        ui_locked: true,
                        player_name: playerNameForReserve || cur.player_name || ""
                      };
                      return { ...prev, [chosenKey]: updated };
                    });

                    setAvailableBays(prev => (prev.includes(bayNum) ? prev : [...prev, bayNum]));

                    const selTok = serviceTokens.find(t => t.key === chosenKey) || selectedMainService;
                    setNewPlayerLog(prev => {
                      const fill = {
                        bay_number: String(bayNum),
                        time_in: manualStartPH ? manualStartPH.toISOString() : "",
                        time_out: manualEndPH ? manualEndPH.toISOString() : "",
                        ui_label_start: manualStartPH ? fmt(manualStartPH) : "",
                        ui_label_end: manualEndPH ? fmt(manualEndPH) : "",
                        ui_locked: true,
                        player_name: playerNameForReserve,
                        selected_service: selTok ? {
                          key: chosenKey,
                          product_id: selTok.product_id,
                          name: selTok.name,
                          duration: selTok.duration,
                        } : prev?.selected_service
                      };
                      const same =
                        String(prev?.bay_number || "") === String(fill.bay_number || "") &&
                        String(prev?.time_in || "") === String(fill.time_in || "") &&
                        String(prev?.time_out || "") === String(fill.time_out || "") &&
                        String(prev?.ui_label_start || "") === String(fill.ui_label_start || "") &&
                        String(prev?.ui_label_end || "") === String(fill.ui_label_end || "") &&
                        !!prev?.ui_locked === !!fill.ui_locked &&
                        String((prev?.player_name || "").trim()) === String((fill.player_name || "").trim());
                      return same ? prev : { ...(prev || {}), ...fill };
                    });

                    setReservedSlot({
                      bay_number: bayNum,
                      time_in: manualStartPH ? manualStartPH.toISOString() : "",
                      time_out: manualEndPH ? manualEndPH.toISOString() : "",
                      member_id: selectedMember?.id,
                      player_name: playerNameForReserve,
                      or_number: orNumber,
                      ui_label_start: manualStartPH ? fmt(manualStartPH) : "",
                      ui_label_end: manualEndPH ? fmt(manualEndPH) : "",
                    });

                    toast({
                      title: "Slot Reserved Locally",
                      description: `Bay ${bayNum} reserved from ${fmt(manualStartPH)} to ${fmt(manualEndPH)}. Complete payment to confirm booking.`,
                    });
                  }}
                  title={
                  reservedTagVisible
                     ? `${reservedData?.player_name || "Player"} (reserved by other slot)`
                     : undefined
                  }
                >
                  {/* Show a small badge when this bay is reserved by another service/token */}
                 {reservedTagVisible && (
                    <span className="absolute -top-2 -right-2 text-[10px] px-1 py-0.5 rounded-full bg-pink-200 text-pink-700 font-semibold pointer-events-none">
                      { (reservedData?.player_name && reservedData.player_name.length <= 10) ? reservedData.player_name : (reservedData?.player_name ? reservedData.player_name.slice(0,10) + '…' : 'Player') }
                    </span>
                  )}
                  {isResumeOriginalBay && !isSelected && !reservedTagVisible && (
                    <span className="absolute -top-2 -right-2 text-[10px] px-1 py-0.5 rounded-full bg-blue-200 text-blue-700 font-semibold pointer-events-none">
                      Your Bay
                    </span>
                  )}
                   {displayBayNum}
                 </Button>
               );
             })}
           </div>
         </div>

      {newPlayerLog.bay_number && newPlayerLog.ui_locked && (manualStartPH || newPlayerLog.time_in) && (
        <div className="text-sm text-emerald-600 mt-2 p-2 bg-emerald-50 rounded border border-emerald-200">
          <div className="font-semibold">✅ Booking Reserved</div>
          <div className="text-xs">
            {String(newPlayerLog?.player_name || "").trim() || "-"} • Bay {newPlayerLog.bay_number} • {newPlayerLog.ui_label_start} - {newPlayerLog.ui_label_end}
          </div>
          <div className="text-xs text-gray-600 mt-1">Complete payment to confirm this booking</div>
        </div>
      )}

      {filledSlots > 0 && (
        <div className="mt-4 p-3 bg-emerald-50 rounded border border-emerald-200">
          <div className="text-sm font-semibold text-emerald-800 mb-2">
            Reservation Summary ({filledSlots} player{filledSlots !== 1 ? 's' : ''})
            {drGroupName.trim() && (
              <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                {drGroupName.trim()}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {Object.entries(serviceSelections)
              .filter(([_, data]) => data?.ui_locked && data?.bay_number)
              .map(([key, data], idx) => (
                  <div
                    key={key}
                    onClick={() => viewDrivingBooking(key, data)}
                    className="flex items-start justify-between text-xs bg-white p-2 rounded border border-emerald-100 cursor-pointer hover:bg-emerald-50"
                  >
                  <div>
                    <div className="font-medium text-emerald-900">{data.player_name || "Player"}</div>
                    <div className="text-emerald-700">
                      Bay {data.bay_number} • {data.ui_label_start} - {data.ui_label_end}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                        onClick={(e) => { e.stopPropagation(); cancelReservation(key); }}
                      className="text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                  </div>
              ))}
          </div>
        </div>
      )}

      {(manualStartPH || newPlayerLog.time_in) && bayNumberError && (
        <div className="mt-1 text-xs text-red-600">{bayNumberError}</div>
      )}
    </div>
  );
}

function minutesBetween(start, end) {
  if (!(start instanceof Date) || !(end instanceof Date)) return 0;
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 60000));
}

/**
 * Format minutes as human-readable text (e.g., "1h 30m", "45m")
 */
function formatMinutesAsText(totalMinutes) {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "-";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h > 0 ? h + "h " : ""}${m}m`;
}

/**
 * Parse duration from product data (supports multiple formats)
 */
function parseProductDurationMinutes(product) {
  if (!product) return 60; // default fallback
  
  // 1. Check explicit duration field
  const explicitDuration = Number(product.duration);
  if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
    return Math.round(explicitDuration);
  }
  
  // 2. Parse from product name
  const name = String(product.name || "").toLowerCase();
  
  // Try hour format first
  const hourMatch = name.match(/(\d+(?:\.\d+)?)\s*(hour|hr|hrs|hours)\b/i);
  if (hourMatch) {
    return Math.round(parseFloat(hourMatch[1]) * 60);
  }
  
  // Try minute format
  const minMatch = name.match(/(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes)\b/i);
  if (minMatch) {
    return Math.round(parseFloat(minMatch[1]));
  }
  
  // 3. Common fallback patterns
  if (name.includes("2 hour") || name.includes("2hr") || name.includes("120 min")) return 120;
  if (name.includes("1 hour") || name.includes("1hr") || name.includes("60 min")) return 60;
  if (name.includes("30 min") || name.includes("30min")) return 30;
  if (name.includes("20 min") || name.includes("20min")) return 20;
  
  return 60; // final fallback
}

/**
 * Get player-specific duration from log (checks paused state, transaction items, product data)
 */


/**
 * Compute comprehensive duration information for a player log
 * Returns: { text: string, minutes: number, isInProgress: boolean }
 */


function getPlayerSpecificDuration(log, transactions, products) {
  // 1. Check if paused - use frozen duration
  if (log.paused_planned_duration_minutes) {
    return Number(log.paused_planned_duration_minutes) || 0;
  }

  // ✅ 2. NEW: If log has explicit selected_service with duration, prefer it
  if (log.selected_service && Number.isFinite(Number(log.selected_service.duration))) {
    return Math.max(0, Math.round(Number(log.selected_service.duration)));
  }

  // 3. Golf always uses 240 minutes (4 hours)
  if (log.service_type === "Golf Rates") {
    return 240;
  }

  // 4. For Driving Range - check transaction items (fallback)
  const txn = transactions.find(t => String(t.or_number) === String(log.or_number));
  if (!txn || !Array.isArray(txn.items)) return 60; // fallback

  let totalDuration = 0;
  let foundDRItems = false;

  for (const item of txn.items) {
    const product = products.find(p => String(p.id) === String(item.product_id));

    // Only process Driving Range items
    if (product?.category !== "Driving Range Rates") continue;
    foundDRItems = true;

    // prefer product.duration, then parse name, then default 60
    let perUnit = 60;
    if (product?.duration) {
      perUnit = Number(product.duration);
    } else if (product?.name) {
      const nm = (product.name || "").toLowerCase();
      const m = nm.match(/(\d+(?:\.\d+)?)\s*(hour|hr|hrs|hours)\b/);
      if (m) perUnit = Math.round(parseFloat(m[1]) * 60);
      else {
        const mm = nm.match(/(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes)\b/);
        if (mm) perUnit = Math.round(parseFloat(mm[1]));
      }
    }

    const qty = Math.max(1, Number(item.quantity) || 1);
    totalDuration += perUnit * qty;
  }

  return foundDRItems ? (totalDuration || 60) : 60;
}



function computeDurationForLog({
  log,
  timeIn,
  timeOut,
  golfTimeIn,
  golfTimeOut,
  transactions = [],
  products = []
}) {
  const serviceType = log.service_type;
  
  // ✅ NEW: Priority 0 - Check if log has explicit selected_service with duration
  if (log.selected_service && Number.isFinite(Number(log.selected_service.duration))) {
    const minutes = Math.max(0, Math.round(Number(log.selected_service.duration)));
    return {
      text: formatMinutesAsText(minutes),
      minutes: minutes,
      isInProgress: false,
      source: 'selected_service' // Debug aid
    };
  }
  
  // Case 1: Golf with actual times
  if (serviceType === "Golf Rates" && golfTimeIn && golfTimeOut && golfTimeOut > golfTimeIn) {
    const minutes = minutesBetween(golfTimeIn, golfTimeOut);
    return {
      text: formatMinutesAsText(minutes),
      minutes: minutes,
      isInProgress: false
    };
  }
  
  // Case 2: Driving Range with actual times
  if (timeIn && timeOut && timeOut > timeIn) {
    const minutes = minutesBetween(timeIn, timeOut);
    return {
      text: formatMinutesAsText(minutes),
      minutes: minutes,
      isInProgress: false
    };
  }
  
  // Case 3: In progress (has start, no end)
  if ((serviceType === "Golf Rates" && golfTimeIn && !golfTimeOut) || 
      (timeIn && !timeOut)) {
    return {
      text: "In Progress",
      minutes: 0,
      isInProgress: true
    };
  }
  
  // Case 4: Planned duration (not started or transaction-based)
  const txn = transactions.find(t => String(t.or_number) === String(log.or_number));
  const hasUnfinishedTxn = txn && (
    (txn.startgolf_time && !txn.endgolf_time) || 
    (txn.start_time && !txn.end_time)
  );
  
  if (hasUnfinishedTxn) {
    const plannedMinutes = getPlayerSpecificDuration(log, transactions, products);
    if (plannedMinutes > 0) {
      return {
        text: formatMinutesAsText(plannedMinutes),
        minutes: plannedMinutes,
        isInProgress: true
      };
    }
  }
  
  return {
    text: "-",
    minutes: 0,
    isInProgress: false
  };
}


// (Optional) Adjust table rendering helper for golf logs to prefer precise holes)
function computeHolesLabel(log) {
  // ✅ prefer explicit golfhole or holes
  const h = Number(log?.golfhole || log?.holes || log?.total_holes);
  if (Number.isFinite(h) && (h === 9 || h === 18)) return h === 9 ? "9H" : "18H";
  const ct = String(log?.course_type || "").toLowerCase();
  if (ct.includes("9")) return "9H";
  if (ct.includes("18")) return "18H";
  return "-";
}

function App() {
  React.useEffect(() => {
    document.title = "Pili Grove Club POS";
  }, []);

  //user states — restore from localStorage on first render so refresh stays logged in
  const [user, setUser] = useState(() => readStoredSession().user);
  const [token, setToken] = useState(() => readStoredSession().token);
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const { toast } = useToast();
  // Driving Range reservation completeness stats (from TimeSlotPicker)
  const [drReservationStats, setDrReservationStats] = useState({
    filledSlots: 0,
    totalSlots: 0,
    incompleteKeys: [],
    reservedEntries: [],
  });
  const allDrivingRangeSlotsFilled = React.useMemo(() => {
    if (drReservationStats.totalSlots === 0) return true; // nothing to fill yet
    return drReservationStats.filledSlots === drReservationStats.totalSlots;
  }, [drReservationStats]);
  // Golf reservation completeness stats (from GolfTeeTimeBooking)
  const [golfReservationStats, setGolfReservationStats] = useState({
    filledSlots: 0,
    totalSlots: 0,
    incompleteKeys: []
  });
  const allGolfSlotsFilled = React.useMemo(() => {
    if (golfReservationStats.totalSlots === 0) return true;
    return golfReservationStats.filledSlots === golfReservationStats.totalSlots;
  }, [golfReservationStats]);
// Time-in Logic for the POS tab transaction summary
// const [combinedActiveData, setCombinedActiveData] = useState([]);
  const [currentTime, setCurrentTime] = React.useState(new Date());
//  const [reservedSlot, setReservedSlot] = React.useState(null); // Add this state
  const fmt = (d) =>
    d.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true });

  // tick every 10s to keep UI live
useEffect(() => {
  const timer = setInterval(() => {
    setCurrentTime(new Date());
  }, 1000); // Update every second

  return () => clearInterval(timer); // Cleanup on component unmount
}, []);

const SERVICE_CATEGORIES = ["Golf Rates", "Driving Range Rates"];
const serviceCartItems = cart.filter(item =>
  SERVICE_CATEGORIES.includes(products.find(p => p.id === item.product_id)?.category)
);
const drivingRangeItems = serviceCartItems.filter(item =>
  products.find(p => p.id === item.product_id)?.category === "Driving Range Rates"
);
const golfRatesItems = serviceCartItems.filter(item =>
  products.find(p => p.id === item.product_id)?.category === "Golf Rates"
);
const nonServiceCartItems = cart.filter(item =>
  !SERVICE_CATEGORIES.includes(products.find(p => p.id === item.product_id)?.category)
);

 const totalCartItems = React.useMemo(() => {
   return (cart || []).reduce((sum, it) => sum + (Number(it.quantity) || 1), 0);
 }, [cart]);
// Time-in Logic for the POS tab transaction summary



  // Pick first driving range product (fallback only)
  const selectedDrivingRangeProduct = React.useMemo(() => {
    if (!drivingRangeItems.length) return null;
    return products.find(p => p.id === drivingRangeItems[0].product_id) || null;
  }, [drivingRangeItems, products]);

  // Combined duration from driving range items (quantity-aware)
  const accumulatedDurationMinutes = React.useMemo(() => {
    if (!drivingRangeItems?.length) {
      return (selectedDrivingRangeProduct?.duration || 60); // fallback
    }
    return drivingRangeItems.reduce((sum, item) => {
      const product = products.find((p) => p.id === item.product_id);
      const perUnit =
        (product?.duration && Number(product.duration)) ||
        DRIVING_RANGE_DURATIONS[product?.name] ||
        60;
      return sum + perUnit * (item.quantity ?? 1);
    }, 0);
  }, [drivingRangeItems, products, selectedDrivingRangeProduct]);

  // React.useEffect(() => {
  //   if (!newPlayerLog.time_in) return;
  //   const start = new Date(newPlayerLog.time_in);
  //   const end = new Date(start.getTime() + durationMinutes * 60000);
  //   const current = newPlayerLog.time_out ? new Date(newPlayerLog.time_out).getTime() : null;
  //   if (current !== end.getTime()) {
  //     setNewPlayerLog(prev => ({ ...prev, time_out: end.toISOString() }));
  //   }
  // }, [newPlayerLog.time_in, durationMinutes]);


  // Calculate the next slot start time (rounded up to next interval)

  
  function getNextSlotStart(now, duration) {
    const minutes = now.getMinutes();
    const remainder = minutes % duration;
    const nextMinutes = remainder === 0 ? minutes : minutes + (duration - remainder);
    const nextSlot = new Date(now);
    nextSlot.setMinutes(nextMinutes, 0, 0);
    if (nextSlot < now) nextSlot.setMinutes(nextSlot.getMinutes() + duration);
    return nextSlot;
  }

//   // Example mapping (customize as needed)
// const DRIVING_RANGE_DURATIONS = {
//   "1 Hour - Driving Range": 60,
//   "30 Min - Driving Range": 30,
//   "2 Hour - Driving Range": 120,
//   // Add more as needed
// };

// Manila-timezone-aware same-day comparison
const sameLocalDay = (a, b) => {
  const fmt = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: MANILA_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  try { return fmt(a) === fmt(b); } catch { return false; }
};

const getActiveBookingsForDay = (date) => {
  const target = new Date(date);
  return activePlayerLogs.filter(log =>
    log.time_in && sameLocalDay(new Date(log.time_in), target)
  );
};


function isTimeSlotAvailable(bayNumber, timeIn, durationMinutes = 60, excludeLogId = null) {
  const todayBookings = getActiveBookingsForDay(new Date(timeIn));
  const slotStart = new Date(timeIn);
  const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60000);

  return !todayBookings.some(log => {
    // Skip the booking being rescheduled so it doesn't conflict with itself
    if (excludeLogId && String(log.id) === String(excludeLogId)) return false;
    return (
      log.bay_number === bayNumber &&
      log.time_in &&
      log.time_out &&
      (
        (slotStart >= new Date(log.time_in) && slotStart < new Date(log.time_out)) ||
        (slotEnd > new Date(log.time_in) && slotEnd <= new Date(log.time_out)) ||
        // Overlap check: slot covers any part of booking
        (slotStart < new Date(log.time_out) && slotEnd > new Date(log.time_in))
      )
    );
  });
}

// const selectedDrivingRangeProduct = drivingRangeItems[0]
//   ? products.find(p => p.id === drivingRangeItems[0].product_id)
//   : null;

// // Calculate total duration from all driving range items in the cart
// const totalDrivingRangeDuration = drivingRangeItems.reduce((sum, item) => {
//   const product = products.find(p => p.id === item.product_id);
//   // Use product.duration if available, fallback to DRIVING_RANGE_DURATIONS
//   const duration = product?.duration
//     ? parseInt(product.duration, 10)
//     : DRIVING_RANGE_DURATIONS[product?.name] || 60;
//   return sum + duration * item.quantity;
// }, 0);

// Use this for slot generation
// const durationMinutes = totalDrivingRangeDuration || 60;

// const [golfReservations, setGolfReservations] = useState([]);
const [newGolfReservation, setNewGolfReservation] = useState({
  or_number: "",
  player_name: "",
  member_id: "",
  tee_time: "",
  end_time: "",
  course_type: "18-holes",
  players_count: 1,
  card_gcash: 0,
  cash: 0,
  golf_guest_bookings: [] // ✅ Ensure this field exists
});

// Group booking: number of players sharing the same booking time/bay
const [groupPlayersCount, setGroupPlayersCount] = useState(1);
// Golf-specific group booking count (managed by GolfTeeTimeBooking inline controls)
const [golfGroupBookingCount, setGolfGroupBookingCount] = useState(1);




  //player log states
  const [showGolfRatesOnly, setShowGolfRatesOnly] = useState(false);
  const [activePlayerLogs, setActivePlayerLogs] = useState([]);
  const [newPlayerLog, setNewPlayerLog] = useState({
  or_number: "",
  player_name: "",
  member_id: "",
  bay_number: "",
  time_in: "",             // backend ISO
  time_out: "",            // backend ISO
  ui_label_start: "",      // UI-only label (what user clicked)
  ui_label_end: "",        // UI-only label (computed from start + duration)
  ui_locked: false, // add: prevent external overrides after click
  card_gcash: 0,
  cash: 0,
  is_member: false,
});





// TODO: GUEST BOOKING STATE!!
const [guestBookings, setGuestBookings] = React.useState([]);

// Sync guard to avoid infinite update loops between `newPlayerLog` and `guestBookings`
const guestSyncRef = React.useRef({ source: null, last: null });

// Initialize guestBookings from any incoming newPlayerLog payload (remote -> local)
// Only apply when incoming differs from current local guestBookings and avoid echoing back.
React.useEffect(() => {
  const incoming = Array.isArray(newPlayerLog?.guest_bookings) ? newPlayerLog.guest_bookings : null;
  if (!incoming) return;

  const incomingStr = JSON.stringify(incoming);
  const localStr = JSON.stringify(guestBookings);

  // If this change just originated from local sync, ignore to prevent echo
  if (guestSyncRef.current.source === 'local' && guestSyncRef.current.last === incomingStr) {
    guestSyncRef.current.source = null;
    return;
  }

  if (incomingStr !== localStr) {
    guestSyncRef.current.source = 'remote';
    guestSyncRef.current.last = incomingStr;
    setGuestBookings(incoming);
  }
}, [newPlayerLog]); // intentionally do NOT include guestBookings here

// Keep newPlayerLog updated when guestBookings changes (local -> remote)
// Guarded to avoid looping with the remote -> local sync above.
React.useEffect(() => {
  // normalize to a stable JSON (defensive - handle non-array)
  const normalized = Array.isArray(guestBookings) ? guestBookings : [];
  let nextJson;
  try {
    nextJson = JSON.stringify(normalized);
  } catch (err) {
    // fallback: avoid crashing on circular refs
    console.warn("guestBookings stringify failed, skipping sync", err);
    return;
  }

  // If this update just came from remote and payloads match, clear flag and skip write-back
  if (guestSyncRef.current.source === "remote" && guestSyncRef.current.last === nextJson) {
    guestSyncRef.current.source = null;
    return;
  }

  // If nothing actually changed vs parent's copy, skip update
  const parentJson = JSON.stringify(Array.isArray(newPlayerLog?.guest_bookings) ? newPlayerLog.guest_bookings : []);
  if (parentJson === nextJson) {
    // keep sync refs consistent
    guestSyncRef.current.source = null;
    guestSyncRef.current.last = nextJson;
    return;
  }

  // mark this change as local so incoming effect can ignore the echo
  guestSyncRef.current.source = "local";
  guestSyncRef.current.last = nextJson;

  if (typeof setNewPlayerLog === "function") {
    setNewPlayerLog(prev => {
      const prevGuests = Array.isArray(prev?.guest_bookings) ? prev.guest_bookings : [];
      // avoid unnecessary setState if identical
      try {
        if (JSON.stringify(prevGuests) === nextJson) return prev;
      } catch {
        // ignore stringify errors and continue to set
      }
      return { ...(prev || {}), guest_bookings: normalized };
    });
  }
}, [guestBookings, newPlayerLog?.guest_bookings, setNewPlayerLog])



  //product and cart states
  // const [cart, setCart] = useState([]);
  // const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [period, setPeriod] = useState("Today");
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [isLoadingPlayerLogs, setIsLoadingPlayerLogs] = useState(true);

React.useEffect(() => {
  if (!newPlayerLog.time_in) return;
  // Debug: confirm the banner should change after clicking a slot
  console.log("🟢 time_in updated:", newPlayerLog.time_in, "PH:", 
    new Date(newPlayerLog.time_in).toLocaleString("en-PH", { timeZone: "Asia/Manila" }));
}, [newPlayerLog.time_in]);

// Define all possible tabs
// TODO: Marked For tabs
const allTabs = [
  { value: "pos", label: "POS", icon: <ShoppingCart className="w-4 h-4 mr-2" /> },
  { value: "members", label: "Members", icon: <Users className="w-4 h-4 mr-2" />,},
  { value: "membership-types", label: "Types", icon: <Crown className="w-4 h-4 mr-2" />, },
  { value: "users", label: "Users", icon: <Shield className="w-4 h-4 mr-2" />, admin: true },
  { value: "inventory", label: "Inventory", icon: <Package className="w-4 h-4 mr-2" /> },
  { value: "reports", label: "Reports", icon: <FileSpreadsheet className="w-4 h-4 mr-2" /> },
  { value: "activity-logs", label: "Logs", icon: <Activity className="w-4 h-4 mr-2" />, admin: true },
];




// time-in logic for the pos tab


const getAvailableTimeSlots = (bayNumber, durationMinutes = 60) => {
  const todayBookings = getActiveBookingsForDay(new Date());
  const slots = [];
  const startHour = 6; // e.g., 6AM
  const endHour = 18; // e.g., 6PM
  for (let hour = startHour; hour < endHour; hour++) {
    for (let min = 0; min < 60; min += durationMinutes) {
      const slotStart = new Date();
      slotStart.setHours(hour, min, 0, 0);
      const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60000);

      // Check for overlap
      const overlap = todayBookings.some(log =>
        log.bay_number === bayNumber &&
        log.time_in &&
        log.time_out &&
        (
          (slotStart >= new Date(log.time_in) && slotStart < new Date(log.time_out)) ||
          (slotEnd > new Date(log.time_in) && slotEnd <= new Date(log.time_out))
        )
      );
      if (!overlap) {
        slots.push(slotStart);
      }
    }
  }
  return slots;
};


const [endGolfConfirmLog, setEndGolfConfirmLog] = useState(null);
const [endDrConfirmLog, setEndDrConfirmLog] = useState(null);
const [deletePlayerLogConfirm, setDeletePlayerLogConfirm] = useState(null);
const [deleteAlsoTransaction, setDeleteAlsoTransaction] = useState(true);
const [deletePreview, setDeletePreview] = useState(null);
const [isLoadingDeletePreview, setIsLoadingDeletePreview] = useState(false);
const [isDeletingPlayerLog, setIsDeletingPlayerLog] = useState(false);
const [reportSyncTick, setReportSyncTick] = useState(0);
const canDeletePlayerLog = user?.role === "admin" || user?.role === "puncher" || user?.role === "cashier";
const isAdminUser = user?.role === "admin";

const closeDeletePlayerLogDialog = () => {
  setDeletePlayerLogConfirm(null);
  setDeletePreview(null);
  setDeleteAlsoTransaction(true);
  setIsLoadingDeletePreview(false);
};

const patchPlayerLogsAfterDelete = (log, remainingGroupCount, extraRemovedIds = []) => {
  const removed = new Set([log.id, ...(extraRemovedIds || [])].filter(Boolean));
  const apply = (prev) => {
    const next = prev.filter((item) => !removed.has(item.id));
    if (!remainingGroupCount || remainingGroupCount < 1) return next;
    return next.map((item) => {
      const sameTxn = log.transaction_id && item.transaction_id && String(item.transaction_id) === String(log.transaction_id);
      const sameOr = log.or_number && item.or_number && String(item.or_number) === String(log.or_number);
      const sameService = String(item.service_type || "") === String(log.service_type || "");
      if ((sameTxn || sameOr) && sameService && !item.is_guest) {
        const patched = { ...item, players_count: remainingGroupCount };
        if (item.service_type === "Golf Rates") {
          patched.golf_group_booking_count = remainingGroupCount;
        }
        return patched;
      }
      return item;
    });
  };
  setActivePlayerLogs(apply);
  setPausedPlayerLogs(apply);
  setInactivePlayerLogs(apply);
};

const openDeletePlayerLog = (log) => {
  setDeletePlayerLogConfirm(log);
  setDeleteAlsoTransaction(true);
  fetchDeletePreview(log.id);
};

const formatDeleteMoney = (value) =>
  `₱${Number(value || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const formatDeleteWhen = (value) => {
  if (!value) return "—";
  let str = String(value);
  if (!str.endsWith("Z") && !str.includes("+") && !/[+-]\d{2}:\d{2}$/.test(str)) {
    str += "Z";
  }
  const date = new Date(str);
  if (isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-PH", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Manila",
  });
};

const handleDeletePlayerLog = async (log, alsoDeleteTransaction = false) => {
  if (!log?.id || isDeletingPlayerLog) return;
  setIsDeletingPlayerLog(true);

  const prevActive = activePlayerLogs;
  const prevPaused = pausedPlayerLogs;
  const prevInactive = inactivePlayerLogs;
  const prevTxns = transactions;
  const prevHistoryTxns = cashierTransactions;
  const forceDeleteTransaction = user?.role === "puncher" || user?.role === "cashier";

  // Optimistic: drop the row immediately so the table doesn't wait on a full refetch
  patchPlayerLogsAfterDelete(log, null);
  closeDeletePlayerLogDialog();

  try {
    const url = new URL(`${BACKEND_URL}/api/player-logs/${log.id}`);
    if (alsoDeleteTransaction || forceDeleteTransaction) {
      url.searchParams.set("delete_transaction", "true");
    }
    const res = await fetch(url.toString(), {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const result = await res.json();
      const txnDeleted = result.transaction_deleted;
      const summary = result.summary;
      const remainingGroupCount = Number(result.remaining_group_count) || 0;
      const deletedSiblingIds = result.deleted_sibling_ids || [];

      patchPlayerLogsAfterDelete(log, remainingGroupCount, deletedSiblingIds);

      let description = `Player log for ${log.player_name || log.member_name || "Player"} (Bay ${log.bay_number || "-"}) removed.`;
      const logsDeleted = Number(summary?.logs_count) || (1 + deletedSiblingIds.length);
      if (logsDeleted > 1) {
        description = `${logsDeleted} player logs for this booking were removed.`;
      }
      if (txnDeleted && summary?.transaction) {
        const txn = summary.transaction;
        description += ` Created transaction OR ${txn.or_number || "N/A"} (${formatDeleteMoney(txn.total)}) was also deleted.`;
      }

      toast({
        title: txnDeleted ? "Log & Transaction Deleted" : "Log Deleted",
        description,
        duration: 4000,
      });

      if (txnDeleted) {
        const deletedTxnId = summary?.transaction?.id;
        const deletedOr = summary?.transaction?.or_number;
        const stillPresent = (t) => {
          if (deletedTxnId && String(t.id) === String(deletedTxnId)) return false;
          if (deletedOr && t.or_number && String(t.or_number) === String(deletedOr)) return false;
          return true;
        };
        setTransactions((prev) => prev.filter(stillPresent));
        setCashierTransactions((prev) => prev.filter(stillPresent));
      }
      setReportSyncTick((n) => n + 1);
    } else {
      setActivePlayerLogs(prevActive);
      setPausedPlayerLogs(prevPaused);
      setInactivePlayerLogs(prevInactive);
      setTransactions(prevTxns);
      setCashierTransactions(prevHistoryTxns);
      const error = await res.json().catch(() => ({}));
      toast({
        title: "Error",
        description: error.detail || "Failed to delete player log.",
        variant: "destructive",
        duration: 3000,
      });
    }
  } catch (error) {
    setActivePlayerLogs(prevActive);
    setPausedPlayerLogs(prevPaused);
    setInactivePlayerLogs(prevInactive);
    setTransactions(prevTxns);
    setCashierTransactions(prevHistoryTxns);
    console.error("Error deleting player log:", error);
    toast({
      title: "Error",
      description: error.message || "Failed to delete player log.",
      variant: "destructive",
      duration: 3000,
    });
  } finally {
    setIsDeletingPlayerLog(false);
  }
};

// Fetch preview data when the delete dialog opens (transaction + related player logs)
const fetchDeletePreview = async (logId) => {
  setIsLoadingDeletePreview(true);
  setDeletePreview(null);
  try {
    const res = await fetch(`${BACKEND_URL}/api/player-logs/delete-preview/${logId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setDeletePreview(data);
    }
  } catch (e) {
    console.error("Failed to fetch delete preview:", e);
  } finally {
    setIsLoadingDeletePreview(false);
  }
};

const handleEndGolfSession = async (log) => {
  if (!log?.id) return;
  try {
    const res = await fetch(`${BACKEND_URL}/api/player-logs/time-out/${log.id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      toast({
        title: "Session Ended",
        description: `Golf session for ${log.player_name} has been ended.`,
      });
      fetchPlayerLogs();
    } else {
      const error = await res.json();
      toast({
        title: "Error",
        description: error.detail || "Failed to end session.",
        variant: "destructive",
      });
    }
  } catch (error) {
    toast({
      title: "Error",
      description: error.message,
      variant: "destructive",
    });
  }
};

const handleEndDrSession = async (log) => {
  if (!log?.id) return;
  try {
    const res = await fetch(`${BACKEND_URL}/api/player-logs/time-out/${log.id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      toast({
        title: "Session Ended",
        description: `Driving range session for ${log.player_name || log.member_name || "Player"} has been ended.`,
      });
      fetchPlayerLogs();
    } else {
      const error = await res.json();
      toast({
        title: "Error",
        description: error.detail || "Failed to end session.",
        variant: "destructive",
      });
    }
  } catch (error) {
    toast({
      title: "Error",
      description: error.message,
      variant: "destructive",
    });
  }
};

// Player log
const [playerLogs, setPlayerLogs] = useState([]);
const [inactivePlayerLogs, setInactivePlayerLogs] = useState([]);
const [orNumberError, setOrNumberError] = useState("");
const [isAddPlayerLogDialogOpen, setIsAddPlayerLogDialogOpen] = useState(false);


useEffect(() => {
  if (newPlayerLog.member_id === null) {
    setNewPlayerLog(prev => ({
      ...prev,
      or_number: "",
      card_gcash: 0,
      cash: 0,
    }));
  }
}, [newPlayerLog.member_id]);


const getPeriod = (period) => {
  const now = new Date();
  let start, end;

  if (period === "Today") {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  }

  if (period === "This Week") {
    const day = now.getDay(); // 0 = Sunday
    const diff = now.getDate() - day;
    start = new Date(now.getFullYear(), now.getMonth(), diff);
    end = new Date(start);
    end.setDate(start.getDate() + 7);
  }

  if (period === "This Month") {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  if (period === "This Year") {
    start = new Date(now.getFullYear(), 0, 1);
    end = new Date(now.getFullYear() + 1, 0, 1);
  }

  return { start, end };
}

const getFilteredPeriod = () => {
  const { start, end } = getPeriod(period);

  return inactivePlayerLogs.filter((log) => {
    const date = new Date(log.time_in); // assuming timeIn is your base
    return date >= start && date < end;
  });
}


// combine driving range and golf data
// const combineActiveData = () => {
//   const now = new Date();
  
//   // Filter driving range logs (active ones)
//   const activeDrivingRange = activePlayerLogs.filter(log => 
//     !log.service_type || log.service_type === "Driving Range Rates"
//   ).map(log => ({
//     ...log,
//     service_type: "Driving Range Rates"
//   }));
  
//   // Filter golf reservations (active ones)
//   const activeGolfReservations = golfReservations.filter(reservation => {
//     if (!reservation.tee_time) return false;
//     const teeTime = new Date(reservation.tee_time);
//     const endTime = reservation.end_time ? new Date(reservation.end_time) : null;
    
//     // Include if tee time is in future or currently ongoing
//     return teeTime > now || (endTime && endTime > now);
//   }).map(reservation => ({
//     id: reservation.id,
//     or_number: reservation.or_number,
//     player_name: reservation.player_name,
//     member_name: reservation.player_name,
//     member_id: reservation.member_id,
//     service_type: "Golf Rates",
//     tee_time: reservation.tee_time,
//     time_in: reservation.tee_time, // Map tee_time to time_in for consistency
//     time_out: reservation.end_time, // Map end_time to time_out for consistency
//     end_time: reservation.end_time,
//     course_type: reservation.course_type,
//     players_count: reservation.players_count,
//     card_gcash: reservation.card_gcash || 0,
//     cash: reservation.cash || 0,
//     created_at: reservation.created_at,
//     updated_at: reservation.updated_at
//   }));
  
//   return [...activeDrivingRange, ...activeGolfReservations];
// };

// Update this in useEffect when data changes
// useEffect(() => {
//   const combined = combineActiveData();
//   setCombinedActiveData(combined);
// }, [activePlayerLogs, golfReservations]);



// ✅ Update fetchPlayerLogs to handle both services
const playerLogsInFlightRef = React.useRef(null);

// Live board only needs active + paused. Inactive history (thousands of rows)
// is loaded by reports, not on every POS refresh.
const fetchPlayerLogs = async (skipStateUpdate = false, options = {}) => {
  if (playerLogsInFlightRef.current) return playerLogsInFlightRef.current;
  const silent = Boolean(options && options.silent);
  if (!silent) setIsLoadingPlayerLogs(true);

  const pending = (async () => {
    try {
      const accessToken = localStorage.getItem("token") || token;
      if (!accessToken) return { active: [], paused: [], transactions: null };
      const headers = { Authorization: `Bearer ${accessToken}` };
      if (typeof updateLastActivity === "function") {
        updateLastActivity();
      }
      const liveRes = await fetch(`${BACKEND_URL}/api/player-logs/live`, { headers });
      let activeData = [];
      let pausedData = [];
      let txData = null;

      if (liveRes.ok) {
        const live = await liveRes.json();
        activeData = Array.isArray(live?.active) ? live.active : [];
        pausedData = Array.isArray(live?.paused) ? live.paused : [];
        if (Array.isArray(live?.transactions)) {
          txData = live.transactions;
          if (!skipStateUpdate) setTransactions(txData);
        }
      } else {
        const [activeRes, pausedRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/player-logs/active`, { headers }),
          fetch(`${BACKEND_URL}/api/player-logs/paused`, { headers }),
        ]);
        if (!activeRes.ok) {
          console.error("❌ Failed to fetch active player logs");
          return { active: [], paused: [], transactions: null };
        }
        const [legacyActive, legacyPaused] = await Promise.all([
          activeRes.json(),
          pausedRes.ok ? pausedRes.json() : Promise.resolve([]),
        ]);
        activeData = Array.isArray(legacyActive) ? legacyActive : [];
        pausedData = Array.isArray(legacyPaused) ? legacyPaused : [];
      }

      if (!skipStateUpdate) {
        setActivePlayerLogs(activeData);
        setPausedPlayerLogs(pausedData);
      }

      return { active: activeData, paused: pausedData, transactions: txData };
    } catch (error) {
      console.error("❌ Error fetching player logs:", error);
      return { active: [], paused: [], transactions: null };
    } finally {
      if (!silent) setIsLoadingPlayerLogs(false);
    }
  })();

  playerLogsInFlightRef.current = pending.finally(() => {
    playerLogsInFlightRef.current = null;
  });
  return playerLogsInFlightRef.current;
};

// Wrapper to ensure the refresh button shows a loading state for at least a short time
const handleRefreshClick = async () => {
  if (isLoadingPlayerLogs) return;
  setIsLoadingPlayerLogs(true);
  const MIN_MS = 700;
  const delay = new Promise((res) => setTimeout(res, MIN_MS));
  try {
    await Promise.all([fetchPlayerLogs(), delay]);
  } catch (e) {
    // fetchPlayerLogs already logs errors
  } finally {
    setIsLoadingPlayerLogs(false);
  }
};

// Debug: log loading state changes (helps verify value flips when clicking)
useEffect(() => {
  if (process.env.NODE_ENV !== "production") {
    console.log("isLoadingPlayerLogs ->", isLoadingPlayerLogs);
  }
}, [isLoadingPlayerLogs]);


useEffect(() => {
  if (token) {
    const loadData = async () => {
      const board = await fetchPlayerLogs();
      await Promise.all([
        fetchProducts(),
        fetchMembers(),
        fetchLowStockItems(),
        Array.isArray(board?.transactions) ? Promise.resolve(board.transactions) : fetchTransactions(),
      ]);
    };
    loadData();
  }
}, [token]);

// Add to your component

  // ✅ FIXED: Cleanup old timeout entries with proper tracking
  React.useEffect(() => {
    const cleanupId = setInterval(() => {
      if (window._timeoutStartTimes) {
        const now = Date.now();
        let cleaned = 0;

        Object.entries(window._timeoutStartTimes).forEach(([logId, timestamp]) => {
          if (now - timestamp > 120000) { // 2 minutes
            delete window._timeoutStartTimes[logId];
            if (window._timedOutLogs) window._timedOutLogs.delete(logId);
            cleaned++;
          }
        });

        if (cleaned > 0) {
          console.log(`🧹 Auto-cleaned ${cleaned} stale timeout entries`);
        }
      }
    }, 120000); // Every 2 minutes

    activeIntervalsRef.current.add(cleanupId);

    return () => {
      clearInterval(cleanupId);
      activeIntervalsRef.current.delete(cleanupId);
    };
  }, []); // ✅ Empty deps - only run once

// Background refresh for player logs is handled by the consolidated interval below (depends on [token] only).
// Removed duplicate useEffect that had activePlayerLogs.length in deps, which caused race conditions.



const [pausedPlayerLogs, setPausedPlayerLogs] = useState([]);
const [showPausedTable, setShowPausedTable] = useState(false);

// Add fetch function for paused logs
const fetchPausedPlayerLogs = async () => {
  if (!token) return;
  
  try {
    const res = await fetch(`${BACKEND_URL}/api/player-logs/paused`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    
    if (res.ok) {
      const data = await res.json();
      setPausedPlayerLogs(data);
    }
  } catch (error) {
    console.error("❌ Error fetching paused logs:", error);
  }
};

// Paused logs are included in fetchPlayerLogs; no extra token-effect fetch here.


const parseManilaDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    if (dateStr instanceof Date) return dateStr;
    let isoStr = String(dateStr).trim();

    // if it's plain "HH:MM" treat as today at that time in Manila
    if (/^\d{1,2}:\d{2}$/.test(isoStr)) {
      const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
      const [h, m] = isoStr.split(":").map(Number);
      return new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
    }

    // convert "YYYY-MM-DD HH:MM" to ISO "YYYY-MM-DDTHH:MM"
    if (!isoStr.includes("T") && /^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}/.test(isoStr)) {
      isoStr = isoStr.replace(" ", "T");
    }

    // append Manila offset if no timezone info
    if (!/[Z\+\-]\d{2}:?\d{2}$/.test(isoStr)) {
      isoStr = isoStr + "+08:00";
    }

    return new Date(isoStr);
  } catch (e) {
    console.error("parseManilaDate error:", dateStr, e);
    return null;
  }
};

// Add pause handler
// TODO: Fix where only 1 player log can be paused instead of multiple
const handlePauseLog = async (logId) => {
  try {
    const log = activePlayerLogs.find(l => l.id === logId);
    if (!log) {
      toast({
        title: "Error",
        description: "Log not found",
        variant: "destructive",
      });
      return;
    }

    const txn = findTxnForLog(log, transactions);

   // Priority 1: Use resumed_time_in if exists
    // Priority 3: Fall back to transaction start_time (HH:MM -> today in Manila)
    let snapTimeIn = null;
    if (log.resumed_time_in) {
      snapTimeIn = parseManilaDate(log.resumed_time_in);
      console.log(`✅ Using resumed_time_in for pause: ${snapTimeIn}`);
    } else if (txn?.start_time && /^\d{1,2}:\d{2}$/.test(txn.start_time)) {
      const [h, m] = txn.start_time.split(":").map(Number);
      const todayPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
      snapTimeIn = new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, m, 0, 0);
      console.log(`✅ Using txn.start_time for pause: ${snapTimeIn}`);
    }

    // Priority 1: Use resumed_time_out if exists
    // Priority 3: Otherwise we'll calculate from snapTimeIn + planned duration (no direct log.time_out)
    let snapTimeOut = null;
    if (log.resumed_time_out) {
      snapTimeOut = parseManilaDate(log.resumed_time_out);
      console.log(`✅ Using resumed_time_out for pause: ${snapTimeOut}`);
    } else if (snapTimeIn) {
      const planned = getPlannedDurationMinutes(log);
      if (planned > 0) {
        snapTimeOut = new Date(snapTimeIn.getTime() + planned * 60000);
        console.log(`✅ Calculated snapTimeOut from snapTimeIn + planned (${planned}m): ${snapTimeOut}`);
      }
    }

     const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
    const planned = getPlannedDurationMinutes(log);
    let remainingMsAtPause = null;
    if (snapTimeOut) {
      remainingMsAtPause = snapTimeOut.getTime() - nowPH.getTime();
    } else if (snapTimeIn && planned > 0) {
      remainingMsAtPause = (snapTimeIn.getTime() + planned * 60000) - nowPH.getTime();
    }

    const body = {
      pause_time: (typeof toManilaOffsetIso === "function") ? toManilaOffsetIso(nowPH) : nowPH.toISOString(),
      remaining_ms_at_pause: remainingMsAtPause,
      time_out_at_pause: snapTimeOut ? ((typeof toManilaOffsetIso === "function") ? toManilaOffsetIso(snapTimeOut) : snapTimeOut.toISOString()) : null,
      time_in_at_pause: snapTimeIn ? ((typeof toManilaOffsetIso === "function") ? toManilaOffsetIso(snapTimeIn) : snapTimeIn.toISOString()) : null,
      paused_planned_duration_minutes: planned
    };

    const res = await fetch(`${BACKEND_URL}/api/player-logs/pause/${logId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });

    if (res.ok) {
      toast({ title: "Booking Paused", description: "The driving range booking has been paused successfully." });
      await fetchPlayerLogs();
      await fetchPausedPlayerLogs();
    } else {
      const error = await res.json().catch(() => ({}));
      toast({ title: "Error", description: error.detail || "Failed to pause booking", variant: "destructive" });
    }
  } catch (error) {
    console.error("Error pausing log:", error);
    toast({ title: "Error", description: "Failed to pause booking", variant: "destructive" });
  }
};

// Add these states near your other state declarations
const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
const [resumeLog, setResumeLog] = useState(null);
const [resumeManualTime, setResumeManualTime] = useState("");
const [resumeBayNumberError, setResumeBayNumberError] = useState("");
const [resumePlayerLog, setResumePlayerLog] = useState({
  bay_number: "",
  time_in: "",
  time_out: "",
  ui_label_start: "",
  ui_label_end: "",
  ui_locked: false,
});

// ====== RESCHEDULE STATES ======
const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
const [rescheduleLog, setRescheduleLog] = useState(null);
const [rescheduleManualTime, setRescheduleManualTime] = useState("");
const [rescheduleBayNumberError, setRescheduleBayNumberError] = useState("");
const [rescheduleReason, setRescheduleReason] = useState("");
const [reschedulePlayerLog, setReschedulePlayerLog] = useState({
  bay_number: "",
  time_in: "",
  time_out: "",
  ui_label_start: "",
  ui_label_end: "",
  ui_locked: false,
});

// ✅ PREFILL resumePlayerLog + resumeManualTime when resumeLog is opened
React.useEffect(() => {
  if (!resumeLog) return;

  // Prefill player name into the resume player log so TimeSlotPicker's candidateName check passes
  setResumePlayerLog(prev => ({
    ...(prev || {}),
    player_name: String(resumeLog.player_name || prev.player_name || "").trim(),
    // Do NOT prefill bay_number so operator can choose a new bay by default.
    // Keep previous bay_number only if you want to default to original bay.
  }));

  // If resumeLog contains an ISO-like time, convert to "HH:MM" for the picker
  const possibleTime = resumeLog.time_in || resumeLog.resumed_time_in || resumeLog.time_in_at_pause || "";
  if (possibleTime) {
    try {
      const d = new Date(possibleTime);
      if (!Number.isNaN(d.getTime())) {
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        setResumeManualTime(`${hh}:${mm}`);
      }
    } catch (e) {
      // ignore parse errors
    }
  }
}, [resumeLog]);


// Add resume handler
// const handleResumeLog = async (logId) => {
//   try {
//     const res = await fetch(`${BACKEND_URL}/api/player-logs/resume/${logId}`, {
//       method: "POST",
//       headers: { Authorization: `Bearer ${token}` },
//     });
    
//     if (res.ok) {
//       toast({
//         title: "Booking Resumed",
//         description: "The driving range booking has been resumed successfully.",
//       });
      
//       // Refresh both active and paused logs
//       await fetchPlayerLogs();
//       await fetchPausedPlayerLogs();
//     } else {
//       const error = await res.json();
//       toast({
//         title: "Error",
//         description: error.detail || "Failed to resume booking",
//         variant: "destructive",
//       });
//     }
//   } catch (error) {
//     console.error("Error resuming log:", error);
//     toast({
//       title: "Error",
//       description: "Failed to resume booking",
//       variant: "destructive",
//     });
//   }
// };




// Function to filter inactive logs by date range
const filterInactiveLogsByDateRange = (start, end) => {
  return inactivePlayerLogs.filter((log) => {
    try {
      // Use time_in as base date
      const timeIn = log.time_in ? new Date(log.time_in) : null;
      // Fallback to time_out or created_at if time_in is missing
      const date = timeIn || (log.time_out ? new Date(log.time_out) : 
                             (log.created_at ? new Date(log.created_at) : null));
      
      if (!date) return false;
      return date >= start && date < end;
    } catch (err) {
      console.error(`Error filtering log ${log.id}:`, err);
      return false;
    }
  });
};

// Auto-remove player logs that are 5+ minutes overtime
useEffect(() => {
  if (!token) return;
  if (!Array.isArray(activePlayerLogs) || activePlayerLogs.length === 0) return;

  // Track IDs already being processed to prevent duplicate calls
  const processingRef = { current: new Set() };

  const checkOverduePlayerLogs = async () => {
    const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
    const overdueIds = [];

    activePlayerLogs.forEach(log => {
      if (log.is_active === false || log.is_paused) return;
      if (processingRef.current.has(log.id)) return;

      // Skip restore-protected logs (restored without OR number)
      if (log.restore_protected) return;

      let timeOut = null;

      // Driving Range: use log.time_out directly
      if (log.service_type === "Driving Range Rates" && log.time_out) {
        timeOut = parseManilaDate(log.time_out);
      }

      // Golf Rates: use log.time_out (end_time)
      if (log.service_type === "Golf Rates" && log.time_out) {
        timeOut = parseManilaDate(log.time_out);
      }

      if (!timeOut || isNaN(timeOut.getTime())) return;

      const overtimeMs = nowPH - timeOut;
      const overtimeMinutes = Math.floor(overtimeMs / 60000);

      if (overtimeMinutes >= 5) {
        overdueIds.push(log.id);
      }
    });

    if (overdueIds.length === 0) return;

    // Mark as processing
    overdueIds.forEach(id => processingRef.current.add(id));

    console.log(`🔄 Auto-removing ${overdueIds.length} player log(s) with 5+ min overtime`);

    try {
      const results = await Promise.allSettled(
        overdueIds.map(logId =>
          fetchWithAuth(`${BACKEND_URL}/api/player-logs/time-out/${logId}`, {
            method: "POST",
          })
        )
      );

      const successCount = results.filter(r => r.status === "fulfilled" && r.value && r.value.ok).length;
      if (successCount > 0) {
        console.log(`✅ Auto-removed ${successCount} overtime player log(s)`);
        await fetchPlayerLogs();
      }
    } catch (err) {
      console.error("❌ Error auto-removing overtime logs:", err);
    } finally {
      overdueIds.forEach(id => processingRef.current.delete(id));
    }
  };

  // Run immediately
  checkOverduePlayerLogs();

  // Poll every 30 seconds
  const intervalId = setInterval(checkOverduePlayerLogs, 30000);

  return () => clearInterval(intervalId);
}, [activePlayerLogs, token]);

// Update the addPlayerLog function
const addPlayerLog = async (logData) => {
  try {
    const res = await fetchWithAuth(`${BACKEND_URL}/api/player-logs/time-in`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(logData),
    });
    
    if (!res || !res.ok) {
      const errorText = await res.text().catch(() => "Unknown error");
      throw new Error(errorText || `Failed to save player log (${res?.status || 'No response'})`);
    }
    
    return await res.json();
  } catch (err) {
    console.error("addPlayerLog error:", err);
    throw err;
  }
};


const addGolfReservation = async (reservationData) => {
  try {
    const payload = {
      or_number: reservationData.or_number,
      player_name: reservationData.player_name,
      member_id: reservationData.member_id,
      course_type: reservationData.course_type,
      players_count: reservationData.players_count,
      // backend expects time_in/time_out for PlayerLog endpoint
      time_in: reservationData.tee_time,
      time_out: reservationData.end_time,
      card_gcash: reservationData.card_gcash ?? 0,
      cash: reservationData.cash ?? 0,
      service_type: "Golf Rates",
    };

    // addPlayerLog already defined and posts to /api/player-logs/time-in
    const resp = await addPlayerLog(payload);
    return resp;
  } catch (err) {
    console.error("addGolfReservation error:", err);
    throw err;
  }
};

// Add wrapper for golf reservations (uses the same backend endpoint)




// Golf Rates specific functions
// const fetchGolfReservations = async () => {
//   if (!token) {
//     console.log("❌ No token available for fetching golf reservations");
//     return;
//   }
  
//   try {
//     console.log("🔍 Fetching golf reservations...");
    
//     const [activeRes, inactiveRes] = await Promise.all([
//       fetch(`${BACKEND_URL}/api/golf-reservations/active`, {
//         headers: { Authorization: `Bearer ${token}` },
//       }),
//       fetch(`${BACKEND_URL}/api/golf-reservations/inactive`, {
//         headers: { Authorization: `Bearer ${token}` },
//       })
//     ]);

//     const active = activeRes.ok ? await activeRes.json() : [];
//     const inactive = inactiveRes.ok ? await inactiveRes.json() : [];
    
//     console.log("✅ Golf reservations fetched:", { 
//       active: active.length, 
//       inactive: inactive.length 
//     });
    
//     setGolfReservations([...active, ...inactive]);
//   } catch (error) {
//     console.error("❌ Error fetching golf reservations:", error);
//     setGolfReservations([]);
//   }
// };

// const addGolfReservation = async (reservationData) => {
//   try {
//     const res = await fetchWithAuth(`${BACKEND_URL}/api/golf-reservations`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${token}`,
//       },
//       body: JSON.stringify(reservationData),
//     });
    
//     if (!res || !res.ok) {
//       const errorText = await res.text().catch(() => "Unknown error");
//       throw new Error(errorText || `Failed to save golf reservation (${res?.status || 'No response'})`);
//     }
    
//     return await res.json();
//   } catch (err) {
//     console.error("addGolfReservation error:", err);
//     throw err;
//   }
// };

// ✅ Update isGolfTimeSlotAvailable to use activePlayerLogs
function isGolfTimeSlotAvailable(teeTime, durationMinutes = 240) { // 4 hours for golf
  const slotStart = new Date(teeTime);
  const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60000);

  return !activePlayerLogs.some(log =>
    log.service_type === "Golf Rates" &&
    log.time_in &&
    log.time_out &&
    (
      (slotStart >= new Date(log.time_in) && slotStart < new Date(log.time_out)) ||
      (slotEnd > new Date(log.time_in) && slotEnd <= new Date(log.time_out)) ||
      (slotStart < new Date(log.time_out) && slotEnd > new Date(log.time_in))
    )
  );
}


// Filter tabs based on user role
const visibleTabs = allTabs.filter(tab => !tab.admin || user?.role === "admin");
const gridColsClass = `grid-cols-${visibleTabs.length}`;

  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  
const [selectedMember, setSelectedMember] = useState({
  type: "walkin",
  name: "",
  discount_percentage: 0,
  membership_type: "Walk-in",
});
  const [memberSearch, setMemberSearch] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cardNumber, setCardNumber] = useState("");
  const [cardType, setCardType] = useState(""); // Card type for card payments
  const [batchNumber, setBatchNumber] = useState(""); // Card batch number
  const [cardValidator, setCardValidator] = useState(false);
  const formatCardNumber = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "").slice(0, 16); // Limit to 16 digits
    // Group into 4s and join with spaces
    return digits.replace(/(.{4})/g, "$1 ").trim();};

  // Card types for debit/credit card dropdown with numeric values
  const cardTypes = [
    { value: "1", label: "Master Card/Visa/BancNet" },
    { value: "2", label: "JCB" },
    { value: "3", label: "BDO Union Pay" },
    { value: "4", label: "Diners Club International" },
    { value: "5", label: "American Express" },
    { value: "6", label: "Discover" },
  ];
  const [transactionNumber, setTransactionNumber] = useState("");
  const [orNumber, setOrNumber] = useState("");
  const [orList, setOrList] = useState([]);
  const isPuncher = user?.role === "puncher";
  // Helper: refetch OR list from API.
  // - Keeps the current OR if it's still valid.
  // - Otherwise selects the first available OR.
  const refetchCashierOrList = async ({ forceSelectFirst = false } = {}) => {
    if (!token) return;
    // Puncher should NOT allocate OR numbers.
    if (isPuncher) {
      setOrList([]);
      setOrNumber("");
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/cashier-or`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response || !response.ok) {
        let body = "";
        try {
          body = await response.text();
        } catch (e) {
          body = "";
        }
        console.error("/api/cashier-or failed:", response?.status, body);
        return;
      }

      const data = await response.json();
      const sorted = Array.isArray(data)
        ? data.map(String).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        : [];

      setOrList(sorted);

      if (sorted.length === 0) return;

      const current = String(orNumber || "");
      const currentStillValid = !!current && sorted.includes(current);
      if (forceSelectFirst || !currentStillValid) {
        setOrNumber(sorted[0]);
      }
    } catch (e) {
      console.error("Failed to fetch cashier OR list:", e);
    }
  };
  
  useEffect(() => {
    if (token && !isPuncher) refetchCashierOrList();
  }, [token, isPuncher]);

  // If the user is puncher, force-clear OR state.
  useEffect(() => {
    if (isPuncher) {
      setOrList([]);
      setOrNumber("");
    }
  }, [isPuncher]);

  
  // ID type for special discounts (PWD / Senior Citizen)
  const [idType, setIdType] = useState("none"); // "none" | "pwd" | "senior"
  const [idNumber, setIdNumber] = useState("");
  
  // Family Discount Feature - tracks eligible persons (seniors/PWD) and per-item discount quantities
  const [familyDiscountEnabled, setFamilyDiscountEnabled] = useState(false);
  const [totalFamilyMembers, setTotalFamilyMembers] = useState(1); // Total people in family/group
  const [eligibleDiscountPersons, setEligibleDiscountPersons] = useState(1); // Number of seniors/PWD eligible for discount
  // Per-item discount tracking: { [product_id]: { discountQty: number } }
  const [itemDiscountSelections, setItemDiscountSelections] = useState({});
  
  // Discount modal state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]); // Array of instanceKeys
  const [discountIdType, setDiscountIdType] = useState('none');
  const [discountIdNumber, setDiscountIdNumber] = useState('');
  const [discountCustomerName, setDiscountCustomerName] = useState(''); // Name of the discount holder (PWD/Senior)
  
  // Item-level discounts: { [instanceKey]: { type: 'pwd'|'senior', idNumber: string } }
  const [itemDiscounts, setItemDiscounts] = useState({});
  
  const [paymentStatus, setPaymentStatus] = useState("");
  const [selectedBank, setSelectedBank] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const isProcessingPaymentRef = React.useRef(false);
  const [activeTab, setActiveTab] = useState("pos");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCartSummaryModalOpen, setIsCartSummaryModalOpen] = useState(false);
  const [showCheckoutPage, setShowCheckoutPage] = useState(false);

  const [headerDate, setHeaderDate] = useState(
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" }) // "YYYY-MM-DD" in Philippine time
  );
  const headerDateRef = React.useRef(null);
  const headerDateDisplay = React.useMemo(() => {
    try {
      return new Date(headerDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  }, [headerDate]);

  // new: compact short date (DD/MM/YY) for small screens
  const headerDateCompact = React.useMemo(() => {
    try {
      return new Date(headerDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      }); // e.g. "12/11/25"
    } catch {
      return "";
    }
  }, [headerDate]);


  
// Add these new states for history
const [showHistoryTable, setShowHistoryTable] = useState(false);

// Cashier Transactions — fetched directly from backend for the selected date
const [cashierTransactions, setCashierTransactions] = useState([]);
const [isLoadingCashierHistory, setIsLoadingCashierHistory] = useState(false);

const fetchCashierHistory = React.useCallback(async (date) => {
  if (!token || !date) return;
  setIsLoadingCashierHistory(true);
  try {
    const res = await fetchWithAuth(`${BACKEND_URL}/api/transactions/cashier-history?date=${encodeURIComponent(date)}`);
    if (res && res.ok) {
      const data = await res.json();
      setCashierTransactions(Array.isArray(data) ? data : []);
    }
  } catch (e) {
    console.error("Error fetching cashier history:", e);
  } finally {
    setIsLoadingCashierHistory(false);
  }
}, [token]);

// Fetch cashier history when history table is opened or date changes
React.useEffect(() => {
  if (showHistoryTable && headerDate) {
    fetchCashierHistory(headerDate);
  }
}, [showHistoryTable, headerDate, fetchCashierHistory]);

const [showTxnSummaryModal, setShowTxnSummaryModal] = useState(false);
const [selectedTxn, setSelectedTxn] = useState(null);
const [txnOrInput, setTxnOrInput] = useState("");
const [isTxnUpdating, setIsTxnUpdating] = useState(false);
const [closingTransaction, setClosingTransaction] = useState(false);
const [showReceiptModal, setShowReceiptModal] = useState(false);
const [receiptData, setReceiptData] = useState(null);

useEffect(() => {
  if (!showTxnSummaryModal) {
    setClosingTransaction(false);
  }
}, [showTxnSummaryModal]);

const normalizeWorkflowStatus = (val) => {
  const s = String(val || "").trim().toLowerCase();
  if (!s) return "pending";
  if (s === "in progress" || s === "in-progress" || s === "processing") return "in_progress";
  if (s === "cancelled" || s === "canceled") return "cancelled";
  if (s === "complete" || s === "completed") return "completed";
  if (s === "pending") return "pending";
  return s;
};

const txnStatusLabel = (tx) => {
  const wf = normalizeWorkflowStatus(tx?.workflow_status);
  if (wf === "completed") return "Completed";
  if (wf === "cancelled") return "Cancelled";
  if (wf === "in_progress") return "In Progress";
  return "Pending";
};

const txnStatusBadgeClass = (tx) => {
  const wf = normalizeWorkflowStatus(tx?.workflow_status);
  if (wf === "completed") return "bg-emerald-100 text-emerald-700";
  if (wf === "cancelled") return "bg-red-100 text-red-700";
  if (wf === "in_progress") return "bg-yellow-100 text-yellow-700";
  return "bg-gray-100 text-gray-700";
};

const openTxnModal = (tx) => {
  console.log("🔍 openTxnModal - Full transaction object:", tx);
  console.log("🔍 Card Type ID:", tx?.cardtype_id, "Type:", typeof tx?.cardtype_id);
  console.log("🔍 Batch No:", tx?.batch_no, "Type:", typeof tx?.batch_no);
  console.log("🔍 Card Number:", tx?.card_number);
  console.log("🔍 Transaction Number:", tx?.transaction_number);
  setSelectedTxn(tx || null);
  setTxnOrInput(String(tx?.or_number || "").trim());
  setShowTxnSummaryModal(true);
};

const syncTransactionToQA = async (transaction) => {
  if (!transaction) return false;
  try {
    const extRes = await fetchWithAuth(`${BACKEND_URL}/api/save-transaction`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transaction }),
    });

    if (!extRes) {
      throw new Error("AUTH_EXPIRED");
    }

    let extBody = null;
    try {
      extBody = await extRes.clone().json();
    } catch (e) {
      const text = await extRes.text().catch(() => "");
      extBody = text ? { detail: text } : null;
    }

    if (!extRes.ok) {
      // Log full error details for debugging
      console.error("QA sync error response:", extBody);
      
      const base =
        (extBody && (extBody.detail || extBody.message)) ||
        (extBody && extBody.body && (extBody.body.detail || extBody.body.message)) ||
        `External sync failed (status ${extRes.status})`;
      const errs =
        (extBody && extBody.errors) ||
        (extBody && extBody.body && extBody.body.errors) ||
        null;
      
      // Collect ALL validation errors, not just the first one
      let errMessages = [];
      try {
        if (errs && typeof errs === "object") {
          for (const [field, msgs] of Object.entries(errs)) {
            const msg = Array.isArray(msgs) ? msgs[0] : String(msgs || "");
            if (msg) errMessages.push(`${field}: ${msg}`);
          }
        }
      } catch {}

      toast && toast({
        title: "QA sync failed",
        description: errMessages.length > 0 
          ? errMessages.slice(0, 3).join("; ") + (errMessages.length > 3 ? ` (+${errMessages.length - 3} more)` : "")
          : String(base || "").trim(),
        variant: "destructive",
      });
      return false;
    }

    return true;
  } catch (e) {
    console.warn("QA save-transaction error", e);
    toast && toast({
      title: "QA sync failed",
      description: "Could not push transaction to QA. Local transaction was saved.",
      variant: "destructive",
    });
    return false;
  }
};

const patchTransaction = async (txId, patch) => {
  if (!txId) return null;
  setIsTxnUpdating(true);
  try {
    const res = await fetchWithAuth(`${BACKEND_URL}/api/transactions/${encodeURIComponent(String(txId))}`, {
      method: "PATCH",
      body: JSON.stringify(patch || {}),
    });
    if (!res || !res.ok) {
      let detail = "Failed to update transaction";
      try {
        const data = await res.json();
        detail = data?.detail || detail;
      } catch {}
      toast && toast({ title: "Update failed", description: detail, variant: "destructive" });
      return null;
    }

    let updated = null;
    try {
      updated = await res.json();
      if (updated && selectedTxn && String(selectedTxn.id) === String(updated.id)) {
        setSelectedTxn(updated);
      }
    } catch {}

    await fetchTransactions();
    // Refresh cashier history if the table is open
    if (showHistoryTable && headerDate) {
      fetchCashierHistory(headerDate);
    }
    // If backend propagates OR to player logs, refresh so OR columns update immediately.
    try {
      await fetchPlayerLogs();
    } catch (e) {
      // best-effort
    }
    toast && toast({ title: "Updated", description: "Transaction updated successfully." });
    return updated;
  } catch (e) {
    toast && toast({ title: "Update failed", description: e?.message || "Unknown error", variant: "destructive" });
    return null;
  } finally {
    setIsTxnUpdating(false);
  }
};




    const [reservedSlot, setReservedSlot] = React.useState(null); // Add this state
    // Lifted state for golf tee-time UI selection so parent can clear it
    const [reservedGolfSlot, setReservedGolfSlot] = React.useState(null);
    const resetTimeSelection = React.useCallback((opts = {}) => {
      // clear manual inputs and UI-only reservation state
      setManualTime("");
      setManualTeeTime("");
      setReservedSlot(null);
      setReservedGolfSlot(null);
      // reset UI selection for player log (keep member info, cart etc.)
      setNewPlayerLog(prev => ({
        ...prev,
        bay_number: "",
        ui_locked: false,
        time_in: "",
        time_out: "",
        ui_label_start: "",
        ui_label_end: "",
      })); 
    }, []);





  const [isCancelled, setIsCancelled] = useState(false);
  // New state for player logs
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const [bayNumberError, setBayNumberError] = useState("");
  // Example logData object (must match backend PlayerLog model)
  const [manualTime, setManualTime] = React.useState("");
  const [manualTeeTime, setManualTeeTime] = React.useState("");

// TODO: PLAYER NAME STATE GUARD
// ✅ FIXED: Only sync member name on INITIAL member selection, never overwrite typed player_name
const _lastSyncedMemberNameRef = React.useRef(null);
const _userHasTypedPlayerNameRef = React.useRef(false);

// Track when user manually types in player_name field
React.useEffect(() => {
  if (newPlayerLog?.player_name && newPlayerLog.player_name.trim() !== "") {
    _userHasTypedPlayerNameRef.current = true;
  }
}, [newPlayerLog?.player_name]);

// Track when user manually types in player_name field
React.useEffect(() => {
  if (newPlayerLog?.player_name && newPlayerLog.player_name.trim() !== "") {
    _userHasTypedPlayerNameRef.current = true;
  }
}, [newPlayerLog?.player_name]);

// Replace previous behavior: DO NOT write selectedMember.name into newPlayerLog.player_name.
// Only sync the member_id (and only when operator hasn't typed a custom player name).
React.useEffect(() => {
  if (!selectedMember) return;

  // If operator already typed a name, never overwrite it
  if (_userHasTypedPlayerNameRef.current) return;

  setNewPlayerLog(prev => {
    const currentMemberId = prev?.member_id ?? null;
    if (String(currentMemberId) === String(selectedMember.id)) return prev;
    return { ...(prev || {}), member_id: selectedMember.id ?? null };
  });
}, [selectedMember?.id, setNewPlayerLog]);

// Reset the typed flag when modal closes or transaction completes
React.useEffect(() => {
  if (!isCartSummaryModalOpen) {
    _userHasTypedPlayerNameRef.current = false;
    _lastSyncedMemberNameRef.current = null;
  }
}, [isCartSummaryModalOpen]);


  // Function to find which bays are available for a specific time
function getAvailableBaysForTime(time, durationMins) {
  if (!time) return [];
  
  // Convert string time to Date if needed
  const startTime = typeof time === 'string' ? new Date(time) : time;
  const endTime = new Date(startTime.getTime() + durationMins * 60000);
  
  // Check all 50 bays
  const availableBays = [];
  for (let bayNum = 1; bayNum <= 50; bayNum++) {
    // Use existing isTimeSlotAvailable function
    if (isTimeSlotAvailable(bayNum.toString(), startTime.toISOString(), durationMins)) {
      availableBays.push(bayNum.toString());
    }
  }
  
  return availableBays;
}

  // player logs

      // Clear tee-time + local reservation every time the transaction summary modal opens
  React.useEffect(() => {
    if (isCartSummaryModalOpen) {
      // clear the manual tee time input (resets the <input type="time" value={manualTeeTime} />)
      setManualTeeTime("");
      // clear the UI reserved slot banner
      setReservedGolfSlot(null);
      // clear any staged golf reservation payload
      setNewGolfReservation(prev => ({
        ...prev,
        player_name: "",      // ensure player name is not auto-filled
        tee_time: "",
        end_time: "",
        golf_guest_bookings: []
      }));
    }
  }, [isCartSummaryModalOpen, setManualTeeTime, setReservedGolfSlot, setNewGolfReservation]);

// Clear manualTime whenever any modal that contains the time picker is opened
useEffect(() => {
  if (isCartSummaryModalOpen || isResumeModalOpen || isAddPlayerLogDialogOpen) {
    // ensure time input is empty on open
    setManualTime("");
  }
}, [isCartSummaryModalOpen, isResumeModalOpen, isAddPlayerLogDialogOpen]);


const handleStartTime = async (logId) => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/player-logs/${logId}/time-in`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }
    });
    const data = await res.json();
    if (res.ok) {
      toast({
        title: "Player time-in started!",
        description: `Time-in recorded.`,
        status: "success",
      });
      fetchPlayerLogs();
    } else {
      toast({
        title: "Error",
        description: data.detail || "Could not start time-in.",
        status: "error",
      });
    }
  } catch (error) {
    toast({
      title: "Error",
      description: error.message,
      status: "error",
    });
  }
};
const handleEndTime = async (logId) => {
  try {
    await fetch(`${BACKEND_URL}/api/player-logs/time-out/${logId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchPlayerLogs(); // updates POS tab
    generateReport();  // <-- THIS updates the Reports tab
    toast({
      title: "Player timed out!",
      description: "Player log ended successfully.",
      status: "success",
    });
  } catch (error) {
    toast({
      title: "Error",
      description: error.message,
      status: "error",
    });
  }
};

useEffect(() => {
  if (!newPlayerLog.or_number) {
    // If OR number is cleared, reset amounts and member link but keep player_name untouched (let operator control it)
    setNewPlayerLog(prev => ({
      ...prev,
      card_gcash: 0,
      cash: 0,
      // leave player_name as-is (do not auto-clear unless you want)
      member_id: null,
    }));
    return;
  }
  if (!transactions.length) return;

  const txn = transactions.find(
    t => t.or_number === newPlayerLog.or_number
  );
  if (txn) {
    let card_gcash = 0;
    let cash = 0;
    if (txn.payment_method === "cash") {
      cash = txn.total_amount;
    } else if (txn.payment_method === "card" || txn.payment_method === "online") {
      card_gcash = txn.total_amount;
    }
    setNewPlayerLog(prev => ({
      ...prev,
      card_gcash,
      cash,
      payment_method: txn.payment_method,
      // DO NOT overwrite prev.player_name: operator-controlled
      member_id: txn.member_id || prev.member_id,
    }));
  }
}, [newPlayerLog.or_number, transactions]);




//-------end player logs--------
    
  useEffect(() => {
    setFilteredProducts(selectedCategory === "all"
      ? products
      : products.filter((product) => (product.category === selectedCategory)))
  }, [products, selectedCategory]);

  const categories = [
    "Golf Equipment Rental",
    "Golf Rates",
    "Driving Range Rates",
  ];

  const onlinePaymentBanks = [
    { value: "gcash", label: "GCash", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/57/GCash-logo.svg/1200px-GCash-logo.svg.png" },
    { value: "maya", label: "Maya", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4c/Maya_logo.svg/1200px-Maya_logo.svg.png" },
    { value: "bdo", label: "BDO", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/68/BDO_Logo.svg/1200px-BDO_Logo.svg.png" },
    { value: "unionbank", label: "UnionBank", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5b/UnionBank_logo.svg/1200px-UnionBank_logo.svg.png" }
  ];

  // Safety-net restore: token without user, or missing activity timestamp
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    if (!savedToken) return;
    if (!localStorage.getItem("last_activity")) {
      localStorage.setItem("last_activity", String(Date.now()));
    }
    if (!token) setToken(savedToken);
    if (user) return;
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        setUser(JSON.parse(raw));
        return;
      }
    } catch (e) {
      // fall through to /api/auth/me
    }
    fetch(`${BACKEND_URL}/api/auth/me`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${savedToken}`,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setUser(data);
        try {
          localStorage.setItem("user", JSON.stringify(data));
        } catch (e) { /* ignore */ }
      })
      .catch(() => {});
  }, []);

  // ✅ Add ref to track if logout is in progress (prevents race conditions)
  const isLoggingOutRef = React.useRef(false);
  const activeIntervalsRef = React.useRef(new Set());

const logoutAndRedirect = (reason) => {
  if (isLoggingOutRef.current) return; // debounce
  isLoggingOutRef.current = true;

  console.warn("Forcing logout:", reason);
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("refresh_token");
  } catch (e) { /* ignore */ }

  // clear app state
  setToken(null);
  setUser(null);
  // stop background intervals if you have (clear them here)

  // redirect once
  window.location.href = "/"; // or your login route
};

  // Fetch data functions
  // ✅ FIXED: Enhanced fetchWithAuth with better 401 handling
  const fetchWithAuth = async (url, options = {}) => {
    // If already logging out, abort all requests
    if (isLoggingOutRef.current) {
      console.warn("⚠️ Logout in progress, aborting request to", url);
      return null;
    }

    const accessToken = localStorage.getItem("token") || token || null;
    if (!accessToken) {
      console.warn("❌ No token available");
      return null;
    }

    // Session inactivity check — missing timestamp is not expired
    if (typeof isSessionExpired === "function" && isSessionExpired()) {
      console.warn("⏱️ Session expired after 7 weeks");
      handleLogout();
      return null;
    }

    if (typeof updateLastActivity === "function") {
      updateLastActivity();
    }

    // Build headers
    const headers = { ...(options.headers || {}) };
    if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }
    headers["Authorization"] = `Bearer ${accessToken}`;

    try {
      const res = await fetch(url, { ...options, headers });

      // ✅ ENHANCED 401 handling with debouncing
      if (res.status === 401) {
        // Check if we're already logging out
        if (isLoggingOutRef.current) {
          return null;
        }

        let body = null;
        try {
          body = await res.clone().json().catch(() => null);
        } catch (e) { /* ignore */ }

        console.warn("🚨 401 Unauthorized received:", body);

        const detail = body && (body.detail != null ? body.detail : body);
        const detailText = typeof detail === "string" ? detail : "";
        const shouldLogout =
          detailText === "token_expired" ||
          detailText === "user_deactivated" ||
          detailText === "Could not validate credentials" ||
          detailText === "Not authenticated";
        if (shouldLogout) {
          handleLogout();
        }

        return null;
      }

      return res;
    } catch (err) {
      // Network errors
      console.error("🌐 Network error in fetchWithAuth:", err);
      throw err;
    }
  };

  const fetchProducts = async () => {
    if (!token) return;
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/active-products`);
      if (response && response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

    // Live-board HTTP poll only starts from the WS fallback (90s).
    // A second interval here used to double-hit /active + /paused.


  // Fetch products when switching to POS; live logs already load on login.
useEffect(() => {
  if (token && activeTab === "pos") {
    fetchProducts();
  }
}, [activeTab, token]);

  const fetchMembers = async () => {
    if (!token) return;
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/members`);
      if (response && response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

const transactionsInFlightRef = React.useRef(null);

const fetchTransactions = async (options = {}) => {
  if (transactionsInFlightRef.current) return transactionsInFlightRef.current;
  const silent = Boolean(options && options.silent);
  const accessToken = localStorage.getItem("token") || token;
  if (!accessToken) return [];
  if (!silent) setIsLoadingTransactions(true);

  const pending = (async () => {
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/transactions`);
      if (response && response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setTransactions(rows);
        return rows;
      }
      return [];
    } catch (error) {
      console.error("Error fetching transactions:", error);
      return [];
    } finally {
      if (!silent) setIsLoadingTransactions(false);
    }
  })();

  transactionsInFlightRef.current = pending.finally(() => {
    transactionsInFlightRef.current = null;
  });
  return transactionsInFlightRef.current;
};

const refreshLiveBoard = async (options = {}) => {
  const silent = Boolean(options && options.silent);
  const board = await fetchPlayerLogs(false, { silent });
  if (!Array.isArray(board?.transactions)) {
    return fetchTransactions({ silent });
  }
  return board.transactions;
};

  // ════════════════════════════════════════════════════════════════════
  // Realtime: WebSocket with automatic HTTP polling fallback
  // ════════════════════════════════════════════════════════════════════
  const realtimeRef = React.useRef({
    ws: null,
    reconnectTimer: null,
    backoffMs: 1000,
    refreshTimer: null,
    consecutiveFailures: 0,
    pollingInterval: null,       // HTTP polling fallback interval
    wsConnected: false,          // Track current WS state
    slowRetryTimer: null,        // Periodic WS retry even after exhausting fast attempts
    pendingResources: null,
  });

  // Shared refresh: used by WS change events. Polling uses a lighter live-board path.
  const doFullRefresh = React.useCallback(
    async (reason = "change") => {
      if (!token) return;
      if (isLoggingOutRef.current) return;
      try {
        await Promise.all([
          fetchProducts(),
          refreshLiveBoard({ silent: true }),
          !isPuncher ? refetchCashierOrList() : Promise.resolve(),
          showHistoryTable && headerDate ? fetchCashierHistory(headerDate) : Promise.resolve(),
        ]);
      } catch (e) {
        console.warn("⚠️ Refresh failed:", reason, e);
      }
    },
    [token]
  );

  const scheduleRealtimeRefresh = React.useCallback(
    (reason = "change") => {
      if (!token) return;
      if (isLoggingOutRef.current) return;
      const resource = String(reason || "").split(":")[0] || "change";
      if (!realtimeRef.current.pendingResources) {
        realtimeRef.current.pendingResources = new Set();
      }
      realtimeRef.current.pendingResources.add(resource);
      if (realtimeRef.current.refreshTimer) return;

      realtimeRef.current.refreshTimer = setTimeout(async () => {
        realtimeRef.current.refreshTimer = null;
        const resources = realtimeRef.current.pendingResources || new Set();
        realtimeRef.current.pendingResources = new Set();
        const needsLogs = resources.has("player_logs");
        const needsTx = resources.has("transactions");
        const needsOr = resources.has("or_list");
        const needsFull = [...resources].some(
          (r) => r !== "player_logs" && r !== "transactions" && r !== "or_list"
        );
        if (needsFull) {
          await doFullRefresh(reason);
          return;
        }
        await Promise.all([
          needsLogs ? fetchPlayerLogs(false, { silent: true }) : Promise.resolve(),
          needsTx && !needsLogs ? fetchTransactions({ silent: true }) : Promise.resolve(),
          needsOr && !isPuncher ? refetchCashierOrList() : Promise.resolve(),
        ]);
      }, 800);
    },
    [token, doFullRefresh]
  );

  const startPollingFallback = React.useCallback(() => {
    const wsState = realtimeRef.current;
    if (wsState.pollingInterval) return;
    console.warn("🟠 Realtime socket unavailable — using HTTP live-board poll (90s)");
    wsState.pollingInterval = setInterval(() => {
      if (isLoggingOutRef.current) return;
      fetchPlayerLogs(false, { silent: true });
    }, 90000);
  }, []);

  const stopPollingFallback = React.useCallback(() => {
    const wsState = realtimeRef.current;
    if (wsState.pollingInterval) {
      clearInterval(wsState.pollingInterval);
      wsState.pollingInterval = null;
    }
  }, []);

  React.useEffect(() => {
    if (!token) return;

    const wsState = realtimeRef.current;
    // After a working socket drops (backend restart), retry quickly.
    // After repeated handshake failures (nginx not upgrading), back off hard.
    const WS_RETRY_STEPS_MS = [2000, 5000, 15000, 30000, 60000];
    const WS_SLOW_RETRY_MS = 5 * 60 * 1000;

    const cleanup = () => {
      if (wsState.reconnectTimer) {
        clearTimeout(wsState.reconnectTimer);
        wsState.reconnectTimer = null;
      }
      if (wsState.slowRetryTimer) {
        clearTimeout(wsState.slowRetryTimer);
        wsState.slowRetryTimer = null;
      }
      if (wsState.refreshTimer) {
        clearTimeout(wsState.refreshTimer);
        wsState.refreshTimer = null;
      }
      if (wsState.pollingInterval) {
        clearInterval(wsState.pollingInterval);
        wsState.pollingInterval = null;
      }
      if (wsState.ws) {
        try {
          wsState.ws.close();
        } catch (e) {
          // ignore
        }
        wsState.ws = null;
      }
      wsState.wsConnected = false;
    };

    const buildWsUrl = () => {
      const base = new URL(BACKEND_URL);
      base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
      // Production nginx usually proxies only /api/* to FastAPI. /ws hits the SPA.
      const originPath = String(base.pathname || "/").replace(/\/$/, "");
      base.pathname = `${originPath}/api/ws`.replace(/\/{2,}/g, "/");
      if (!base.pathname.startsWith("/")) base.pathname = `/${base.pathname}`;
      base.search = "";
      return base.toString();
    };

    const scheduleReconnect = () => {
      if (wsState.reconnectTimer || wsState.slowRetryTimer) return;
      const n = wsState.consecutiveFailures || 1;
      const delay = n <= WS_RETRY_STEPS_MS.length
        ? WS_RETRY_STEPS_MS[n - 1]
        : WS_SLOW_RETRY_MS;
      const timerKey = delay >= WS_SLOW_RETRY_MS ? "slowRetryTimer" : "reconnectTimer";
      wsState[timerKey] = setTimeout(() => {
        wsState[timerKey] = null;
        if (!wsState.wsConnected && !isLoggingOutRef.current) {
          connectOnce();
        }
      }, delay);
    };

    const connectOnce = () => {
      if (isLoggingOutRef.current) return;
      if (wsState.wsConnected) return;
      if (wsState.ws && (wsState.ws.readyState === WebSocket.CONNECTING || wsState.ws.readyState === WebSocket.OPEN)) {
        return;
      }

      try {
        const wsUrl = buildWsUrl();
        const ws = new WebSocket(wsUrl);
        wsState.ws = ws;

        ws.onopen = () => {
          try {
            ws.send(JSON.stringify({ type: "auth", token }));
          } catch (e) {
            // ignore
          }
          wsState.backoffMs = 1000;
          wsState.consecutiveFailures = 0;
          wsState.wsConnected = true;
          stopPollingFallback();
          if (process.env.NODE_ENV !== "production") {
            console.log("🟢 Realtime WS connected");
          }
        };

        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg && msg.type === "ping") return;
            if (msg && msg.type === "change") {
              if (msg.resource === "or_list" && msg.action === "updated") {
                if (!isPuncher) refetchCashierOrList();
              } else {
                scheduleRealtimeRefresh(`${msg.resource}:${msg.action}`);
              }
            }
          } catch (e) {
            // ignore non-JSON
          }
        };

        ws.onclose = () => {
          const wasConnected = wsState.wsConnected;
          wsState.ws = null;
          wsState.wsConnected = false;
          if (isLoggingOutRef.current) return;
          // A drop after a successful handshake is usually a backend restart.
          if (wasConnected) {
            wsState.consecutiveFailures = 0;
          }
          wsState.consecutiveFailures = (wsState.consecutiveFailures || 0) + 1;
          startPollingFallback();
          scheduleReconnect();
        };

        ws.onerror = () => {
          // onclose handles fallback; avoid extra reconnect storms
        };
      } catch (e) {
        wsState.consecutiveFailures = (wsState.consecutiveFailures || 0) + 1;
        startPollingFallback();
        scheduleReconnect();
      }
    };

    connectOnce();
    return cleanup;
  }, [token, scheduleRealtimeRefresh, startPollingFallback, stopPollingFallback]);

// Determine golf course type from cart items
  const determineCourseFromCart = () => {
    if (!golfRatesItems || golfRatesItems.length === 0) {
      return { 
        type: "18-holes", 
        display: "18 Holes",
        totalHoles: 18,
        courses: [],
        combinedDisplay: "18 Holes"
      };
    }
    
    let totalHoles = 0;
    const courses = [];
    
    // Check all golf items in cart and accumulate holes
    for (const golfItem of golfRatesItems) {
      const product = products.find(p => p.id === golfItem.product_id);
      
      if (product && product.name) {
        const productName = product.name.toLowerCase();
        let holes = 18; // default
        let courseType = "18-holes";
        
        // Detect hole count from product name
        if (productName.includes("9 holes") || 
            productName.includes("9-holes") || 
            productName.includes("nine holes")) {
          holes = 9;
          courseType = "9-holes";
        } else if (productName.includes("18 holes") || 
                   productName.includes("18-holes") || 
                   productName.includes("eighteen holes")) {
          holes = 18;
          courseType = "18-holes";
        }
        
        // Add to courses array with quantity consideration
        for (let i = 0; i < golfItem.quantity; i++) {
          courses.push({
            name: product.name,
            type: courseType,
            holes: holes
          });
          totalHoles += holes;
        }
      }
    }
    
    // Determine combined course type based on total holes
    let combinedType = "18-holes";
    let combinedDisplay = `${totalHoles} Holes`;
    
    if (totalHoles === 9) {
      combinedType = "9-holes";
      combinedDisplay = "9 Holes";
    } else if (totalHoles === 18) {
      combinedType = "18-holes";
      combinedDisplay = "18 Holes";
    } else if (totalHoles === 27) {
      combinedType = "27-holes";
      combinedDisplay = "27 Holes (9H + 18H)";
    } else {
      combinedType = `${totalHoles}-holes`;
      combinedDisplay = `${totalHoles} Holes (Combined)`;
    }
    
    return { 
      type: combinedType,
      display: combinedDisplay,
      totalHoles: totalHoles,
      courses: courses,
      combinedDisplay: combinedDisplay
    };
  };

  const fetchLowStockItems = async () => {
    if (!token) return;
    try {
      const response = await fetchWithAuth(
        `${BACKEND_URL}/api/inventory/low-stock`
      );
      if (response && response.ok) {
        const data = await response.json();
        setLowStockItems(data);
      }
    } catch (error) {
      console.error("Error fetching low stock items:", error);
    }
  };

const searchMembers = async (query) => {
  if (!query.trim() || !token) {
    setMemberSearchResults([]);
    return;
  }

  try {
    const response = await fetchWithAuth(
      `${BACKEND_URL}/api/members` // Get all members
    );
    if (response && response.ok) {
      const data = await response.json();
      // Filter by name only (case-insensitive)
      const filtered = data.filter(member =>
        member.name.toLowerCase().includes(query.toLowerCase())
      );
      setMemberSearchResults(filtered);
    }
  } catch (error) {
    console.error("Error searching members:", error);
  }
};



  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchMembers(memberSearch);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [memberSearch, token]);



  // 7-week POS session. Login resets this clock via updateLastActivity().
  const SESSION_TIMEOUT_MS = 7 * 7 * 24 * 60 * 60 * 1000;

  const updateLastActivity = () => {
    try {
      localStorage.setItem("last_activity", String(Date.now()));
    } catch (e) {
      console.warn("Could not set last_activity", e);
    }
  };

  const clearLastActivity = () => {
    try {
      localStorage.removeItem("last_activity");
    } catch (e) {}
  };

  const isSessionExpired = () => {
    try {
      const raw = localStorage.getItem("last_activity");
      if (!raw) return false;
      const ts = Number(raw);
      if (!ts) return false;
      return Date.now() - ts > SESSION_TIMEOUT_MS;
    } catch (e) {
      return false;
    }
  };


  

// TODO: Login/logout handlers
  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem("token", userToken);
    localStorage.setItem("user", JSON.stringify(userData));
    // New login starts a fresh 7-week session
    updateLastActivity();

    // Soft-delete: clear stale notifications on every login so the list starts fresh.
    // Underlying sales-report data in the DB is NOT affected.
    setNotifications([]);
    setSuppressedOvertimeKeys(new Set());
    setOvertimeResetMap({});
    if (window._overtimeSoundPlayed) window._overtimeSoundPlayed.clear();

    // Clear dismissed notifications on the backend for a fresh session
    fetch(`${BACKEND_URL}/api/dismissed-notifications`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${userToken}` },
    }).catch(() => {});
  };

  // Set default tab based on user role
  useEffect(() => {
    if (user?.role === "cashier" || user?.role === "puncher") {
      setActiveTab("pos");
    }
  }, [user]);

  

  // ✅ Centralized logout function with interval cleanup
  const handleLogout = React.useCallback(() => {
    if (isLoggingOutRef.current) {
      console.log("⚠️ Logout already in progress, skipping");
      return;
    }
    
    isLoggingOutRef.current = true;
    console.log("🔒 Logging out and cleaning up...");

    // Best-effort: also clear upstream QA session/token
    try {
      const currentJwt = localStorage.getItem("token");
      if (currentJwt) {
        fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${currentJwt}`,
          },
        }).catch(() => {});
        fetch(`${BACKEND_URL}/api/external/logout`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${currentJwt}`,
          },
        }).catch((e) => {
          console.warn("⚠️ External logout failed (ignored):", e);
        });
      }
    } catch (e) {
      console.warn("⚠️ Could not initiate external logout (ignored):", e);
    }

    // Clear all active intervals
    activeIntervalsRef.current.forEach(id => {
      clearInterval(id);
      console.log(`✅ Cleared interval ${id}`);
    });
    activeIntervalsRef.current.clear();

    // Clear storage
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("last_activity");
      localStorage.removeItem("refresh_token");
    } catch (e) {
      console.error("Error clearing localStorage:", e);
    }

    // Clear state
    setToken(null);
    setUser(null);
    setCart([]);
    setSelectedMember(null);
    setOrNumber("");

    // Reset flag after small delay
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  }, []);

  const memberSearchInputRef = React.useRef(null);
  const walkinInputRef = React.useRef(null);

  // Auto-focus scheduler for walk-in input (2 minute countdown by default)
  const walkinAutoFocusIntervalRef = React.useRef(null);
  const walkinAutoFocusRemainingRef = React.useRef(0);
  const [walkinAutoFocusRemaining, setWalkinAutoFocusRemaining] = useState(0);

  const clearWalkinAutoFocus = () => {
    if (walkinAutoFocusIntervalRef.current) {
      clearInterval(walkinAutoFocusIntervalRef.current);
      walkinAutoFocusIntervalRef.current = null;
    }
    walkinAutoFocusRemainingRef.current = 0;
    setWalkinAutoFocusRemaining(0);
  };

const scheduleWalkinAutoFocus = (seconds = 5, { force = false } = {}) => {
  // If already scheduled and caller didn't request force, do nothing (prevents restart on multiple addToCart)
  if (walkinAutoFocusIntervalRef.current && !force) return;

  clearWalkinAutoFocus();

  // only schedule when walk-in selected and name is empty
  if (!(selectedMember?.type === "walkin" && !(selectedMember.name || "").trim())) return;

  walkinAutoFocusRemainingRef.current = seconds;
  setWalkinAutoFocusRemaining(seconds);

  walkinAutoFocusIntervalRef.current = setInterval(() => {
    walkinAutoFocusRemainingRef.current -= 1;
    setWalkinAutoFocusRemaining(walkinAutoFocusRemainingRef.current);

    if (walkinAutoFocusRemainingRef.current <= 0) {
      // Final action: focus input and optionally auto-select the first search result
      clearWalkinAutoFocus();

      try {
        if (selectedMember?.type === "walkin" && !(selectedMember.name || "").trim()) {
          if (Array.isArray(memberSearchResults) && memberSearchResults.length > 0) {
            setSelectedMember(memberSearchResults[0]);
          }
          if (walkinInputRef.current && typeof walkinInputRef.current.focus === "function") {
            walkinInputRef.current.focus();
          }
        }
      } catch (e) {
        console.warn("walkin auto-focus failed:", e);
      }
    }
  }, 1000);
};

  // Cancel scheduler if user types a walk-in name or selects a non-walkin member
  useEffect(() => {
    if (selectedMember?.type !== "walkin" || (selectedMember?.name || "").trim()) {
      clearWalkinAutoFocus();
    }
    return () => {};
  }, [selectedMember?.type, selectedMember?.name]);

  // cleanup on unmount
  useEffect(() => {
    return () => clearWalkinAutoFocus();
  }, []);



  // Cart functions
const addToCart = (product) => {
  // Only enforce stock for items, not services
  if (itemCategories.includes(product.category)) {
    const stock = product?.stock_quantity ?? Infinity;
    if (stock <= 0) {
      toast({
        title: "Out of stock",
        description: `${product.name} is currently unavailable.`,
      });
      return;
    }
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (item) => item.product_id === product.id
      );
      if (existingItem) {
        const nextQty = Math.min(existingItem.quantity + 1, stock);
        if (nextQty === existingItem.quantity) {
          toast({
            title: "Stock limit reached",
            description: `Only ${stock} in stock for ${product.name}.`,
          });
          return prevCart;
        }
        return prevCart.map((item) =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [
          ...prevCart,
          {
            product_id: product.id,
            product_name: product.name,
            price: product.price,
            quantity: 1,
          },
        ];
      }
    });
  } else {
    // For services, just add to cart (no stock check)
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (item) => item.product_id === product.id
      );
      if (existingItem) {
        return prevCart.map((item) =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [
          ...prevCart,
          {
            product_id: product.id,
            product_name: product.name,
            price: product.price,
            quantity: 1,
          },
        ];
      }
    });
  }
  // After adding to cart:
  // - if walk-in selected and name empty: schedule a 2-minute countdown then focus (non-obtrusive)
  // - else immediately focus the member search
  if (selectedMember?.type === "walkin" && !(selectedMember.name || "").trim()) {
    // schedule 5 seconds to auto-focus (non-obtrusive)
    scheduleWalkinAutoFocus(5);
    // optionally show a subtle toast letting user know (so it's not surprising)
  } else {
    // immediate focus for member search if no selected member
    if ((!selectedMember || selectedMember?.type === "member") && memberSearchInputRef.current) {
        scheduleWalkinAutoFocus(5);
    }
  }

  


};





  // ✅ FIXED: Session timeout with proper cleanup
  React.useEffect(() => {
    if (!token) return;

    updateLastActivity();

    const onActivity = () => updateLastActivity();
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "click"];

    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const sessionCheckId = setInterval(() => {
      if (isLoggingOutRef.current) return;
      
      if (!localStorage.getItem("last_activity")) return;
      
      if (isSessionExpired()) {
        console.warn("⏱️ Session expired after 7 weeks");
        handleLogout();
      }
    }, 30 * 1000); // Check every 30s

    activeIntervalsRef.current.add(sessionCheckId);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(sessionCheckId);
      activeIntervalsRef.current.delete(sessionCheckId);
    };
  }, [token, handleLogout]);


const updateCartItemQuantity = (productId, newQuantity) => {
  const product = products.find((p) => p.id === productId);
  // If it's a service, allow any quantity >= 1
  if (product && !itemCategories.includes(product.category)) {
    if (newQuantity <= 0) {
      setCart((prevCart) =>
        prevCart.filter((item) => item.product_id !== productId)
      );
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product_id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
    return;
  }
  // ...existing item logic for stock enforcement...
  const stock = product?.stock_quantity ?? Infinity;
  if (newQuantity <= 0) {
    setCart((prevCart) =>
      prevCart.filter((item) => item.product_id !== productId)
    );
    return;
  }
  const clamped = Math.min(newQuantity, stock);
  if (newQuantity > stock) {
    toast({
      title: "Stock limit reached",
      description: `Only ${stock} in stock for ${product?.name || "item"}.`,
    });
  }
  setCart((prevCart) =>
    prevCart.map((item) =>
      item.product_id === productId ? { ...item, quantity: clamped } : item
    )
  );
};

  const removeFromCart = (productId) => {
    setCart((prevCart) =>
      prevCart.filter((item) => item.product_id !== productId)
    );
  };

  const clearCart = () => {
    setCart([]);
    setSelectedMember(null);
    setMemberSearch("");
    setMemberSearchResults([]);
    setPaymentStatus("");
    setOrNumber("");
  };

// Calculate totals
const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

// ✅ UPDATED: Allow stacking of member + ID discounts
const ID_DISCOUNTS = { pwd: 20, senior: 20 };

// Member discount (only applies if selected client is a MEMBER)
const memberDiscountPct = (selectedMember && selectedMember.type !== "walkin")
  ? Number(selectedMember.discount_percentage || 0)
  : 0;

// ID discount (PWD/Senior - applies ONLY for WALK-IN clients)
const idDiscountPct = (idType && idType !== "none") ? (ID_DISCOUNTS[idType] || 0) : 0;


// ✅ NEW: Stack both discounts (additive)
const appliedDiscountPercentage = Number(memberDiscountPct || 0) + Number(idDiscountPct || 0);

// ✅ Family Discount Calculation - applies discount only to selected items/quantities for eligible persons
const familyDiscountAmount = React.useMemo(() => {
  if (!familyDiscountEnabled || appliedDiscountPercentage <= 0) return 0;
  
  let totalDiscount = 0;
  cart.forEach(item => {
    const selection = itemDiscountSelections[item.product_id];
    const discountQty = selection?.discountQty || 0;
    if (discountQty > 0) {
      // Calculate discount for the selected quantity of this item
      const itemDiscountableAmount = item.price * discountQty;
      totalDiscount += itemDiscountableAmount * (appliedDiscountPercentage / 100);
    }
  });
  return totalDiscount;
}, [familyDiscountEnabled, cart, itemDiscountSelections, appliedDiscountPercentage]);

// Calculate discount amount - use family discount if enabled, otherwise standard discount
const discountAmount = familyDiscountEnabled 
  ? familyDiscountAmount 
  : subtotal * (appliedDiscountPercentage / 100);

// Total after discount (never negative)
const total = Math.max(0, parseFloat((subtotal - discountAmount).toFixed(2)));

// Calculate how many items are selected for discount (for display)
const totalDiscountedItems = React.useMemo(() => {
  if (!familyDiscountEnabled) return 0;
  return Object.values(itemDiscountSelections).reduce((sum, sel) => sum + (sel?.discountQty || 0), 0);
}, [familyDiscountEnabled, itemDiscountSelections]);

React.useEffect(() => {
  // only clear ID fields when no member is selected (not when a member is chosen)
  if (!selectedMember) {
    setIdType("none");
    setIdNumber("");
  }
}, [selectedMember]);

// Reset family discount selections when cart changes
React.useEffect(() => {
  // Clean up selections for items no longer in cart
  setItemDiscountSelections(prev => {
    const cartProductIds = new Set(cart.map(item => item.product_id));
    const newSelections = {};
    Object.keys(prev).forEach(productId => {
      if (cartProductIds.has(productId)) {
        newSelections[productId] = prev[productId];
      }
    });
    return newSelections;
  });
}, [cart]);

// Reset family discount when ID type changes to none
React.useEffect(() => {
  if (idType === "none") {
    setFamilyDiscountEnabled(false);
    setItemDiscountSelections({});
    setEligibleDiscountPersons(1);
    setTotalFamilyMembers(1);
  }
}, [idType]);


  // Expand cart items to individual instances for discount modal
  const getExpandedCartItems = () => {
    const expanded = [];
    cart.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        expanded.push({
          ...item,
          instanceKey: `${item.product_id}-${i}`,
          instanceIndex: i,
          singlePrice: item.price
        });
      }
    });
    return expanded;
  };

  // Calculate item discount for individual instances
  const calculateItemDiscount = (instanceKey) => {
    const itemDiscount = itemDiscounts[instanceKey];
    let discountPct = selectedMember?.discount_percentage || 0;
    
    if (itemDiscount) {
      const idDiscount = itemDiscount.type === 'pwd' ? 20 : itemDiscount.type === 'senior' ? 20 : 0;
      discountPct = Math.max(discountPct, idDiscount);
    }
    
    return discountPct;
  };

  // Calculate totals with item-level discounts
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;

    const expandedItems = getExpandedCartItems();
    expandedItems.forEach(item => {
      const itemTotal = item.singlePrice;
      const discountPct = calculateItemDiscount(item.instanceKey);
      const itemDiscountAmount = (itemTotal * discountPct) / 100;
      
      subtotal += itemTotal;
      totalDiscount += itemDiscountAmount;
    });

    // ✅ Add extra golf group player cost (these are virtual tokens not in the cart)
    const golfExtraCost = golfReservationStats?.extraCost || 0;
    subtotal += golfExtraCost;

    // ✅ Add extra DR group player cost (these are virtual tokens not in the cart)
    const drExtraCost = drReservationStats?.extraCost || 0;
    subtotal += drExtraCost;

    return {
      subtotal,
      discountAmount: totalDiscount,
      total: subtotal - totalDiscount
    };
  };

  const { subtotal: newSubtotal, discountAmount: newDiscountAmount, total: newTotal } = calculateTotals();

  // Payment functions
  async function refreshAfterPayment() {
  await Promise.all([
    fetchProducts(),
    refreshLiveBoard(),
  ]);
}

  // --- offline queue + health check helpers ---
  const PENDING_TX_KEY = "piligrove_pending_transactions";

  const pushPendingTransaction = (tx) => {
    try {
      const arr = JSON.parse(localStorage.getItem(PENDING_TX_KEY) || "[]");
      arr.push({ tx, created_at: new Date().toISOString() });
      localStorage.setItem(PENDING_TX_KEY, JSON.stringify(arr));
      return true;
    } catch (e) {
      console.error("Failed to enqueue pending transaction", e);
      return false;
    }
  };

  const flushPendingTransactions = async () => {
    try {
      const arr = JSON.parse(localStorage.getItem(PENDING_TX_KEY) || "[]");
      if (!arr || arr.length === 0) return;
      for (const entry of [...arr]) {
        try {
          const res = await fetchWithAuth(`${BACKEND_URL}/api/transactions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(entry.tx),
          });
          if (res && res.ok) {
            // ✅ FIXED: Remove from localStorage immediately after local backend succeeds
            // This prevents duplicate processing when QA sync fails
            const current = JSON.parse(localStorage.getItem(PENDING_TX_KEY) || "[]");
            const idx = current.findIndex(e => e.created_at === entry.created_at && JSON.stringify(e.tx) === JSON.stringify(entry.tx));
            if (idx >= 0) {
              current.splice(idx, 1);
              localStorage.setItem(PENDING_TX_KEY, JSON.stringify(current));
            }

            // After local success, also push to QA (best effort, don't block)
            try {
              const extRes = await fetchWithAuth(`${BACKEND_URL}/api/save-transaction`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ transaction: entry.tx }),
              });
              if (!extRes || !extRes.ok) {
                console.warn("QA sync failed for flushed transaction, but local transaction was saved");
              }
            } catch (e) {
              console.warn("QA sync error for flushed transaction:", e);
            }
          } else {
            // stop on first failure (server still not accepting)
            break;
          }
        } catch (err) {
          // network still down — stop and retry later
          break;
        }
      }
    } catch (e) {
      console.error("flushPendingTransactions error", e);
    }
  };

  const isBackendAlive = async (timeoutMs = 2000) => {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(`${BACKEND_URL}/api/health`, { method: "GET", signal: controller.signal });
      clearTimeout(id);
      return res && res.ok;
    } catch (e) {
      return false;
    }
  };

  // ✅ FIXED: Pending transactions flush with cleanup
  React.useEffect(() => {
    const flushId = setInterval(() => {
      if (isLoggingOutRef.current) return;
      flushPendingTransactions();
    }, 60 * 1000);

    activeIntervalsRef.current.add(flushId);

    return () => {
      clearInterval(flushId);
      activeIntervalsRef.current.delete(flushId);
    };
  }, [token]); // ✅ Depend on token to restart when user logs in


React.useEffect(() => {
  if (!selectedMember || selectedMember.type !== "walkin") {
    setIdType("none");
    setIdNumber("");
  }
}, [selectedMember]);



const processPayment = async () => {
  // ✅ FIXED: Prevent multiple simultaneous payment processing
  if (isProcessingPaymentRef.current) {
    console.warn("Payment already in progress, ignoring duplicate call");
    return;
  }
  isProcessingPaymentRef.current = true;
  setIsProcessingPayment(true); // ✅ Show loading indicator immediately

  try { // ✅ Outer try/finally ensures isProcessingPaymentRef is ALWAYS reset

  // Initial validations (use toast for consistent UX)
  if (cart.length === 0) {
    toast({ title: "Cart empty", description: "Please add items to cart before checkout.", variant: "destructive" });
    return;
  }

  if (!selectedMember) {
    toast({ title: "Member required", description: "Please select a member before completing the purchase.", variant: "destructive" });
    return;
  }



  // Early completeness check for Driving Range multi-slot reservations
  if (drivingRangeItems.length > 0 && !allDrivingRangeSlotsFilled) {
    toast({
      title: "Incomplete Driving Range Reservations",
      description: `Fill all ${drReservationStats.totalSlots} slot${drReservationStats.totalSlots === 1 ? '' : 's'} before confirming payment. (${drReservationStats.filledSlots}/${drReservationStats.totalSlots} filled)`,
      variant: "destructive",
    });
    return;
  }

  // Early completeness check for Golf multi-slot reservations
  if (golfRatesItems.length > 0 && !allGolfSlotsFilled) {
    toast({
      title: "Incomplete Golf Reservations",
      description: `Fill all ${golfReservationStats.totalSlots} slot${golfReservationStats.totalSlots === 1 ? '' : 's'} before confirming payment. (${golfReservationStats.filledSlots}/${golfReservationStats.totalSlots} filled)`,
      variant: "destructive",
    });
    return;
  }

  // Ensure we have latest transactions before duplicate OR check
  try {
    await fetchTransactions();
  } catch (e) {
    console.warn("Could not refresh transactions before validation:", e);
  }

  // ✅ HOISTED: Build effective DR selection outside the if-block so it is
  // accessible both during validation AND when building the transaction payload.
  const buildDrEffective = () => {
    let eff = { ...(newPlayerLog || {}) };
    try {
      const entries = (drReservationStats && Array.isArray(drReservationStats.reservedEntries)) ? drReservationStats.reservedEntries : [];
      const needs = !eff.bay_number || !eff.ui_locked || !eff.time_in || !eff.time_out || !String(eff.player_name || "").trim();
      if (entries.length > 0 && needs) {
        let chosen = null;
        if (String(eff.player_name || "").trim()) {
          const nameLc = String(eff.player_name).trim().toLowerCase();
          chosen = entries.find(e => String(e.player_name || "").trim().toLowerCase() === nameLc) || null;
        }
        if (!chosen) chosen = entries[0] || null;
        if (chosen) {
          eff = {
            ...eff,
            player_name: eff.player_name || chosen.player_name || "",
            bay_number: eff.bay_number || chosen.bay_number || "",
            time_in: eff.time_in || chosen.time_in || "",
            time_out: eff.time_out || chosen.time_out || "",
            ui_label_start: eff.ui_label_start || chosen.ui_label_start || "",
            ui_label_end: eff.ui_label_end || chosen.ui_label_end || "",
            ui_locked: !!(eff.ui_locked || chosen.ui_locked),
            selected_service: eff.selected_service || chosen.selected_service || null,
          };
        }
      }
    } catch (e) {
      console.warn("DR hydration from summary failed:\n", e);
    }
    return eff;
  };
  const drEffective = buildDrEffective();

  // Driving range specific validations
  if (drivingRangeItems.length > 0) {

    // Require player name (now uses hydrated drEffective)
    const drPlayerName = String(drEffective?.player_name || "").trim();
    if (!drPlayerName) {
      toast({
        title: "Player Name Required",
        description: "Enter a player name before confirming payment for driving range.",
        variant: "destructive",
      });
      return;
    }

    // Require bay selection + reservation (ui_locked indicates slot reserved locally)
    if (!drEffective.bay_number) {
      toast({
        title: "Bay Not Selected",
        description: "Select a bay and reserve a slot before confirming payment.",
        variant: "destructive",
      });
      return;
    }
    if (!drEffective.ui_locked) {
      toast({
        title: "Slot Not Reserved",
        description: "Reserve the bay/time slot (it must show as booked) before payment.",
        variant: "destructive",
      });
      return;
    }

    // Require time_in / time_out (reservation times)
    if (!drEffective.time_in || !drEffective.time_out) {
      toast({
        title: "Time Slot Required",
        description: "Please select and reserve a time slot for driving range booking.",
        variant: "destructive",
      });
      return;
    }

    // Validate time slot availability one final time
    {
      const isoToHHmm = (s) => {
        try {
          if (!s) return "";
          if (/^\d{1,2}:\d{2}/.test(String(s))) return String(s);
          if (String(s).includes("T")) {
            const d = new Date(s);
            if (!isNaN(d)) {
              const ph = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Manila" }));
              return `${String(ph.getHours()).padStart(2, "0")}:${String(ph.getMinutes()).padStart(2, "0")}`;
            }
          }
        } catch {}
        return "";
      };
      const startForCheck = isoToHHmm(drEffective.time_in) || manualTime || "";
      if (!isTimeSlotAvailable(drEffective.bay_number, startForCheck, accumulatedDurationMinutes)) {
      toast({
        title: "Time Slot No Longer Available",
        description: "The selected time slot is no longer available. Please choose another time.",
        variant: "destructive",
      });
      return;
    }
    }

    // Validate guest bookings (if present) each must have guest_name and time_in/time_out
    if (Array.isArray(newPlayerLog.guest_bookings) && newPlayerLog.guest_bookings.length > 0) {
      const invalidGuest = newPlayerLog.guest_bookings.find(g => !String(g.guest_name || g.player_name || "").trim() || !g.time_in || !g.time_out);
      if (invalidGuest) {
        toast({
          title: "Incomplete Guest Entry",
          description: "Each guest must have a name and reserved time slot.",
          variant: "destructive",
        });
        return;
      }
    }
  }

  // OR number handling:
  // OR assignment is now handled in the Cashier Transactions table/modal.
  // During payment/booking creation we keep OR as null to avoid duplicate checks and allow processing.
  const effectiveOrNumber = null;

// Golf rates specific validations
if (golfRatesItems.length > 0) {
  // Require primary golf player name
  const golfPlayerName = String(newGolfReservation?.player_name || selectedMember?.name || "").trim();
  if (!golfPlayerName) {
    toast({
      title: "Golf Player Name Required",
      description: "Enter the main golf player name before confirming payment.",
      variant: "destructive",
    });
    return;
  }

  // main tee time required (skip if all tokens are reserved and synced as guests)
  const guestsForGolf = Array.isArray(newGolfReservation.golf_guest_bookings)
    ? newGolfReservation.golf_guest_bookings
    : [];
  const allTokensSyncedAsGuests =
    golfReservationStats.totalSlots > 0 &&
    golfReservationStats.filledSlots === golfReservationStats.totalSlots &&
    guestsForGolf.length === golfReservationStats.totalSlots;
  if (!allTokensSyncedAsGuests && (!newGolfReservation.tee_time || !newGolfReservation.end_time)) {
    toast({
      title: "Tee Time Required",
      description: "Please select a tee time for golf booking.",
      variant: "destructive",
    });
    return;
  }

  // validate additional golf guests (if any)
  const guests = guestsForGolf;

  const invalidGuests = guests
    .map((g, idx) => {
      const name = (g.guest_name || g.player_name || "").trim();
      const tee = (g.tee_time || g.time_in || "").toString().trim();
      const end = (g.end_time || g.time_out || "").toString().trim();
      return { idx, missingName: !name, missingTee: !tee, missingEnd: !end };
    })
    .filter(g => g.missingName || g.missingTee || g.missingEnd);

  if (invalidGuests.length > 0) {
    const first = invalidGuests[0];
    const parts = [];
    if (first.missingName) parts.push("name");
    if (first.missingTee) parts.push("tee time");
    if (first.missingEnd) parts.push("end time");
    toast({
      title: "Incomplete Guest Booking",
      description: `Guest #${first.idx + 1} is missing ${parts.join(" and ")}. Please fill it before confirming payment.`,
      variant: "destructive",
    });
    return;
  }
}

// ✅ Validate that all group slots are reserved before payment
if (golfRatesItems.length > 0 && golfGroupBookingCount > 1) {
  const bookingsCount = Array.isArray(newGolfReservation?.golf_guest_bookings) ? newGolfReservation.golf_guest_bookings.length : 0;
  if (bookingsCount < golfGroupBookingCount) {
    console.warn(`⚠️ Golf group has ${golfGroupBookingCount} players but only ${bookingsCount} slots are reserved`);
    toast({
      title: "Incomplete Group Booking",
      description: `You have ${golfGroupBookingCount} players in the group but only ${bookingsCount} slots are reserved. Please reserve tee times for all players.`,
      variant: "destructive",
    });
    setIsProcessingPayment(false);
    return;
  }
}

// ✅ CRITICAL: Capture golf booking snapshot NOW (validation just confirmed this data exists).
// React effects can clear newGolfReservation.golf_guest_bookings asynchronously, so
// we take a deep snapshot here to prevent stale/empty data in the transaction payload.
const _golfGuestBookingsSnapshot = (golfRatesItems.length > 0 && Array.isArray(newGolfReservation?.golf_guest_bookings))
  ? JSON.parse(JSON.stringify(newGolfReservation.golf_guest_bookings))
  : [];
console.log(`🏌️ Golf guest bookings snapshot captured: ${_golfGuestBookingsSnapshot.length} entries (expected: ${golfGroupBookingCount})`);


  console.log("🚀 Starting payment process with:", {
    cart: cart.length + " items",
    member: selectedMember.name,
    paymentMethod: paymentMethod,
    total: total.toFixed(2),
    drivingRangeReservation: drivingRangeItems.length > 0 ? (() => {
      const eff = (typeof drEffective !== 'undefined') ? drEffective : (typeof buildDrEffective === 'function' ? buildDrEffective() : (newPlayerLog || {}));
      return { bay: eff.bay_number, timeIn: eff.time_in, timeOut: eff.time_out };
    })() : null
  });

  setPaymentStatus("");

  // Helper: convert "HH:mm" to Manila ISO (+08:00)
const hhmmToManilaIso = (hhmm) => {
  if (!hhmm) return null;
  if (typeof hhmm !== "string") return null;
  if (hhmm.includes("T") || hhmm.includes("+") || hhmm.endsWith("Z")) return hhmm;
  const [h, m] = hhmm.split(":").map(Number);
  const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
  const dt = new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
  try { return toManilaOffsetIso(dt); } catch { return dt.toISOString().replace("Z", "+08:00"); }
};

// Safe add minutes to a Manila-ISO string
const addMinutesIsoPH = (iso, minutes) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  d.setMinutes(d.getMinutes() + Number(minutes || 0));
  try { return toManilaOffsetIso(d); } catch { return d.toISOString().replace("Z", "+08:00"); }
};

  
        // Compute main Driving Range start/end preferring reserved ISO then manualTime
      const __drEff = (drivingRangeItems.length > 0) ? ((typeof drEffective !== 'undefined') ? drEffective : (typeof buildDrEffective === 'function' ? buildDrEffective() : (newPlayerLog || {}))) : null;
      const mainDRStartISO = drivingRangeItems.length > 0 ? ((__drEff && __drEff.time_in) || manualTime) : null;

  // Duration: prefer selected service from TimeSlotPicker, then accumulated
  const mainDRDurationMins = drivingRangeItems.length > 0
    ? Number(newPlayerLog?.selected_service?.duration || accumulatedDurationMinutes || 60)
    : 0;

  const mainDREndISO = mainDRStartISO
    ? ((__drEff && __drEff.time_out) || addMinutesIsoPH(mainDRStartISO, mainDRDurationMins))
    : null;

    // Clean guest_bookings: remove duplicates and prevent a guest entry identical to main
    const drGuestBookingsRaw = Array.isArray(newPlayerLog?.guest_bookings)
      ? newPlayerLog.guest_bookings
      : [];
    const mainKey = [
      String((__drEff && __drEff.bay_number) || newPlayerLog?.bay_number || "").trim(),
      String((__drEff && __drEff.player_name) || newPlayerLog?.player_name || "").trim().toLowerCase(),
      String(mainDRStartISO || "").trim(),
      String(mainDREndISO || "").trim(),
    ].join("|");

    const seenGuestKeys = new Set();
    const drGuestBookingsClean = drGuestBookingsRaw.filter((g) => {
      const gName = String(g?.guest_name || g?.player_name || "").trim();
      const gBay = String(g?.bay_number || "").trim();
      const gIn = String(g?.time_in || "").trim();
      const gOut = String(g?.time_out || "").trim();

      // drop incomplete entries early
      if (!gName || !gBay || !gIn) return false;

      const guestKey = [gBay, gName.toLowerCase(), gIn, gOut].join("|");
      if (guestKey === mainKey) return false;
      if (seenGuestKeys.has(guestKey)) return false;
      seenGuestKeys.add(guestKey);
      return true;
    });


    // Create transaction object
    const transactionId =
      (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
        ? `txn_${crypto.randomUUID()}`
        : `txn_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    const normalizedItems = cart.map(it => ({
      product_id: String(it.product_id ?? it.productId ?? it.id ?? ""),
      product_name: String(it.product_name ?? it.name ?? ""),
      unit_price: Number(it.price ?? it.unit_price ?? 0),
      price: Number(it.price ?? it.unit_price ?? 0),
      quantity: Number(it.quantity ?? it.qty ?? 1),
    }));

    // ✅ Append extra golf group player items (not in cart, tracked via golfReservationStats)
    const extraGolfItems = Array.isArray(golfReservationStats?.extraItems) ? golfReservationStats.extraItems : [];
    if (extraGolfItems.length > 0) {
      // Aggregate extras by product_id
      const extraAgg = {};
      extraGolfItems.forEach(ex => {
        const pid = String(ex.product_id || "");
        if (!pid) return;
        if (extraAgg[pid]) {
          extraAgg[pid].quantity += 1;
        } else {
          extraAgg[pid] = { ...ex, quantity: 1 };
        }
      });
      // Merge into normalizedItems: increment existing or add new
      Object.values(extraAgg).forEach(extra => {
        const existingIdx = normalizedItems.findIndex(it => it.product_id === extra.product_id);
        if (existingIdx !== -1) {
          normalizedItems[existingIdx] = {
            ...normalizedItems[existingIdx],
            quantity: normalizedItems[existingIdx].quantity + extra.quantity,
          };
        } else {
          normalizedItems.push({
            product_id: String(extra.product_id),
            product_name: String(extra.product_name || ""),
            unit_price: Number(extra.price || 0),
            price: Number(extra.price || 0),
            quantity: extra.quantity,
          });
        }
      });
    }

    // ✅ Append extra DR group player items (not in cart, tracked via drReservationStats)
    const extraDrItems = Array.isArray(drReservationStats?.extraItems) ? drReservationStats.extraItems : [];
    if (extraDrItems.length > 0) {
      const drExtraAgg = {};
      extraDrItems.forEach(ex => {
        const pid = String(ex.product_id || "");
        if (!pid) return;
        if (drExtraAgg[pid]) {
          drExtraAgg[pid].quantity += 1;
        } else {
          drExtraAgg[pid] = { ...ex, quantity: 1 };
        }
      });
      Object.values(drExtraAgg).forEach(extra => {
        const existingIdx = normalizedItems.findIndex(it => it.product_id === extra.product_id);
        if (existingIdx !== -1) {
          normalizedItems[existingIdx] = {
            ...normalizedItems[existingIdx],
            quantity: normalizedItems[existingIdx].quantity + extra.quantity,
          };
        } else {
          normalizedItems.push({
            product_id: String(extra.product_id),
            product_name: String(extra.product_name || ""),
            unit_price: Number(extra.price || 0),
            price: Number(extra.price || 0),
            quantity: extra.quantity,
          });
        }
      });
    }

    // client-side sanity check (prevents Pydantic 422)
    for (let i = 0; i < normalizedItems.length; i++) {
      const it = normalizedItems[i];
      if (!it.product_id || !it.product_name || !Number.isFinite(it.price) || !Number.isFinite(it.quantity)) {
        alert(`Invalid cart item at index ${i}. Ensure product_id, product_name, price and quantity are set.`);
        setIsProcessingPayment(false);
        return;
      }
    }

// Build main golf times (ISO if reserved locally, fallback to first booking entry)
// ✅ Use snapshot instead of re-reading from state (prevents race condition with React effects)
const firstGolfBooking = _golfGuestBookingsSnapshot[0] || null;
const mainGolfStartIso = newGolfReservation?.tee_time || (reservedGolfSlot?.tee_time ?? null) || (firstGolfBooking?.tee_time ?? null);
const mainGolfEndIso = newGolfReservation?.end_time || (reservedGolfSlot?.end_time ?? null) || (firstGolfBooking?.end_time ?? null);

// Normalize golf guests (UI -> payload)
// ✅ Use snapshot to avoid stale/empty data from React effect clearing
const golfGuestsNormalized = _golfGuestBookingsSnapshot.map(g => {
  return {
  guest_name: g.guest_name ?? null,
  product_id: g.product_id ?? (g.service_key ? String(g.service_key).split("#")[0] : null),
  product_name: g.product_name ?? null,
  service_key: g.service_key ?? null,
  tee_time: g.tee_time ?? null,
  end_time: g.end_time ?? null,
  holes: (g.holes !== undefined && g.holes !== null) ? Number(g.holes) : (g.total_holes !== undefined ? Number(g.total_holes) : null)
  };
});

// Build transaction payload
const transaction = {
  id: transactionId,
  items: normalizedItems,
  items_normalized: normalizedItems,
  subtotal: parseFloat(newSubtotal.toFixed(2)),
  discount_amount: parseFloat(newDiscountAmount.toFixed(2)),
  total_amount: parseFloat(newTotal.toFixed(2)),
  payment_status: "Confirmed",
  payment_method: paymentMethod,
  payment_gateway: selectedBank || null,
  account_number: bankAccountNumber || null,
  cardtype_id: cardType || null, // Numeric card type ID (1-6)
  card_number: cardNumber || null, // Nullable
  batch_no: batchNumber || null, // Card batch number (nullable)
  transaction_number: transactionNumber || null, // Nullable (Card Trace No)
  or_number: effectiveOrNumber,
  bay_number: (__drEff && __drEff.bay_number) || newPlayerLog.bay_number,
  id_type: null, // No longer using global ID type
  id_number: null, // No longer using global ID number
  item_discounts: itemDiscounts, // Item-level discount information
  member_id: selectedMember.id,
  
  // ✅ NEW: Conditional member_name based on service type
  member_name: (() => {
    // If both DR and Golf are in cart, prefer DR player name (main transaction holder)
    if (drivingRangeItems.length > 0) {
      return String(newPlayerLog?.player_name || "").trim();
    }
    // If only Golf is in cart, use golf player name
    if (golfRatesItems.length > 0) {
      return String(newGolfReservation?.player_name || "").trim();
    }
    // Fallback to walk-in or member name
    return selectedMember?.name || "Walk-in";
  })(),

  // Driving Range
  start_time: (__drEff && __drEff.time_in) || newPlayerLog.time_in || null,
  guest_bookings: (drGuestBookingsClean || []).map(g => ({
    guest_name: g.guest_name || "Guest",
    bay_number: g.bay_number || "",
    time_in: g.time_in || null,
    time_out: g.time_out || null,
    duration_minutes: g.duration_minutes || null,
    product_id: g.product_id || null,
    product_name: g.product_name || null,
    service_key: g.service_key || null,
  })),
  selected_service: newPlayerLog.selected_service || null,

  // Golf
  startgolf_time: mainGolfStartIso || (manualTeeTime ? hhmmToManilaIso(manualTeeTime) : null),
  endgolf_time: mainGolfEndIso || (manualTeeTime ? addMinutesIsoPH(hhmmToManilaIso(manualTeeTime), 240) : null),
  golf_guest_bookings: golfGuestsNormalized,

  // Group booking
  players_count: groupPlayersCount > 1 ? groupPlayersCount : 1,
  golf_group_booking_count: golfGroupBookingCount > 1 ? golfGroupBookingCount : 1,
  dr_group_name: (drReservationStats?.groupName || "").trim() || null,
  golf_group_name: (newGolfReservation?.group_name || "").trim() || null,

  // Flags
  has_driving_range: drivingRangeItems.length > 0,
  has_golf_rates: golfRatesItems.length > 0,

  timestamp: new Date().toISOString(),
};



    // Payment method specific validations
    if (paymentMethod === "card") {
      // Only card type is required, card number and trace number are optional
      if (!cardType) {
        toast({
          title: "Transaction Failed!",
          description: "Please select a card type (Debit/Credit Card type).",
          variant: "destructive",
        });
        return;
      }

      // Skip card validation since card number is optional
      // Card validation was removed to allow flexible card number entry
    }

    if (paymentMethod === "online") {
      if (!selectedBank || !bankAccountNumber || !transactionNumber) {
        toast({
          title: "Transaction Failed!",
          description: "Bank selection, account number, and transaction reference number are required for online payments.",
          variant: "destructive",
        });
        return;
      }
    }

    // 1. Create transaction in backend FIRST
    console.log("🚀 Creating transaction:", transaction);
    if (paymentMethod === "card") {
      console.log("💳 Card payment details being sent:", {
        cardtype_id: transaction.cardtype_id,
        card_number: transaction.card_number,
        batch_no: transaction.batch_no,
        transaction_number: transaction.transaction_number,
        raw_cardType: cardType,
        raw_batchNumber: batchNumber,
      });
    }

        // health-check: queue if backend unreachable
    const alive = await isBackendAlive();
    if (!alive) {
      const queued = pushPendingTransaction(transaction);
      if (queued) {
        toast({
          title: "Offline — Transaction queued",
          description: "Backend unreachable. Transaction saved locally and will be retried automatically.",
          variant: "destructive",
        });
        clearCart();
        setShowCheckoutPage(false);
        setIsCartSummaryModalOpen(false);
        setIsProcessingPayment(false);
        setPaymentStatus("✅ Transaction queued locally (offline). Will retry automatically.");
        // refresh UI data lightly
        await Promise.all([fetchProducts(), refreshLiveBoard()]);
        return;
      } else {
        throw new Error("Backend unreachable and queuing failed. Try again later.");
      }
    }


    const transactionResponse = await fetchWithAuth(`${BACKEND_URL}/api/transactions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(transaction),
    });

    // If fetchWithAuth returned null it already cleared auth and redirected -> abort here
    if (!transactionResponse) {
      throw new Error("AUTH_EXPIRED");
    }

    if (!transactionResponse.ok) {
      // improved error logging
      let errorData;
      try {
        errorData = await transactionResponse.clone().json();
      } catch (e) {
        errorData = await transactionResponse.text().catch(() => ({ message: "no body" }));
      }
      console.error(" Transaction failed:", transactionResponse.status, errorData);
      const serverMsg = errorData && (errorData.detail || errorData.message) ? (errorData.detail || errorData.message) : JSON.stringify(errorData);

      // Try to confirm whether transaction actually exists on server (avoid false-negative on 500/duplicate situations)
      let serverTxns = [];
      try {
        serverTxns = await fetchTransactions({ silent: true });
        if (!Array.isArray(serverTxns)) serverTxns = [];
      } catch (e) {
        console.warn("Could not refresh transactions list after transaction error:", e);
      }
      // For no-OR flows (e.g., puncher), always use transaction id.
      const txnExistsOnServer = serverTxns.some(t => String(t.id) === String(transactionId));

      if (txnExistsOnServer) {
        console.warn("Transaction appears on server despite non-OK response. Continuing as success.");
        // proceed (don't throw) — downstream code will refresh and create player-logs if missing
      } else if (transactionResponse.status >= 500) {
        console.warn("Backend returned 5xx; queuing transaction locally and continuing UI success flow.");

        const queued = pushPendingTransaction(transaction);
        if (queued) {
          setIsCartSummaryModalOpen(false);
          setShowCheckoutPage(false);
          setIsProcessingPayment(false);
          setPaymentStatus("✅ Transaction queued due to server error. Will retry automatically.");
          toast({
            title: "Transaction queued",
            description: "Server error occurred (5xx). Transaction was queued locally and will be retried automatically.",
            variant: "destructive",
          });
          await Promise.all([fetchProducts(), refreshLiveBoard()]);
          // continue normal post-payment steps
        } else {
          throw new Error(serverMsg || `Failed to create transaction (status ${transactionResponse.status})`);
        }
      } else {
        // validation errors or client errors should surface
        throw new Error(serverMsg || `Failed to create transaction (status ${transactionResponse.status})`);
      }
    }

    console.log("✅ Transaction created successfully");

    // 2. Always attempt to push to QA after local transaction
    try {
      const extRes = await fetchWithAuth(`${BACKEND_URL}/api/save-transaction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transaction }),
      });

      if (!extRes) {
        throw new Error("AUTH_EXPIRED");
      }

      let extBody = null;
      try {
        extBody = await extRes.clone().json();
      } catch (e) {
        const text = await extRes.text().catch(() => "");
        extBody = text ? { detail: text } : null;
      }

      if (!extRes.ok) {
        const base =
          (extBody && (extBody.detail || extBody.message)) ||
          (extBody && extBody.body && (extBody.body.detail || extBody.body.message)) ||
          `External sync failed (status ${extRes.status})`;
        const errs =
          (extBody && extBody.errors) ||
          (extBody && extBody.body && extBody.body.errors) ||
          null;
        let firstErr = "";
        try {
          if (errs && typeof errs === "object") {
            const k = Object.keys(errs)[0];
            const v = errs[k];
            firstErr = Array.isArray(v) ? String(v[0] || "") : String(v || "");
          }
        } catch {}

        toast({
          title: "QA sync failed",
          description: [String(base || "").trim(), String(firstErr || "").trim()].filter(Boolean).join(" — "),
          variant: "destructive",
        });
      }
    } catch (e) {
      console.warn("QA save-transaction error", e);
      toast({
        title: "QA sync failed",
        description: "Could not push transaction to QA. Local transaction was saved.",
        variant: "destructive",
      });
    }

//TODO: adjust stock quantities for sold items
      try {
        // Only consider physical item categories (not services)
        const soldItems = cart.filter(it =>
          itemCategories.includes((products.find(p => String(p.id) === String(it.product_id)) || {}).category)
        );

        if (soldItems.length > 0) {
          // Use fetchWithAuth so Authorization header and error handling are consistent
          const adjustPromises = soldItems.map(it => {
            const productId = String(it.product_id);
            const qty = Number(it.quantity || 1);
            return fetchWithAuth(
              `${BACKEND_URL}/api/products/${encodeURIComponent(productId)}/adjust-stock`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  adjustment_type: "subtract",
                  quantity: qty,
                  reason: effectiveOrNumber
                    ? `Sale OR ${effectiveOrNumber}`
                    : `Sale TX ${transaction?.id || transactionId}`
                })
              }
            )
              .then(async (res) => {
                if (!res || !res.ok) {
                  // try to read body for debug but don't throw to avoid breaking payment flow
                  const text = await res.text().catch(() => res.statusText || "error");
                  console.warn(`Adjust stock failed for ${productId}: ${res.status} ${text}`);
                  return { ok: false, productId };
                }
                // optimistic local update for UI
                setProducts(prev =>
                  prev.map(p =>
                    String(p.id) === productId
                      ? { ...p, stock_quantity: Math.max(0, (Number(p.stock_quantity) || 0) - qty) }
                      : p
                  )
                );
                return { ok: true, productId };
              })
              .catch(err => {
                console.warn(`Adjust stock request error for ${productId}:`, err);
                return { ok: false, productId };
              });
          });

          // run all adjustments but don't fail the whole payment if some fail
          const results = await Promise.allSettled(adjustPromises);
          console.log("Stock adjust results:", results);
          // refresh low stock list if available
          try { await fetchLowStockItems && fetchLowStockItems(); } catch (e) { /* ignore */ }
        }
      } catch (e) {
        console.warn("Failed to call adjust-stock endpoints:", e);
        toast && toast({
          title: "Stock update warning",
          description: "Stock adjustment failed for some items. Backend will be retried by admin.",
          variant: "destructive",
        });
      }




// Parse duration from product or name (fallback 60)
const parseProductDurationMins = (prod) => {
  if (!prod) return 60;
  const d = Number(prod.duration);
  if (Number.isFinite(d) && d > 0) return Math.round(d);
  const name = String(prod.name || "").toLowerCase();
  const h = name.match(/(\d+(?:\.\d+)?)\s*(hour|hr|hrs|hours)\b/);
  const m = name.match(/(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes)\b/);
  if (h) return Math.round(parseFloat(h[1]) * 60);
  if (m) return Math.round(parseFloat(m[1]));
  return 60;
};

// TODO: GOLF CREATE & INSERT TO TABLE
// --- Golf Rates Log (MAIN + GUESTS, batched create) ---
try {
  // ✅ FIXED: Double-check cart actually contains golf items (not just golfRatesItems state)
  const actualGolfItemsInCart = (cart || []).filter(it => {
    const name = String(it.product_name || it.name || "").toLowerCase();
    return name.includes("golf") && (name.includes("cart") || name.includes("rates") || name.includes("hole"));
  });
  
  const has_golf_rates = actualGolfItemsInCart.length > 0;
  
  // ✅ FIXED: Skip golf log creation if cart only has driving range items
  const hasDrivingRangeOnly = drivingRangeItems.length > 0 && actualGolfItemsInCart.length === 0;
  
  if (hasDrivingRangeOnly) {
    console.log("ℹ️ Cart has Driving Range items only — skipping golf log creation");
  } else if (has_golf_rates) {
    // 1) compute how many golf slots were purchased (quantity-aware)
    // ✅ FIX: Include extra group players — they aren't in the cart but are valid slots
    const cartGolfCount = (actualGolfItemsInCart || []).reduce((s, it) => s + (Number(it.quantity || 1)), 0);
    const golfCount = Math.max(cartGolfCount, Number(transaction.golf_group_booking_count) || 1);
    if (golfCount <= 0) {
      console.log("ℹ️ golfCount is 0 — skipping golf creation");
    } else {
      // unified parser (ISO with tz, HH:mm -> today Manila)
      const parseGolfTime = (val) => {
        if (!val) return null;
        const s = String(val);
        if (s.includes("T") && (s.includes("+") || s.endsWith("Z"))) return new Date(s);
        if (/^\d{1,2}:\d{2}$/.test(s)) {
          const [h, m] = s.split(":").map(Number);
          const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
          return new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
        }
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
      };

      const time_in_dt = parseGolfTime(transaction.startgolf_time);
      let time_out_dt = parseGolfTime(transaction.endgolf_time);
      if (time_in_dt && !time_out_dt) time_out_dt = new Date(time_in_dt.getTime() + 4 * 60 * 60 * 1000);

      // derive courses and total holes from cart
      let total_holes = 0;
      const courses = [];
      for (const it of transaction.items || []) {
        const nm = (it.product_name || "").toLowerCase();
        if (nm.includes("golf") && (nm.includes("cart") || nm.includes("rates"))) {
          let holes = 18, ctype = "18-holes";
          if (nm.includes("9 holes") || nm.includes("9-holes") || nm.includes("nine holes")) { holes = 9; ctype = "9-holes"; }
          const qty = parseInt(it.quantity || 1);
          for (let i = 0; i < qty; i++) {
            courses.push({ name: it.product_name, type: ctype, holes });
            total_holes += holes;
          }
        }
      }
      const course_type = courses.length === 1 ? courses[0].type : (total_holes ? `${total_holes}-holes` : "18-holes");

      // ✅ UNIFIED: Build ALL golf payloads from golf_guest_bookings (each entry has correct per-token product data)
      let allGolfBookings = Array.isArray(transaction.golf_guest_bookings) ? [...transaction.golf_guest_bookings] : [];
      console.log(`🏌️ Golf bookings from transaction: ${allGolfBookings.length} entries for ${golfCount} group slots`);

      // ✅ FALLBACK: If golf_guest_bookings has fewer entries than golfCount, generate missing entries
      // This handles edge cases where the sync effect didn't fully propagate before snapshot
      if (allGolfBookings.length < golfCount && time_in_dt) {
        console.warn(`⚠️ Golf bookings (${allGolfBookings.length}) < golfCount (${golfCount}). Generating ${golfCount - allGolfBookings.length} fallback entries.`);
        for (let i = allGolfBookings.length; i < golfCount; i++) {
          // Use the first booking's tee time or the main tee time as fallback
          const refBooking = allGolfBookings[0] || {};
          allGolfBookings.push({
            guest_name: `Player ${i + 1}`,
            service_key: `golf_fallback_${i}`,
            product_id: refBooking.product_id || (actualGolfItemsInCart[0] ? String(actualGolfItemsInCart[0].product_id || actualGolfItemsInCart[0].id || "") : null),
            product_name: refBooking.product_name || (actualGolfItemsInCart[0] ? String(actualGolfItemsInCart[0].product_name || actualGolfItemsInCart[0].name || "") : null),
            tee_time: refBooking.tee_time || time_in_dt.toISOString(),
            end_time: refBooking.end_time || time_out_dt?.toISOString() || null,
            holes: refBooking.holes || (course_type.includes("9") ? 9 : 18),
          });
        }
      }

      // Dedup guard: use transaction_id when OR can be null (avoid collisions across transactions)
      const txIdKey = String(transaction?.id || "");
      const txOrKey = String(transaction?.or_number || "").trim();

      // Build a payload for EACH golf booking entry (index 0 = main player, rest = other players)
      let allGolfPayloads = allGolfBookings.map((gb, idx) => {
        const g_tee = parseGolfTime(gb.tee_time);
        const g_end = gb.end_time ? parseGolfTime(gb.end_time) : (g_tee ? new Date(g_tee.getTime() + 4 * 60 * 60 * 1000) : null);
        if (!g_tee || !g_end) return null;

        let g_service_name = "Golf Course";
        let g_holes = 18;
        if (gb.product_id) {
          const prod = products.find(p => String(p.id) === String(gb.product_id));
          if (prod) {
            g_service_name = prod.name;
            g_holes = String(prod.name || "").toLowerCase().includes("9") ? 9 : 18;
          }
        }
        if (gb.holes) g_holes = parseInt(gb.holes) || g_holes;

        const isMain = idx === 0;
        return {
          transaction_id: transaction.id,
          or_number: transaction.or_number,
          player_name: (gb.guest_name || "").trim() || (isMain ? String(newGolfReservation?.player_name || transaction.member_name || "Walk-in") : `Player ${idx + 1}`),
          member_id: isMain ? transaction.member_id : null,
          service_type: "Golf Rates",
          course_type: `${g_holes}-holes`,
          time_in: g_tee.toISOString(),
          time_out: g_end.toISOString(),
          card_gcash: 0.0,
          cash: 0.0,
          is_active: true,
          total_holes: g_holes,
          individual_courses: [{ name: g_service_name, holes: g_holes }],
          golf_service_name: g_service_name,
          golfhole: g_holes,
          is_guest: false,
          players_count: isMain ? (transaction.players_count || 1) : 1,
          golf_group_booking_count: transaction.golf_group_booking_count || 1,
          service_key: gb.service_key || `golf_${idx}`,
          slot_index: idx,
          group_name: (newGolfReservation?.group_name || "").trim() || null,
        };
      }).filter(Boolean);

      // Limit to purchased slots
      if (allGolfPayloads.length > golfCount) {
        allGolfPayloads = allGolfPayloads.slice(0, golfCount);
        console.warn(`ℹ️ Truncated golf payloads to match purchased slots: ${golfCount}`);
      }

      // Local dedupe: remove entries already in activePlayerLogs
      const dedupedGolfPayloads = allGolfPayloads.filter(p =>
        !(activePlayerLogs || []).some(l => {
          const sameTx = txIdKey && String(l?.transaction_id || "") === txIdKey;
          const sameOr = txOrKey && String(l?.or_number || "").trim() === txOrKey;
          if (!sameTx && !sameOr) return false;
          if (String(l?.service_type || "") !== "Golf Rates") return false;
          // Match by service_key if available (unique per slot)
          if (p?.service_key && l?.service_key) {
            return String(l.service_key) === String(p.service_key);
          }
          // Fallback: match by player_name + time_in
          if (String(l?.player_name || "") !== String(p?.player_name || "")) return false;
          if (!l?.time_in || !p?.time_in) return false;
          return new Date(l.time_in).getTime() === new Date(p.time_in).getTime();
        })
      );

      // Split: first entry = main, rest = guests (for batch-time-in endpoint)
      const batchMain = dedupedGolfPayloads.length > 0 ? dedupedGolfPayloads[0] : null;
      const batchGuests = dedupedGolfPayloads.slice(1);

      const batchPayload = {
        main: batchMain,
        guests: batchGuests
      };

      // If nothing to create, skip
      if (!batchPayload.main && (!batchPayload.guests || batchPayload.guests.length === 0)) {
        console.log("ℹ️ No new golf payloads to create (main exists and/or no guests to add). Skipping batch create.");
      } else {
        console.log(`📤 Sending batch create: main=${!!batchMain} + ${batchGuests.length} guest(s) = ${dedupedGolfPayloads.length} total`);
        console.log(`🏌️ Golf payloads detail:`, dedupedGolfPayloads.map(p => ({
          name: p.player_name, holes: p.golfhole, service_key: p.service_key
        })));

        // POST with retry (up to 3 attempts)
        let batchSuccess = false;
        let lastBatchErr = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const res = await fetchWithAuth(`${BACKEND_URL}/api/player-logs/batch-time-in`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(batchPayload),
            });

            if (!res || !res.ok) {
              const text = await (res ? res.text().catch(() => "") : Promise.resolve(""));
              throw new Error(`Batch endpoint failed: ${res?.status} ${text}`);
            }

            const json = await res.json();
            const created = (json.created || []).length;
            const updated = (json.updated || []).length;
            const errors = (json.errors || []).length;
            console.log(`✅ Batch complete (attempt ${attempt}): Created ${created}, Updated ${updated}, Errors ${errors}`);

            toast({
              title: "Golf Booking Confirmed",
              description: `Main player${batchPayload.guests.length ? ` + ${batchPayload.guests.length} guest(s)` : ""} registered successfully`,
            });
            batchSuccess = true;
            break; // success — stop retrying
          } catch (retryErr) {
            lastBatchErr = retryErr;
            console.warn(`⚠️ Golf batch attempt ${attempt}/3 failed:`, retryErr.message);
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, 1500 * attempt)); // backoff: 1.5s, 3s
            }
          }
        }

        // If all retries failed, warn user — do NOT queue for background recovery
        // (expired sessions should not be re-created later)
        if (!batchSuccess) {
          console.error("❌ Golf logs batch failed after 3 retries:", lastBatchErr);
          toast({
            title: "⚠️ Golf Booking Failed",
            description: "Player logs could not be created. The transaction was saved — you can still assign an OR number from the Cashier Transactions list.",
            variant: "destructive",
          });
        }

        // refresh logs
        try { await fetchPlayerLogs(); } catch (e) { console.warn("Could not refresh player logs:", e); }
      }
    }
  } // end of has_golf_rates block
} catch (e) {
  console.error("❌ Golf logs batch failed:", e);
  // Do NOT queue for background recovery — expired sessions should not be re-created
  toast({
    title: "⚠️ Golf Booking Failed",
    description: "Player logs could not be created. You can still assign an OR number from the Cashier Transactions list. " + (e.message || ""),
    variant: "destructive",
  });
}

            // // --- Ensure guest golf player-logs are created too (if any) ---
            // try {
            //   const guestList = newGolfReservation?.guest_golf_bookings
            //     || newGolfReservation?.guest_bookings
            //     || (newPlayerLog?.guest_bookings ? newPlayerLog.guest_bookings : []) || [];

            //   if (Array.isArray(guestList) && guestList.length > 0) {
            //     for (const g of guestList) {
            //       try {
            //         // derive tee/end ISO for guest (support hh:mm or full ISO)
            //         const guestTee = g.tee_time || g.time_in || (g.time_in && hhmmToManilaIso(g.time_in)) || hhmmToManilaIso(manualTeeTime);
            //         let guestEnd = g.end_time || g.time_out;
            //         if (!guestEnd && guestTee) {
            //           const dtg = new Date(guestTee);
            //           dtg.setHours(dtg.getHours() + 4);
            //           guestEnd = (typeof toManilaOffsetIso === "function") ? toManilaOffsetIso(dtg) : dtg.toISOString().replace("Z", "+08:00");
            //         }

            //         const guestPayload = {
            //           or_number: orNumber,
            //           player_name: g.guest_name || g.player_name || "Guest",
            //           member_id: null,
            //           time_in: guestTee,
            //           time_out: guestEnd,
            //           service_type: "Golf Rates",
            //           course_type: g.course_type || courseInfo.type,
            //           players_count: 1,
            //           total_holes: Number(g.holes || g.total_holes) || courseInfo.totalHoles,
            //           individual_courses: g.individual_courses || (g.product_id ? [{ name: g.product_name || "", holes: Number(g.holes || courseInfo.totalHoles) }] : courseInfo.courses),
            //           golfhole: Number(g.holes) || (courseInfo.courses && courseInfo.courses[0]?.holes) || courseInfo.totalHoles,
            //           is_guest: true,
            //           card_gcash: 0,
            //           cash: 0,
            //         };

            //         console.log("🚀 creating guest golf player-log:", guestPayload);
            //         await addPlayerLog(guestPayload);
            //       } catch (guestErr) {
            //         console.error("❌ failed to create guest golf player-log:", guestErr, g);
            //         // continue creating other guests; notify staff if needed
            //       }
            //     } // for guests
            //     // refresh logs once after all guest logs created
            //     await fetchPlayerLogs();
            //   }
            // } catch (guestWrapErr) {
            //   console.warn("Error creating guest golf logs:", guestWrapErr);
            // }


// TODO: DRIVING RANGE PLAYER CREATE AFTER TRANSACTION SUCCESS
    // 2) Save Driving Range logs (main + guests) after payment success
  if (drivingRangeItems.length > 0 && ((__drEff && __drEff.bay_number) || newPlayerLog.bay_number) && mainDRStartISO && mainDREndISO) {
    // ✅ FIXED: Do NOT create main player log here - the backend /api/transactions endpoint already creates it!
    // This was causing duplicate player logs for the main player.
    // Only show the success toast.
    toast({
      title: "Driving Range Booking Confirmed!",
      description: `Bay ${(__drEff && __drEff.bay_number) || newPlayerLog.bay_number} reserved for ${selectedMember.name} from ${(__drEff && __drEff.ui_label_start) || newPlayerLog.ui_label_start} to ${(__drEff && __drEff.ui_label_end) || newPlayerLog.ui_label_end}`,
    });

    // Guests: compute each start/end from their selected service
    // ✅ NOTE: Guest logs are also created by the backend from transaction.guest_bookings
    // Only create guest logs here if the backend didn't receive them properly
    if (Array.isArray(newPlayerLog.guest_bookings) && newPlayerLog.guest_bookings.length > 0) {
      console.log(`ℹ️ ${newPlayerLog.guest_bookings.length} guest bookings will be handled by backend transaction endpoint`);
      // Skip frontend guest creation - backend handles it from transaction.guest_bookings
    }
  }




  // TODO: GOLF PLAYER CREATE AFTER TRANSACTION SUCCESS
// 3. ✅ Golf Rates - NO separate API call needed!
// The transaction endpoint already created the Golf Rates player log
// Just show success toast
// if (golfRatesItems.length > 0 && newGolfReservation.tee_time && newGolfReservation.end_time) {
//   const courseInfo = determineCourseFromCart();
  
//   console.log("✅ Golf reservation was created automatically by transaction endpoint");
//   console.log("Golf booking details:", {
//     or_number: orNumber,
//     course: courseInfo.display,
//     tee_time: newGolfReservation.tee_time,
//     end_time: newGolfReservation.end_time,
//     member: selectedMember?.name
//   });
  
//   toast({
//     title: "Golf Tee Time Confirmed!",
//     description: `${courseInfo.display} reserved for ${newGolfReservation.players_count} player(s) at ${fmt(new Date(newGolfReservation.tee_time))}`,
//   });
// }

// 4. Refresh data and cleanup
await Promise.all([
  fetchProducts(),
  refreshLiveBoard(),
]);

    // Clear form and show success
    setPaymentStatus("✅ Payment completed successfully! Transaction has been recorded.");
    // TODO: Fix stock update reduce when transaction is done
    try {
      // Only adjust stock for item categories (not services)
      const updatedProducts = (products || []).map((p) => {
        const cartItem = cart.find(ci => String(ci.product_id) === String(p.id));
        if (!cartItem) return p;
        if (!itemCategories.includes(p.category)) return p;
        const current = Number(p.stock_quantity ?? Infinity);
        const qty = Number(cartItem.quantity ?? 1);
        return { ...p, stock_quantity: Math.max(0, current - qty) };
      });
      setProducts(updatedProducts);

      // update lowStockItems snapshot too (optional)
      setLowStockItems(updatedProducts.filter(p => itemCategories.includes(p.category) && Number(p.stock_quantity ?? 0) <= Number(p.low_stock_threshold ?? 0)));
    } catch (e) {
      console.warn("Stock update (optimistic) failed:", e);
    }

    clearCart();
    setItemDiscounts({}); // Clear applied discounts after transaction
    setGroupPlayersCount(1); // Reset group booking count
    setGolfGroupBookingCount(1); // Reset golf group booking count
    setShowCheckoutPage(false);
    setIsCartSummaryModalOpen(false);

    // After a successful transaction, refresh OR list so the next available
    // cashier OR is ready for the next checkout.
    if (!isPuncher) {
      try {
        await refetchCashierOrList({ forceSelectFirst: true });
      } catch (e) {
        // ignore
      }
    }

    // Clear payment-specific inputs
    if (paymentMethod === "card") {
      setCardType("");
      setCardNumber("");
      setBatchNumber("");
      setTransactionNumber("");
    }
    if (paymentMethod === "online") {
      setSelectedBank("");
      setBankAccountNumber("");
      setTransactionNumber("");
    }

    // Clear driving range reservation state
setNewPlayerLog({
  or_number: "",
  player_name: "",
  member_id: "",
  bay_number: "",
  time_in: "",
  time_out: "",
  ui_label_start: "",
  ui_label_end: "",
  ui_locked: false,
  card_gcash: 0,
  cash: 0,
  is_member: false,
  guest_bookings: [], // ✅ Clear DR guests
});

    // Show success toast
    const paymentTypeText = paymentMethod === "cash" ? "cash" : 
                           paymentMethod === "card" ? "card" : 
                           `${selectedBank?.toUpperCase()} online payment`;

    toast({
      title: "✅ Transaction Successful!",
      description: `Payment of ₱${total.toFixed(2)} completed successfully via ${paymentTypeText}. OR: ${effectiveOrNumber || "N/A"}`,
      variant: "default",
    });

    console.log(`✅ Payment and reservation completed successfully`);

  } finally {
    setIsProcessingPayment(false);
    isProcessingPaymentRef.current = false;
  }
};

const getPlannedDurationMinutes = (log) => {
  // If paused snapshot exists, prefer it
  if (log?.paused_planned_duration_minutes) {
    return Number(log.paused_planned_duration_minutes) || 0;
  }

  if (log.service_type === "Golf Rates") {
    return 240; // 4 hours fixed for golf
  }

  const txn = findTxnForLog(log, transactions);
  if (!txn || !txn.items) return 0;

  let total = 0;
  let hasDR = false;

  txn.items.forEach(it => {
    const prod = products.find(p => p.id === it.product_id);
    const qty = (it.quantity || 1);

    // Only consider Driving Range Rates items
    if (prod?.category !== "Driving Range Rates") {
      return; // skip non-DR items entirely
    }
    hasDR = true;

    if (prod?.duration) {
      total += Number(prod.duration) * qty;
      return;
    }

    // Parse only when the product is DR
    const name = (prod?.name || "").toLowerCase();

    const hourMatch = name.match(/(\d+(?:\.\d+)?)\s*(hour|hr|hrs|hours)\b/);
    if (hourMatch) {
      total += Math.round(parseFloat(hourMatch[1]) * 60) * qty;
      return;
    }

    const minMatch = name.match(/(\d+(?:\.\d+)?)\s*(min|mins|minute|minutes)\b/);
    if (minMatch) {
      total += Math.round(parseFloat(minMatch[1])) * qty;
      return;
    }

    // No blind “60-minute” fallback for non-matching names
  });

  // If nothing matched but it is clearly a DR booking, fallback once
  return hasDR ? (total || 60) : 0;
};


// -------- NOTIFICATION FEATURE--------
// Add these new state variables in your App component (around line 5800, near other state declarations)
const [notifications, setNotifications] = useState([]);
// const [unreadCount, setUnreadCount] = useState(0);
const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
const [lastNotificationCheck, setLastNotificationCheck] = useState(Date.now());

// Track which calendar day the notifications belong to (Manila time)
const [notificationDay, setNotificationDay] = useState(() => {
  const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
  return `${nowPH.getFullYear()}-${nowPH.getMonth()}-${nowPH.getDate()}`;
});
const [isDeleteFinishedModalOpen, setIsDeleteFinishedModalOpen] = useState(false);
const [isDeletingFinished, setIsDeletingFinished] = useState(false);
const [isDeletePlayerLogsModalOpen, setIsDeletePlayerLogsModalOpen] = useState(false);
const [isDeletingPlayerLogs, setIsDeletingPlayerLogs] = useState(false);
const [isDeleteTransactionsModalOpen, setIsDeleteTransactionsModalOpen] = useState(false);
const [isDeletingTransactions, setIsDeletingTransactions] = useState(false);

// State for highlighting overtime players in the table
const [highlightedPlayerKey, setHighlightedPlayerKey] = useState(null);

// Add new state near other notification states
// suppressedOvertimeKeys are now loaded from the backend on mount (persist across refresh)
const [suppressedOvertimeKeys, setSuppressedOvertimeKeys] = useState(new Set());
const [overtimeResetMap, setOvertimeResetMap] = useState({});
const [dismissedKeysLoaded, setDismissedKeysLoaded] = useState(false);

// Track which overtime keys have already played their sound (loaded from backend)
if (!window._overtimeSoundPlayed) window._overtimeSoundPlayed = new Set();

// ── Fetch dismissed notification keys from backend on mount ──
useEffect(() => {
  if (!token) return;
  let cancelled = false;
  (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/dismissed-notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || cancelled) return;
      const data = await res.json();
      const docs = data.dismissed || [];
      const dismissed = new Set();
      docs.forEach(d => {
        if (d.key && d.dismissed_at) dismissed.add(d.key);
        if (d.key && d.sound_played) window._overtimeSoundPlayed.add(d.key);
      });
      if (!cancelled) {
        setSuppressedOvertimeKeys(prev => {
          const merged = new Set(prev);
          dismissed.forEach(k => merged.add(k));
          return merged;
        });
        setDismissedKeysLoaded(true);
        console.log(`[Notifications] Loaded ${dismissed.size} dismissed keys from server`);
      }
    } catch (e) {
      console.warn('[Notifications] Failed to load dismissed keys:', e);
      if (!cancelled) setDismissedKeysLoaded(true); // allow detection to proceed even if backend fails
    }
  })();
  return () => { cancelled = true; };
}, [token]);

// Helper: persist dismissed keys to the backend (fire-and-forget)
const persistDismissedToServer = (keys, soundPlayedKeys) => {
  if (!token) return;
  const body = {};
  if (keys && keys.length) body.keys = keys;
  if (soundPlayedKeys && soundPlayedKeys.length) body.sound_played_keys = soundPlayedKeys;
  if (!body.keys && !body.sound_played_keys) return;
  fetch(`${BACKEND_URL}/api/dismissed-notifications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  }).catch(e => console.warn('[Notifications] Failed to persist dismiss:', e));
};

// Helper: build overtime key (same pattern used when creating notifications)
const buildOvertimeKey = (orNumber, bayNumber) =>
  `overtime|${String(orNumber || "").trim()}|${String(bayNumber || "").trim()}`;

// When user clears specific notification, suppress future ones for that OR/Bay
const suppressOvertimeFromNotification = (notif) => {
  if (!notif || notif.type !== "overtime") return;
  setSuppressedOvertimeKeys(prev => {
    const next = new Set(prev);
    next.add(notif.key);
    return next;
  });
  
  // Mark reset time so overtime minutes restart from now
  setOvertimeResetMap(prev => ({ ...prev, [notif.key]: Date.now() }));

  // Persist to backend so it survives page refresh
  persistDismissedToServer([notif.key], [notif.key]);

  // Also cancel any pending forced timeout tracking
  try {
    if (window._timedOutLogs || window._timeoutStartTimes) {
      const affected = (activePlayerLogs || []).filter(l =>
        String(l.or_number) === String(notif.orNumber) &&
        String(l.bay_number) === String(notif.bayNumber)
      );
      affected.forEach(l => {
        if (window._timedOutLogs) window._timedOutLogs.delete(l.id);
        if (window._timeoutStartTimes) delete window._timeoutStartTimes[l.id];
      });
    }
  } catch {}
};

// ── AUTO-CLEAR stale notifications when the calendar day rolls over (Manila time) ──
// If the user leaves the POS open overnight, all previous-day overtime
// notifications are silently cleared so they don't pile up the next morning.
useEffect(() => {
  const dayClearInterval = setInterval(() => {
    const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
    const todayKey = `${nowPH.getFullYear()}-${nowPH.getMonth()}-${nowPH.getDate()}`;
    setNotificationDay(prev => {
      if (prev !== todayKey) {
        // Day changed → clear all overtime notifications & suppression state
        setNotifications(old => old.filter(n => n.type !== "overtime"));
        setSuppressedOvertimeKeys(new Set());
        setOvertimeResetMap({});
        console.log("[Notifications] Day rolled over — cleared stale overtime notifications");
        return todayKey;
      }
      return prev;
    });
  }, 60_000); // check every minute
  return () => clearInterval(dayClearInterval);
}, []);

// Auto‑unsuppress when a log is no longer overtime (prevents permanent silence)
useEffect(() => {
  setSuppressedOvertimeKeys(prev => {
    if (!prev.size) return prev;
    const next = new Set(prev);
    const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
    // Re-run same logic used for overtime detection to know current overtime
    (activePlayerLogs || []).forEach(log => {
      const key = buildOvertimeKey(log.or_number, log.bay_number);
      if (!next.has(key)) return;

      // Determine end time similar to detectOvertimePlayers
      let timeOut = null;
      try {
        const tx = transactions.find(t => String(t.or_number) === String(log.or_number));
        const parse = (v) => {
          if (!v) return null;
          if (String(v).includes("T")) {
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
          }
          if (/^\d{1,2}:\d{2}$/.test(String(v))) {
            const [h,m] = String(v).split(":").map(Number);
            const todayPH = new Date(new Date().toLocaleString("en-US",{timeZone:"Asia/Manila"}));
            return new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, m, 0, 0);
          }
          const d = new Date(v);
          return isNaN(d.getTime()) ? null : d;
        };
        if (log.was_resumed && log.resumed_time_out) timeOut = parse(log.resumed_time_out);
        else if (log.time_out) timeOut = parse(log.time_out);
        else if (tx?.end_time) timeOut = parse(tx.end_time);
        else if (tx?.endgolf_time) timeOut = parse(tx.endgolf_time);

        if (timeOut && timeOut.getTime() > nowPH.getTime()) {
          // Not overtime anymore -> remove suppression
          next.delete(key);
        }
      } catch {}
    });
    return next;
  });
}, [activePlayerLogs, transactions]);




// Modify detectOvertimePlayers to skip suppressed keys and apply reset grace
const detectOvertimePlayers = React.useCallback(() => {
  if (!Array.isArray(activePlayerLogs) || activePlayerLogs.length === 0) return [];

  const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
  const results = [];

  const parseHhMmToManila = (timeStr) => {
    if (!timeStr) return null;
    const s = String(timeStr).trim();
    const m = s.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM|am|pm))?$/);
    if (m) {
      let h = parseInt(m[1], 10);
      const mm = parseInt(m[2], 10);
      const ampm = m[3];
      if (ampm) {
        const up = ampm.toUpperCase();
        if (up === "PM" && h !== 12) h += 12;
        if (up === "AM" && h === 12) h = 0;
      }
      const todayPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
      return new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, mm, 0, 0);
    }
    return parseManilaDate(String(s));
  };

  (activePlayerLogs || []).forEach((log) => {
    try {
      if (log.is_paused || log.is_active === false) return;

      const txn = (transactions || []).find(t => String(t.or_number) === String(log.or_number)) || null;

      let timeIn = null;
      if (log.was_resumed && log.resumed_time_in) timeIn = parseManilaDate(log.resumed_time_in);
      else if (log.time_in) timeIn = parseManilaDate(log.time_in);
      else if (txn?.startgolf_time) {
        const s = String(txn.startgolf_time || "");
        timeIn = (s.includes("T") || s.includes("-")) ? new Date(s) : parseHhMmToManila(s);
      } else if (txn?.start_time) {
        timeIn = parseHhMmToManila(txn.start_time);
      }
      if (!(timeIn instanceof Date) || isNaN(timeIn.getTime())) return;
      if (nowPH < timeIn) return;

      let timeOut = null;
      if (log.was_resumed && log.resumed_time_out) timeOut = parseManilaDate(log.resumed_time_out);
      else if (log.time_out) timeOut = parseManilaDate(log.time_out);
      else if (txn?.endgolf_time) {
        const s = String(txn.endgolf_time || "");
        timeOut = (s.includes("T") || s.includes("-")) ? new Date(s) : parseHhMmToManila(s);
      } else if (txn?.end_time) {
        timeOut = parseHhMmToManila(txn.end_time);
      } else {
        const planned = Number(getPlannedDurationMinutes(log) || 0);
        if (planned > 0) timeOut = new Date(timeIn.getTime() + planned * 60000);
      }
      if (!(timeOut instanceof Date) || isNaN(timeOut.getTime())) return;
      if (timeOut.getTime() <= timeIn.getTime()) {
        timeOut = new Date(timeOut.getTime() + 24 * 60 * 60 * 1000);
      }

      const key = buildOvertimeKey(log.or_number, log.bay_number);

      // Skip entirely if suppressed
      if (suppressedOvertimeKeys.has(key)) return;

      // ✅ Apply reset grace: if cleared, recompute overtime from max(original end, reset time)
      const resetTs = overtimeResetMap[key];
      let effectiveEnd = timeOut;
      if (resetTs && resetTs > timeOut.getTime()) {
        // treat reset as pushing end forward to resetTs (overtime restarts)
        effectiveEnd = new Date(resetTs);
      }

      const overtimeMins = Math.max(0, Math.floor((nowPH.getTime() - effectiveEnd.getTime()) / 60000));
      if (overtimeMins >= 1) {
        results.push({
          id: log.id,
            or_number: log.or_number,
          player_name: log.player_name || log.member_name || "Unknown",
          bay_number: log.bay_number,
          overtimeMins,
          timeOut: effectiveEnd,
          tee_time: timeIn,
          severity: overtimeMins >= 2 ? "critical" : "high"
        });
      }
    } catch (err) {
      console.error("detectOvertimePlayers error for", log?.id, err);
    }
  });

  return results;
}, [activePlayerLogs, transactions, parseManilaDate, getPlannedDurationMinutes, suppressedOvertimeKeys, overtimeResetMap]);



const unreadCountFromNotifications = React.useMemo(
  () => Array.isArray(notifications) ? notifications.filter(n => !n.read && !n.dismissed).length : 0,
  [notifications]
);

// Count overtime players from notifications (simpler, avoids circular deps)
const overtimePlayersCount = React.useMemo(() => {
  return notifications.filter(n => n.type === "overtime" && !n.dismissed).length;
}, [notifications]);

// Final badge number: use notification count for unread overtime alerts
const unreadCount = Math.max(unreadCountFromNotifications, overtimePlayersCount);


// Add this compute block right after notification state declarations
const statusCounts = React.useMemo(() => {
  const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
  let notStarted = 0, ongoing = 0, finished = 0;
  const notStartedDetails = [];

  (activePlayerLogs || []).forEach((log) => {
    try {
      if (log?.is_active === false) return; // skip inactive
      const txn = (transactions || []).find(t => String(t.or_number) === String(log.or_number)) || null;

      // Resolve generic timeIn first (resumed -> explicit -> txn.start_time HH:MM)
      let timeIn = null;
      if (log.was_resumed && log.resumed_time_in) {
        timeIn = parseManilaDate(log.resumed_time_in);
      } else if (log.time_in) {
        timeIn = parseManilaDate(log.time_in);
      } else if (txn?.startgolf_time) {
        const s = String(txn.startgolf_time || "");
        if (s.includes("T") || s.includes("-")) {
          const d = new Date(s);
          timeIn = Number.isNaN(d.getTime()) ? null : d;
        } else if (/^\d{1,2}:\d{2}$/.test(s)) {
          const [h, m] = s.split(":").map(Number);
          const todayPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
          timeIn = new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, m, 0, 0);
        }
      } else if (txn?.start_time) {
        const s = String(txn.start_time || "");
        if (/^\d{1,2}:\d{2}$/.test(s)) {
          const [h, m] = s.split(":").map(Number);
          const todayPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
          timeIn = new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, m, 0, 0);
        }
      }

      if (!(timeIn instanceof Date) || isNaN(timeIn.getTime())) {
        // ignore if no valid start
      }

      // Golf-specific tee time (prefer explicit golf field)
      let golfTimeIn = null;
      if (txn?.startgolf_time) {
        const s = String(txn.startgolf_time || "");
        if (s.includes("T") || s.includes("-")) {
          const d = new Date(s);
          golfTimeIn = Number.isNaN(d.getTime()) ? null : d;
        } else if (/^\d{1,2}:\d{2}$/.test(s)) {
          const [h, m] = s.split(":").map(Number);
          const todayPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
          golfTimeIn = new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, m, 0, 0);
        }
      } else if (log.time_in && log.service_type === "Golf Rates") {
        golfTimeIn = parseManilaDate(log.time_in);
      }

      // choose effective start: golfTimeIn for golf bookings, otherwise explicit/resolved timeIn
      const effectiveStart = (log.service_type === "Golf Rates" && golfTimeIn) ? golfTimeIn : timeIn;

      // Resolve timeOut (resumed_time_out -> log.time_out -> txn.endgolf_time -> txn.end_time -> computed from planned duration)
      let timeOut = null;
      if (log.was_resumed && log.resumed_time_out) {
        timeOut = parseManilaDate(log.resumed_time_out);
      } else if (log.time_out) {
        timeOut = parseManilaDate(log.time_out);
      } else if (txn?.endgolf_time) {
        const s = String(txn.endgolf_time || "");
        if (s.includes("T") || s.includes("-")) {
          const d = new Date(s);
          timeOut = Number.isNaN(d.getTime()) ? null : d;
        } else if (/^\d{1,2}:\d{2}$/.test(s)) {
          const [h, m] = s.split(":").map(Number);
          const todayPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
          timeOut = new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, m, 0, 0);
        }
      } else if (txn?.end_time) {
        const s = String(txn.end_time || "");
        if (/^\d{1,2}:\d{2}$/.test(s)) {
          const [h, m] = s.split(":").map(Number);
          const todayPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
          timeOut = new Date(todayPH.getFullYear(), todayPH.getMonth(), todayPH.getDate(), h, m, 0, 0);
        }
      } else if (effectiveStart) {
        const planned = Number(getPlannedDurationMinutes(log) || 0);
        if (planned > 0) timeOut = new Date(effectiveStart.getTime() + planned * 60000);
      }

      if (!(effectiveStart instanceof Date) || isNaN(effectiveStart.getTime())) {
        // If there's no valid effective start, classify as not started
        notStarted++;
        notStartedDetails.push({
          or_number: log.or_number,
          player_name: log.player_name || log.member_name || "Walk-in",
          tee_time: golfTimeIn ? golfTimeIn : timeIn ? timeIn : null
        });
        return;
      }

      if (nowPH < effectiveStart) {
        notStarted++;
        notStartedDetails.push({
          or_number: log.or_number,
          player_name: log.player_name || log.member_name || "Walk-in",
          tee_time: effectiveStart
        });
        return;
      }

      if (!(timeOut instanceof Date) || isNaN(timeOut.getTime())) {
        // No known end yet -> ongoing
        ongoing++;
        return;
      }

      // normalize next-day case
      if (timeOut.getTime() <= effectiveStart.getTime()) {
        timeOut = new Date(timeOut.getTime() + 24 * 60 * 60 * 1000);
      }

      if (nowPH >= timeOut) {
        finished++;
      } else {
        ongoing++;
      }
    } catch (e) {
      console.error("statusCounts parse error:", e);
    }
  });

  // find earliest tee time (for display), format for Manila
  let notStartedEarliestTee = null;
  const teeDates = notStartedDetails
    .map(d => d.tee_time instanceof Date ? d.tee_time : (d.tee_time ? new Date(d.tee_time) : null))
    .filter(Boolean)
    .sort((a, b) => a - b);
  if (teeDates.length) {
    const t = teeDates[0];
    notStartedEarliestTee = t.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Manila" });
  }

  return { notStarted, ongoing, finished, notStartedDetails, notStartedEarliestTee };
}, [activePlayerLogs, transactions, products, parseManilaDate, getPlannedDurationMinutes]);


// Add notification creation logic - use regular function to avoid circular deps
const createOvertimeNotifications = (overtimePlayers, currentSuppressedKeys) => {
  if (!Array.isArray(overtimePlayers) || overtimePlayers.length === 0) return;
  setNotifications(prev => {
    const nowIso = new Date().toISOString();
    const result = [...prev];
    let shouldPlaySound = false;
    
    overtimePlayers.forEach(player => {
      const key = buildOvertimeKey(player.or_number, player.bay_number);
      // Skip if suppressed - use passed-in snapshot
      if (currentSuppressedKeys && currentSuppressedKeys.has(key)) return;

      const idx = result.findIndex(n => n.key === key);
      const overtimeMins = Number(player.overtimeMins || 0);
      const severity = player.severity || (overtimeMins >= 2 ? "critical" : "high");
      const playerName = player.player_name || player.member_name || "Unknown";
      const notifPayload = {
        id: `notif_${key}_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
        key,
        playerId: player.id || null,
        type: "overtime",
        severity,
        title: `Bay ${player.bay_number || "-"} - Overtime`,
        message: `${playerName} is ${overtimeMins} minute${overtimeMins !== 1 ? "s" : ""} overtime`,
        orNumber: player.or_number || null,
        bayNumber: player.bay_number || null,
        overtimeMins,
        timestamp: nowIso,
        read: false,
        dismissed: false,
      };
      
      if (idx >= 0) {
        const existing = result[idx];
        // Only update message/severity/overtimeMins — KEEP original createdAt & timestamp
        if (overtimeMins > Number(existing.overtimeMins || 0) || existing.severity !== severity) {
          result[idx] = {
            ...existing,
            severity,
            message: notifPayload.message,
            overtimeMins,
            // Preserve createdAt & timestamp so TTL still works from first appearance
          };
          // Move to top of list
          const [updated] = result.splice(idx, 1);
          result.unshift(updated);
        }
      } else {
        // Brand-new notification — set createdAt (never changes)
        notifPayload.createdAt = nowIso;
        result.unshift(notifPayload);

        // Play sound only ONCE per overtime key (tracked across re-renders + backend)
        if (!window._overtimeSoundPlayed.has(key)) {
          window._overtimeSoundPlayed.add(key);
          shouldPlaySound = true;
          // Persist sound-played to backend so it won't replay after page refresh
          persistDismissedToServer(null, [key]);
        }
      }
    });
    
    // Cap notifications to prevent memory issues
    const capped = result.slice(0, 50);
    if (shouldPlaySound) {
      try { playNotificationSound(); } catch {}
    }
    return capped;
  });
};


const playNotificationSound = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,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');
    audio.volume = 0.3;
    audio.play().catch(() => {}); // Ignore errors if sound fails
  } catch (e) {
    console.log('Notification sound failed:', e);
  }
};

// Run detection continuously - simplified to avoid circular dependencies
React.useEffect(() => {
  // Don't start detection until dismissed keys have been loaded from the backend.
  // This prevents the race condition where the loop fires with an empty suppressedOvertimeKeys
  // set, re-creating notifications that were previously dismissed.
  if (!token || !dismissedKeysLoaded) return;

  let cancelled = false;
  let intervalId = null;

  const runCheckOnce = () => {
    if (cancelled) return;
    try {
      // Get current overtime players directly
      const nowPH = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
      const overtimePlayers = [];
      const logsSnapshot = activePlayerLogs || [];
      const txnsSnapshot = transactions || [];

      logsSnapshot.forEach((log) => {
        try {
          if (log.is_paused || log.is_active === false) return;

          const txn = txnsSnapshot.find(t => String(t.or_number) === String(log.or_number)) || null;

          // Parse time in
          let timeIn = null;
          if (log.was_resumed && log.resumed_time_in) timeIn = new Date(log.resumed_time_in);
          else if (log.time_in) timeIn = new Date(log.time_in);
          else if (txn?.startgolf_time) {
            const s = String(txn.startgolf_time || "");
            if (s.includes("T") || s.includes("-")) timeIn = new Date(s);
            else if (/^\d{1,2}:\d{2}$/.test(s)) {
              const [h,m] = s.split(":").map(Number);
              timeIn = new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
            }
          } else if (txn?.start_time && /^\d{1,2}:\d{2}$/.test(String(txn.start_time))) {
            const [h,m] = String(txn.start_time).split(":").map(Number);
            timeIn = new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
          }
          if (!(timeIn instanceof Date) || isNaN(timeIn.getTime())) return;
          if (nowPH < timeIn) return;

          // Parse time out
          let timeOut = null;
          if (log.was_resumed && log.resumed_time_out) timeOut = new Date(log.resumed_time_out);
          else if (log.time_out) timeOut = new Date(log.time_out);
          else if (txn?.endgolf_time) {
            const s = String(txn.endgolf_time || "");
            if (s.includes("T") || s.includes("-")) timeOut = new Date(s);
            else if (/^\d{1,2}:\d{2}$/.test(s)) {
              const [h,m] = s.split(":").map(Number);
              timeOut = new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
            }
          } else if (txn?.end_time && /^\d{1,2}:\d{2}$/.test(String(txn.end_time))) {
            const [h,m] = String(txn.end_time).split(":").map(Number);
            timeOut = new Date(nowPH.getFullYear(), nowPH.getMonth(), nowPH.getDate(), h, m, 0, 0);
          }
          if (!(timeOut instanceof Date) || isNaN(timeOut.getTime())) return;
          if (timeOut.getTime() <= timeIn.getTime()) {
            timeOut = new Date(timeOut.getTime() + 24 * 60 * 60 * 1000);
          }

          const key = buildOvertimeKey(log.or_number, log.bay_number);
          const overtimeMins = Math.max(0, Math.floor((nowPH.getTime() - timeOut.getTime()) / 60000));
          
          // Only show notifications for players in the 1-5 minute overtime window
          // After 5 minutes overtime the notification auto-clears and won't reappear
          if (overtimeMins >= 1 && overtimeMins <= 5) {
            overtimePlayers.push({
              id: log.id,
              or_number: log.or_number,
              player_name: log.player_name || log.member_name || "Unknown",
              bay_number: log.bay_number,
              overtimeMins,
              timeOut,
              tee_time: timeIn,
              severity: overtimeMins >= 2 ? "critical" : "high"
            });
          }
        } catch (err) {
          console.error("Overtime check error:", err);
        }
      });

      // Create notifications for overtime players
      if (overtimePlayers.length > 0) {
        createOvertimeNotifications(overtimePlayers, suppressedOvertimeKeys);
      }

      // Build set of valid overtime keys: players currently in 1-5 min window AND not suppressed
      const validOvertimeKeys = new Set(
        overtimePlayers
          .filter(p => !suppressedOvertimeKeys.has(buildOvertimeKey(p.or_number, p.bay_number)))
          .map(p => buildOvertimeKey(p.or_number, p.bay_number))
      );

      const nowPHForDismiss = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
      const todayStartPH = new Date(nowPHForDismiss.getFullYear(), nowPHForDismiss.getMonth(), nowPHForDismiss.getDate());
      const keysToSuppress = []; // collect keys to persist to backend in one call

      setNotifications(prev => prev.filter(notif => {
        if (notif.type !== "overtime") return true;

        // Remove notifications whose timestamp is from a previous day
        if (notif.timestamp) {
          const notifDate = new Date(new Date(notif.timestamp).toLocaleString("en-US", { timeZone: "Asia/Manila" }));
          if (notifDate < todayStartPH) return false;
        }

        // Keep only if player is currently in the 1-5 minute overtime window and not suppressed.
        // This means notifications for players at 6+ minutes overtime (like 279 min or 40 min)
        // are automatically removed — no createdAt/TTL needed, works across page refresh.
        if (!validOvertimeKeys.has(notif.key)) {
          if (notif.key) keysToSuppress.push(notif.key);
          return false;
        }
        return true;
      }));

      // Suppress removed keys and persist to backend so they won't reappear
      if (keysToSuppress.length > 0) {
        setSuppressedOvertimeKeys(s => {
          const n = new Set(s);
          keysToSuppress.forEach(k => n.add(k));
          return n;
        });
        persistDismissedToServer(keysToSuppress, keysToSuppress);
      }
    } catch (err) {
      console.error("Notification runCheck error:", err);
    }
  };

  // Run after a small delay to avoid initial render conflicts
  const initialTimeout = setTimeout(() => {
    if (!cancelled) {
      runCheckOnce();
      // Then run every 30 seconds
      intervalId = setInterval(runCheckOnce, 30000);
    }
  }, 1000);

  return () => {
    cancelled = true;
    clearTimeout(initialTimeout);
    if (intervalId) clearInterval(intervalId);
  };
  // Only depend on token and data arrays - not on functions
}, [token, activePlayerLogs, transactions, suppressedOvertimeKeys, dismissedKeysLoaded]);
// Notification action handlers (around line 6080)
const markAsRead = (notificationId) => {
  setNotifications(prev =>
    prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
  );
  // setUnreadCount(prev => Math.max(0, prev - 1));
};

const markAllAsRead = () => {
  setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  // setUnreadCount(0);
};





// Update single dismiss to also reset overtime and restart grace
const dismissNotification = (notificationId) => {
  setNotifications(prev => {
    const target = prev.find(n => n.id === notificationId);
    if (target && target.type === "overtime") {
      // Handle suppression inline to avoid stale closure
      setSuppressedOvertimeKeys(suppressed => {
        const next = new Set(suppressed);
        if (target.key) next.add(target.key);
        return next;
      });
      setOvertimeResetMap(resetMap => ({ ...resetMap, [target.key]: Date.now() }));
      // Mark sound as played so it won't re-trigger if page refreshes
      if (target.key && window._overtimeSoundPlayed) window._overtimeSoundPlayed.add(target.key);
      // Persist to backend
      if (target.key) persistDismissedToServer([target.key], [target.key]);
    }
    return prev.filter(n => n.id !== notificationId);
  });
};

const handleDeleteAllPlayers = async () => {
  setIsDeletingFinished(true);
  try {
    const response = await fetch(`${BACKEND_URL}/api/player-logs/all`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const result = await response.json();
      toast({
        title: "Success",
        description: result.message || `Deleted ${result.deleted_count} player(s)`,
        duration: 3000,
      });
      
      // Clear ALL notifications and refresh player logs
      setNotifications([]);
      await fetchPlayerLogs();
      setIsDeleteFinishedModalOpen(false);
    } else {
      const error = await response.json();
      toast({
        title: "Error",
        description: error.detail || "Failed to delete players",
        variant: "destructive",
        duration: 3000,
      });
    }
  } catch (error) {
    console.error("Error deleting players:", error);
    toast({
      title: "Error",
      description: "Failed to delete players",
      variant: "destructive",
      duration: 3000,
    });
  } finally {
    setIsDeletingFinished(false);
  }
};

const handleHardResetPlayerLogs = async () => {
  setIsDeletingPlayerLogs(true);
  try {
    const response = await fetch(`${BACKEND_URL}/api/player-logs/all`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const result = await response.json();
      toast({
        title: "Player Logs Deleted",
        description: result.message || `Deleted ${result.deleted_count} player log(s)`,
        duration: 3000,
      });
      setNotifications([]);
      await fetchPlayerLogs();
      setIsDeletePlayerLogsModalOpen(false);
    } else {
      const error = await response.json();
      toast({ title: "Error", description: error.detail || "Failed to delete player logs", variant: "destructive", duration: 3000 });
    }
  } catch (error) {
    console.error("Error deleting player logs:", error);
    toast({ title: "Error", description: "Failed to delete player logs", variant: "destructive", duration: 3000 });
  } finally {
    setIsDeletingPlayerLogs(false);
  }
};

const handleHardResetTransactions = async () => {
  setIsDeletingTransactions(true);
  try {
    const response = await fetch(`${BACKEND_URL}/api/transactions/all`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const result = await response.json();
      toast({
        title: "Transactions Deleted",
        description: result.message || `Deleted ${result.deleted_count} transaction(s)`,
        duration: 3000,
      });
      await fetchTransactions();
      setIsDeleteTransactionsModalOpen(false);
    } else {
      const error = await response.json();
      toast({ title: "Error", description: error.detail || "Failed to delete transactions", variant: "destructive", duration: 3000 });
    }
  } catch (error) {
    console.error("Error deleting transactions:", error);
    toast({ title: "Error", description: "Failed to delete transactions", variant: "destructive", duration: 3000 });
  } finally {
    setIsDeletingTransactions(false);
  }
};

const handleNotificationClick = (notification) => {
  markAsRead(notification.id);
  
  // Navigate to POS tab and highlight the player
  setActiveTab("pos");
  
  // Set the highlighted player key for visual highlighting
  const playerKey = `${notification.orNumber}-${notification.bayNumber}`;
  setHighlightedPlayerKey(playerKey);
  
  // Auto-clear highlight after 5 seconds
  setTimeout(() => {
    setHighlightedPlayerKey(prev => prev === playerKey ? null : prev);
  }, 5000);
  
  // Try to scroll to the player row
  setTimeout(() => {
    const playerRow = document.getElementById(`player-row-${notification.orNumber}-${notification.bayNumber}`);
    if (playerRow) {
      playerRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 100);
  
  toast({
    title: "🔔 Player Located",
    description: `Bay ${notification.bayNumber} - OR ${notification.orNumber} is highlighted`,
    duration: 3000
  });
  
  setIsNotificationPanelOpen(false);
};  




// Modify createOvertimeNotifications to skip suppressed keys


// Update dismissNotification to suppress overtime & clear timeout tracking



// -------- NOTIFICATION FEATURE END --------



  // Check for payment result in URL
  useEffect(() => {
    if (!token) return;

    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const paymentSuccess = urlParams.get("payment_success");
    const paymentCancelled = urlParams.get("payment_cancelled");

    if (sessionId && paymentSuccess) {
      pollPaymentStatus(sessionId);
    } else if (paymentCancelled) {
      setPaymentStatus("Payment was cancelled.");
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [token]);

  // Poll payment status
const pollPaymentStatus = async (sessionId, attempts = 0) => {
  const maxAttempts = 5;

  if (attempts >= maxAttempts) {
    setPaymentStatus(
      "Payment status check timed out. Please check your transaction history."
    );
    return;
  }

  try {
    const response = await fetchWithAuth(
      `${BACKEND_URL}/api/payments/status/${sessionId}`
    );
    if (!response || !response.ok) {
      throw new Error("Failed to check payment status");
    }

    const data = await response.json();

    if (data.payment_status === "paid") {
      setPaymentStatus("Payment successful! Thank you for your purchase.");
      clearCart();
      fetchProducts(); // Refresh inventory
      fetchTransactions();
      setShowCheckoutPage(false);
      setIsCartSummaryModalOpen(false);
      // Clear URL parameters
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname
      );
      return;
    } else if (data.status === "expired") {
      setPaymentStatus("Payment session expired. Please try again.");
      // Clear URL parameters
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname
      );
      return;
    }

    // If payment is still pending, continue polling
    setPaymentStatus("Payment is being processed...");
    setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);

    // NOTE: removed call to undefined `logPlayer` to avoid runtime ReferenceError.
  } catch (error) {
    console.error("Error checking payment status:", error);
    setIsProcessingPayment(false);
    setPaymentStatus("Error checking payment status. Please try again.");
  }
};

  if (!user || !token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      {/* Header */}
    <header className="bg-white border-b-4 shadow-lg border-emerald-500">
  <div className="container px-6 py-4 mx-auto">
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between transition-all duration-300">
      <div className="flex items-center min-w-0 space-x-4">
        <div className="p-2 md:p-3 bg-white border-2 border-gray-200 rounded-lg flex-shrink-0 transition-all duration-300">
          <img
            src="https://customer-assets.emergentagent.com/job_clubhouse-pos/artifacts/2tien3l0_IMG_0267.png"
            alt="Pili Grove Golf Club Logo"
            className="h-10 md:h-12 w-auto object-contain transition-all duration-300"
            style={{ aspectRatio: "1 / 1", maxWidth: "60px" }}
          />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 truncate transition-all duration-300">
            PILI GROVE GOLF CLUB
          </h1>
          <p className="font-medium text-emerald-600 text-sm md:text-base">
            Point of Sale System
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center space-x-2 md:space-x-4 justify-start md:justify-end">
        <div className="flex items-center space-x-2 min-w-0">
          <User className="w-4 h-4 text-gray-600" />
          <span className="text-sm text-gray-600 truncate">{user.full_name}</span>
          <Badge variant="outline">{user.role}</Badge>
        </div>
  {/* Notification Button */}
<div className="relative">
  <Button
    variant="outline"
    size="sm"
    className={`relative ${unreadCount > 0 ? 'border-red-500' : 'border-emerald-300'}`}
    onClick={() => setIsNotificationPanelOpen(!isNotificationPanelOpen)}
  >
    {unreadCount > 0 ? (
      <BellRing className="w-4 h-4 text-red-600 animate-pulse" />
    ) : (
      <Bell className="w-4 h-4 text-emerald-600" />
    )}
    {unreadCount > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
        {unreadCount > 9 ? '9+' : unreadCount}
      </span>
    )}
  </Button>

  {/* Compact status counts next to the bell */}
  <div className="ml-2 inline-flex items-center space-x-2 text-xs text-gray-700">
    <div className="flex items-center space-x-1">
      <span className="font-semibold">Not started</span>
      <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800">{statusCounts.notStarted}</span>
    </div>
    <div className="flex items-center space-x-1">
      <span className="font-semibold">Ongoing</span>
      <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">{statusCounts.ongoing}</span>
    </div>
    <div className="flex items-center space-x-1">
      <span className="font-semibold">Finished</span>
      <span className="px-2 py-0.5 rounded bg-red-100 text-red-800">{statusCounts.finished}</span>
    </div>
    {user?.role === "admin" && (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsDeleteFinishedModalOpen(true)}
        className="h-5 w-5 p-0 hover:bg-red-100 hover:text-red-700"
        title="Delete all players in notifications (admin only)"
      >
        <Trash2 className="w-3 h-3" />
      </Button>
    )}
    {user?.role === "admin" && (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsDeletePlayerLogsModalOpen(true)}
        className="h-5 w-5 p-0 hover:bg-orange-100 hover:text-orange-700"
        title="Hard reset: Delete ALL player logs (admin only)"
      >
        <Database className="w-3 h-3" />
      </Button>
    )}
    {user?.role === "admin" && (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsDeleteTransactionsModalOpen(true)}
        className="h-5 w-5 p-0 hover:bg-purple-100 hover:text-purple-700"
        title="Hard reset: Delete ALL transactions (admin only)"
      >
        <FileText className="w-3 h-3" />
      </Button>
    )}
  </div>

  {/* Hard Reset Player Logs Modal (admin only) */}
  {user?.role === "admin" && (
    <AlertDialog open={isDeletePlayerLogsModalOpen} onOpenChange={setIsDeletePlayerLogsModalOpen}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2">
          <Database className="w-5 h-5 text-orange-600" />
          Hard Reset: Delete ALL Player Logs?
        </AlertDialogTitle>
        <AlertDialogDescription>
          This will <span className="font-bold text-red-600">permanently delete ALL player logs</span> from the database — active, paused, and finished.
          <br /><br />
          <span className="text-xs text-red-500 font-semibold">⚠ This action is irreversible and cannot be undone.</span>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isDeletingPlayerLogs}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={handleHardResetPlayerLogs}
          disabled={isDeletingPlayerLogs}
          className="bg-orange-600 hover:bg-orange-700"
        >
          {isDeletingPlayerLogs ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
              <span>Deleting...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Database className="w-4 h-4" />
              <span>Delete All Player Logs</span>
            </div>
          )}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
    </AlertDialog>
  )}

  {/* Hard Reset Transactions Modal (admin only) */}
  {user?.role === "admin" && (
    <AlertDialog open={isDeleteTransactionsModalOpen} onOpenChange={setIsDeleteTransactionsModalOpen}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-600" />
          Hard Reset: Delete ALL Transactions?
        </AlertDialogTitle>
        <AlertDialogDescription>
          This will <span className="font-bold text-red-600">permanently delete ALL transactions</span> from the database — including sales records, payment history, and OR numbers.
          <br /><br />
          <span className="text-xs text-red-500 font-semibold">⚠ This action is irreversible and cannot be undone.</span>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isDeletingTransactions}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={handleHardResetTransactions}
          disabled={isDeletingTransactions}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {isDeletingTransactions ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
              <span>Deleting...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Delete All Transactions</span>
            </div>
          )}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
    </AlertDialog>
  )}

  {/* Delete All Players Confirmation Modal (admin only) */}
  {user?.role === "admin" && (
    <AlertDialog open={isDeleteFinishedModalOpen} onOpenChange={setIsDeleteFinishedModalOpen}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-red-600" />
          Delete All Players in Notifications?
        </AlertDialogTitle>
        <AlertDialogDescription>
          This will permanently delete <span className="font-bold text-red-600">{notifications.length}</span> player log(s) from the database.
          <br />
          <span className="text-xs text-gray-600">({statusCounts.notStarted} not started, {statusCounts.ongoing} ongoing, {statusCounts.finished} finished)</span>
          <br /><br />
          This action cannot be undone. Are you sure you want to continue?
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isDeletingFinished}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={handleDeleteAllPlayers}
          disabled={isDeletingFinished}
          className="bg-red-600 hover:bg-red-700"
        >
          {isDeletingFinished ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
              <span>Deleting...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Trash2 className="w-4 h-4" />
              <span>Delete All</span>
            </div>
          )}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
    </AlertDialog>
  )}

  {/* Notification Dropdown Panel */}
  {isNotificationPanelOpen && (
    <div className="absolute right-0 top-12 w-96 bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-50 max-h-[600px] overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-emerald-500 to-teal-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BellRing className="w-5 h-5 text-white" />
            <h3 className="font-bold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <Badge className="bg-red-600 text-white">{unreadCount} new</Badge>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {notifications.filter(n => !n.read).length > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-white hover:underline"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={() => setIsNotificationPanelOpen(false)}
              className="text-white hover:bg-white/20 rounded p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Notification List */}
      <div className="overflow-y-auto max-h-[500px]">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No notifications</p>
            <p className="text-sm">You're all caught up!</p>
          </div>
        ) : (
          notifications.map((notif) => (
            <div
              key={notif.id}
              className={`p-4 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                !notif.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              } ${notif.dismissed ? 'opacity-50' : ''}`}
              onClick={() => handleNotificationClick(notif)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    {notif.severity === 'critical' && (
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    )}
                    {notif.severity === 'high' && (
                      <Clock className="w-4 h-4 text-orange-600" />
                    )}
                    {notif.severity === 'medium' && (
                      <Clock className="w-4 h-4 text-yellow-600" />
                    )}
                    <span className={`font-semibold text-sm ${
                      notif.severity === 'critical' ? 'text-red-700' :
                      notif.severity === 'high' ? 'text-orange-700' :
                      'text-yellow-700'
                    }`}>
                      {notif.title}
                    </span>
                    {!notif.read && (
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 mb-1">{notif.message}</p>
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span>OR: {notif.orNumber}</span>
                    <span>•</span>
                    <span>{new Date(notif.timestamp).toLocaleTimeString('en-PH', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}</span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissNotification(notif.id);
                  }}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 bg-gray-50 border-t text-center">
              <button
                onClick={() => {
                  const allKeys = [];
                  setSuppressedOvertimeKeys(prev => {
                    const next = new Set(prev);
                    notifications.forEach(n => {
                      if (n.type === "overtime") {
                        next.add(n.key);
                        allKeys.push(n.key);
                        // reset overtime base time
                        setOvertimeResetMap(prevMap => ({ ...prevMap, [n.key]: Date.now() }));
                        suppressOvertimeFromNotification(n);
                      }
                    });
                    return next;
                  });
                  setNotifications([]);
                  setIsNotificationPanelOpen(false);
                  // Also mark sounds as played so they don't re-trigger
                  notifications.forEach(n => {
                    if (n.key && window._overtimeSoundPlayed) window._overtimeSoundPlayed.add(n.key);
                  });
                  // Persist all dismissed keys to backend
                  if (allKeys.length > 0) persistDismissedToServer(allKeys, allKeys);
                }}
                className="text-sm text-red-600 hover:underline"
              >
                Clear all notifications
              </button>
        </div>
      )}
    </div>
  )}
</div>
        <Badge
          variant="outline"
          className="text-emerald-600 border-emerald-300"
        >
          Cart: {cart.length} items
        </Badge>
        <Badge
          variant="outline"
          className="text-orange-600 border-orange-300"
        >
          Low Stock: {lowStockItems.length}
        </Badge>
        <Button onClick={handleLogout} variant="outline" size="sm">
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  </div>
</header>


      <div className="container   py-6 mx-auto">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
        <TabsList className={`flex w-full max-w-4xl ${gridColsClass} bg-white shadow-md justify-items-stretch`}>
          {visibleTabs.map(tab => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="w-full justify-center data-[state=active]:bg-emerald-500 data-[state=active]:text-white"
            >
              {tab.icon}
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

          {/* POS Tab */}
          <TabsContent value="pos" className="container mx-auto"> 
              {showCheckoutPage ? (
                // TODO: CHECKOUT PAGE VIEW
    // 🔄 CHECKOUT PAGE VIEW (replaces POS content)
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Checkout</h2>
          <p className="text-sm text-gray-500 mt-0.5">{cart.length} item{cart.length !== 1 ? 's' : ''} in cart &middot; {selectedMember?.name || "No member selected"}</p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            setShowCheckoutPage(false);
            setActiveTab("pos");
          }}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to POS
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT COLUMN: Items + Services */}
        <div className="lg:col-span-2 space-y-4">

          {/* Member Card */}
          <div className="p-4 border rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {(selectedMember?.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-emerald-900 truncate">{selectedMember?.name || "—"}</div>
                <div className="flex items-center gap-3 text-xs text-gray-600 mt-0.5">
                  <span>
                    <Crown className="w-3 h-3 inline mr-0.5 text-emerald-500" />
                    {selectedMember?.membership_type || "Walk-in"}
                  </span>
                  {(selectedMember?.discount_percentage || 0) > 0 && (
                    <span className="text-green-700 font-medium">
                      <Tag className="w-3 h-3 inline mr-0.5" />
                      {selectedMember?.discount_percentage}% base discount
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cart Items */}
          <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <div className="font-semibold text-sm text-gray-700">
                <ShoppingCart className="w-4 h-4 inline mr-1.5 text-gray-500" />
                Order Items ({getExpandedCartItems().length})
              </div>
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7"
                onClick={() => setShowDiscountModal(true)}
              >
                <Tag className="w-3 h-3 mr-1" />
                Apply Discount
              </Button>
            </div>
            <div className="divide-y max-h-[340px] overflow-y-auto">
              {getExpandedCartItems().map((item) => {
                const itemDiscountPct = calculateItemDiscount(item.instanceKey);
                const hasSpecialDiscount = itemDiscounts[item.instanceKey];
                const discountedPrice = hasSpecialDiscount ? Math.round(item.singlePrice * (1 - itemDiscountPct / 100) * 100) / 100 : item.singlePrice;
                // Determine category badge
                const prod = products.find(p => String(p.id) === String(item.product_id));
                const cat = prod?.category || "";
                const isService = cat === "Driving Range Rates" || cat === "Golf Rates";

                return (
                  <div key={item.instanceKey} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">{item.product_name}</span>
                        {isService && (
                          <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full flex-shrink-0">Service</span>
                        )}
                      </div>
                      {hasSpecialDiscount ? (
                        <div className="flex flex-col gap-0.5 mt-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">
                              {hasSpecialDiscount.type.toUpperCase()} 20%
                            </span>
                            {hasSpecialDiscount.customerName && (
                              <span className="text-[10px] text-orange-600 truncate max-w-[150px]">
                                {hasSpecialDiscount.customerName}
                              </span>
                            )}
                            <button
                              onClick={() => {
                                const newDiscounts = { ...itemDiscounts };
                                delete newDiscounts[item.instanceKey];
                                setItemDiscounts(newDiscounts);
                              }}
                              className="text-red-400 hover:text-red-600 transition-colors ml-auto"
                              title="Remove discount"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedItems([item.instanceKey]);
                            setShowDiscountModal(true);
                          }}
                          className="text-[11px] text-blue-600 hover:text-blue-800 mt-0.5 hover:underline"
                        >
                          + Add discount
                        </button>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {hasSpecialDiscount ? (
                        <div>
                          <span className="text-xs text-gray-400 line-through">₱{item.singlePrice.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                          <div className="text-sm font-semibold text-green-600">₱{discountedPrice.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</div>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">₱{item.singlePrice.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {getExpandedCartItems().length === 0 && (
                <div className="px-4 py-8 text-center text-gray-400 text-sm">
                  <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No items in cart
                </div>
              )}
            </div>
          </div>

          {/* Driving Range Booking */}
          {drivingRangeItems.length > 0 && (
            <div className="border rounded-xl bg-emerald-50 border-emerald-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-emerald-200 bg-emerald-100/50">
                <div className="font-semibold text-sm text-emerald-800 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Driving Range Reservation
                </div>
              </div>
              <div className="p-4 text-black">
                <TimeSlotPicker
                  newPlayerLog={newPlayerLog}
                  setNewPlayerLog={setNewPlayerLog}
                  durationMinutes={accumulatedDurationMinutes}
                  isTimeSlotAvailable={isTimeSlotAvailable}
                  bayNumberError={bayNumberError}
                  selectedMember={selectedMember}
                  orNumber={orNumber}
                  toast={toast}
                  manualTime={manualTime}
                  setManualTime={setManualTime}
                  activePlayerLogs={activePlayerLogs}
                  transactions={transactions}
                  products={products}
                  isCartSummaryModalOpen={showCheckoutPage}
                  cart={cart}
                  setCart={setCart}
                  drivingRangeItems={drivingRangeItems}
                  onReservationStatsChange={setDrReservationStats}
                  groupPlayersCount={groupPlayersCount}
                  setGroupPlayersCount={setGroupPlayersCount}
                />
              </div>
            </div>
          )}

          {/* Golf Booking */}
          {golfRatesItems.length > 0 && (
            <div className="border rounded-xl bg-blue-50 border-blue-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-blue-200 bg-blue-100/50">
                <div className="font-semibold text-sm text-blue-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Golf Tee Time Reservation
                </div>
              </div>
              <div className="p-4">
                <GolfTeeTimeBooking
                  newGolfReservation={newGolfReservation}
                  setNewGolfReservation={setNewGolfReservation}
                  selectedMember={selectedMember}
                  orNumber={orNumber}
                  toast={toast}
                  isGolfTimeSlotAvailable={isGolfTimeSlotAvailable}
                  golfRatesItems={golfRatesItems}
                  products={products}
                  determineCourseFromCart={determineCourseFromCart}
                  setManualTeeTime={setManualTeeTime}
                  manualTeeTime={manualTeeTime}
                  reservedGolfSlot={reservedGolfSlot}
                  setReservedGolfSlot={setReservedGolfSlot}
                  isCartSummaryModalOpen={showCheckoutPage}
                  members={members}
                  setCart={setCart}
                  onGolfReservationStatsChange={setGolfReservationStats}
                  golfGroupBookingCount={golfGroupBookingCount}
                  setGolfGroupBookingCount={setGolfGroupBookingCount}
                />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Payment + Totals (sticky on desktop) */}
        <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">

          {/* Payment Method */}
          <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b bg-violet-50">
              <div className="font-semibold text-sm text-violet-800 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Payment Method
              </div>
            </div>
            <div className="p-4 space-y-3">
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="text-sm w-full bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">💵 Cash</SelectItem>
                  <SelectItem value="card">💳 Credit/Debit Card</SelectItem>
                  <SelectItem value="online">🌐 Online Payment</SelectItem>
                </SelectContent>
              </Select>

              {/* Card payment fields */}
              {paymentMethod === "card" && (
                <div className="space-y-3 pt-1">
                  <div>
                    <Label htmlFor="cardType" className="text-xs font-medium text-gray-600">
                      Card Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={cardType} onValueChange={setCardType}>
                      <SelectTrigger className="mt-1 text-sm w-full bg-white">
                        <SelectValue placeholder="Choose card type" />
                      </SelectTrigger>
                      <SelectContent>
                        {cardTypes.map((card) => (
                          <SelectItem key={card.value} value={card.value}>
                            {card.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cardPaymentAmount" className="text-xs font-medium text-gray-600">
                      Payment Amount
                    </Label>
                    <Input
                      id="cardPaymentAmount"
                      value={`₱${newTotal.toFixed(2)}`}
                      readOnly
                      className="mt-1 text-sm w-full bg-gray-100 text-gray-700 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cardNumber" className="text-xs font-medium text-gray-600">
                      Card Number
                    </Label>
                    <Input
                      id="cardNumber"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      placeholder="Enter card number"
                      className="mt-1 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="batchNumber" className="text-xs font-medium text-gray-600">
                        Batch No.
                      </Label>
                      <Input
                        id="batchNumber"
                        value={batchNumber}
                        onChange={(e) => setBatchNumber(e.target.value)}
                        placeholder="Batch #"
                        className="mt-1 text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="transactionNumber" className="text-xs font-medium text-gray-600">
                        Trace No.
                      </Label>
                      <Input
                        id="transactionNumber"
                        value={transactionNumber}
                        onChange={(e) => setTransactionNumber(e.target.value)}
                        placeholder="Trace #"
                        className="mt-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Online payment fields */}
              {paymentMethod === "online" && (
                <div className="space-y-3 pt-1">
                  <div>
                    <Label htmlFor="bankSelect" className="text-xs font-medium text-gray-600">
                      Payment Provider
                    </Label>
                    <Select value={selectedBank} onValueChange={setSelectedBank}>
                      <SelectTrigger className="mt-1 text-sm w-full bg-white">
                        <SelectValue placeholder="Choose provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {onlinePaymentBanks.map((bank) => (
                          <SelectItem key={bank.value} value={bank.value}>
                            {bank.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="bankAccountNumber" className="text-xs font-medium text-gray-600">
                      Account Number
                    </Label>
                    <Input
                      id="bankAccountNumber"
                      value={bankAccountNumber}
                      onChange={(e) => setBankAccountNumber(e.target.value)}
                      placeholder="Enter account number"
                      className="mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="onlineTransactionNumber" className="text-xs font-medium text-gray-600">
                      Reference Number
                    </Label>
                    <Input
                      id="onlineTransactionNumber"
                      value={transactionNumber}
                      onChange={(e) => setTransactionNumber(e.target.value)}
                      placeholder="Enter reference number"
                      className="mt-1 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary & Pay */}
          <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50">
              <div className="font-semibold text-sm text-gray-700">Order Summary</div>
            </div>
            <div className="p-4 space-y-2.5">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span>₱{newSubtotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
              </div>
              {newDiscountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span className="flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Discount
                  </span>
                  <span>-₱{newDiscountAmount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {/* Show applied discount summary */}
              {Object.keys(itemDiscounts).length > 0 && (
                <div className="text-xs text-orange-600 bg-orange-50 px-2 py-1.5 rounded">
                  {(() => {
                    const holders = [...new Set(Object.values(itemDiscounts).map(d => d.customerName).filter(Boolean))];
                    const types = [...new Set(Object.values(itemDiscounts).map(d => d.type?.toUpperCase()).filter(Boolean))];
                    return (
                      <span>
                        {types.join(", ")} discount{Object.keys(itemDiscounts).length > 1 ? ` on ${Object.keys(itemDiscounts).length} items` : ""}
                        {holders.length > 0 && <> &mdash; {holders.join(", ")}</>}
                      </span>
                    );
                  })()}
                </div>
              )}
              {groupPlayersCount > 1 && (
                <div className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1.5 rounded flex items-center gap-1">
                  <Users className="w-3 h-3" /> DR Group: {groupPlayersCount} players
                  {drReservationStats?.groupName && (
                    <span className="text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded-full text-[10px] font-medium">{drReservationStats.groupName}</span>
                  )}
                  {(drReservationStats?.extraCost || 0) > 0 && (
                    <span className="ml-auto font-medium">+₱{drReservationStats.extraCost.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              )}
              {golfGroupBookingCount > 1 && (
                <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1.5 rounded flex items-center gap-1">
                  <Users className="w-3 h-3" /> Golf Group: {golfGroupBookingCount} players
                  {(golfReservationStats?.extraCost || 0) > 0 && (
                    <span className="ml-auto font-medium">+₱{golfReservationStats.extraCost.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-lg font-bold text-gray-900">
                <span>Total</span>
                <span>₱{newTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <div className="p-4 pt-0 space-y-2">
              <Button
                className="w-full bg-emerald-500 text-white hover:bg-emerald-600 h-11 text-base font-semibold shadow-sm"
                onClick={() => {
                  if (drivingRangeItems.length > 0 && !allDrivingRangeSlotsFilled) {
                    toast({
                      title: "Incomplete Driving Range Reservations",
                      description: `Fill all ${drReservationStats.totalSlots} slot${drReservationStats.totalSlots === 1 ? '' : 's'} before confirming payment. (${drReservationStats.filledSlots}/${drReservationStats.totalSlots} filled)`,
                      variant: "destructive",
                    });
                    return;
                  }
                  if (golfRatesItems.length > 0 && !allGolfSlotsFilled) {
                    toast({
                      title: "Incomplete Golf Reservations",
                      description: `Fill all ${golfReservationStats.totalSlots} slot${golfReservationStats.totalSlots === 1 ? '' : 's'} before confirming payment. (${golfReservationStats.filledSlots}/${golfReservationStats.totalSlots} filled)`,
                      variant: "destructive",
                    });
                    return;
                  }
                  processPayment();
                }}
                disabled={isProcessingPayment || cart.length === 0}
              >
                {isProcessingPayment ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Check className="w-4 h-4" />
                    <span>Confirm &amp; Pay ₱{newTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-500 hover:text-gray-700 text-sm h-9"
                onClick={() => {
                  setShowCheckoutPage(false);
                  setActiveTab("pos");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Discount Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-emerald-600" />
              Apply Discount
            </DialogTitle>
            <DialogDescription>
              Select items, choose the discount type, and enter the ID holder's details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Item Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Select Items</Label>
                <button
                  onClick={() => {
                    const allKeys = getExpandedCartItems().map(i => i.instanceKey);
                    setSelectedItems(prev => prev.length === allKeys.length ? [] : allKeys);
                  }}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {selectedItems.length === getExpandedCartItems().length ? "Deselect all" : "Select all"}
                </button>
              </div>
              <div className="border rounded-lg max-h-44 overflow-y-auto divide-y">
                {getExpandedCartItems().map(item => {
                  const alreadyDiscounted = itemDiscounts[item.instanceKey];
                  return (
                    <label
                      key={item.instanceKey}
                      className={`flex items-center space-x-2.5 px-3 py-2.5 cursor-pointer transition-colors ${
                        selectedItems.includes(item.instanceKey) ? "bg-emerald-50" : "hover:bg-gray-50"
                      } ${alreadyDiscounted ? "opacity-50" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.instanceKey)}
                        disabled={!!alreadyDiscounted}
                        onChange={() => {
                          if (alreadyDiscounted) return;
                          setSelectedItems(prev => 
                            prev.includes(item.instanceKey) 
                              ? prev.filter(key => key !== item.instanceKey)
                              : [...prev, item.instanceKey]
                          );
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm flex-1 truncate">{item.product_name}</span>
                      {alreadyDiscounted && (
                        <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">
                          {alreadyDiscounted.type.toUpperCase()}
                        </span>
                      )}
                      <span className="text-sm text-gray-500 font-medium flex-shrink-0">
                        ₱{item.singlePrice.toFixed(2)}
                      </span>
                    </label>
                  );
                })}
              </div>
              {selectedItems.length > 0 && (
                <p className="text-xs text-emerald-600 mt-1.5">{selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""} selected</p>
              )}
            </div>

            {/* ID Type */}
            <div>
              <Label className="text-sm font-medium">Discount Type</Label>
              <Select value={discountIdType} onValueChange={setDiscountIdType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Discount</SelectItem>
                  <SelectItem value="pwd">PWD (20% off)</SelectItem>
                  <SelectItem value="senior">Senior Citizen (20% off)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ID Number + Holder Name side by side */}
            {discountIdType !== 'none' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">ID Number <span className="text-red-500">*</span></Label>
                  <Input
                    value={discountIdNumber}
                    onChange={(e) => setDiscountIdNumber(e.target.value)}
                    placeholder="e.g. 12345678"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">ID Holder Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={discountCustomerName}
                    onChange={(e) => setDiscountCustomerName(e.target.value)}
                    placeholder="Full name on ID"
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Preview */}
            {discountIdType !== 'none' && selectedItems.length > 0 && discountIdNumber.trim() && discountCustomerName.trim() && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-800">
                <Check className="w-3.5 h-3.5 inline mr-1" />
                {discountIdType.toUpperCase()} 20% discount will be applied to {selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""} for <strong>{discountCustomerName.trim()}</strong> (ID: {discountIdNumber.trim()})
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowDiscountModal(false);
                setSelectedItems([]);
                setDiscountIdType('none');
                setDiscountIdNumber('');
                setDiscountCustomerName('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (discountIdType === 'none' || !discountIdNumber.trim()) {
                  alert('Please select ID type and enter ID number');
                  return;
                }
                if (!discountCustomerName.trim()) {
                  alert('Please enter the name of the discount holder');
                  return;
                }

                const newDiscounts = { ...itemDiscounts };
                selectedItems.forEach(instanceKey => {
                  newDiscounts[instanceKey] = {
                    type: discountIdType,
                    idNumber: discountIdNumber,
                    customerName: discountCustomerName.trim()
                  };
                });

                setItemDiscounts(newDiscounts);
                setShowDiscountModal(false);
                setSelectedItems([]);
                setDiscountIdType('none');
                setDiscountIdNumber('');
                setDiscountCustomerName('');
              }}
              disabled={selectedItems.length === 0 || discountIdType === 'none' || !discountIdNumber.trim() || !discountCustomerName.trim()}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              Apply Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  ) : (
      // TODO: POS STANDARD VIEW
            // 🔄 STANDARD POS VIEW
          <div
                className="
                  pos-grid
                  grid grid-cols-1
                  lg:grid-cols-[minmax(0,1fr)_minmax(320px,360px)]
                  xl:grid-cols-[minmax(0,1fr)_minmax(340px,380px)]
                  gap-4 justify-center
                "
              >
                {/* Left Section: Player Logs + Products */}
                <div className="space-y-6 min-w-0">

<Card className="mb-3 shadow-xl bg-white rounded">
<CardHeader
  className={`p-3 rounded relative flex items-center ${
    showGolfRatesOnly
      ? "bg-gradient-to-r from-blue-700 to-blue-500"
      : "bg-gradient-to-r from-emerald-500 to-teal-500"
  }`}
  style={{ minHeight: 56 }}
>

  {/* responsive CSS for compact-date-control */}
  {/* Right-side refresh control removed (moved beside Pause button) */}

  {/* Centered Title with transition and indication */}
  <div className="absolute header-center flex justify-center items-center pointer-events-none" style={{ top: 0, bottom: 0 }}>
    <h3
      className={`text-sm font-extrabold tracking-wide uppercase drop-shadow text-center transition-colors duration-300 ${
        showGolfRatesOnly ? "text-blue-50" : "text-white"
      }`}
    >
      {showGolfRatesOnly
        ? (
          <span>
            Active Player Logs
            <span className="ml-2 px-2 py-1 rounded bg-blue-600/60 text-xs font-bold text-blue-100 transition-all duration-300">Golf Table</span>
          </span>
        )
        : (
          <span>
            Active Player Logs
            <span className="ml-5 px-2 py-1 rounded bg-emerald-600/60 text-xs font-bold text-emerald-100 transition-all duration-300">Driving Range Table</span>
          </span>
        )
      }
    </h3>
  </div>
{/* Compact history date filter — positioned left and vertically centered with the header title */}
<div
  className={`absolute left-3 top-1/3 transform -translate-y-1/3 z-20 pointer-events-auto rounded-md shadow-sm compact-date-control`}
  // style kept minimal; width now controlled by CSS for responsiveness
  aria-hidden={false}
>
  <div
    className={`flex items-center gap-3 rounded-md px-2 py-1 min-w-0 date-wrapper ${
      showGolfRatesOnly
        ? "bg-gradient-to-r from-blue-600 to-indigo-600 border border-blue-400"
        : "bg-gradient-to-r from-emerald-600 to-teal-600 border border-emerald-400"
    }`}
  >
    {/* calendar trigger (keeps same behavior) */}
    <button
      type="button"
      onClick={() => {
        if (headerDateRef.current) {
          if (typeof headerDateRef.current.showPicker === "function") {
            headerDateRef.current.showPicker();
          } else {
            headerDateRef.current.click();
          }
        }
      }}
      className="p-0 m-0 bg-transparent border-0 flex items-center justify-center cursor-pointer flex-shrink-0"
      aria-label="Choose date"
      title={headerDateDisplay}
    >
      <Calendar className="w-5 h-5 text-white flex-shrink-0" />
    </button>

    {/* hidden native input — selection updates UI */}
    <input
      ref={headerDateRef}
      type="date"
      value={headerDate}
      onChange={(e) => setHeaderDate(e.target.value)}
      className="sr-only"
      aria-hidden="true"
    />

        {/* textual date (hidden on small) */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs uppercase tracking-wide text-white/80 flex date-label">
            Date
          </span>
          <span
            className="text-xs text-white whitespace-nowrap header-date-text"
            title={headerDateDisplay}
          >
            {headerDateDisplay}
          </span>
          {/* compact numeric date for small screens */}
          <span
            className="text-xs text-white whitespace-nowrap header-date-compact"
            title={headerDateCompact}
          >
            {headerDateCompact}
          </span>
        </div>

    {/* History button: shows full label on desktop, icon only on smaller screens */}
    <button
      type="button"
      onClick={() => setShowHistoryTable(!showHistoryTable)}
      className="ml-2 gap-2 px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 text-white/90 text-xs font-medium transition-colors history-button"
      title={`Show history for ${headerDateDisplay}`}
      aria-pressed={showHistoryTable}
    >
      {/* full text (hidden on small screens) */}
      <span className="history-text">History</span>
      {/* icon fallback for small screens (hidden on large) */}
      <span className="history-icon" aria-hidden>
        📜
      </span>
    </button>
  </div>

{/* responsive CSS for compact-date-control */}
<style>{`
  .compact-date-control { width: 250px; }
  /* Reserve space on the right so centered title doesn't overlap header controls */
  /* Reserve a larger area on both sides so centered title won't overlap controls */
  .header-center { left: 4rem; right: 10rem; max-width: calc(100% - 14rem); }
  .compact-date-control .history-icon { display: none; }
  .compact-date-control .history-text { display: inline; }
  .compact-date-control .date-label { display: inline; }
 /* show full date on desktop (no ellipsis) */
  .compact-date-control .header-date-text { display: inline; white-space: nowrap; max-width: none; overflow: visible; text-overflow: clip; }
  .compact-date-control .header-date-compact { display: none; } /* hidden by default */

  @media (max-width: 1280px) {
    /* collapse to icons, remove reserved width so table doesn't get pushed */
    .compact-date-control { width: auto; min-width: 0; }
    .compact-date-control .date-label { display: none !important; }
    .compact-date-control .header-date-text { display: none !important; }
    .compact-date-control .header-date-compact { display: inline !important; } /* show compact date */
    .compact-date-control .history-text { display: none !important; }
    .compact-date-control .history-icon { display: inline-flex !important; font-size: 16px !important; line-height: 1; }
    /* tighten padding to save space */
    .compact-date-control .date-wrapper { padding-left: 6px; padding-right: 6px; gap: 6px; }
    .compact-date-control .history-button { padding: 4px; width: 34px; height: 34px; display: inline-flex; align-items: center; justify-content: center; }
    /* shrink calendar icon */
    .compact-date-control .date-wrapper button svg { width: 16px !important; height: 16px !important; }
    /* reduce reserved space on narrow screens */
    .header-center { left: 3rem; right: 6rem; max-width: calc(100% - 9rem); }
  }
`}</style>

  {/* History Table Panel (appears below the date control) */}
  {showHistoryTable && (
    <div className="absolute left-0 top-full mt-2 w-[1100px] bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-50 max-h-[500px] overflow-hidden">
      <div className="p-3 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold text-gray-800">Transactions History</h4>
          <span className="text-sm text-gray-500">• {headerDateDisplay}</span>
          {cashierTransactions.length > 0 && (
            <Badge className="ml-2 bg-blue-100 text-blue-700">
              {cashierTransactions.length} records
            </Badge>
          )}
        </div>
        <button
          onClick={() => setShowHistoryTable(false)}
          className="text-gray-600 hover:bg-gray-100 rounded p-1 transition-colors"
          aria-label="Close history"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-auto max-h-[420px]">
        {isLoadingCashierHistory ? (
          <div className="p-8 text-center text-gray-400">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm">Loading transactions…</p>
          </div>
        ) : cashierTransactions.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium text-lg">No transactions</p>
            <p className="text-sm">No records found for {headerDateDisplay}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr className="text-xs text-left">
                <th className="px-3 py-2">No.</th>
                <th className="px-3 py-2">Puncher Name</th>
                <th className="px-3 py-2">Player</th>
                <th className="px-3 py-2">Service</th>
                <th className="px-3 py-2">Items / Services</th>
                <th className="px-3 py-2">OR Number</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Discount</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {cashierTransactions.map((tx, i) => {
                const items = Array.isArray(tx?.items) ? tx.items : [];
                const itemsText = items
                  .map((it) => {
                    const name = String(it?.product_name || it?.name || "Item");
                    const qty = Number(it?.quantity || 1);
                    return qty > 1 ? `${name}×${qty}` : name;
                  })
                  .join(", ");
                const shortItems = itemsText.length > 70 ? `${itemsText.slice(0, 70)}…` : (itemsText || "-");

                const orValue = String(tx?.or_number || "").trim();
                const orLabel = orValue ? orValue : "No OR yet";

                const amount = Number(tx?.total_amount || 0) || 0;
                const amountText = `₱${amount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
                const wf = normalizeWorkflowStatus(tx?.workflow_status);
                const isDone = wf === "completed" || wf === "cancelled";

                return (
                  <tr key={tx?.id || i} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{i + 1}</td>
                    <td className="px-3 py-2 text-xs font-medium">{tx?.cashier || "-"}</td>
                    <td className="px-3 py-2 text-xs font-medium">
                      <span className={tx?.member_name === "Walk-in" ? "text-gray-500" : "text-blue-600"}>
                        {tx?.member_name || "Walk-in"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {tx?.has_driving_range && (
                          <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Driving Range</span>
                        )}
                        {tx?.has_golf_rates && (
                          <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Golf</span>
                        )}
                        {!tx?.has_driving_range && !tx?.has_golf_rates && (
                          <span className="text-[10px] text-gray-400">Items only</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs space-y-0.5 max-w-[200px]">
                        {items.length > 0 ? (
                          <>
                            {items.slice(0, 4).map((it, idx) => {
                              const name = String(it?.product_name || it?.name || "Item");
                              const qty = Number(it?.quantity || 1);
                              const price = Number(it?.price || 0);
                              const lineTotal = price * qty;
                              const nameLower = name.toLowerCase();
                              const colorClass = /golf/i.test(nameLower) ? "text-blue-600" : /driving\s*range/i.test(nameLower) ? "text-green-600" : "text-gray-600";
                              return (
                                <div key={idx}>
                                  <span className={colorClass}>{qty > 1 ? `${qty}× ` : ""}{name}</span>
                                  <span className="text-gray-400 ml-1">₱{lineTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                                </div>
                              );
                            })}
                            {items.length > 4 && (
                              <div className="text-gray-400">+{items.length - 4} more</div>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {orValue ? (
                        <span
                          className="cursor-pointer hover:text-blue-600 hover:underline font-medium"
                          onClick={(e) => { e.stopPropagation(); openTxnModal(tx); }}
                          title="Click to edit OR number"
                        >
                          {orValue}
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-100 font-semibold whitespace-nowrap"
                          onClick={(e) => { e.stopPropagation(); openTxnModal(tx); }}
                        >
                          Assign OR
                        </Button>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium text-right text-xs">
                      {amountText}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-green-600">
                      {`₱${(Number(tx?.discount_amount || 0)).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`}
                    </td>
                    

                    <td className="px-3 py-2">
                      <Badge className={`${txnStatusBadgeClass(tx)} pointer-events-none`}>{txnStatusLabel(tx)}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openTxnModal(tx)}
                          className="h-7 px-2 text-xs"
                        >
                          View
                        </Button>
                        {wf === "completed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setReceiptData(tx);
                              setShowReceiptModal(true);
                            }}
                            className="h-7 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          >
                            <Printer className="w-3.5 h-3.5 mr-1" />
                            Print
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => {
                            openTxnModal(tx);
                            patchTransaction(tx?.id, { workflow_status: "in_progress" });
                          }}
                          disabled={isDone || isTxnUpdating}
                          className="h-7 px-2 text-xs bg-yellow-500 hover:bg-yellow-600"
                        >
                          Process
                        </Button>
                        {/* <Button
                          size="sm"
                          onClick={() => patchTransaction(tx?.id, { workflow_status: "completed" })}
                          disabled={isDone || isTxnUpdating}
                          className="h-7 px-2 text-xs bg-emerald-500 hover:bg-emerald-600"
                        >
                          Complete
                        </Button> */}
                        {/* <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            const ok = window.confirm("Cancel this transaction?");
                            if (!ok) return;
                            patchTransaction(tx?.id, { workflow_status: "cancelled" });
                          }}
                          disabled={isDone || isTxnUpdating}
                          className="h-7 px-2 text-xs"
                        >
                          Cancel
                        </Button> */}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )}

  {/* Transaction Summary Modal (OR assignment + details) */}
  <Dialog
    open={showTxnSummaryModal}
    onOpenChange={(open) => {
      setShowTxnSummaryModal(open);
      if (!open) {
        setSelectedTxn(null);
        setTxnOrInput("");
      }
    }}
  >
    <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">Transaction Summary</span>
            {selectedTxn?.or_number && (
              <span className="text-sm font-mono bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">OR #{selectedTxn.or_number}</span>
            )}
          </div>
          {selectedTxn && (
            <Badge className={`${txnStatusBadgeClass(selectedTxn)} pointer-events-none text-xs`}>
              {txnStatusLabel(selectedTxn)}
            </Badge>
          )}
        </DialogTitle>
        <DialogDescription className="text-xs">
          {selectedTxn?.timestamp
            ? new Date(selectedTxn.timestamp).toLocaleString("en-PH", { timeZone: "Asia/Manila", weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })
            : ""}
          {selectedTxn?.id && <span className="ml-2 text-gray-400">ID: {selectedTxn.id}</span>}
        </DialogDescription>
      </DialogHeader>

      {!selectedTxn ? (
        <div className="text-sm text-gray-500">No transaction selected.</div>
      ) : (
        <div className="space-y-3">
          {/* ── Customer & Service Info ── */}
          <div className="border rounded-lg p-3 bg-gradient-to-r from-gray-50 to-white">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Customer</div>
                <div className="font-semibold text-gray-800">{selectedTxn.member_name || "Walk-in"}</div>
                {selectedTxn.member_id && <div className="text-[10px] text-gray-400">Member ID: {selectedTxn.member_id}</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Puncher</div>
                <div className="font-medium text-gray-700">{selectedTxn.cashier || "-"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Payment</div>
                <div className="font-medium text-gray-700 capitalize">{String(selectedTxn.payment_method || "-")}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Service</div>
                <div className="flex flex-wrap gap-1">
                  {selectedTxn.has_driving_range && (
                    <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Driving Range</span>
                  )}
                  {selectedTxn.has_golf_rates && (
                    <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Golf</span>
                  )}
                  {!selectedTxn.has_driving_range && !selectedTxn.has_golf_rates && (
                    <span className="text-[10px] text-gray-400">Items only</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Card Payment Details ── */}
          {selectedTxn.payment_method === "card" && (
            <div className="border rounded-lg p-3 bg-blue-50/60">
              <div className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                <span>💳</span> Card Payment Details
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div>
                  <div className="text-[10px] text-gray-500">Card Type</div>
                  <div className="font-medium text-sm">
                    {(() => {
                      const cardTypeLabels = {
                        "1": "Master Card/Visa/BancNet",
                        "2": "JCB",
                        "3": "BDO Union Pay",
                        "4": "Diners Club International",
                        "5": "American Express",
                        "6": "Discover",
                      };
                      return cardTypeLabels[String(selectedTxn.cardtype_id || "")] || selectedTxn.cardtype_id || "-";
                    })()}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500">Card Number</div>
                  <div className="font-medium">{selectedTxn.card_number || "-"}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500">Batch No</div>
                  <div className="font-medium">{selectedTxn.batch_no || "-"}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500">Trace No</div>
                  <div className="font-medium">{selectedTxn.transaction_number || "-"}</div>
                </div>
              </div>
            </div>
          )}

          {/* ── Online Payment Details ── */}
          {selectedTxn.payment_method === "online" && (
            <div className="border rounded-lg p-3 bg-green-50/60">
              <div className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1.5">
                <span>🌐</span> Online Payment Details
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-[10px] text-gray-500">Gateway</div>
                  <div className="font-medium">{String(selectedTxn.payment_gateway || "-").toUpperCase()}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500">Account Number</div>
                  <div className="font-medium">{selectedTxn.account_number || "-"}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500">Transaction No</div>
                  <div className="font-medium">{selectedTxn.transaction_number || "-"}</div>
                </div>
              </div>
            </div>
          )}

          {/* ── Items & Services ── */}
          <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="bg-gray-100 px-4 py-3 flex items-center justify-between">
              <div className="text-base font-bold text-gray-800 flex items-center gap-2">
                🛒 Items & Services
                <span className="bg-gray-300 text-gray-700 text-sm px-2 py-0.5 rounded-full font-semibold">
                  {(Array.isArray(selectedTxn.items) ? selectedTxn.items : []).reduce((s, it) => s + (Number(it?.quantity) || 1), 0)}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                OR: <span className="font-bold text-gray-800">{(String(txnOrInput || "").trim() || String(selectedTxn?.or_number || "").trim()) || "—"}</span>
              </div>
            </div>
            <div className="max-h-[400px] overflow-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-sm text-left text-gray-500 border-b-2 border-gray-200 font-semibold uppercase tracking-wide">
                    <th className="px-4 py-3">Item / Service</th>
                    <th className="px-4 py-3 text-center w-20">Qty</th>
                    <th className="px-4 py-3 text-right w-32">Unit Price</th>
                    <th className="px-4 py-3 text-right w-32">Total</th>
                  </tr>
                </thead>
                <tbody className="text-base">
                  {(Array.isArray(selectedTxn.items) ? selectedTxn.items : []).map((it, idx) => {
                    const qty = Number(it?.quantity || 1) || 1;
                    const price = Number(it?.unit_price ?? it?.price ?? 0) || 0;
                    const line = price * qty;
                    const pid = it?.product_id || it?.id;
                    const itemDiscs = selectedTxn.item_discounts || {};
                    let discApplied = null;
                    for (let inst = 0; inst < qty; inst++) {
                      const key = `${pid}-${inst}`;
                      if (itemDiscs[key]) { discApplied = itemDiscs[key]; break; }
                    }
                    const pn = (it?.product_name || "").toLowerCase();
                    const isService = pn.includes("golf") || pn.includes("driving range") || pn.includes("range");
                    return (
                      <tr key={idx} className={`border-b last:border-b-0 ${discApplied ? "bg-red-50/50" : "hover:bg-gray-50"}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {isService && <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-bold shrink-0">SVC</span>}
                            <span className="font-semibold text-gray-900 text-base">{String(it?.product_name || it?.name || "Item")}</span>
                          </div>
                          {discApplied && (
                            <div className="text-sm text-red-500 mt-1 flex items-center gap-1.5 pl-1">
                              <span className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-semibold text-xs">{discApplied.type === "pwd" ? "PWD" : discApplied.type === "senior" ? "Senior" : discApplied.type}</span>
                              {discApplied.customerName && <span className="text-red-500 font-medium">({discApplied.customerName})</span>}
                              {discApplied.idNumber && <span className="text-red-400">ID: {discApplied.idNumber}</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700 font-semibold text-base">{qty}</td>
                        <td className="px-4 py-3 text-right text-gray-700 text-base">₱{price.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900 text-base">₱{line.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Totals Breakdown ── */}
            <div className="bg-gray-100 px-4 py-3 space-y-2 border-t-2 border-gray-200">
              {(() => {
                const txnSubtotal = Number(selectedTxn.subtotal || 0);
                const txnDiscount = Number(selectedTxn.discount_amount || selectedTxn.discount || 0);
                const txnTotal = Number(selectedTxn.total_amount || 0);
                const hasDiscount = txnDiscount > 0;
                const itemDiscs = selectedTxn.item_discounts || {};
                const discKeys = Object.keys(itemDiscs);
                const discHolders = [];
                const seenNames = new Set();
                discKeys.forEach((k) => {
                  const d = itemDiscs[k];
                  if (d && d.customerName && !seenNames.has(d.customerName)) {
                    seenNames.add(d.customerName);
                    discHolders.push(d);
                  }
                });
                return (
                  <>
                    <div className="flex items-center justify-between text-base">
                      <div className="text-gray-600 font-medium">Subtotal</div>
                      <div className="text-gray-800 font-semibold">₱{(hasDiscount ? txnSubtotal : txnTotal).toLocaleString("en-PH", { minimumFractionDigits: 2 })}</div>
                    </div>
                    {hasDiscount && (
                      <>
                        <div className="flex items-center justify-between text-base">
                          <div className="text-red-600 flex items-center gap-2 font-medium">
                            Discount
                            {(() => {
                              const idType = selectedTxn.id_type || selectedTxn.discount_type || "";
                              const firstKey = discKeys[0];
                              const discType = idType || (firstKey && itemDiscs[firstKey]?.type) || "";
                              if (discType === "pwd") return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold">PWD (20%)</span>;
                              if (discType === "senior") return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold">Senior (20%)</span>;
                              return null;
                            })()}
                            {discKeys.length > 0 && (
                              <span className="text-sm text-red-400">on {discKeys.length} item{discKeys.length > 1 ? "s" : ""}</span>
                            )}
                          </div>
                          <div className="text-red-600 font-bold text-lg">-₱{txnDiscount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</div>
                        </div>
                        {discHolders.length > 0 && (
                          <div className="text-sm text-red-500 pl-3 space-y-1">
                            {discHolders.map((dh, di) => (
                              <div key={di} className="flex items-center gap-2">
                                <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold text-xs">{dh.type === "pwd" ? "PWD" : dh.type === "senior" ? "Senior" : dh.type}</span>
                                <span className="font-semibold">{dh.customerName}</span>
                                {dh.idNumber && <span className="text-red-400">• ID: {dh.idNumber}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    <div className="border-t-2 border-gray-300 pt-2 flex items-center justify-between">
                      <div className="text-gray-900 font-bold text-lg">Total Amount</div>
                      <div className={`text-2xl font-bold ${hasDiscount ? "text-green-700" : "text-gray-900"}`}>
                        ₱{txnTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* ── Booking Times ── */}
          {(selectedTxn.start_time || selectedTxn.startgolf_time || selectedTxn.bay_number) && (
            <div className="border rounded-lg p-3 bg-amber-50/50">
              <div className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center gap-1.5">
                <span>🕐</span> Booking Details
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {selectedTxn.bay_number && (
                  <div>
                    <div className="text-[10px] text-gray-500">Bay</div>
                    <div className="font-semibold text-gray-800">Bay {selectedTxn.bay_number}</div>
                  </div>
                )}
                {selectedTxn.start_time && (
                  <div>
                    <div className="text-[10px] text-gray-500">DR Time In</div>
                    <div className="font-medium text-gray-700">{(() => { try { return manilaTimeLabel(new Date(selectedTxn.start_time)); } catch { return selectedTxn.start_time; } })()}</div>
                  </div>
                )}
                {selectedTxn.startgolf_time && (
                  <div>
                    <div className="text-[10px] text-gray-500">Tee Time</div>
                    <div className="font-medium text-gray-700">{(() => { try { return manilaTimeLabel(new Date(selectedTxn.startgolf_time)); } catch { return selectedTxn.startgolf_time; } })()}</div>
                  </div>
                )}
                {selectedTxn.endgolf_time && (
                  <div>
                    <div className="text-[10px] text-gray-500">Golf End</div>
                    <div className="font-medium text-gray-700">{(() => { try { return manilaTimeLabel(new Date(selectedTxn.endgolf_time)); } catch { return selectedTxn.endgolf_time; } })()}</div>
                  </div>
                )}
                {(selectedTxn.players_count > 1 || selectedTxn.golf_group_booking_count > 1) && (
                  <div>
                    <div className="text-[10px] text-gray-500">Group Size</div>
                    <div className="font-medium text-gray-700">
                      {selectedTxn.has_golf_rates && selectedTxn.golf_group_booking_count > 1 && `${selectedTxn.golf_group_booking_count} Golf players`}
                      {selectedTxn.has_golf_rates && selectedTxn.golf_group_booking_count > 1 && selectedTxn.has_driving_range && selectedTxn.players_count > 1 && " + "}
                      {selectedTxn.has_driving_range && selectedTxn.players_count > 1 && `${selectedTxn.players_count} DR players`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── OR Number & Actions ── */}
          <div className="border rounded-lg p-3 bg-white">
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-gray-700">OR Number</Label>
                <div className="mt-1 flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="text-sm w-full h-9 px-3 justify-between bg-white border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 flex-1"
                        id="txnOrNumber"
                        disabled={isTxnUpdating || closingTransaction}
                      >
                        <span className={txnOrInput ? "text-gray-900 font-medium" : "text-gray-400"}>
                          {txnOrInput ? `OR #${txnOrInput}` : "Select OR number from booklet"}
                        </span>
                        <span className="ml-2 text-gray-400">▼</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-[--radix-popper-anchor-width] max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
                    >
                      {orList.length === 0 ? (
                        <DropdownMenuItem disabled className="px-3 py-2 text-sm text-gray-400">
                          No OR numbers available
                        </DropdownMenuItem>
                      ) : (
                        orList.map((num) => (
                          <DropdownMenuItem
                            key={num}
                            onClick={() => setTxnOrInput(String(num))}
                            className="px-3 py-2 text-sm cursor-pointer transition-all duration-150 ease-out\n           hover:bg-blue-600 hover:text-white\n           focus:bg-blue-600 focus:text-white\n           data-[highlighted]:bg-blue-600 data-[highlighted]:text-white\n           active:bg-blue-700"
                          >
                            {num}
                          </DropdownMenuItem>
                        ))
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div className="flex items-end justify-end gap-2">
                <Button
                  onClick={async () => {
                    setClosingTransaction(true);
                    const hasOrNumber = txnOrInput.trim() !== "";

                    // ── Validate OR number against external API before closing ──
                    if (hasOrNumber) {
                      try {
                        const valRes = await fetchWithAuth(`${BACKEND_URL}/api/validate-or-number`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ or_number: txnOrInput.trim(), transaction_id: selectedTxn.id }),
                        });
                        if (valRes && valRes.ok) {
                          const valData = await valRes.json();
                          if (!valData.valid) {
                            // OR number has a conflict
                            if (valData.needs_retry && valData.conflicting_transaction_id) {
                              // Another local transaction used this OR but failed QA sync
                              const retryChoice = window.confirm(
                                `${valData.reason}\n\nWould you like to retry syncing the conflicting transaction now? If successful, this OR number will become unavailable and you'll need to pick a different one.`
                              );
                              if (retryChoice) {
                                try {
                                  const retryRes = await fetchWithAuth(`${BACKEND_URL}/api/transactions/retry-qa-sync/${encodeURIComponent(valData.conflicting_transaction_id)}`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                  });
                                  if (retryRes && retryRes.ok) {
                                    toast && toast({ title: "Conflicting Transaction Synced", description: "The other transaction was synced to QA. Please select a different OR number.", duration: 8000 });
                                  } else {
                                    toast && toast({ title: "Sync Retry Failed", description: "Could not sync the conflicting transaction. Please contact an admin or choose a different OR number.", variant: "destructive", duration: 8000 });
                                  }
                                } catch (retryErr) {
                                  toast && toast({ title: "Sync Retry Failed", description: retryErr?.message || "Unknown error", variant: "destructive" });
                                }
                              }
                              // Refresh OR list after retry attempt
                              try { await refetchCashierOrList(); } catch {}
                              setClosingTransaction(false);
                              return;
                            } else {
                              // OR used by another transaction (already synced) or consumed externally
                              toast && toast({ title: "OR Number Unavailable", description: valData.reason, variant: "destructive", duration: 8000 });
                              // Refresh OR list to remove stale entries
                              try { await refetchCashierOrList(); } catch {}
                              setTxnOrInput("");
                              setClosingTransaction(false);
                              return;
                            }
                          }
                        }
                      } catch (valErr) {
                        console.warn("OR validation check failed (proceeding anyway):", valErr);
                        // Fail-open: if validation endpoint is unreachable, proceed with close
                      }
                    }

                    const updated = await patchTransaction(selectedTxn.id, {
                      or_number: txnOrInput,
                      workflow_status: hasOrNumber ? "completed" : "in_progress",
                    });
                    if (updated) {
                      if (hasOrNumber) {
                        const qaSynced = await syncTransactionToQA(updated);
                        if (!qaSynced) {
                          // QA sync failed — retry once automatically
                          console.warn("QA sync failed on close, retrying via retry endpoint...");
                          try {
                            const retryRes = await fetchWithAuth(`${BACKEND_URL}/api/transactions/retry-qa-sync/${encodeURIComponent(String(updated.id))}`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                            });
                            if (retryRes && retryRes.ok) {
                              toast && toast({ title: "QA Sync Recovered", description: "Transaction was successfully pushed to external system on retry." });
                            } else {
                              toast && toast({
                                title: "⚠️ QA Sync Failed",
                                description: "Transaction closed locally but NOT pushed to external system. Use 'Retry QA Sync' later to push it.",
                                variant: "destructive",
                                duration: 10000,
                              });
                            }
                          } catch (retryErr) {
                            console.error("QA retry also failed:", retryErr);
                            toast && toast({
                              title: "⚠️ QA Sync Failed",
                              description: "Transaction closed locally but NOT pushed to external system. Use 'Retry QA Sync' later to push it.",
                              variant: "destructive",
                              duration: 10000,
                            });
                          }
                        }
                        if (!isPuncher) {
                          try {
                            await refetchCashierOrList({ forceSelectFirst: true });
                          } catch (e) { /* ignore */ }
                        }
                        // Show receipt after closing
                        setReceiptData(updated);
                        setShowReceiptModal(true);
                      } else {
                        toast && toast({
                          title: "Transaction Not Completed",
                          description: "No OR number assigned. Transaction saved as In Progress.",
                          variant: "destructive",
                          duration: 5000,
                        });
                      }
                      setShowTxnSummaryModal(false);
                    }
                  }}
                  disabled={isTxnUpdating || closingTransaction}
                  className="h-9 px-4 text-xs"
                >
                  {isTxnUpdating ? (
                    <div className="flex items-center space-x-1.5">
                      <div className="w-3 h-3 border-b-2 border-white rounded-full animate-spin"></div>
                      <span>Closing...</span>
                    </div>
                  ) : (
                    "Close Transaction"
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowTxnSummaryModal(false)}
                  disabled={isTxnUpdating || closingTransaction}
                  className="h-9 px-4 text-xs"
                >
                  Cancel
                </Button>
                {/* Retry QA Sync button for completed transactions that failed to sync */}
                {selectedTxn?.or_number && selectedTxn?.qa_synced === false && (
                  <Button
                    variant="outline"
                    className="h-9 px-4 text-xs border-orange-400 text-orange-600 hover:bg-orange-50"
                    disabled={isTxnUpdating || closingTransaction}
                    onClick={async () => {
                      setIsTxnUpdating(true);
                      try {
                        const retryRes = await fetchWithAuth(`${BACKEND_URL}/api/transactions/retry-qa-sync/${encodeURIComponent(String(selectedTxn.id))}`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                        });
                        if (retryRes && retryRes.ok) {
                          toast && toast({ title: "QA Sync Success", description: "Transaction pushed to external system." });
                          setSelectedTxn(prev => prev ? { ...prev, qa_synced: true } : prev);
                          await fetchTransactions();
                        } else {
                          let detail = "Retry failed";
                          try { const d = await retryRes.json(); detail = d?.detail || d?.body?.detail || detail; } catch {}
                          toast && toast({ title: "QA Sync Retry Failed", description: detail, variant: "destructive" });
                        }
                      } catch (e) {
                        toast && toast({ title: "QA Sync Retry Failed", description: e?.message || "Unknown error", variant: "destructive" });
                      } finally {
                        setIsTxnUpdating(false);
                      }
                    }}
                  >
                    Retry QA Sync
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>

  
</div>

<style>{`
  /* Compact controls: show icon-only on <=1280px; allow wrapping instead of reserving table space */
  @media (max-width: 1280px) {
    .compact-controls {
      gap: 0.5rem;
      flex-wrap: wrap;
      align-items: center;
    }
    /* hide full-text buttons and show compact icons */
    .compact-controls .btn-full { display: none !important; }
    .compact-controls .btn-icon { 
      display: inline-flex !important;
      min-width: 36px !important;
      width: 36px !important;
      height: 36px !important;
      padding: 6px !important;
      justify-content: center;
      align-items: center;
    }
    /* remove reserved padding so table can use full width */
    .table-container { padding-right: 0 !important; }
  }

  /* default: hide icon-only duplicates on large screens */
  .compact-controls .btn-icon { display: none; }
`}</style>

{/* Button at the right (responsive: full text on desktop, icon-only on smaller screens) */}
<div className="ml-auto flex items-center gap-x-3 compact-controls" style={{ zIndex: 1 }}>
  {!showGolfRatesOnly && (
    <>


      {/* icon-only fallback (tablet / small) */}

      <Button
        size="sm"
        variant="outline"
        aria-label={showPausedTable ? "Active Table" : "Paused Table"}
        title={showPausedTable ? "Active Table" : "Paused Table"}
        onClick={() => setShowPausedTable(!showPausedTable)}
        className={`${showPausedTable ? "bg-purple-600 text-white" : "bg-gray-600 text-white"} hover:bg-gray-700 btn-icon`}
        style={{ width: 100, minWidth: 100, height: 36, padding: 6, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
      >
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <Pause className="w-4 h-4" />
          {pausedPlayerLogs.length > 0 && (
            <Badge className="paused-badge bg-white text-purple-600" style={{ padding: "2px 6px", fontSize: 10 }}>
              {pausedPlayerLogs.length}
            </Badge>
          )}
        </div>
      </Button>

      {/* Refresh button placed beside Pause (icon-only) */}
      {/* <Button
        size="sm"
        onClick={() => fetchPlayerLogs()}
        className="bg-yellow-500 hover:bg-yellow-600 btn-icon"
        aria-label="Refresh player logs"
        title="Refresh player logs"
        style={{ width: 36, minWidth: 36, height: 36, padding: 6, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
      >
        <RefreshCw className={`w-4 h-4 ${isLoadingPlayerLogs ? "animate-spin" : ""}`} />
      </Button> */}

      {/* small CSS for badge placement + responsive width */}
      <style>{`
        .paused-badge{
          position: absolute;
          top: 50%;
          left: -28px; /* places badge to the right of the Pause icon (between icon and button edge) */
          transform: translateY(-50%);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          padding: 2px 6px;
          font-size: 10px;
          border-radius: 999px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.06);
          white-space: nowrap;
        }
        /* Tablet / small screens: keep badge readable and aligned slightly more centered */
        @media (max-width: 1280px) {
          .paused-badge {
            left: -32px;
            min-width: 20px;
            padding-left: 8px;
            padding-right: 8px;
            text-align: center;
          }
        }
      `}</style>
    </>
  )}

  {/* Golf / Driving toggle - full + icon-only */}
  <>

    <Button
      size="sm"
      variant="outline"
      aria-label={showGolfRatesOnly ? "Driving Range Table" : "Golf Table"}
      title={showGolfRatesOnly ? "Driving Range Table" : "Golf Table"}
      className={`${showGolfRatesOnly ? "bg-blue-600 text-white" : "bg-green-600 text-white"} hover:bg-opacity-80 btn-icon [@media(min-width:1360px)]:px-3 [@media(min-width:1360px)]:h-8 [@media(min-width:1360px)]:w-[2000px] [@media(min-width:1360px)]:justify-between`}
      onClick={() => {
        setShowGolfRatesOnly((prev) => !prev);
        setShowPausedTable(false);
      }}
      style={{ width: 156, minWidth: 56, height: 36, padding: 6, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
    >
      {/* choose an appropriate icon */}
      <Clock className="w-4 h-4 [@media(min-width:1280px)]:hidden" />
      <span className="hidden [@media(min-width:1360px)]:inline text-xs font-semibold">
        {showGolfRatesOnly ? "Driving Range Table" : "Golf Table"}
      </span>
    </Button>

    {/* Refresh button for golf table */}
    <Button
      size="sm"
      onClick={handleRefreshClick}
      disabled={isLoadingPlayerLogs}
      className={`bg-yellow-500 hover:bg-yellow-600 btn-icon ml-2 ${isLoadingPlayerLogs ? "opacity-60 cursor-not-allowed" : ""}`}
      aria-label="Refresh player logs"
      title="Refresh player logs"
      style={{ width: 36, minWidth: 36, height: 36, padding: 6, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
    >
      {isLoadingPlayerLogs ? (
        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <RefreshCw className="w-4 h-4" />
      )}
    </Button>
  </>
</div>

</CardHeader>
<CardContent className="p-0">
  <div className="table-container overflow-x-auto text-sm">
<table className={`w-full text-xs table-fixed ${
  showGolfRatesOnly ? "bg-white" : ""
}`}>
<colgroup>
  {showGolfRatesOnly ? (
    <>
      <col className="w-[5%]" />  {/* Course (9H/18H) */}
      <col className="w-[8%]" />  {/* OR Number */}
      <col className="w-[17%]" /> {/* Player Name */}
      <col className="w-[9%]" />  {/* Tee Time */}
      <col className="w-[9%]" />  {/* End Time */}
      <col className="w-[12%]" /> {/* Duration */}
      <col className="w-[12%]" /> {/* Estimated Time */}
      <col className="w-[8%]" />  {/* Status */}
      <col className="w-[20%]" /> {/* Action */}
    </>
  ) : (
    <>
      <col className="w-[5%]" />  {/* Bay Number */}
      <col className="w-[8%]" />  {/* OR Number */}
      <col className="w-[17%]" /> {/* Player Name */}
      <col className="w-[9%]" />  {/* Time In */}
      <col className="w-[9%]" />  {/* Time Out */}
      <col className="w-[12%]" /> {/* Duration */}
      <col className="w-[12%]" /> {/* Estimated Time */}
      <col className="w-[8%]" />  {/* Status */}
      <col className="w-[20%]" /> {/* Actions */}
    </>
  )}
</colgroup>
<thead className={`sticky top-0 z-10 ${showGolfRatesOnly ? "bg-gradient-to-r from-blue-50 to-blue-100 text-blue-900" : "bg-gradient-to-r from-green-50 to-green-100 text-green-900"}`}>
  <tr className={`border-b-2 ${showGolfRatesOnly ? "border-blue-300" : "border-green-400"}`}>
    {showGolfRatesOnly ? (
      <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider">Course</th>
    ) : (
      <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider">Bay</th>
    )}
    <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider">OR #</th>
    <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider">Player</th>
    <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider">
      {showGolfRatesOnly ? "Tee Time" : "Time In"}
    </th>
    <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider">
      {showGolfRatesOnly ? "End Time" : "Time Out"}
    </th>
    <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider">Duration</th>
    <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider">Est. Time</th>
    <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider">Status</th>
    {!showGolfRatesOnly && (
      <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider">Actions</th>
    )}
    {showGolfRatesOnly && (
      <th className="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wider">Actions</th>
    )}
  </tr>
</thead>
</table>

<div className={`overflow-y-auto ${activePlayerLogs.length > 6 ? "max-h-[400px]" : ""}`}>
  <table className={`w-full text-sm table-fixed ${showGolfRatesOnly ? "bg-white" : ""}`}>
<colgroup>
  {showGolfRatesOnly ? (
    <>
      <col className="w-[5%]" />  {/* Course (9H/18H) */}
      <col className="w-[8%]" />  {/* OR Number */}
      <col className="w-[17%]" /> {/* Player Name */}
      <col className="w-[9%]" />  {/* Tee Time */}
      <col className="w-[9%]" />  {/* End Time */}
      <col className="w-[12%]" /> {/* Duration */}
      <col className="w-[12%]" /> {/* Estimated Time */}
      <col className="w-[8%]" />  {/* Status */}
      <col className="w-[20%]" /> {/* Action */}
    </>
  ) : (
    <>
      <col className="w-[5%]" />  {/* Bay Number */}
      <col className="w-[8%]" />  {/* OR Number */}
      <col className="w-[17%]" /> {/* Player Name */}
      <col className="w-[9%]" />  {/* Time In */}
      <col className="w-[9%]" />  {/* Time Out */}
      <col className="w-[12%]" /> {/* Duration */}
      <col className="w-[12%]" /> {/* Estimated Time */}
      <col className="w-[8%]" />  {/* Status */}
      <col className="w-[20%]" /> {/* Actions */}
    </>
  )}
</colgroup>
    <tbody>

      
      {/* NEW: build rows with an optional pending Driving Range selection */}
{(() => {
  // TODO: PLAYER LOG TABLE
function displayPlayerName(log) {
  // Priority 1: Explicitly typed player_name from the input field
  const typedName = String(log?.player_name || "").trim();
  if (typedName) {
    return typedName;
  }
  
  // Priority 3: Default
  return "-";
}

function punchedByBadge(log) {
  const name = String(log?.punched_by_name || "").trim();
  if (!name) return null;
  return (
    <span className="text-[9px] font-medium text-amber-700 truncate max-w-[140px]" title={`Punched by ${name}`}>
      {name}
    </span>
  );
}

    // Show paused table
if (showPausedTable) {
  if (pausedPlayerLogs.length === 0) {
    return (
      <tr>
        <td colSpan={9} className="py-8 text-center text-gray-300">
          <Pause className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">No paused bookings</p>
        </td>
      </tr>
    );
  }
  
return pausedPlayerLogs.map((log, idx) => {
  const txn = findTxnForLog(log, transactions);
  const uniqueKey = `paused-${log.or_number}-${log.id || idx}`;

  const nowManila = new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" });
  const todayManila = new Date(nowManila);

  // --- ✅ FIX: Prefer resumed times over original transaction times ---
  let timeIn = null;
  
  // Priority 1: Check if this log has resumed times (from previous resume)
  if (log.resumed_time_in) {
    timeIn = parseManilaDate(log.resumed_time_in);
    console.log(`📌 Using resumed_time_in for paused log ${log.id}:`, timeIn);
  }
  // Priority 3: Fall back to transaction start_time
  else if (txn?.start_time && /^\d{1,2}:\d{2}$/.test(txn.start_time)) {
    const [h, m] = txn.start_time.split(":").map(Number);
    const startDate = new Date(todayManila);
    startDate.setHours(h, m, 0, 0);
    timeIn = startDate;
    // console.log(`📌 Using txn.start_time for paused log ${log.id}:`, timeIn);
  }

  // --- ✅ FIX: Prefer resumed timeout over calculated timeout ---
  let timeOut = null;
  
  // Priority 1: Check if this log has resumed timeout (from previous resume)
  if (log.resumed_time_out) {
    timeOut = parseManilaDate(log.resumed_time_out);
    // console.log(`📌 Using resumed_time_out for paused log ${log.id}:`, timeOut);
  }
  // Priority 3: Calculate from timeIn + duration (fallback)
  else if (timeIn && txn?.items && Array.isArray(txn.items)) {
    let totalDurationMinutes = 0;
    txn.items.forEach(item => {
      const product = products.find(p => p.id === item.product_id);
      let durationPerUnit = 60; // default
      if (product?.duration) {
        durationPerUnit = Number(product.duration) || 60;
      } else if (product?.name) {
        const name = product.name.toLowerCase();
        const match = name.match(/(\d+)\s*(hour|hr|hrs|hours)/);
        if (match) {
          durationPerUnit = parseInt(match[1], 10) * 60;
        } else {
          const minMatch = name.match(/(\d+)\s*(min|mins|minute|minutes)/);
          if (minMatch) {
            durationPerUnit = parseInt(minMatch[1], 10);
          }
        }
      }
      totalDurationMinutes += durationPerUnit * (item.quantity || 1);
    });
    if (totalDurationMinutes > 0) {
      timeOut = new Date(timeIn.getTime() + totalDurationMinutes * 60000);
      // console.log(`📌 Calculated timeOut for paused log ${log.id}:`, timeOut);
    }
  }

  // Use FROZEN remaining time from database
  const remainingMsAtPause = typeof log.remaining_ms_at_pause === "number" ? log.remaining_ms_at_pause : null;

    // --- Estimated Time (FROZEN - uses stored snapshot) ---
    function getEstimatedTimePaused() {
      // ✅ Only use the frozen remaining time stored at pause
      if (remainingMsAtPause !== null) {
        if (remainingMsAtPause < 0) {
          // Was already overtime when paused
          const overtimeMins = Math.floor(Math.abs(remainingMsAtPause) / 60000);
          const h = Math.floor(overtimeMins / 60);
          const m = overtimeMins % 60;
          return {
            text: `+${h > 0 ? h + "h " : ""}${m}m overtime`,
            color: "bg-red-100 text-red-700"
          };
        } else {
          // Had time remaining when paused
          const remainingMins = Math.floor(remainingMsAtPause / 60000);
          const h = Math.floor(remainingMins / 60);
          const m = remainingMins % 60;
          return {
            text: `${h > 0 ? h + "h " : ""}${m}m left`,
            color: "bg-blue-100 text-blue-700"
          };
        }
      }
      
      // Fallback if no frozen time stored
      return { text: "Paused", color: "bg-purple-100 text-purple-700" };
    }
    
    const estimatedTimeInfo = getEstimatedTimePaused();

    const pausedGroupName = log.group_name
      || (log.service_type === "Golf Rates" ? txn?.golf_group_name : txn?.dr_group_name)
      || null;
    const isPausedGroupBooking = Boolean(pausedGroupName);
    const isPausedGuest = Boolean(log.is_guest);
    const pausedGroupTotal = Number(log.players_count) || 1;

    return (
      <tr 
        key={uniqueKey} 
        className={`border-b border-gray-200 bg-purple-50/60 hover:bg-purple-100/60 transition-colors cursor-pointer ${txn && !txn.or_number ? 'hover:bg-blue-50' : ''}`}
        style={isPausedGroupBooking ? { borderLeft: '3px solid rgb(168, 85, 247)' } : {}}
        onClick={() => {
          if (txn && !txn.or_number) {
            openTxnModal(txn);
          }
        }}
      >
        <td className="px-2 py-1.5 text-sm">
          {log.bay_number ? (
            <span className="inline-flex items-center justify-center bg-purple-100 text-purple-800 px-2 py-0.5 rounded-md text-[10px] font-bold shadow-sm border border-purple-200">
              B{log.bay_number}
            </span>
          ) : (
            <span className="text-gray-400 text-[10px]">—</span>
          )}
        </td>
        <td className="px-2 py-1.5 text-xs font-mono text-gray-600">{log.or_number || "-"}</td>
        <td className="px-2 py-1.5 text-xs font-medium text-gray-900">
          {isPausedGroupBooking ? (
            <div className="flex flex-col gap-0.5">
              <span className="truncate max-w-[160px] font-semibold text-gray-900 leading-tight">{log.player_name || log.member_name}</span>
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3 text-violet-500 flex-shrink-0" />
                <span className="text-[10px] font-semibold text-violet-600 truncate max-w-[100px]">{pausedGroupName}</span>
                {!isPausedGuest && pausedGroupTotal > 1 && (
                  <span className="text-[9px] font-medium text-violet-400">({pausedGroupTotal})</span>
                )}
              </div>
              {punchedByBadge(log)}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              <span className="truncate max-w-[160px]">{log.player_name || log.member_name}</span>
              {punchedByBadge(log)}
            </div>
          )}
        </td>
        <td className="px-2 py-1.5 text-center text-xs text-gray-700">
          {timeIn ? <span className="font-medium">{timeIn.toLocaleTimeString("en-PH", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}</span> : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-2 py-1.5 text-center text-xs text-gray-700">
          {timeOut ? <span className="font-medium">{timeOut.toLocaleTimeString("en-PH", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}</span> : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-2 py-1.5 text-center text-xs font-semibold text-gray-800">Paused</td>
        <td className="px-2 py-1.5 text-center">
          <span className={`text-[10px] px-2 py-0.5 rounded-md font-semibold shadow-sm ${estimatedTimeInfo.color}`}>
            {estimatedTimeInfo.text}
          </span>
        </td>
        <td className="px-2 py-1.5 text-center">
          <span className="text-[10px] px-2 py-0.5 rounded-md font-semibold shadow-sm bg-purple-100 text-purple-700 border border-purple-200">
            Paused
          </span>
        </td>
          <td className="px-2 py-1.5 text-center">
            <div className="flex justify-center items-center gap-0.5 h-full">
              <Button
                size="sm"
                style={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  minHeight: 24,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setResumeLog(log);
                  setResumePlayerLog({
                    bay_number: log.bay_number || "",
                    time_in: "",
                    time_out: "",
                    ui_label_start: "",
                    ui_label_end: "",
                    ui_locked: false,
                  });
                  setResumeManualTime("");
                  setResumeBayNumberError("");
                  setIsResumeModalOpen(true);
                }}
                className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                aria-label="Resume"
              >
                <Play className="w-3 h-3" />
              </Button>
              {/* Delete button for paused entries (double-punch correction) */}
              {canDeletePlayerLog && (
              <Button
                size="sm"
                style={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  minHeight: 24,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  openDeletePlayerLog(log);
                }}
                className="bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-700 border border-gray-200 hover:border-red-200 rounded-full"
                aria-label="Delete this player log"
                title="Delete this player log (e.g. double punch correction)"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
              )}
            </div>
          </td>
      </tr>
    );
  });
}
  
  // Show active table
  const displayRows = (showGolfRatesOnly
    ? activePlayerLogs.filter(log => log.service_type === "Golf Rates")
    : activePlayerLogs.filter(log => log.service_type === "Driving Range Rates")
  );

  if (displayRows.length === 0) {
    return (
          <tr>
            <td colSpan={8} className="py-8 text-center  text-gray-300">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <div className="flex-inline items-center justify-center">
                <p className="text-lg font-medium">
                  {showGolfRatesOnly
                    ? "No active golf reservations found"
                    : "No active driving range bookings found"}
                </p>
              </div>
            </td>
          </tr>
    );
  }
  
// Add helper to parse Manila times consistently
function parseManilaDate(dateStr) {
  if (!dateStr) return null;
  
  try {
    // If it's already a Date object, return it
    if (dateStr instanceof Date) return dateStr;
    
    let isoStr = String(dateStr);
    
    // Add Manila timezone if missing
    if (!isoStr.includes('+') && !isoStr.includes('Z')) {
      isoStr += '+08:00';
    }
    
    return new Date(isoStr);
  } catch (e) {
    console.error("Date parsing error:", dateStr, e);
    return null;
  }
}


function getDisplayTimeInOut(log, txn) {
  if (log.was_resumed && log.resumed_time_in && log.resumed_time_out) {
    return {
      timeIn: parseManilaDate(log.resumed_time_in),
      timeOut: parseManilaDate(log.resumed_time_out),
      isResumed: true,
    };
  }
  if (log.time_in && log.time_out) {
    return {
      timeIn: parseManilaDate(log.time_in),
      timeOut: parseManilaDate(log.time_out),
      isResumed: false,
    };
  }
  if (txn?.start_time) {
    const [h, m] = txn.start_time.split(":").map(Number);
    const today = new Date();
    return {
      timeIn: new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0, 0),
      timeOut: null,
      isResumed: false,
    };
  }
  return { timeIn: null, timeOut: null, isResumed: false };
}

  // Helper: derive planned duration (minutes)
  function getPlannedDurationMinutes(log) {
    if (log.service_type === "Golf Rates") {
      // Golf reservations are typically 4 hours (240 minutes)
      return 240;
    }
    
    // For driving range, use existing logic
    const txn = findTxnForLog(log, transactions);
    if (!txn || !txn.items) return 0;
    let total = 0;
    txn.items.forEach(it => {
      const prod = products.find(p => p.id === it.product_id);
      if (prod?.category === "Driving Range Rates" && prod?.duration) {
        total += Number(prod.duration) * (it.quantity || 1);
      }
    });
    return total;
  }

  const activeRows = displayRows.map((log, idx) => {
    const txn = findTxnForLog(log, transactions);
    const isMissingOr = !log.or_number || (txn && !txn.or_number);
    const uniqueKey = `${log.service_type || "unknown"}-${log.or_number || "no-or"}-${log.id || idx}`;
    const isGuest = Boolean(log.is_guest);
    // Resolve group name: prefer log-level, fallback to transaction-level
    const groupName = log.group_name
      || (log.service_type === "Golf Rates" ? txn?.golf_group_name : txn?.dr_group_name)
      || null;
    
// TODO: PLAYER TIME IN/OUT DISPLAY LOGIC
// --- TIME IN ---
let timeIn = null;
if (log.was_resumed && log.resumed_time_in) {
  // resumed booking (highest priority)
  timeIn = parseManilaDate(log.resumed_time_in);
} else if (log.time_in) {
  // explicit time stored on the log (guest logs / resumed-created logs)
  timeIn = parseManilaDate(log.time_in);
} else if (txn?.start_time) {
  // fallback to transaction start_time "HH:MM"
  const [h, m] = txn.start_time.split(":").map(Number);
  const today = new Date();
  timeIn = new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0, 0);
}
    // ✅ PRIORITY 1: Use resumed_time_in if this log was resumed


    // --- Golf TIME IN ---
let golfTimeIn = null;
if (log.time_in && log.service_type === "Golf Rates") {
  // Per-log explicit time (guests or resumed logs) should take precedence
  golfTimeIn = parseManilaDate(log.time_in) || null;
} else if (txn?.startgolf_time) {
  // Fallback to transaction tee time only when log has no explicit time_in
  if (String(txn.startgolf_time).includes("T") || String(txn.startgolf_time).includes("-")) {
    golfTimeIn = new Date(txn.startgolf_time);
  } else if (/^\d{1,2}:\d{2}$/.test(String(txn.startgolf_time))) {
    const [h, m] = String(txn.startgolf_time).split(":").map(Number);
    const today = new Date();
    golfTimeIn = new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0, 0);
  }
}

// --- TIME OUT ---
let timeOut = null;
if (log.was_resumed && log.resumed_time_out) {
  // resumed booking (highest priority)
  timeOut = parseManilaDate(log.resumed_time_out);
} else if (log.time_out) {
  // explicit end time on the log (guest logs / resumed-created logs)
  timeOut = parseManilaDate(log.time_out);
} else if (timeIn) {
  // compute from planned duration if no explicit time_out
  const planned = getPlannedDurationMinutes(log);
  if (planned > 0) {
    timeOut = new Date(timeIn.getTime() + planned * 60000);
  }
}

// --- GOLF TIME OUT --- (replace previous block similarly)
let golfTimeOut = null;
if (log.time_out && log.service_type === "Golf Rates") {
  golfTimeOut = parseManilaDate(log.time_out) || null;
} else if (txn?.endgolf_time) {
  if (String(txn.endgolf_time).includes("T") || String(txn.endgolf_time).includes("-")) {
    golfTimeOut = new Date(txn.endgolf_time);
  } else if (/^\d{1,2}:\d{2}$/.test(String(txn.endgolf_time))) {
    const [h, m] = String(txn.endgolf_time).split(":").map(Number);
    const today = new Date();
    golfTimeOut = new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0, 0);
  }
} else if (golfTimeIn) {
  // default duration for golf (4 hours) if nothing else provided
  golfTimeOut = new Date(golfTimeIn.getTime() + 4 * 60 * 60 * 1000);
}

    // --- ESTIMATED TIME ---
    function getManilaTime() {
      const now = new Date();
      return new Date(now.toLocaleString("en-US", { timeZone: "Asia/Manila" }));
    }
    const nowPH = getManilaTime();
    
// --- IMPROVED OVERTIME DETECTION LOGIC ---
let isInactive = false;
let overtimeMins = 0;
let estimatedTimeInfo = { text: "Not Started", color: "bg-green-100 text-green-700" };

// Check for overtime based on service type
if (log.service_type === "Golf Rates" && golfTimeOut && nowPH > golfTimeOut) {
  const overtimeMs = nowPH - golfTimeOut;
  overtimeMins = Math.floor(overtimeMs / 60000);
  const h = Math.floor(overtimeMins / 60);
  const m = overtimeMins % 60;
  
  estimatedTimeInfo.text = `+${h > 0 ? h + "h " : ""}${m}m overtime`;
  // keep warning (red) at 4m
  estimatedTimeInfo.color = overtimeMins >= 4 ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700";
  
} else if (log.service_type === "Driving Range Rates" && timeOut && nowPH > timeOut) {
  const overtimeMs = nowPH - timeOut;
  overtimeMins = Math.floor(overtimeMs / 60000);
  const h = Math.floor(overtimeMins / 60);
  const m = overtimeMins % 60;
  
  estimatedTimeInfo.text = `+${h > 0 ? h + "h " : ""}${m}m overtime`;
  // keep warning (red) at 4m
  estimatedTimeInfo.color = overtimeMins >= 4 ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700";
  
} else {
  // Not in overtime - use regular time estimation logic
  estimatedTimeInfo = getEstimatedTime();
}
    
    function getEstimatedTime() {
      // --- Golf Rates ---
      if (log.service_type === "Golf Rates" && golfTimeIn) {
        // If tee time is in the future
        if (nowPH < golfTimeIn) {
          const timeUntilStart = golfTimeIn - nowPH;
          const minsUntilStart = Math.floor(timeUntilStart / 60000);
          const hoursUntilStart = Math.floor(minsUntilStart / 60);
          const remainingMins = minsUntilStart % 60;
          return {
            text: `Starts in ${hoursUntilStart > 0 ? hoursUntilStart + "h " : ""}${remainingMins}m`,
            color: "bg-yellow-100 text-yellow-700"
          };
        }
        // If finished
        if (golfTimeOut && nowPH >= golfTimeOut) {
          const overtimeMs = nowPH - golfTimeOut;
          const overtimeMins = Math.floor(overtimeMs / 60000);
          const h = Math.floor(overtimeMins / 60);
          const m = overtimeMins % 60;
          return {
            text: `+${h > 0 ? h + "h " : ""}${m}m overtime`,
            color: "bg-red-100 text-red-700"
          };
        }
        // If ongoing
        if (golfTimeOut) {
          const remainingMs = golfTimeOut - nowPH;
          const remainingMins = Math.floor(remainingMs / 60000);
          const h = Math.floor(remainingMins / 60);
          const m = remainingMins % 60;
          return {
            text: `${h > 0 ? h + "h " : ""}${m}m left`,
            color: "bg-blue-100 text-blue-700"
          };
        }
        // If only started, show elapsed
        const elapsedMs = nowPH - golfTimeIn;
        const elapsedMins = Math.floor(elapsedMs / 60000);
        const eh = Math.floor(elapsedMins / 60);
        const em = elapsedMins % 60;
        return {
          text: `${eh > 0 ? eh + "h " : ""}${em}m elapsed`,
          color: "bg-blue-100 text-blue-700"
        };
      }

      // --- Driving Range Rates ---
      if (timeIn) {
        if (nowPH < timeIn) {
          const timeUntilStart = timeIn - nowPH;
          const minsUntilStart = Math.floor(timeUntilStart / 60000);
          const hoursUntilStart = Math.floor(minsUntilStart / 60);
          const remainingMins = minsUntilStart % 60;
          return {
            text: `Starts in ${hoursUntilStart > 0 ? hoursUntilStart + "h " : ""}${remainingMins}m`,
            color: "bg-yellow-100 text-yellow-700"
          };
        }
        if (timeOut && nowPH >= timeOut) {
          const overtimeMs = nowPH - timeOut;
          const overtimeMins = Math.floor(overtimeMs / 60000);
          const h = Math.floor(overtimeMins / 60);
          const m = overtimeMins % 60;
          return {
            text: `+${h > 0 ? h + "h " : ""}${m}m overtime`,
            color: "bg-red-100 text-red-700"
          };
        }
        if (timeOut) {
          const remainingMs = timeOut - nowPH;
          const remainingMins = Math.floor(remainingMs / 60000);
          const h = Math.floor(remainingMins / 60);
          const m = remainingMins % 60;
          return {
            text: `${h > 0 ? h + "h " : ""}${m}m left`,
            color: "bg-blue-100 text-blue-700"
          };
        }
        const elapsedMs = nowPH - timeIn;
        const elapsedMins = Math.floor(elapsedMs / 60000);
        const eh = Math.floor(elapsedMins / 60);
        const em = elapsedMins % 60;
        return {
          text: `${eh > 0 ? eh + "h " : ""}${em}m elapsed`,
          color: "bg-blue-100 text-blue-700"
        };
      }

      return { text: "Not Started", color: "bg-green-100 text-green-700" };
    }

    // --- STATUS ---
    function getStatus({ log, txn, timeIn, timeOut, golfTimeIn, golfTimeOut, showGolfRatesOnly, nowPH }) {
      // Default status
      let status = { text: "Not Started", color: "text-green-700 bg-green-100" };

      // --- Golf Rates ---
      if (log.service_type === "Golf Rates") {
        if (golfTimeIn) {
          if (nowPH < golfTimeIn) {
            status = { text: "Not Started", color: "text-green-700 bg-green-100" };
          } else if (golfTimeOut && nowPH >= golfTimeOut) {
            status = { text: "Finished", color: "text-red-700 bg-red-100" };
          } else if (golfTimeOut) {
            if (nowPH >= golfTimeOut) {
              status = { text: "Playing (Overtime)", color: "text-red-700 bg-red-100" };
            } else {
              status = { text: "Playing", color: "text-blue-700 bg-blue-100" };
            }
          } else {
            status = { text: "Playing", color: "text-blue-700 bg-blue-100" };
          }
        }
        return status;
      }

      // --- Driving Range Rates ---
      if (timeIn) {
        if (nowPH < timeIn) {
          status = { text: "Not Started", color: "text-green-700 bg-green-100" };
        } else if (timeOut && nowPH >= timeOut) {
          status = { text: "Finished", color: "text-red-700 bg-red-100" };
        } else if (timeOut) {
          if (nowPH >= timeOut) {
            status = { text: "Ongoing (Overtime)", color: "text-red-700 bg-red-100" };
          } else {
            status = { text: "Ongoing", color: "text-blue-700 bg-blue-100" };
          }
        } else {
          status = { text: "Ongoing", color: "text-blue-700 bg-blue-100" };
        }
        return status;
      }

      return status;
    }

    // --- DURATION ---
  const durationInfo = computeDurationForLog({
    log,
    timeIn,
    timeOut,
    golfTimeIn,
    golfTimeOut,
    transactions,
    products
  });
    
    const duration = durationInfo.text;
// derive a friendly service label when log.selected_service is not present
const deriveServiceLabel = (log, txn, products) => {
  // prefer explicit selected_service
  if (log?.selected_service?.name) return log.selected_service.name;

  // prefer log's own product_name/product_id (set for guest DR entries)
  if (log?.product_name) return log.product_name;
  if (log?.product_id) {
    const prod = products.find(p => String(p.id) === String(log.product_id));
    if (prod) {
      return prod.name || "";
    }
  }

  // try to find a driving-range product from the transaction items
  const items = Array.isArray(txn?.items) ? txn.items : [];
  const drItem = items.find(it => {
    const prod = products.find(p => String(p.id) === String(it.product_id));
    return prod && prod.category === "Driving Range Rates";
  });

  if (drItem) {
    const prod = products.find(p => String(p.id) === String(drItem.product_id));
    if (prod) {
      const mins = parseProductDurationMinutes(prod); // helper already present in file
      if (mins % 60 === 0) {
        const hours = mins / 60;
        return `Driving Range - ${hours} Hour${hours > 1 ? "s" : ""}`;
      } else {
        return `Driving Range - ${mins} min`;
      }
    }
  }

  // fallback: show product name if available
  const firstItem = items[0];
  if (firstItem) {
    const prod = products.find(p => String(p.id) === String(firstItem.product_id));
    if (prod) return prod.name || "";
  }

  return "";
};

const selectedServiceLabel = deriveServiceLabel(log, txn, products);

    const status = getStatus({
      log,
      txn,
      timeIn,
      timeOut,
      golfTimeIn,
      golfTimeOut,
      showGolfRatesOnly,
      nowPH
    });
    
    const canPause = (() => {
  // booking must have started (now >= timeIn) and not already finished,
  // and not flagged inactive/finished/paused
  if (!timeIn) return false;
  if (nowPH < timeIn) return false; // not started yet -> disable
  if (timeOut && nowPH >= timeOut) return false; // already finished/overtime -> disable
  if (isInactive) return false;
  if (status?.text === "Finished") return false;
  if (log?.is_paused) return false;
  return true;
})();

    // End permission: cannot end before start, cannot end after already finished/marked inactive, and cannot end while paused
    const canEnd = (() => {
      const start = (log.service_type === "Golf Rates") ? golfTimeIn : timeIn;
      // const end = (log.service_type === "Golf Rates") ? golfTimeOut : timeOut; // not used to block ending
      if (!start) return false;                    // no known start
      if (nowPH < start) return false;             // hasn't started yet

      // If backend already marked inactive -> cannot end
      if (log?.is_active === false) return false;

      // If backend recorded an explicit time_out and that recorded time is already in the past,
      // treat the log as finished and disallow ending again.
      // Note: allow ending even if a recorded time_out exists (overtime).
      // We only block ending when the backend explicitly marked the log inactive
      // (handled earlier) or when the booking hasn't started yet or is paused.

      // If the booking is paused, do not allow ending while paused
      if (log?.is_paused) return false;

      // Allow ending while playing or when overtime (now past planned end)
      return true;
    })();

    // Add overtime-based row styling
    const rowClass = `border-b border-gray-300 hover:bg-gray-50 
      ${overtimeMins >= 4 ? "bg-red-50" : overtimeMins > 0 ? "bg-yellow-50" : ""}`;

    // Check if this row should be highlighted from notification click
    const playerRowKey = `${log.or_number}-${log.bay_number}`;
    const isHighlightedFromNotification = highlightedPlayerKey === playerRowKey;

    // Determine if this is a group booking row
    const isGroupBooking = Boolean(groupName);
    const groupPlayersTotal = Number(log.players_count) || 1;
    const isMainGroupPlayer = isGroupBooking && !isGuest;
    const isGroupMember = isGroupBooking && isGuest;

    return (
          <tr 
            key={uniqueKey}
            id={`player-row-${log.or_number}-${log.bay_number}`}
            className={`border-b transition-all duration-300
              ${isMissingOr
                ? "border-amber-200 bg-amber-50/60 hover:bg-amber-100"
                : isHighlightedFromNotification 
                  ? "border-gray-200 bg-yellow-200 ring-2 ring-yellow-400 animate-pulse shadow-lg" 
                  : overtimeMins >= 3 
                    ? "border-gray-200 bg-red-50 text-red-900 shadow-inner ring-1 ring-red-200" 
                    : overtimeMins > 0 
                      ? "border-gray-200 bg-amber-50/60 hover:bg-amber-50" 
                      : isGroupBooking
                        ? "border-gray-200 bg-violet-50/40 hover:bg-violet-50/70"
                        : "border-gray-200 hover:bg-slate-50"}
              ${overtimeMins >= 5 && isInactive ? "line-through text-gray-400 opacity-60" : ""}
              cursor-pointer
            `}
            style={isMissingOr ? { borderLeft: '3px solid rgb(245, 158, 11)' } : isGroupBooking ? { borderLeft: '3px solid rgb(139, 92, 246)' } : {}}
            onClick={() => {
              if (isMissingOr && txn) {
                openTxnModal(txn);
              }
            }}
          >
            {/* TODO: GOLF HOLES */}
        {/* Course Column (Golf) / Bay Column (Driving Range) */}
            {showGolfRatesOnly ? (
              <td className="px-2 py-1.5 text-sm">
                {(() => {
                  const golfHoles = (() => {
                    // prefer backend-provided selected hole count
                    if (Number.isFinite(Number(log?.golfhole))) return Number(log.golfhole);
                    // fallback to first course holes if present
                    if (Array.isArray(log?.individual_courses) && log.individual_courses.length) {
                      const h = Number(log.individual_courses[0]?.holes);
                      if (Number.isFinite(h)) return h;
                    }
                    // fallback to numeric total_holes if single-course logs (guest logs already set equal)
                    if (Number.isFinite(Number(log?.total_holes))) return Number(log.total_holes);
                    // last fallback based on course_type text (9-holes/18-holes)
                    const ct = String(log?.course_type || "").toLowerCase();
                    if (ct.includes("9")) return 9;
                    return 18;
                  })();
                  return (
                    <span className="inline-flex items-center justify-center bg-blue-100 text-blue-800 px-2 py-0.5 rounded-md text-[10px] font-bold shadow-sm border border-blue-200">
                      {golfHoles}H
                    </span>
                  );
                })()}
              </td>
            ) : (
          <td className="px-2 py-1.5 text-sm">
            {log.bay_number ? (
              <span className="inline-flex items-center justify-center bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md text-[10px] font-bold shadow-sm border border-emerald-200">
                B{log.bay_number}
              </span>
            ) : (
              <span className="text-gray-400 text-[10px]">—</span>
            )}
          </td>
        )}
        
        {/* OR Number */}
        <td className={`px-2 py-1.5 text-xs font-mono ${isMissingOr ? "text-amber-600 font-semibold" : "text-gray-600"}`}>
          {log.or_number || (isMissingOr ? "No OR" : "-")}
        </td>
        
        {/* Player Name + Group Info */}
   <td className="px-2 py-1.5 text-xs font-medium text-gray-900">
     {isGroupBooking ? (
       <div className="flex flex-col gap-0.5">
         <span className="truncate max-w-[160px] font-semibold text-gray-900 leading-tight">{displayPlayerName(log)}</span>
         <div className="flex items-center gap-1">
           <Users className="w-3 h-3 text-violet-500 flex-shrink-0" />
           <span className="text-[10px] font-semibold text-violet-600 truncate max-w-[100px]">{groupName}</span>
           {isMainGroupPlayer && groupPlayersTotal > 1 && (
             <span className="text-[9px] font-medium text-violet-400">({groupPlayersTotal})</span>
           )}
         </div>
         {punchedByBadge(log)}
       </div>
     ) : (
       <div className="flex flex-col gap-0.5">
         <span className="truncate max-w-[160px]">{displayPlayerName(log)}</span>
         {punchedByBadge(log)}
       </div>
     )}
   </td>

        
        {/* Time In / Tee Time */}
        <td className="px-2 py-1.5 text-center text-xs text-gray-700">
          {log.service_type === "Golf Rates" && golfTimeIn ? (
            <span className="font-medium">
              {golfTimeIn.toLocaleTimeString("en-PH", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone: "Asia/Manila",
              })}
            </span>
          ) : timeIn ? (
            <span className="font-medium">
              {timeIn.toLocaleTimeString("en-PH", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone: "Asia/Manila",
              })}
            </span>
          ) : <span className="text-gray-300">—</span>}
        </td>
        
        {/* Time Out / End Time */}
        <td className="px-2 py-1.5 text-center text-xs text-gray-700">
          {log.service_type === "Golf Rates" && golfTimeOut ? (
            <span className="font-medium">
              {golfTimeOut.toLocaleTimeString("en-PH", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone: "Asia/Manila",
              })}
            </span>
          ) : timeOut ? (
            <span className="font-medium">
              {timeOut.toLocaleTimeString("en-PH", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone: "Asia/Manila",
              })}
            </span>
          ) : <span className="text-gray-300">—</span>}
        </td>
          {/* Duration column - now shows service-specific duration */}
          <td className="px-2 py-1.5 text-center">
            <div className="text-xs">
              <div className="font-semibold text-gray-800">{duration}</div>
              {/* Show service label for Driving Range */}
              {log.service_type === "Driving Range Rates" && selectedServiceLabel && (
                <div className="text-gray-500 text-[10px]">
                  ({selectedServiceLabel})
                </div>
              )}
              {/* Show course info for Golf Rates */}
                {log.service_type === "Golf Rates" && (
                  (() => {
                    const golfHoles = (() => {
                      // prefer backend-provided selected hole count
                      if (Number.isFinite(Number(log?.golfhole))) return Number(log.golfhole);
                      // fallback to first course holes if present
                      if (Array.isArray(log?.individual_courses) && log.individual_courses.length) {
                        const h = Number(log.individual_courses[0]?.holes);
                        if (Number.isFinite(h)) return h;
                      }
                      // fallback to numeric total_holes if single-course logs (guest logs already set equal)
                      if (Number.isFinite(Number(log?.total_holes))) return Number(log.total_holes);
                      // last fallback based on course_type text (9-holes/18-holes)
                      const ct = String(log?.course_type || "").toLowerCase();
                      if (ct.includes("9")) return 9;
                      return 18;
                    })();
                    return (
                      <div className="text-blue-600 text-[10px]">
                        ({golfHoles}H{log.course_type ? ` • ${log.golfhole}-Holes` : ""})
                      </div>
                    );
                  })()
                )}
            </div>
          </td>
        {/* Estimated Time - Enhanced with overtime indicators */}
        <td className="px-2 py-1.5 text-center">
            <span className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-colors duration-300 shadow-sm ${estimatedTimeInfo.color}`}>
              {estimatedTimeInfo.text}
            </span>
            {overtimeMins >= 5 && (
              <span
                role="status"
                aria-live="polite"
                className="block mt-1 text-[10px] font-bold text-red-600 animate-pulse"
                title="This booking will be auto-removed soon"
              >
                Auto-removing
              </span>
            )}  
        </td>
        
        {/* Status */}
        <td className="px-2 py-1.5 text-center">
          <span className={`text-[10px] px-2 py-0.5 rounded-md font-semibold shadow-sm ${status.color}`}>
            {status.text}
          </span>
          {isMissingOr && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                Needs OR
              </span>
            </div>
          )}
        </td>
                
        {/* Actions */}
        {!showGolfRatesOnly && (
          <td className="px-1 py-1.5 text-center">
            <div className="flex items-center justify-center gap-0.5 flex-nowrap">
              {isMissingOr ? (
                <>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-100 font-semibold whitespace-nowrap"
                  onClick={(e) => { e.stopPropagation(); txn && openTxnModal(txn); }}
                >
                  Assign OR
                </Button>
                {canDeletePlayerLog && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); openDeletePlayerLog(log); }}
                  aria-label="Delete this player log"
                  title="Delete this player log (e.g. double punch correction)"
                  className="p-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-700 border-gray-200 hover:border-red-200"
                  style={{ borderWidth: 1 }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
                )}
                </>
              ) : (
                <>
              
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); canPause && handlePauseLog(log.id); }}
                disabled={!canPause}
                aria-label={canPause ? "Pause this booking" : "Cannot pause booking"}
                title={canPause ? "Pause this booking" : "Cannot pause before start or after finish"}
                className={`p-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform ${
                  canPause ? "hover:scale-105" : "opacity-60 cursor-not-allowed"
                }`}
                style={{ borderWidth: 1 }}
              >
                {status.text === "Not Started" ? (
                  <span className="flex items-center justify-center w-full h-full rounded-full bg-green-100 text-green-700 border-green-200">
                    <Power className="w-3 h-3" />
                  </span>
                ) : isInactive ? (
                  <span className="flex items-center justify-center w-full h-full rounded-full bg-gray-300 text-gray-700 border-gray-200">
                    <Power className="w-3 h-3" />
                  </span>
                ) : timeOut && nowPH > timeOut ? (
                  <span className="flex items-center justify-center w-full h-full rounded-full bg-red-100 text-red-700 border-red-200">
                    <AlertTriangle className="w-3 h-3" />
                  </span>
                ) : (
                  <span className="flex items-center justify-center w-full h-full rounded-full bg-blue-100 text-blue-700 border-blue-200">
                    <Pause className={`w-3 h-3 ${canPause ? "text-yellow-700" : "text-gray-400"}`} />
                  </span>
                )}
              </Button>
          

              {/* Reschedule button for Driving Range */}
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setRescheduleLog(log);
                  setReschedulePlayerLog({
                    bay_number: log.bay_number || "",
                    time_in: "",
                    time_out: "",
                    ui_label_start: "",
                    ui_label_end: "",
                    ui_locked: false,
                    player_name: log.player_name || "",
                  });
                  setRescheduleReason("");
                  setRescheduleBayNumberError("");
                  setRescheduleManualTime("");
                  setIsRescheduleModalOpen(true);
                }}
                disabled={!log.is_active || log.is_paused || isInactive}
                aria-label="Reschedule this booking"
                title={!log.is_active || isInactive ? "Cannot reschedule inactive booking" : log.is_paused ? "Resume first to reschedule" : "Reschedule this booking to a new time"}
                className={`p-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform ${
                  log.is_active && !log.is_paused && !isInactive
                    ? "bg-indigo-100 hover:bg-indigo-200 text-indigo-700 border-indigo-200 hover:scale-105"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200 opacity-60"
                }`}
                style={{ borderWidth: 1 }}
              >
                <RefreshCw className="w-3 h-3" />
              </Button>

              {/* End Time button (X) for Driving Range */}
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); canEnd && setEndDrConfirmLog(log); }}
                disabled={!canEnd}
                aria-label={
                  canEnd
                    ? `End this booking${overtimeMins > 0 ? " (Overtime)" : ""}`
                    : overtimeMins > 0
                    ? "End this booking (Overtime)"
                    : "Cannot end booking"
                }
                title={
                  canEnd
                    ? `End this booking${overtimeMins > 0 ? " (Overtime)" : ""}`
                    : overtimeMins > 0
                    ? "End this booking (Overtime)"
                    : "Cannot end before start or after finish / while paused"
                }
                className={`p-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                  canEnd
                    ? "bg-red-100 hover:bg-red-200 text-red-700 border-red-200"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                }`}
                style={{ borderWidth: 1 }}
              >
                <X className="w-3 h-3" />
              </Button>

              {/* Delete button for Driving Range (double-punch correction) */}
              {canDeletePlayerLog && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); openDeletePlayerLog(log); }}
                aria-label="Delete this player log"
                title="Delete this player log (e.g. double punch correction)"
                className="p-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-700 border-gray-200 hover:border-red-200"
                style={{ borderWidth: 1 }}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
              )}
                </>
              )}
            </div>
          </td>
        )}
            {showGolfRatesOnly && (
              <td className="px-1 py-1.5 text-center">
              {isMissingOr ? (
                <div className="flex items-center justify-center gap-0.5 flex-nowrap">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-100 font-semibold whitespace-nowrap"
                  onClick={(e) => { e.stopPropagation(); txn && openTxnModal(txn); }}
                >
                  Assign OR
                </Button>
                {canDeletePlayerLog && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); openDeletePlayerLog(log); }}
                  aria-label="Delete this player log"
                  title="Delete this player log (e.g. double punch correction)"
                  className="p-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-700 border-gray-200 hover:border-red-200"
                  style={{ borderWidth: 1 }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
                )}
                </div>
              ) : (
              <div className="flex items-center justify-center gap-0.5 flex-nowrap">
              {/* Reschedule button for Golf */}
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setRescheduleLog(log);
                  setReschedulePlayerLog({
                    bay_number: "",
                    time_in: "",
                    time_out: "",
                    ui_label_start: "",
                    ui_label_end: "",
                    ui_locked: false,
                    player_name: log.player_name || "",
                  });
                  setRescheduleReason("");
                  setRescheduleBayNumberError("");
                  setRescheduleManualTime("");
                  setIsRescheduleModalOpen(true);
                }}
                disabled={!log.is_active || log.is_paused || isInactive}
                aria-label="Reschedule this booking"
                title={!log.is_active || isInactive ? "Cannot reschedule inactive booking" : log.is_paused ? "Resume first to reschedule" : "Reschedule this booking to a new time"}
                className={`flex items-center justify-center transition-colors ${
                  log.is_active && !log.is_paused && !isInactive
                    ? "bg-indigo-100 hover:bg-indigo-200 text-indigo-700 border-indigo-200"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200 opacity-60"
                } p-0 w-6 h-6 rounded-full`}
                style={{ borderWidth: 1 }}
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
              {/* End Time button for Golf */}
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); canEnd && setEndGolfConfirmLog(log); }}
                disabled={!canEnd}
                aria-label={
                  canEnd
                    ? `End this booking${overtimeMins > 0 ? " (Overtime)" : ""}`
                    : overtimeMins > 0
                    ? "End this booking (Overtime)"
                    : "Cannot end booking"
                }
                title={
                  canEnd
                    ? `End this booking${overtimeMins > 0 ? " (Overtime)" : ""}`
                    : overtimeMins > 0
                    ? "End this booking (Overtime)"
                    : "Cannot end before start or after finish / while paused"
                }
                className={`flex items-center justify-center transition-colors ${
                  canEnd
                    ? "bg-red-100 hover:bg-red-200 text-red-700 border-red-200"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                } p-0 w-6 h-6 rounded-full`}
                style={{ borderWidth: 1 }}
              >
                <X className="w-3 h-3" />
              </Button>
              {/* Delete button for Golf (double-punch correction) */}
              {canDeletePlayerLog && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); openDeletePlayerLog(log); }}
                aria-label="Delete this player log"
                title="Delete this player log (e.g. double punch correction)"
                className="p-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-700 border-gray-200 hover:border-red-200"
                style={{ borderWidth: 1 }}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
              )}
              </div>
              )}
              </td>
            )}
      </tr>
    );
  });

  return activeRows;
})()}
    </tbody>
  </table>
</div>
  </div>
</CardContent>
</Card>


                <Card className="shadow-xl">
                  <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-emerald-500 to-teal-500">
                    <CardTitle className="flex items-center space-x-2">
                      <Package className="w-5 h-5" />
                      <span>Products</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Category Filter */}
                    <div className="mb-6">
                      <Select
                        value={selectedCategory}
                        onValueChange={setSelectedCategory}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {categories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Products Grid */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {filteredProducts.map((product) => (
                        <Card
                          key={product.id}
                          className="transition-shadow border-2 cursor-pointer hover:shadow-lg hover:border-emerald-300"
                          onClick={() => addToCart(product)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-semibold text-gray-800">
                                {product.name}
                              </h3>
                              <Badge
                                variant="secondary"
                                style={{ pointerEvents: "none" }}
                                className="bg-emerald-100 text-emerald-800"
                              >
                                ₱{product.price.toFixed(2)}
                              </Badge>
                            </div>
                            <p className="mb-2 text-sm text-gray-600">
                              {product.description}
                            </p>
                            <div className="flex items-center justify-between">
                              {itemCategories.includes(product.category) && (
                                <Badge
                                  style={{ pointerEvents: "none" }}
                                  variant={product.stock_quantity <= product.low_stock_threshold ? "destructive" : "default"}
                                  className={product.stock_quantity <= product.low_stock_threshold ? "" : "bg-green-100 text-green-800"}
                                >
                                  Stock: {product.stock_quantity}
                                </Badge>
                              )}
                              <Button
                                size="sm"
                                className="bg-emerald-500 hover:bg-emerald-600"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cart & Checkout Section */}
              <div className="w-[400px] space-y-6">
                {/* Member Selection Tab System */}
                <Card className="border-2 border-blue-300 shadow-xl">
                  <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-blue-500 to-purple-500">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Users className="w-5 h-5" />
                        <span>Select Client</span>
                      </div>
                      <Badge
                        variant="secondary"
                        className="text-white bg-red-500"
                        style={{ pointerEvents: "none" }}
                      >
                        Required
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <Tabs value={selectedMember?.type || "member"} onValueChange={(tab) => {
                      if (tab === "walkin") {
                        setSelectedMember({ type: "walkin", name: "", discount_percentage: 0, membership_type: "Walk-in" });
                        setMemberSearch("");
                        setMemberSearchResults([]);
                      } else {
                        setSelectedMember(null);
                      }
                    }}>
                      <TabsList className="mb-4 grid grid-cols-2 w-full">
                        <TabsTrigger value="member" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                          <Users className="w-4 h-4 mr-2" />
                          Member
                        </TabsTrigger>
                        <TabsTrigger value="walkin" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                          <User className="w-4 h-4 mr-2" />
                          Walk-in
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="member">
                        <div className="space-y-3">
                          <div className="relative">
                            <Search className="absolute w-4 h-4 text-gray-400 left-3 top-3" />
                              <Input
                                ref={memberSearchInputRef}
                                placeholder="Search members by name, email, or phone..."
                                value={memberSearch}
                                onChange={(e) => setMemberSearch(e.target.value)}
                                className="pl-8"
                              />
                          </div>
                          {memberSearchResults.length > 0 && (
                            <div className="relative z-50 overflow-y-auto bg-white border rounded-lg shadow-sm max-h-40">
                              {memberSearchResults.map((member) => (
                                <div
                                  key={member.id}
                                  className="p-3 transition-colors border-b cursor-pointer hover:bg-blue-50 last:border-b-0"
                                  onClick={() => {
                                    setSelectedMember(member);
                                    setMemberSearch("");
                                    setMemberSearchResults([]);
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="font-medium text-gray-800">{member.name}</div>
                                      <div className="text-sm text-gray-500">{member.email}</div>
                                      <div className="text-sm text-gray-500">{member.phone}</div>
                                    </div>
                                    <div className="text-right">
                                      <Badge variant="outline" className="text-blue-600 border-blue-300">
                                        {member.membership_type}
                                      </Badge>
                                      <div className="text-sm font-medium text-green-600">
                                        {member.discount_percentage}% discount
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {memberSearch.length > 0 && memberSearchResults.length === 0 && (
                            <div className="space-y-3">
                              <Alert className="border-yellow-500 bg-yellow-50">
                                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                                <AlertDescription className="text-yellow-700">
                                  No members found for "{memberSearch}".
                                </AlertDescription>
                              </Alert>
                              <div className="text-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setActiveTab("members");
                                  }}
                                  className="text-blue-600 border-blue-500 hover:bg-blue-50"
                                >
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  Register New Member
                                </Button>
                              </div>
                            </div>
                          )}
                          {selectedMember && selectedMember.type !== "walkin" && (
                            <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <Check className="w-5 h-5 text-green-600" />
                                  <span className="font-semibold text-green-800">
                                    Selected Member
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedMember(null);
                                    setMemberSearch("");
                                  }}
                                  className="text-red-500 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="space-y-1">
                                <div className="font-medium text-green-800">{selectedMember.name}</div>
                                <div className="text-sm text-green-700">{selectedMember.email}</div>
                                <div className="text-sm text-green-700">{selectedMember.phone}</div>
                                <div className="flex items-center mt-2 space-x-2">
                                  <Badge style={{ pointerEvents: "none" }} variant="secondary" className="text-blue-800 bg-blue-100">
                                    {selectedMember.membership_type}
                                  </Badge>
                                  <Badge variant="outline" className="text-green-600 border-green-300">
                                    {selectedMember.discount_percentage}% Discount Applied
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      <TabsContent value="walkin">
                        <div className="space-y-3">
                          <Label htmlFor="walkinName">Walk-in Client Name</Label>
                            <Input
                              ref={walkinInputRef}
                              id="walkinName"
                              placeholder="Enter walk-in client name"
                              value={selectedMember?.type === "walkin" ? selectedMember.name : ""}
                              onChange={(e) =>
                                setSelectedMember({
                                  type: "walkin",
                                  name: e.target.value,
                                  discount_percentage: 0,
                                  membership_type: "Walk-in",
                                })
                              }
                              required
                            />
                          {selectedMember?.type === "walkin" && selectedMember.name && (
                            <div className="p-4 border border-purple-200 rounded-lg bg-purple-50">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <Check className="w-5 h-5 text-purple-600" />
                                  <span className="font-semibold text-purple-800">
                                    Walk-in Client Selected
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setSelectedMember(null)}
                                  className="text-red-500 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="space-y-1">
                                <div className="font-medium text-purple-800">{selectedMember.name}</div>
                                <Badge style={{ pointerEvents: "none" }} variant="secondary" className="text-blue-800 bg-blue-100">
                                  Walk-in
                                </Badge>
                                <Badge variant="outline" className="mx-2 text-gray-600 border-gray-300">
                                  No Discount
                                </Badge>
                              </div>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
                          {/* TODO: CART transaction history and payment status */}
<Card className="shadow-xl">
  <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-orange-500 to-red-500">
    <CardTitle className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <ShoppingCart className="w-5 h-5" />
        <span>Cart ({cart.length})</span>
      </div>
      {cart.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearCart}
          className="text-white hover:bg-white/20"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </CardTitle>
  </CardHeader>
  <CardContent className="p-4">
    {cart.length === 0 ? (
      <div className="py-8 text-center">
        <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p className="mb-2 text-gray-500">No items in cart</p>
        <div className="space-y-1 text-sm text-gray-400">
          <p>1. Add products to cart</p>
          <p>2. Select a member (required)</p>
          <p>3. Choose payment method</p>
          <p>4. Complete purchase</p>
        </div>
      </div>
    ) : (
      <div className="space-y-3">
        {cart.map((item) => (
          <div
            key={item.product_id}
            className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
          >
            <div className="flex-1">
              <div className="text-sm font-medium">
                {item.product_name}
              </div>
              <div className="text-sm text-gray-600">
                ₱{item.price.toFixed(2)} each
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  updateCartItemQuantity(
                    item.product_id,
                    item.quantity - 1
                  )
                }
                className="w-8 h-8 p-0"
              >
                <Minus className="w-3 h-3" />
              </Button>
              <span className="w-8 font-medium text-center">
                {item.quantity}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  updateCartItemQuantity(
                    item.product_id,
                    item.quantity + 1
                  )
                }
                className="w-8 h-8 p-0"
              >
                <Plus className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeFromCart(item.product_id)}
                className="w-8 h-8 p-0 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}

        <Separator className="my-4" />

 
        {/* Totals */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Subtotal:</span>
            <span>₱{subtotal.toFixed(2)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>
                {familyDiscountEnabled 
                  ? `Family Discount (${totalDiscountedItems} items):`
                  : `Discount:`
                }
              </span>
              <span>-₱{discountAmount.toFixed(2)}</span>
            </div>
          )}
          {familyDiscountEnabled && totalDiscountedItems > 0 && (
            <div className="text-xs text-emerald-600">
              {eligibleDiscountPersons} of {totalFamilyMembers} members @ {appliedDiscountPercentage}%
            </div>
          )}
          <Separator />
          <div className="flex justify-between text-lg font-bold">
            <span>Total:</span>
            <span>₱{total.toFixed(2)}</span>
          </div>
        </div>
        {/* Checkout Button */}
        <Button
          className={`w-full font-semibold py-3 ${
            !selectedMember ||
            (selectedMember.type === "walkin" && !selectedMember.name.trim()) ||
            cart.length === 0
              ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed"
              : "bg-emerald-500 hover:bg-emerald-600"
          } text-white`}
          onClick={async () => {
            console.log("Review Purchase button clicked");
            console.log("selectedMember:", selectedMember);
            console.log("cart.length:", cart.length);

            if (
              selectedMember &&
              (selectedMember.type !== "walkin" || selectedMember.name.trim()) &&
              cart.length > 0
            ) {
              // Refetch OR list right before checkout so the available ORs are up-to-date.
              // (Do not hard-fail checkout if the external API is down.)
              try {
                await refetchCashierOrList();
              } catch (e) {
                // ignore
              }

              // open full-page checkout inside POS tab (hide modal)
              setShowCheckoutPage(true);
              setIsCartSummaryModalOpen(false);
              setActiveTab("pos"); // keep tab selected
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          disabled={
            isProcessingPayment ||
            !selectedMember ||
            (selectedMember.type === "walkin" && !selectedMember.name.trim()) ||
            cart.length === 0
          }
        >
          {isProcessingPayment ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
              <span>Processing...</span>
            </div>
          ) : !selectedMember ? (
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Select Member to Continue</span>
            </div>
          ) : selectedMember.type === "walkin" && !selectedMember.name.trim() ? (
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Write a Name to Continue</span>
            </div>
          ) : cart.length === 0 ? (
            <div className="flex items-center space-x-2">
              <ShoppingCart className="w-4 h-4" />
              <span>Add Items to Cart</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <CreditCard className="w-4 h-4" />
              <span>Proceed to Payment</span>
            </div>
          )}
        </Button>

        {selectedMember?.type === "walkin" && cart.length > 0 && !selectedMember.name.trim() && (
          <Alert className="mt-3 border-red-500 bg-red-50">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-700">
              <strong>Name Required:</strong> Write a name for the non-member (walk-in) above to complete this purchase.
            </AlertDescription>
          </Alert>
        )}

        {!selectedMember && cart.length > 0 && (
          <Alert className="mt-3 border-red-500 bg-red-50">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-700">
              <strong>Member Required:</strong> Please search and select a member from the list above to complete this purchase.
            </AlertDescription>
          </Alert>
        )}
      </div>
    )}
  </CardContent>
</Card>

                {/* Low Stock Alert - POS Sidebar */}
                    {itemCategories.map((category) => {
                      const items = lowStockItems.filter(item => item.category === category);
                      if (items.length === 0) return null;
                      return (
                        <Card className="shadow-xl mb-4" key={category}>
                          <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-red-500 to-orange-500">
                            <CardTitle className="flex items-center space-x-2">
                              <AlertTriangle className="w-5 h-5" />
                              <span>Low Stock Alert: {category}</span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-4">
                            <div className="space-y-2 overflow-y-auto max-h-40">
                              {items.slice(0, 3).map((item) => (
                                <div
                                  key={item.id}
                                  className="p-2 border border-red-200 roundeds bg-red-50"
                                >
                                  <div className="text-sm font-medium text-red-800">
                                    {item.name}
                                  </div>
                                  <div className="text-xs text-red-600">
                                    Stock: {item.stock_quantity}
                                  </div>
                                </div>
                              ))}
                              {items.length > 3 && (
                                <div className="text-xs text-center text-gray-500">
                                  +{items.length - 3} more items
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
              </div>
            </div>
          )}
          </TabsContent>



          {/* TODO: tab content access for any role type users */}
          {/* Members Tab */}
  
          <TabsContent value="members">
            <MemberManagement token={token} user={user} />
          </TabsContent>
        
          {/* Membership Types Tab - Admin Only */}
          
            <TabsContent value="membership-types">
              <MembershipTypeManagement token={token} user={user} />
            </TabsContent>
          

          {/* Users Tab - Admin Only */}
          {user?.role === "admin" && (
            <TabsContent value="users">
              <UserManagement token={token} user={user} />
            </TabsContent>
          )}

          {/* Inventory Tab */}
          <TabsContent value="inventory">
              <ProductManagement
                token={token}
                user={user}
                fetchLowStockItems={fetchLowStockItems} 
              />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <ReportManagement token={token} user={user} products={products} transactions={transactions} reportSyncTick={reportSyncTick} />
          </TabsContent>

          {/* Activity Logs Tab (Admin Only) */}
          <TabsContent value="activity-logs">
            <ActivityLogsManagement token={token} user={user} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Cart Summary Modal
      {console.log(
        "🔍 Modal render - isCartSummaryModalOpen:",
        isCartSummaryModalOpen
      )} */}
          <Dialog
            open={isCartSummaryModalOpen}
            onOpenChange={(open) => {
              setIsCartSummaryModalOpen(open);
              // only clear selection when the modal is closed (user cancelled/completed)
              if (!open) {
                resetTimeSelection();
              }
            }}
          >
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader className="p-4 mb-4 -m-6 text-white rounded-t-lg bg-gradient-to-r from-emerald-500 to-teal-500">
            <DialogTitle className="flex items-center space-x-2 text-lg">
              <ShoppingCart className="w-5 h-5" />
              <span>Transaction Summary</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-emerald-100">
              Review and confirm your transaction
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Cart Items - Compact */}
<div>
  <div className="flex items-center justify-between mt-3">
    <div className="flex items-center space-x-2">
      <Package className="w-4 h-4 text-gray-600" />
      <span className="font-medium text-gray-800">
        Items ({cart.length})
      </span>
    </div>
  </div>
  <div className="space-y-2 overflow-y-auto max-h-32">
    {cart.map((item) => (
      <div
        key={item.product_id}
        className="flex items-center justify-between p-2 rounded-md bg-gray-50"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {item.product_name}
          </div>
          <div className="text-xs text-gray-500">
            ₱{item.price.toFixed(2)} × {item.quantity}
          </div>
        </div>
        <span className="ml-2 text-sm font-semibold text-emerald-600">
          ₱{(item.price * item.quantity).toFixed(2)}
        </span>
      </div>
    ))}
  </div>
</div>
            {/* Member Information - Compact */}
            <div className="p-2 border rounded-md bg-emerald-50 border-emerald-200 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium text-emerald-800 text-xs">Member</span>
                </div>
                <Badge
                  variant="outline"
                  className="text-emerald-600 border-emerald-300 text-xs"
                >
                  {appliedDiscountPercentage}% off
                </Badge>
              </div>

              <div className="mt-2">
                <div className="text-xs font-semibold text-emerald-800 truncate">
                  {selectedMember?.name || "-"}
                </div>
                <div className="text-[11px] text-emerald-600">
                  {selectedMember?.membership_type || "Walk-in"}
                </div>
              </div>

              {/* OR Number (read-only) + Total */}
              <div className="mt-1 grid grid-cols-2 gap-2 items-end text-xs">
                <div>
                  <Label className="text-[11px] font-medium">OR Number</Label>
                  <div className=" font-mono text-sm text-black  px-2 py-1 rounded">
                    {orNumber ? String(orNumber) : "—"}
                  </div>
                </div>

                <div className="text-right">
                  <Label className="text-[11px] font-medium">Total</Label>
                  <div className="mt-1 text-sm font-bold text-gray-800">
                    ₱{Number(total || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>


 
            
    {/* --- Time Slot Picker --- */}
{drivingRangeItems.length > 0 && (
  <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 mb-4">
    <h4 className="font-bold text-emerald-700 mb-2 flex items-center">
      <Clock className="w-4 h-4 mr-2" /> Driving Range Booking
    </h4>
<TimeSlotPicker
    newPlayerLog={newPlayerLog}
    setNewPlayerLog={setNewPlayerLog}
    durationMinutes={accumulatedDurationMinutes}
    isTimeSlotAvailable={isTimeSlotAvailable}
    bayNumberError={bayNumberError}
    selectedMember={selectedMember}
    orNumber={orNumber}
    toast={toast}
    manualTime={manualTime}
    setManualTime={setManualTime}
    activePlayerLogs={activePlayerLogs}
    transactions={transactions}
    products={products}
    getAvailableBaysForTime={getAvailableBaysForTime}
    isCartSummaryModalOpen={isCartSummaryModalOpen}
    cart={cart}
    setCart={setCart}
    drivingRangeItems={drivingRangeItems}
  onReservationStatsChange={setDrReservationStats}
  />
</div>
)}


{/* --- Golf Rates Booking Section --- */}
{golfRatesItems.length > 0 && (
  <div className="rounded-lg border border-blue-300 bg-blue-50 p-4 mb-4">
    <h4 className="font-bold text-blue-700 mb-2 flex items-center">
      <Clock className="w-4 h-4 mr-2" /> Golf Rates Booking
    </h4>
<GolfTeeTimeBooking
  newGolfReservation={newGolfReservation}
  setNewGolfReservation={setNewGolfReservation}
  selectedMember={selectedMember}
  orNumber={orNumber}
  // addGolfReservation={addGolfReservation}
  // fetchGolfReservations={fetchGolfReservations}
  toast={toast}
  isGolfTimeSlotAvailable={isGolfTimeSlotAvailable}
  golfRatesItems={golfRatesItems}
  products={products}
  determineCourseFromCart={determineCourseFromCart}
  setManualTeeTime={setManualTeeTime}
  manualTeeTime={manualTeeTime}
  reservedGolfSlot={reservedGolfSlot}
  setReservedGolfSlot={setReservedGolfSlot}
  isCartSummaryModalOpen={isCartSummaryModalOpen} // <-- NEW
  members={members}          
  setCart={setCart}
  onGolfReservationStatsChange={setGolfReservationStats}
  golfGroupBookingCount={golfGroupBookingCount}
  setGolfGroupBookingCount={setGolfGroupBookingCount}
/>
  </div>
)}

            {/* Action Buttons - Compact */}
            <div className="flex pt-2 space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsCartSummaryModalOpen(false)}
                className="flex-1 text-sm text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                onClick={processPayment}
                disabled={isProcessingPayment}
                className="flex-1 text-sm text-white bg-emerald-500 hover:bg-emerald-600"
              >
                {isProcessingPayment ? (
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 border-b-2 border-white rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <Check className="w-4 h-4" />
                    <span>Confirm Payment</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Resume Booking Modal */}
<Dialog open={isResumeModalOpen} onOpenChange={setIsResumeModalOpen}>
  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
    <DialogHeader>
      <DialogTitle className="flex items-center space-x-2">
        <Play className="w-5 h-5 text-green-600" />
        <span>Resume Booking</span>
      </DialogTitle>
      <DialogDescription>
        Pick a new time slot for this paused booking
      </DialogDescription>
    </DialogHeader>

    {resumeLog && (
      <div className="space-y-4">
        {/* Booking Info Card */}
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="font-medium text-purple-900">Player:</span>
              <span className="text-purple-700">{resumeLog.player_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-purple-900">OR Number:</span>
              <span className="text-purple-700">{resumeLog.or_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-purple-900">Original Bay:</span>
              <span className="text-purple-700">Bay {resumeLog.bay_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-purple-900">Remaining Time:</span>
              <span className="text-green-700 font-bold">
                {resumeLog.remaining_ms_at_pause 
                  ? `${Math.floor(resumeLog.remaining_ms_at_pause / 60000)} minutes`
                  : "Unknown"}
              </span>
            </div>
          </div>
        </div>

            {/* Time Slot Picker for Resume */}
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4">
              <h4 className="font-bold text-emerald-700 mb-3 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Select New Time Slot
              </h4>
              
              <TimeSlotPicker
                newPlayerLog={resumePlayerLog}
                setNewPlayerLog={setResumePlayerLog}
                durationMinutes={
                  resumeLog.remaining_ms_at_pause 
                    ? Math.ceil(resumeLog.remaining_ms_at_pause / 60000)
                    : 60
                }
                isTimeSlotAvailable={isTimeSlotAvailable}
                bayNumberError={resumeBayNumberError}
                selectedMember={{
                  id: resumeLog.member_id,
                  name: resumeLog.player_name,
                  type: "member",
                }}
                orNumber={resumeLog.or_number}
                toast={toast}
                manualTime={resumeManualTime}
                setManualTime={setResumeManualTime}
                activePlayerLogs={activePlayerLogs}
                transactions={transactions}
                products={products}
                getAvailableBaysForTime={getAvailableBaysForTime}
              // <-- resume helpers so the picker can detect the paused booking
                isResume={true}
                resumeOriginalBay={String(resumeLog.bay_number || "")}
                resumePlayerName={String(resumeLog.player_name || "")}
                onReservationStatsChange={setDrReservationStats}
              />
            </div>

            {/* Validation Messages */}
            {(!resumePlayerLog.bay_number || !resumePlayerLog.time_in) && (
              <Alert className="border-yellow-500 bg-yellow-50">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <AlertDescription className="text-yellow-700">
                  Please complete both steps to resume the booking
                </AlertDescription>
              </Alert>
            )}

            {resumeBayNumberError && (
              <Alert className="border-red-500 bg-red-50">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  {resumeBayNumberError}
                </AlertDescription>
              </Alert>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsResumeModalOpen(false);
                  setResumeLog(null);
                  setResumePlayerLog({
                    bay_number: "",
                    time_in: "",
                    time_out: "",
                    ui_label_start: "",
                    ui_label_end: "",
                    ui_locked: false,
                  });
                  setResumeManualTime("");
                  setResumeBayNumberError("");
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              
                <Button
                  className="bg-green-500 hover:bg-green-600 text-white"
                  disabled={
                    // require player name as well
                    !resumePlayerLog.player_name ||
                    !String(resumePlayerLog.player_name).trim() ||
                    !resumePlayerLog.bay_number ||
                    !resumePlayerLog.time_in ||
                    !resumePlayerLog.time_out ||
                    !resumePlayerLog.ui_locked
                  }
                  onClick={async () => {
                    try {
                      // require player name before proceeding
                      if (!resumePlayerLog.player_name || !String(resumePlayerLog.player_name).trim()) {
                        setResumeBayNumberError("Player name required for resume.");
                        return;
                      }

                      // Final validation
                      const remainingDuration = resumeLog.remaining_ms_at_pause 
                        ? Math.ceil(resumeLog.remaining_ms_at_pause / 60000)
                        : 60;

                      if (!isTimeSlotAvailable(
                        resumePlayerLog.bay_number,
                        resumePlayerLog.time_in,
                        remainingDuration
                      )) { 
                        setResumeBayNumberError("Selected time slot is no longer available");
                        return;
                      }

                      console.log("🚀 Sending resume request:", {
                        bay_number: resumePlayerLog.bay_number,
                        time_in: resumePlayerLog.time_in,
                        time_out: resumePlayerLog.time_out,
                      });

                      // Send resume request with new booking details
                      const res = await fetch(
                        `${BACKEND_URL}/api/player-logs/resume/${resumeLog.id}`,
                        {
                          method: "POST",
                          headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            bay_number: resumePlayerLog.bay_number,
                            time_in: resumePlayerLog.time_in,
                            time_out: resumePlayerLog.time_out,
                            remaining_duration_ms: resumeLog.remaining_ms_at_pause,
                          }),
                        }
                      );

                      if (res.ok) {
                        const result = await res.json();
                        console.log("✅ Resume successful:", result);
                        
                        toast({
                          title: "Booking Resumed Successfully",
                          description: `Bay ${resumePlayerLog.bay_number} • ${resumePlayerLog.ui_label_start} - ${resumePlayerLog.ui_label_end}`,
                        });

                        // ✅ IMPORTANT: Refresh both active and paused logs
                        await Promise.all([
                          fetchPlayerLogs(),
                          fetchPausedPlayerLogs()
                        ]);
                        
                        // ✅ Also refresh transactions to ensure consistency
                        await fetchTransactions();
                        
                        // Close modal and reset form
                        setIsResumeModalOpen(false);
                        setResumeLog(null);
                        setResumePlayerLog({
                          bay_number: "",
                          time_in: "",
                          time_out: "",
                          ui_label_start: "",
                          ui_label_end: "",
                          ui_locked: false,
                        });
                        setResumeManualTime("");
                        setResumeBayNumberError("");
                      } else {
                        const error = await res.json();
                        console.error("❌ Resume failed:", error);
                        toast({
                          title: "Resume Failed",
                          description: error.detail || "Failed to resume booking",
                          variant: "destructive",
                        });
                      }
                    } catch (error) {
                      console.error("❌ Error resuming log:", error);
                      toast({
                        title: "Error",
                        description: "Failed to resume booking",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Confirm Resume
                </Button> 
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>


    {/* ====== RESCHEDULE BOOKING MODAL ====== */}
    <Dialog open={isRescheduleModalOpen} onOpenChange={setIsRescheduleModalOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <RefreshCw className="w-5 h-5 text-indigo-600" />
            <span>Reschedule Booking</span>
          </DialogTitle>
          <DialogDescription>
            Move this booking to a new time slot
            {rescheduleLog?.reschedule_count > 0 && (
              <span className="ml-2 text-xs text-amber-600 font-semibold">
                (Rescheduled {rescheduleLog.reschedule_count} time{rescheduleLog.reschedule_count > 1 ? "s" : ""} before)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {rescheduleLog && (
          <div className="space-y-3">
            {/* Current Booking Info Card */}
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <h4 className="text-xs font-bold text-indigo-800 mb-1.5">Current Booking</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="font-medium text-indigo-900">Player:</span>
                  <span className="text-indigo-700 truncate ml-2">{rescheduleLog.player_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-indigo-900">OR #:</span>
                  <span className="text-indigo-700">{rescheduleLog.or_number || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-indigo-900">Service:</span>
                  <span className="text-indigo-700 truncate ml-2">{rescheduleLog.service_type}</span>
                </div>
                {rescheduleLog.service_type === "Driving Range Rates" && (
                  <div className="flex justify-between">
                    <span className="font-medium text-indigo-900">Bay:</span>
                    <span className="text-indigo-700">Bay {rescheduleLog.bay_number}</span>
                  </div>
                )}
                {rescheduleLog.service_type === "Golf Rates" && rescheduleLog.course_type && (
                  <div className="flex justify-between">
                    <span className="font-medium text-indigo-900">Course:</span>
                    <span className="text-indigo-700">{rescheduleLog.course_type}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium text-indigo-900">Time:</span>
                  <span className="text-indigo-700">
                    {rescheduleLog.time_in
                      ? new Date(rescheduleLog.time_in).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true })
                      : "—"
                    }
                    {" → "}
                    {rescheduleLog.time_out
                      ? new Date(rescheduleLog.time_out).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true })
                      : "—"
                    }
                  </span>
                </div>
                {/* Remaining / Elapsed time for ongoing bookings */}
                {rescheduleLog.time_in && rescheduleLog.time_out && (() => {
                  const originalMs = new Date(rescheduleLog.time_out) - new Date(rescheduleLog.time_in);
                  const elapsedMs = Math.max(0, Date.now() - new Date(rescheduleLog.time_in).getTime());
                  const remainingMs = Math.max(0, originalMs - elapsedMs);
                  const isOngoing = elapsedMs > 0 && remainingMs > 0;
                  const elapsedMin = Math.floor(elapsedMs / 60000);
                  const remainingMin = Math.max(Math.ceil(remainingMs / 60000), 0);
                  const originalMin = Math.round(originalMs / 60000);
                  const pct = Math.min(100, Math.round((elapsedMs / originalMs) * 100));
                  return isOngoing ? (
                    <div className="col-span-2 mt-1 p-2 bg-white/70 rounded border border-indigo-200">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-indigo-900 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Duration
                        </span>
                        <span className="text-indigo-700">
                          {elapsedMin}m elapsed / {originalMin}m total
                        </span>
                      </div>
                      <div className="w-full bg-indigo-200 rounded-full h-1.5 mb-1">
                        <div
                          className="bg-indigo-600 h-1.5 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-indigo-500">{pct}% elapsed</span>
                        <span className="font-bold text-green-700 text-xs">
                          {remainingMin} min remaining
                        </span>
                      </div>
                    </div>
                  ) : !isOngoing && elapsedMs === 0 ? (
                    <div className="col-span-2 mt-1">
                      <span className="text-xs text-gray-500 italic">Not started yet — full {originalMin} min duration</span>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>

            {/* New Time Slot Picker */}
            <div className="rounded-lg border border-indigo-300 bg-indigo-50/50 p-3">
              <h4 className="font-semibold text-sm text-indigo-700 mb-2 flex items-center">
                <Clock className="w-3.5 h-3.5 mr-1.5" />
                Select New Time Slot
              </h4>

              {rescheduleLog.service_type === "Driving Range Rates" ? (
                <TimeSlotPicker
                  newPlayerLog={reschedulePlayerLog}
                  setNewPlayerLog={setReschedulePlayerLog}
                  durationMinutes={(() => {
                    // Smart remaining time: use remaining duration instead of original
                    if (rescheduleLog.time_in && rescheduleLog.time_out) {
                      const originalDuration = new Date(rescheduleLog.time_out) - new Date(rescheduleLog.time_in);
                      const elapsed = Math.max(0, Date.now() - new Date(rescheduleLog.time_in).getTime());
                      const remaining = originalDuration - elapsed;
                      // At least 10 minutes, round up to nearest minute
                      return Math.max(Math.ceil(remaining / 60000), 10);
                    }
                    return 60; // default
                  })()}
                  isTimeSlotAvailable={isTimeSlotAvailable}
                  bayNumberError={rescheduleBayNumberError}
                  selectedMember={{
                    id: rescheduleLog.member_id,
                    name: rescheduleLog.player_name,
                    type: "member",
                  }}
                  orNumber={rescheduleLog.or_number}
                  toast={toast}
                  manualTime={rescheduleManualTime}
                  setManualTime={setRescheduleManualTime}
                  activePlayerLogs={activePlayerLogs}
                  transactions={transactions}
                  products={products}
                  getAvailableBaysForTime={getAvailableBaysForTime}
                  isResume={false}
                  onReservationStatsChange={setDrReservationStats}
                  isReschedule={true}
                  rescheduleExcludeId={rescheduleLog.id}
                />
              ) : (
                /* Golf: simple time inputs since golf doesn't have bay constraints */
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Tee Time</label>
                    <input
                      type="time"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={reschedulePlayerLog.ui_label_start || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        const [h, m] = val.split(":").map(Number);
                        const now = new Date();
                        const dt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
                        // compute duration from original booking
                        let durationMs = 4 * 60 * 60 * 1000; // default 4h for golf
                        if (rescheduleLog.time_in && rescheduleLog.time_out) {
                          durationMs = new Date(rescheduleLog.time_out) - new Date(rescheduleLog.time_in);
                        }
                        const endDt = new Date(dt.getTime() + durationMs);
                        // Manila timezone offset
                        const manilaOffset = "+08:00";
                        const toISO = (d) => {
                          const year = d.getFullYear();
                          const mon = String(d.getMonth() + 1).padStart(2, "0");
                          const day = String(d.getDate()).padStart(2, "0");
                          const hr = String(d.getHours()).padStart(2, "0");
                          const min = String(d.getMinutes()).padStart(2, "0");
                          const sec = String(d.getSeconds()).padStart(2, "0");
                          return `${year}-${mon}-${day}T${hr}:${min}:${sec}${manilaOffset}`;
                        };
                        setReschedulePlayerLog(prev => ({
                          ...prev,
                          time_in: toISO(dt),
                          time_out: toISO(endDt),
                          ui_label_start: val,
                          ui_label_end: `${String(endDt.getHours()).padStart(2, "0")}:${String(endDt.getMinutes()).padStart(2, "0")}`,
                          ui_locked: true,
                        }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New End Time</label>
                    <input
                      type="time"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
                      value={reschedulePlayerLog.ui_label_end || ""}
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">End time auto-calculated from original duration</p>
                  </div>
                </div>
              )}
            </div>

            {/* Reason for rescheduling */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Reason <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                rows={1}
                placeholder="e.g., Player requested time change, weather delay..."
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
              />
            </div>

            {/* Validation Messages */}
            {rescheduleBayNumberError && (
              <div className="p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs text-red-700 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {rescheduleBayNumberError}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => {
                  setIsRescheduleModalOpen(false);
                  setRescheduleLog(null);
                  setReschedulePlayerLog({ bay_number: "", time_in: "", time_out: "", ui_label_start: "", ui_label_end: "", ui_locked: false });
                  setRescheduleManualTime("");
                  setRescheduleBayNumberError("");
                  setRescheduleReason("");
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>

              <Button
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
                disabled={
                  !reschedulePlayerLog.time_in ||
                  !reschedulePlayerLog.time_out ||
                  (rescheduleLog.service_type === "Driving Range Rates" && !reschedulePlayerLog.bay_number)
                }
                onClick={async () => {
                  try {
                    setRescheduleBayNumberError("");

                    // For DR, validate bay availability
                    if (rescheduleLog.service_type === "Driving Range Rates") {
                      const durationMs = rescheduleLog.time_in && rescheduleLog.time_out
                        ? new Date(rescheduleLog.time_out) - new Date(rescheduleLog.time_in)
                        : 60 * 60000;
                      const durationMins = Math.ceil(durationMs / 60000);
                      if (typeof isTimeSlotAvailable === "function" && !isTimeSlotAvailable(
                        reschedulePlayerLog.bay_number,
                        reschedulePlayerLog.time_in,
                        durationMins,
                        rescheduleLog.id // exclude current log from conflict check
                      )) {
                        setRescheduleBayNumberError("Selected time slot is not available for this bay");
                        return;
                      }
                    }

                    const payload = {
                      time_in: reschedulePlayerLog.time_in,
                      time_out: reschedulePlayerLog.time_out,
                      reason: rescheduleReason,
                    };
                    if (rescheduleLog.service_type === "Driving Range Rates" && reschedulePlayerLog.bay_number) {
                      payload.bay_number = reschedulePlayerLog.bay_number;
                    }

                    console.log("📅 Sending reschedule request:", payload);

                    const res = await fetch(
                      `${BACKEND_URL}/api/player-logs/reschedule/${rescheduleLog.id}`,
                      {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                      }
                    );

                    if (res.ok) {
                      const result = await res.json();
                      console.log("✅ Reschedule successful:", result);

                      toast({
                        title: "Booking Rescheduled",
                        description: `${rescheduleLog.player_name} → ${reschedulePlayerLog.ui_label_start} - ${reschedulePlayerLog.ui_label_end}${
                          rescheduleLog.service_type === "Driving Range Rates" ? ` • Bay ${reschedulePlayerLog.bay_number}` : ""
                        }`,
                      });

                      // Refresh logs
                      await fetchPlayerLogs();

                      // Close modal
                      setIsRescheduleModalOpen(false);
                      setRescheduleLog(null);
                      setReschedulePlayerLog({ bay_number: "", time_in: "", time_out: "", ui_label_start: "", ui_label_end: "", ui_locked: false });
                      setRescheduleManualTime("");
                      setRescheduleBayNumberError("");
                      setRescheduleReason("");
                    } else {
                      const error = await res.json();
                      console.error("❌ Reschedule failed:", error);
                      setRescheduleBayNumberError(error.detail || "Failed to reschedule booking");
                    }
                  } catch (error) {
                    console.error("❌ Error rescheduling:", error);
                    toast({
                      title: "Error",
                      description: "Failed to reschedule booking",
                      variant: "destructive",
                    });
                  }
                }}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Confirm Reschedule
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>


    {/* End Golf Session Confirmation Dialog */}
<Dialog
  open={!!endGolfConfirmLog}
  onOpenChange={(open) => {
    if (!open) setEndGolfConfirmLog(null);
  }}
>
  <DialogContent className="max-w-md">
    <DialogHeader>
      <DialogTitle>Confirm End Session</DialogTitle>
      <DialogDescription>
        This will end the golf session and mark the booking as finished.
      </DialogDescription>
    </DialogHeader>

    {endGolfConfirmLog && (
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 rounded border">
          <div className="text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Player</span>
              <span className="font-medium">{endGolfConfirmLog.player_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">OR Number</span>
              <span className="font-medium">{endGolfConfirmLog.or_number || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Bay / Course</span>
              <span className="font-medium">{endGolfConfirmLog.bay_number || endGolfConfirmLog.total_holes + "H"}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => setEndGolfConfirmLog(null)}
          >
            Cancel
          </Button>

          <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            onClick={async () => {
              try {
                // pass the whole log object to existing handler
                await handleEndGolfSession(endGolfConfirmLog);
              } catch (e) {
                console.error("Error ending session:", e);
              } finally {
                setEndGolfConfirmLog(null);
              }
            }}
          >
            Confirm End
          </Button>
        </div>
      </div>
    )}
  </DialogContent>
</Dialog>


  {/* End Driving Range Session Confirmation Dialog */}
  <Dialog
    open={!!endDrConfirmLog}
    onOpenChange={(open) => {
      if (!open) setEndDrConfirmLog(null);
    }}
  >
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Confirm End Session</DialogTitle>
        <DialogDescription>
          This will end the driving range session and mark the booking as finished.
        </DialogDescription>
      </DialogHeader>

      {endDrConfirmLog && (
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded border">
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Player</span>
                <span className="font-medium">{endDrConfirmLog.player_name || endDrConfirmLog.member_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">OR Number</span>
                <span className="font-medium">{endDrConfirmLog.or_number || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Bay</span>
                <span className="font-medium">{endDrConfirmLog.bay_number ? `B${endDrConfirmLog.bay_number}` : "-"}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setEndDrConfirmLog(null)}
            >
              Cancel
            </Button>

            <Button
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={async () => {
                try {
                  await handleEndDrSession(endDrConfirmLog);
                } catch (e) {
                  console.error("Error ending DR session:", e);
                } finally {
                  setEndDrConfirmLog(null);
                }
              }}
            >
              Confirm End
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>

  {/* Delete Player Log Confirmation Dialog */}
  <Dialog
    open={!!deletePlayerLogConfirm}
    onOpenChange={(open) => {
      if (!open && !isDeletingPlayerLog) {
        closeDeletePlayerLogDialog();
      }
    }}
  >
    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-700">
          <Trash2 className="w-5 h-5" />
          Delete Booking
        </DialogTitle>
        <DialogDescription>
          Review the created transaction and player logs below. This cannot be undone.
        </DialogDescription>
      </DialogHeader>

      {deletePlayerLogConfirm && (() => {
        const clicked = deletePlayerLogConfirm;
        const pool = [...(activePlayerLogs || []), ...(pausedPlayerLogs || [])];
        const seen = new Set();
        const localRelated = [];
        pool.forEach((item) => {
          if (!item?.id || seen.has(item.id)) return;
          const sameTxn = clicked.transaction_id && item.transaction_id && String(item.transaction_id) === String(clicked.transaction_id);
          const sameOr = clicked.or_number && item.or_number && String(item.or_number) === String(clicked.or_number);
          if (sameTxn || sameOr) {
            seen.add(item.id);
            localRelated.push(item);
          }
        });
        if (!seen.has(clicked.id)) localRelated.unshift(clicked);

        const relatedLogs = (deletePreview?.related_logs?.length ? deletePreview.related_logs : localRelated);
        const logsCount = relatedLogs.length;
        const txn = deletePreview?.transaction || null;
        const canDeleteTxn = isAdminUser
          ? !!txn
          : (deletePreview
              ? !!deletePreview.can_delete_transaction
              : !!(clicked.transaction_id || clicked.or_number));
        const willDeleteTxn = isAdminUser ? (deleteAlsoTransaction && !!txn) : canDeleteTxn;
        const willDeleteLogsCount = willDeleteTxn ? logsCount : 1;

        return (
        <div className="space-y-4">
          <div className="p-3 rounded-lg border border-red-200 bg-red-50">
            <p className="text-sm font-semibold text-red-800">
              {willDeleteLogsCount} player log{willDeleteLogsCount === 1 ? "" : "s"}
              {willDeleteTxn ? " and the created transaction" : ""} will be deleted.
            </p>
            <p className="text-xs text-red-700 mt-1">
              {willDeleteTxn
                ? "The sales record created for this booking will be removed together with every player on it."
                : "Only this player log will be removed. The sales transaction will stay."}
            </p>
          </div>

          <div className="p-3 bg-red-50 rounded border border-red-200">
            <p className="text-xs font-semibold text-red-700 mb-2 uppercase tracking-wide">
              Player Logs ({logsCount} {logsCount === 1 ? "item" : "items"})
            </p>
            <div className="space-y-2">
              {relatedLogs.map((row, idx) => (
                <div
                  key={row.id || idx}
                  className={`rounded-md border px-2 py-2 text-sm ${row.id === clicked.id ? "border-red-300 bg-white" : "border-red-100 bg-white/70"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-900">
                      {row.player_name || row.member_name || "Player"}
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-red-600">
                      {row.is_guest ? "Guest" : "Main"}
                      {row.id === clicked.id ? " · selected" : ""}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
                    <span>OR: {row.or_number || "—"}</span>
                    <span>{row.service_type === "Golf Rates" ? "Golf" : (row.bay_number ? `Bay ${row.bay_number}` : (row.service_type || "—"))}</span>
                    {row.group_name ? <span>Group: {row.group_name}</span> : null}
                    {row.punched_by_name ? <span>Punched by: {row.punched_by_name}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {isLoadingDeletePreview && (
            <div className="flex items-center gap-2 text-sm text-gray-500 p-3">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Loading transaction details...
            </div>
          )}

          {txn && (
            <div className="p-3 bg-orange-50 rounded border border-orange-200">
              <p className="text-xs font-semibold text-orange-700 mb-2 uppercase tracking-wide">
                Transaction Created{willDeleteTxn ? " — will be deleted" : ""}
              </p>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">OR Number</span>
                  <span className="font-medium">{txn.or_number || "—"}</span>
                </div>
                {(txn.created_by || txn.cashier) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created by</span>
                    <span className="font-medium">{txn.created_by || txn.cashier}</span>
                  </div>
                )}
                {txn.member_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Member</span>
                    <span className="font-medium">{txn.member_name}</span>
                  </div>
                )}
                {txn.timestamp && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created at</span>
                    <span className="font-medium">{formatDeleteWhen(txn.timestamp)}</span>
                  </div>
                )}
                {txn.payment_method && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment</span>
                    <span className="font-medium">{txn.payment_method}</span>
                  </div>
                )}
                {(txn.items || []).length > 0 && (
                  <div className="mt-2 space-y-1">
                    <span className="text-gray-600 text-xs font-medium">
                      Items ({txn.items_count || txn.items.length})
                    </span>
                    {txn.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between pl-2 text-xs">
                        <span className="text-gray-700">{item.quantity || 1}× {item.product_name || item.name || "Item"}</span>
                        <span className="font-medium">{formatDeleteMoney(item.price)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between border-t border-orange-200 pt-1 mt-1">
                  <span className="text-gray-700 font-semibold">Total</span>
                  <span className="font-bold text-orange-800">{formatDeleteMoney(txn.total)}</span>
                </div>
              </div>
            </div>
          )}

          {isAdminUser && txn && (
            <label className="flex items-start gap-2 p-2 rounded bg-yellow-50 border border-yellow-300 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5 accent-red-600"
                checked={deleteAlsoTransaction}
                onChange={(e) => setDeleteAlsoTransaction(e.target.checked)}
              />
              <span className="text-sm text-yellow-900">
                <strong>Also delete the created transaction</strong>
                <br />
                <span className="text-xs text-yellow-700">
                  Removes OR #{txn.or_number || "N/A"} ({formatDeleteMoney(txn.total)}) and all {logsCount} player log{logsCount === 1 ? "" : "s"} from this booking.
                </span>
              </span>
            </label>
          )}

          {!isLoadingDeletePreview && deletePreview && !txn && (
            <div className="text-sm text-gray-500 italic p-2">
              No associated transaction found in sales report. Only the selected player log will be deleted.
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              disabled={isDeletingPlayerLog}
              onClick={closeDeletePlayerLogDialog}
            >
              Cancel
            </Button>

            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isDeletingPlayerLog}
              onClick={async () => {
                const shouldDeleteTxn = isAdminUser
                  ? (deleteAlsoTransaction && !!txn)
                  : !!deletePreview?.can_delete_transaction;
                await handleDeletePlayerLog(clicked, shouldDeleteTxn);
              }}
            >
              {isDeletingPlayerLog ? (
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              {isDeletingPlayerLog
                ? "Deleting..."
                : willDeleteTxn
                  ? `Delete ${willDeleteLogsCount} log${willDeleteLogsCount === 1 ? "" : "s"} & transaction`
                  : "Delete Log"}
            </Button>
          </div>
        </div>
        );
      })()}
    </DialogContent>
  </Dialog>

  {/* ====== PRINTABLE RECEIPT MODAL ====== */}
  <Dialog
    open={showReceiptModal}
    onOpenChange={(open) => {
      setShowReceiptModal(open);
      if (!open) setReceiptData(null);
    }}
  >
    <DialogContent className="max-w-md bg-white p-0 overflow-hidden receipt-dialog">
      <DialogHeader className="px-6 pt-5 pb-2 receipt-header-hide">
        <DialogTitle className="flex items-center gap-2 text-base">
          <Printer className="w-4 h-4" />
          Transaction Receipt
        </DialogTitle>
        <DialogDescription className="text-xs">
          Transaction closed successfully. Print or close this receipt.
        </DialogDescription>
      </DialogHeader>

      {receiptData && (() => {
        const items = Array.isArray(receiptData.items) ? receiptData.items : [];
        const txnSubtotal = Number(receiptData.subtotal || 0);
        const txnDiscount = Number(receiptData.discount_amount || receiptData.discount || 0);
        const txnTotal = Number(receiptData.total_amount || 0);
        const hasDiscount = txnDiscount > 0;
        const itemDiscs = receiptData.item_discounts || {};
        const discKeys = Object.keys(itemDiscs);
        const discType = receiptData.id_type || receiptData.discount_type || (discKeys[0] && itemDiscs[discKeys[0]]?.type) || "";
        const cardTypeLabels = {
          "1": "Master Card/Visa/BancNet",
          "2": "JCB",
          "3": "BDO Union Pay",
          "4": "Diners Club International",
          "5": "American Express",
          "6": "Discover",
        };

        return (
          <div className="px-4 pb-4">
            {/* Printable receipt area */}
            <div id="receipt-print-area" className="receipt-paper bg-white mx-auto" style={{ width: "302px", fontFamily: "'Courier New', Courier, monospace", fontSize: "12px", lineHeight: "1.4", padding: "12px 8px" }}>
              {/* Logo & Header */}
              <div style={{ textAlign: "center", marginBottom: "8px" }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_clubhouse-pos/artifacts/2tien3l0_IMG_0267.png"
                  alt="Pili Grove Golf Club"
                  style={{ width: "60px", height: "60px", objectFit: "contain", margin: "0 auto 4px", display: "block", borderRadius: "50%" }}
                />
                <div style={{ fontWeight: "bold", fontSize: "14px", letterSpacing: "1px" }}>PILI GROVE</div>
                <div style={{ fontWeight: "bold", fontSize: "14px", letterSpacing: "1px" }}>GOLF CLUB</div>
                <div style={{ fontSize: "10px", color: "#666", marginTop: "2px" }}>Pili, Camarines Sur</div>
              </div>

              {/* Dashed separator */}
              <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }}></div>

              {/* OR Number & Date */}
              <div style={{ textAlign: "center", marginBottom: "4px" }}>
                <div style={{ fontWeight: "bold", fontSize: "14px" }}>OR #{receiptData.or_number || "—"}</div>
                <div style={{ fontSize: "10px", color: "#555" }}>
                  {receiptData.timestamp
                    ? (() => {
                        let ts = String(receiptData.timestamp);
                        if (!ts.endsWith("Z") && !ts.includes("+")) ts += "Z";
                        return new Date(ts).toLocaleString("en-PH", {
                          timeZone: "Asia/Manila",
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        });
                      })()
                    : "—"}
                </div>
              </div>

              {/* Customer & Cashier */}
              <div style={{ fontSize: "11px", marginBottom: "2px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Customer:</span>
                  <span style={{ fontWeight: "bold", maxWidth: "180px", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{receiptData.member_name || "Walk-in"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Cashier:</span>
                  <span style={{ fontWeight: "bold" }}>{receiptData.closed_by_cashier_name || receiptData.cashier || "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Payment:</span>
                  <span style={{ fontWeight: "bold", textTransform: "capitalize" }}>{receiptData.payment_method || "—"}</span>
                </div>
              </div>

              {/* Card/Online payment details */}
              {receiptData.payment_method === "card" && (
                <div style={{ fontSize: "10px", color: "#555", marginBottom: "2px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Card Type:</span>
                    <span>{cardTypeLabels[String(receiptData.cardtype_id || "")] || receiptData.cardtype_id || "—"}</span>
                  </div>
                </div>
              )}
              {receiptData.payment_method === "online" && (
                <div style={{ fontSize: "10px", color: "#555", marginBottom: "2px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Gateway:</span>
                    <span style={{ textTransform: "uppercase" }}>{receiptData.payment_gateway || "—"}</span>
                  </div>
                  {receiptData.account_number && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Acct #:</span>
                      <span>{receiptData.account_number}</span>
                    </div>
                  )}
                  {receiptData.transaction_number && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Txn #:</span>
                      <span>{receiptData.transaction_number}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Dashed separator */}
              <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }}></div>

              {/* Items header */}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "11px", marginBottom: "4px", borderBottom: "1px solid #ccc", paddingBottom: "2px" }}>
                <span style={{ flex: "1" }}>ITEM</span>
                <span style={{ width: "30px", textAlign: "center" }}>QTY</span>
                <span style={{ width: "65px", textAlign: "right" }}>PRICE</span>
                <span style={{ width: "70px", textAlign: "right" }}>TOTAL</span>
              </div>

              {/* Items */}
              {items.map((it, idx) => {
                const qty = Number(it?.quantity || 1) || 1;
                const price = Number(it?.unit_price ?? it?.price ?? 0) || 0;
                const line = price * qty;
                const pid = it?.product_id || it?.id;
                let discApplied = null;
                for (let inst = 0; inst < qty; inst++) {
                  const key = `${pid}-${inst}`;
                  if (itemDiscs[key]) { discApplied = itemDiscs[key]; break; }
                }
                return (
                  <div key={idx} style={{ marginBottom: "3px", fontSize: "11px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <span style={{ flex: "1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "4px" }}>{it?.product_name || it?.name || "Item"}</span>
                      <span style={{ width: "30px", textAlign: "center" }}>{qty}</span>
                      <span style={{ width: "65px", textAlign: "right" }}>{price.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                      <span style={{ width: "70px", textAlign: "right", fontWeight: "bold" }}>{line.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                    </div>
                    {discApplied && (
                      <div style={{ fontSize: "9px", color: "#c00", paddingLeft: "4px" }}>
                        {discApplied.type === "pwd" ? "PWD" : discApplied.type === "senior" ? "Senior" : discApplied.type} Disc.
                        {discApplied.customerName ? ` (${discApplied.customerName})` : ""}
                        {discApplied.idNumber ? ` ID:${discApplied.idNumber}` : ""}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Dashed separator */}
              <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }}></div>

              {/* Totals */}
              <div style={{ fontSize: "11px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Subtotal:</span>
                  <span>&#8369;{(hasDiscount ? txnSubtotal : txnTotal).toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                </div>
                {hasDiscount && (
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#c00" }}>
                    <span>
                      Discount{discType === "pwd" ? " (PWD 20%)" : discType === "senior" ? " (Senior 20%)" : ""}:
                    </span>
                    <span>-&#8369;{txnDiscount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>

              {/* Double line separator */}
              <div style={{ borderTop: "2px double #000", margin: "4px 0" }}></div>

              {/* Grand Total */}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "16px", marginBottom: "4px" }}>
                <span>TOTAL:</span>
                <span>&#8369;{txnTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}</span>
              </div>

              {/* Dashed separator */}
              <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }}></div>

              {/* Service availed */}
              {receiptData.selected_service?.name && (
                <>
                  <div style={{ fontSize: "10px", color: "#555", marginBottom: "2px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontWeight: "bold" }}>Service:</span>
                      <span>{receiptData.selected_service.name}</span>
                    </div>
                  </div>
                  <div style={{ borderTop: "1px dashed #000", margin: "6px 0" }}></div>
                </>
              )}

              {/* Footer */}
              <div style={{ textAlign: "center", fontSize: "11px", marginTop: "4px" }}>
                <div style={{ fontWeight: "bold" }}>Thank you for visiting!</div>
                <div style={{ fontSize: "9px", color: "#888", marginTop: "2px" }}>Pili Grove Golf Club</div>
              </div>

              {/* Cut line */}
              <div style={{ borderTop: "1px dashed #ccc", margin: "8px 0 0 0" }}></div>
            </div>

            {/* Action Buttons (hidden when printing) */}
            <div className="receipt-actions flex gap-2 mt-4 justify-center">
              <Button
                onClick={() => {
                  window.print();
                }}
                className="bg-emerald-500 hover:bg-emerald-600 text-white h-9 px-5 text-sm"
              >
                <Printer className="w-4 h-4 mr-1.5" />
                Print Receipt
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowReceiptModal(false);
                  setReceiptData(null);
                }}
                className="h-9 px-5 text-sm"
              >
                Close
              </Button>
            </div>
          </div>
        );
      })()}
    </DialogContent>
  </Dialog>

  {/* Print-only CSS for receipt */}
  <style>{`
    @media print {
      /* Remove all top-level elements from flow so they generate no pages */
      body > * {
        display: none !important;
      }
      /* Re-show only the Radix portal that holds the dialog */
      body > [data-radix-portal] {
        display: block !important;
      }
      /* Hide overlay, close button, action buttons, dialog header */
      .receipt-actions,
      .receipt-header-hide,
      [data-radix-dialog-overlay],
      [role="dialog"] > button[class*="absolute"] {
        display: none !important;
      }
      /* Strip all dialog chrome so content flows naturally */
      .receipt-dialog,
      .receipt-dialog [role="dialog"],
      .receipt-dialog [data-radix-dialog-content] {
        all: unset !important;
        display: block !important;
        visibility: visible !important;
      }
      /* Position receipt at page origin */
      #receipt-print-area {
        display: block !important;
        visibility: visible !important;
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;
        width: 80mm !important;
        margin: 0 !important;
        padding: 4mm !important;
        background: white !important;
      }
      #receipt-print-area * {
        visibility: visible !important;
      }
      /* Ensure wrapper around receipt doesn't add space */
      #receipt-print-area ~ * {
        display: none !important;
      }
      html, body {
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      @page {
        size: 80mm auto;
        margin: 0;
      }
    }
  `}</style>

      <Toaster />
    </div> 
    
  );
}


export default App;
